import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { analyserFetchJSON } from '../shared/analyserApi';
import {
  enrollPasskey, fetchEntryStatus, fetchPasskeyStatus,
  manualClose, manualEntry, passkeySupported, saveLaneSettings,
  applyRetuneRow,
  fetchRetuneRunStatus,
  fetchLiqv2Ladder,
  fetchLiqV1Daily,
  fetchLiqKnifeDaily,
  fetchLiqV3Daily,
  fetchLiqUnifiedLedger,
  fetchLiqUnifiedDaily,
  fetchUpbitPbDaily,
  fetchBhPbDaily,
  fetchPumpRideDaily,
  fetchGyeopLongDaily,
  fetchUpbitRescueDaily,
  runRetuneJob,
  saveRetuneCohort,
} from '../shared/passkeyClose';
import type { PasskeyStatus, RetuneRunKind, RetuneRunFail, RetunePrefsLane, Liqv2Ladder, Liqv2LadderConfig, Liqv2LadderDay, LiqV1Daily, LiqV1DailyDay, LiqKnifeDaily, LiqKnifeDailyDay, LiqV3Daily, LiqV3DailyDay, LiqUnifiedLedger, LiqUnifiedDaily, UpbitPbDaily, UpbitPbDailyDay, UpbitPbSeasonBook, UpbitPbSeasonDay, UpbitRescueDaily, UpbitRescueDailyDay, BhPbDaily, Liqv2Catalyst, Liqv2Equity, PumpRideDaily, PumpRideDailyDay, GyeopLongDaily } from '../shared/passkeyClose';
import { ThreeLineStack } from '../shared/ThreeLineStack';
import { useDashboardLocale, useDashboardT } from '../shared/dashboardLocale';
import { binanceChartsPath, type DashboardMessageKey } from '../shared/dashboardMessages';
import { u, uDeep } from '../shared/hardcodedKoEn';
import { binanceH3, binanceMuted } from '../kiwoom/kiwoomTheme';
import {
  DASHBOARD_HIDDEN_LANE_KEYS,
  entryOutcomeLabel,
  filterDashboardHiddenLaneRows,
  filterEnteredOnlyRows,
  filterNeededOnlyRows,
  groupBinanceAlertRows,
  isIntakeSkip,
  liqDisplayTier,
  isLiveBhPending,
  isLiveClWatch,
  isLiveLiqWatch,
  isLivePrWatch,
  isLiveDsWatch,
  isPositionEntry,
  LIQ_WATCH_MIN_DEFAULT,
  mergeNeededOnlyLaneLive,
  PR_WATCH_MIN_DEFAULT,
  manualEntryTarget,
  mergePreChartLiveTail,
  pendingEntryHm,
  pathExitDetail,
  prSettleExitDetail,
  liqv2ExitDetail,
  glSettleExitDetail,
} from './binanceAlertRows';
import { alertTypeLabel } from './binanceAlertTypeLabels';
import { BinanceEntrySparkline } from './BinanceEntrySparkline';
import { BinanceEquityCurveChart } from './BinanceEquityCurveChart';
import { chartPointsWithExit, kstToMs, LONG_ENTRY_ACTIONS } from './chartPoints';
import { displayCoinName } from './upbitCoinNames';
import {
  formatKstIso,
  formatKstDate,
  formatRetuneElapsed,
  LIQ_SCHEDULE,
  type RetuneScheduleDef,
  UPBIT_PB_SCHEDULE,
  BH_PB_SCHEDULE,
  DUAL_SHORT_SCHEDULE,
  PUMP_RIDE_SCHEDULE,
  scheduleRowMeta,
} from './retuneSchedules';

/** 일일채점 구역 제목. 날짜 열은 가로로 밀려도 이 줄은 보이는 왼쪽에 남는다. */
function pinnedScoreTitle(
  colSpan: number,
  color: string,
  border: string | undefined,
  pad: string,
  children: React.ReactNode,
) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0, borderTop: border }}>
        <div style={{
          position: 'sticky', left: 0, zIndex: 2,
          width: '100cqi', boxSizing: 'border-box',
          padding: pad, color, fontSize: '0.64rem', fontWeight: 700,
          background: '#0f0f0f', whiteSpace: 'normal',
        }}>{children}</div>
      </td>
    </tr>
  );
}

export type BinanceShortLiveEvent = {
  time_kst: string;
  time_hm: string;
  chart?: [number, number][];
  /** 이 거래가 **실제로** 들고 있던 분수. 자동 재판정으로 보유가 사이클마다
   *  바뀌므로 cfg 값 하나로 모든 행을 그리면 옛 거래가 늘어나거나 잘린다.
   *  29분이면 29분, 47분이면 47분 -- 행마다 다르다. 없으면 cfg 로 폴백. */
  chart_window_min?: number;
  /** 청캐 WATCH · 업숏 진입대기 프리차트(어두운 선). */
  pre_chart?: [number, number][];
  pre_chart_px?: number;
  pre_chart_window_min?: number;
  /** 수동·외부청산 뒤 홀드 끝까지의 어두운 꼬리. */
  post_chart?: [number, number][];
  /** 진입 시점에 찍힌 보유(분, 진입 기준). 차트 가로축의 행별 출처. */
  hold_min?: number;
  entry_px?: number;
  stop_px?: number;
  type: string;
  action: string;
  /** 바이낸스 퍼프가 없는 심볼 — 백엔드가 **알림 시점에** 판정해 실어준다.
   *  "필요한 알림만" 이 이 플래그로 행을 통째로 접는다(거래 자체가 불가능하다).
   *  화면이 perp 부재로 추론하지 않는 이유: perp 를 원래 안 싣는 액션과 섞인다. */
  no_perp?: boolean;
  /** 감시 창 길이(분). 지났으면 u('감시중') 이 아니라 u('감시종료') 다. */
  watch_min?: number;
  /** 겹롱 대기(초). 15초를 분으로 내리면 창이 사라지거나 1분이 된다. */
  watch_sec?: number;
  /** 청캐 진입모드 (2026-09-04). "pullback" | "cascade_fade".
   *  WATCH(되돌림)/WATCH(fade) 라벨. liq_mode 전환 뒤엔 최신 `to` 가 온다. */
  entry_mode?: string;
  /** liq_mode 전환 (from→to). */
  from?: string;
  to?: string;
  /** liq_mode 이미 전환 SKIP. */
  skipped?: boolean;
  /** 같은 알림에서 나온 행을 잇는 열쇠(감시행 <-> 진입/스킵). */
  signal_id?: string;
  /** addon_short / skip_already_open 등 세부 모드 (linear_rung …). */
  mode?: string;
  /** 편승 청캐버스트 롱 등 보조 경로 표식 (liq_kr_skip). 없으면 KR 알림 편승. */
  source?: string;
  /** 편승 장부 키. liq_skip 행에 버스트롱이 붙었을 때 연결용. */
  pr_sid?: string;
  /** pump_ride / liq 등. 없으면 액션 접두로 레인 추론. */
  lane?: string;
  n_liq?: number;
  pr_burst_long?: boolean;
  symbol: string;
  perp: string;
  kr_name?: string;
  detail: string;
  leverage?: number;
  /** "upbit" | "bithumb" | "coinone" | "unknown" -- absent on pre-2026-07-25
   * events, which are all Upbit by construction (see signal_receiver.py's
   * normalize_exchange()). */
  exchange?: string;
  /** 쌍숏 부모 귀속 (2026-09-06). `upbit`|`bh` — Ex 칸은 트리거 거래소가
   *  아니라 이 값을 따른다(업숏 ON → 업). */
  attr_lane?: string;
  /** signal_receiver classify() -- segment_rise, new_low, ... */
  alert_type?: string;
  /** "bh_newhigh" -- 빗썸 신고가 슬리브 행. 업비트 레인과 절대 섞지 않는다.
   *  주문 자체는 다른 레인과 똑같이 바이낸스 USDT-M 퍼프 숏이고, 빗썸은
   *  신호 출처일 뿐이다. */
  sleeve?: string;
  /** Position size actually staked, and the risk budget it was sized under.
   * The conc0 and btc60 cohorts enter at a reduced risk_pct, so two rows from
   * the same day can carry very different notionals. */
  notional?: number;
  risk_pct?: number;
  /** 자본 대비 명목% (base+(rung−1)×step, 캡 적용). 청캐 v2·업숏·빗숏.
   *  있으면 사이징 칸은 risk_pct 대신 이걸 쓴다. */
  notional_pct?: number;
  ladder_rung?: number;
  ladder_cum_nliq?: number;
  /** Measured wallet cash on settle, not the FEE_RT estimate. Commission is a
   * near-flat 1.7-2.8 USDT per round trip whatever the size, so a small winner
   * keeps far less than its gross: KRW-SAFE realized +8.04 and deposited +6.35.
   * Absent on rows settled before this shipped, and on dry-run rows. */
  realized_usdt?: number;
  commission_usdt?: number;
  funding_usdt?: number;
  net_usdt?: number;
  /** false 면 청산 체결·펀딩이 아직 없어 net_usdt 는 확정이 아니다 (2026-10-15). */
  cash_complete?: boolean;
  /** settle 이벤트의 순수익률(비율). 차트 청산가 역산용. */
  net?: number;
  pnl_usdt?: number;
  /** abort 로 조기청산된 진입 행 표식 (백엔드 mark_aborted_entries). */
  aborted?: boolean;
  /** 사람이 화면 버튼으로 끊은 정산 (2026-08-17). liq_settle · pr_settle 에 붙는다. */
  manual?: boolean;
  /** 편승 pr_settle 탈출 사유 (soft_stop/trail/time/…). Detail 칸은 진입만
   *  읽으므로 exit 사유는 이 필드(+ prSettleExitDetail) 로 낸다. */
  reason?: string;
  /** 겹롱 gl_settle 청산 사유 문장. Detail 칸은 진입 문장을 읽는다. */
  exit_why?: string;
  /** 업숏·빗숏 path exit 사유 (2026-08-31). 라이브는 진작 실어 보내고 있었는데
   *  monitor 화이트리스트와 화면에 읽는 쪽이 없어 EXIT 행이 손익만 보였다
   *  (14:27 KRW-ZORA: H60 체크포인트 main 다리). pr_settle 의 reason 과 같은
   *  자리 -- Detail 칸은 진입만 읽으므로 exit 사유는 이 필드로 낸다. */
  path_exit?: {
    /** "main" | "early" | "late_surge" | "v4miss" | "grind"
     *  | "bh_path_reclaim" | "bh_path_stuck" */
    leg?: string | null;
    /** 진입 대비 최대 역행(%). 숏이라 양수가 불리다. */
    adv_pct?: number | null;
    /** 그 고점에서 되돌아온 비율(%). 작을수록 u("안 돌아온다")는 판정. */
    recovery_pct?: number | null;
    /** 고점이 몇 분 전이었나. */
    peak_age_min?: number | null;
    elapsed_min?: number | null;
    horizon_min?: number | null;
  };
  /** 편승 실보유 분수 (pr_settle). */
  held_min?: number;
  /** 청캐 v2 정산: 연장 여부·조각 수 (Detail 줄에 쓴다) */
  extended?: boolean;
  legs?: number;
  /** 청캐 v2 조각 번호 (1-based) — 각 SHORT 행 */
  leg?: number;
  /** 청캐 v2 조각 상한 (진입·정산 Detail 의 n/max) */
  max_legs?: number;
  /** 편승 최대 유리 (% , pr_settle). */
  mfe_pct?: number;
  /** u("안 끊었으면 얼마였나"). PnL 칸에 실현액 옆 괄호로 낸다 -- 실현액과 절대 같은
   *  색을 쓰지 않는다(보라 = 가정값). */
  abort_cf?: {
    actual_pct?: number | null;
    base_pct?: number | null;
    base_stopped?: boolean | null;
    delta_pct?: number | null;
    base_usdt?: number | null;
    /** "abort"(안 끊었으면) | "stop"(스탑 없었으면) -- 질문이 다르다. */
    kind?: string;
    /** 어느 다리가 끊었나 (analyser 8c8955b, 2026-08-05). "px" | "oi_early" |
     *  "px_cont" | "oi_cvd". kind="stop" 이거나 이 필드가 생기기 전 구행이면
     *  null -- null-safe 로 렌더해야 한다. 집계(abort_report)는 다리 무관 합산을
     *  그대로 주므로, 다리별로 쪼개려면 이 필드로 events 를 group-by 한다
     *  (docs/frontend-abort-counterfactual-api.md §4). */
    leg?: string | null;
    /** "upbit" | "bh_sleeve" -- base_pct 를 시뮬레이션한 보유 창이 레인마다
     *  다르다(업비트 30분 / 빗썸 슬리브 10분). PnL 칸 라벨이 맞는 분수를
     *  보여주려면 필요하다. */
    lane?: string | null;
    /** **이 행을 채점한 규칙**(2026-08-12). 채점된 행은 규칙이 바뀌어도 다시
     *  채점하지 않으므로, 라벨 분수를 지금 config 에서 뽑으면 30분 창에서 잰
     *  숫자에 "100분" 이 붙는다. 스탬프 이전 구행은 null -- 그때만 config 폴백. */
    rule_stop_pct?: number | null;
    rule_hold_exit_min?: number | null;
  };
  /** §5-F 스킵 반사실. u("이 알림에 진입했다면 얼마였나")를 수집된 markPrice@1s 로
   *  **그 시점에 배포돼 있던 규칙**(진입지연/보유/스탑)에 넣어 채점한 값. 규칙은
   *  자동 재판정으로 6시간마다 움직이므로 행마다 다를 수 있고, 쓰인 값은 rule_*
   *  에 같이 온다. **실제로 체결된 돈이 아니다** — PnL 칸에서 색을 달리 쓴다. */
  skip_pnl?: {
    net_pct: number | null;
    stopped?: boolean;
    /** "mark_tick"(1초 실측) | "kline_1m"(수집 이전 구간의 1분봉 대체) */
    source?: string;
    /** "merit" = 필터가 판단해서 거른 것 / "capacity" = 슬롯이 없었을 뿐 */
    bucket?: string;
    skip_reason?: string;
    /** 어느 레인의 판정인가. upbit·bh_sleeve = 배포 라인, bithumb = 리서치 재생.
     *  리서치 값을 실제 게이트 판정으로 오독하지 않도록 툴팁에 같이 낸다. */
    exchange?: string;
    /** **이 행을 채점한 규칙**. abort_cf 의 같은 필드와 같은 이유다 -- 라벨은
     *  지금 config 가 아니라 이 값으로 그려야 숫자와 문구가 같은 규칙을 말한다.
     *  스탬프 이전 구행은 null. */
    rule_stop_pct?: number | null;
    rule_entry_delay_min?: number | null;
    rule_hold_exit_min?: number | null;
    rule_pullback?: { watch_min?: number; hold_after_min?: number } | null;
  };
};

export type BinanceShortLiveOpen = {
  /** 수동 청산 버튼이 지목하는 state 키 (2026-08-17). */
  sid?: string;
  symbol?: string;
  perp?: string;
  kr_name?: string;
  entry_px?: number;
  qty?: number;
  peak_px?: number;
  stop_price?: number;
  notional?: number;
  // open_long 만 채운다. abort→long 은 빗썸 슬리브에서도 열리므로(abort_long_legs)
  // 롱을 전부 upbit 으로 키잉하면 그 건은 알림 행과 안 맞는다.
  lane?: 'upbit' | 'bh_sleeve';
  entry_utc?: string;
  exit_mode?: string;
  abort_leg?: string;
};

export type BinanceShortLiveWatching = {
  symbol?: string;
  perp?: string;
  kr_name?: string;
  oi_chg_pct?: number;
};

/** 진입 대기 큐(mercury-binance dfdda48 이전 97eb602). 업비트 레인은 알림 분 +
 *  entry_delay_min(자동 재판정 적용값, 08-14 현재 통상 40~50분) 뒤에 들어간다 --
 *  그 사이 화면에 아무 것도 없으면 "알림 뜨고 아무 일도 안 일어난다"로 읽힌다
 *  (2026-08-12 KRW-MTL 22:44 알림이 그렇게 보였다). 실제로는 대기 중이었다. */
export type BinanceShortLivePending = {
  symbol?: string;
  perp?: string;
  kr_name?: string;
  lane?: 'upbit' | 'bh_newhigh';
  alert_minute_utc?: string;
  entry_delay_min?: number;
  entry_due_utc?: string;
  mins_to_entry?: number;
  segment_pct?: number;
  runup_60m_pct?: number;
  low_conc_cohort?: boolean;
};

/** 빗썸 신고가 슬리브 (prereg 2026-08-01). 업비트 레인과 슬롯·풀·집계가 전부
 *  분리돼 있다. 현 자본에서는 증거금 예약가드 때문에 진입이 상시 0 이고
 *  skipped.margin 만 쌓이는 것이 정상 동작이다. */
export type BinanceShortLiveBhStats = {
  enabled?: boolean;
  /** 주문이 실제로 나가나 (yaml bh_newhigh_live). enabled 는 마스터 스위치라
   *  2026-08-10 주문 정지 뒤에도 true 다 -- 둘을 섞으면 화면이 안 도는 레인을
   *  도는 것처럼 그린다. */
  live?: boolean;
  k?: number;
  liq_cascade_enabled?: boolean;
  liq_cascade_k?: number;
  liq_cascade_hold_min?: number;
  /** 옛 risk%/tail% 키. 레인 표 명목은 쓰지 않는다 — `notional_usdt` 가
   *  주문과 같은 `bh_sleeve_notional` (base/step/캡) 이다. */
  risk_pct?: number;
  tail_pct?: number;
  /** 0 또는 미설정 = 제한 없음. */
  max_notional_usd?: number;
  base_notional_usdt?: number;
  /** 지금 자본의 rung1 명목. 서버 `bh_sleeve_notional`. 화면이 risk/tail 로
   *  다시 곱하면 주문($1877)과 다른 옛 식($532)이 나온다. */
  notional_usdt?: number | null;
  cap_frac?: number;
  leverage_effective_min?: number | null;
  leverage_effective_max?: number | null;
  realized_usdt?: number;
  open?: number;
  n_entries?: number;
  n_settled?: number;
  n_stopped?: number;
  wins?: number;
  sum_net_pct?: number;
  skipped?: {
    runup?: number;
    upbit_prior?: number;
    segment_high?: number;
    cross_list?: number;
    unmapped?: number;
    slot?: number;
    path_tpk?: number;
    capacity?: number;
    margin?: number;
  };
  /** path-pattern 진입 스킵 문턱 (yaml bh_path_skip_tpk_max). 0=끔. */
  path_skip_tpk_max?: number;
  pullback_enabled?: boolean;
};

export type BinanceShortLiveLongStats = {
  n_watch_oi_squeeze?: number;
  n_long_entries?: number;
  n_long_settled?: number;
  sum_long_net_pct?: number;
};

export type BinanceShortLiveConfig = {
  /** 대시보드 반사실 표시(2026-08-25). false 면 skip_pnl/abort_cf/리포트 숨김.
   *  채점 잡은 별개 -- yaml counterfactual_live. */
  counterfactual_live?: boolean;
  /** 청산 캐스케이드 레인(2026-08-09). 화면이 보유/k 를 그리는 데 쓴다.
   *  enabled 는 마스터(감시·WATCH), live 는 주문 스위치(2026-08-27) --
   *  cand_long_enabled/live 와 같다. 섞으면 inactive 인데 테이블이 비는 사고가 난다. */
  liq_cascade_enabled?: boolean;
  liq_cascade_live?: boolean;
  /** v1 진입 게이트 (yaml liq_v1_enabled). v2·v3 와 별개. */
  liq_v1_enabled?: boolean;
  liq_cascade_k?: number;
  liq_cascade_hold_min?: number;
  /** 후보-롱 레인(2026-08-13). cand_long_live 는 **주문 스위치**이고
   *  cand_long_enabled 는 감시까지 포함한 마스터다 -- 둘이 다르므로 화면에서
   *  합쳐 읽으면 안 된다(감시만 도는 상태가 정상 운용 상태다). */
  cand_long_enabled?: boolean;
  cand_long_live?: boolean;
  /** 업숏 주문 스위치(2026-08-26). false 면 inactive -- 감시·스킵기록은 유지. */
  upbit_live?: boolean;
  /** 빗숏 주문 스위치. 패키지와 별개 — 둘 다 OFF 유지가 기본. */
  bh_newhigh_live?: boolean;
  /** 빗숏 사다리 패키지 (게이트·사이징·선점). 기본 false. */
  bh_ladder_package?: boolean;
  cand_long_k?: number;
  cand_long_lvl_pct?: number;
  /** 1800s+ 구간 레벨 문턱(2026-08-16). 없으면 초반 lvl 과 같다. */
  cand_long_late_lvl_pct?: number;
  cand_long_late_lvl_after_s?: number;
  /** cold 요건(2026-08-17): true 면 뒤 구간(1800s+)은 가운데 구간에서
   *  레벨AND속도를 한 번도 안 채운 후보만 발동한다. 없으면 off(구 동작). */
  cand_long_late_requires_cold?: boolean;
  /** 발동 경과 구간 [[lo,hi], ...](2026-08-15). 비면 1..max_s. */
  cand_long_el_spans?: [number, number][];
  cand_long_vel_pct?: number;
  cand_long_win_s?: number;
  cand_long_max_s?: number;
  cand_long_stop_pct?: number;
  cand_long_nh_stall_s?: number;
  cand_long_hold_min?: number;
  /** 청캐와 같은 식: 명목 = equity × risk% / tail%. 0/미설정이면 아래 고정 달러. */
  cand_long_risk_pct?: number;
  cand_long_tail_pct?: number;
  /** 0 또는 미설정 = 제한 없음. */
  cand_long_max_notional_usd?: number;
  cand_long_notional_usdt?: number;
  /** 후보-롱이 후보로 잡는 빗썸 알림 타입. 화면의 u("필요한 알림만") 필터가 이
   *  목록을 그대로 쓴다 -- 재구현하면 yaml 과 갈라진다. */
  cand_long_alert_types?: string[];
  /** 편승 pump_ride (2026-08-27). enabled≠live. */
  pump_ride_enabled?: boolean;
  pump_ride_live?: boolean;
  pump_ride_k?: number;
  /** 겹롱. 기본 꺼짐. 켜면 업숏과 같은 명목으로 시장가 롱. */
  gyeop_long_live?: boolean;
  gyeop_long_k?: number;
  pump_ride_exchanges?: string[];
  pump_ride_confirm_base_pct?: number;
  /** hybrid 저점 선행 (2026-08-27). */
  pump_ride_hybrid_entry?: boolean;
  pump_ride_hybrid_max_drop_pct?: number;
  pump_ride_hybrid_min_rise_fixed_pct?: number;
  pump_ride_seg_min?: number;
  pump_ride_seg_max_pct?: number;
  /** seg_max 예외 — 알림 seg≥이 값이면 감시 허용·fixed만. */
  pump_ride_seg_max_exempt_pct?: number;
  pump_ride_soft_stop_pct?: number;
  pump_ride_hard_stop_pct?: number;
  pump_ride_leverage?: number;
  pump_ride_hold_min?: number;
  pump_ride_watch_max_min?: number;
  pump_ride_notional_usdt?: number;
  pump_ride_risk_pct?: number;
  pump_ride_tail_pct?: number;
  pump_ride_max_notional_usd?: number;
  pump_ride_trail_arm_pct?: number;
  pump_ride_trail_dd_pct?: number;
  /** 체질 경로확인 (2026-09-20). */
  pump_ride_path_confirm?: boolean;
  pump_ride_path_confirm_w_min?: number;
  pump_ride_path_confirm_pct?: number;
  pump_ride_path_confirm_rung1?: boolean;
  /** 쌍숏 dual_short (2026-09-02). */
  dual_short_enabled?: boolean;
  dual_short_live?: boolean;
  dual_short_k?: number;
  dual_short_leverage?: number;
  /** 사이징: 청캐와 같은 식(risk/tail). 화면에서 재구현 금지 — 노브를 내려받는다. */
  dual_short_risk_pct?: number;
  dual_short_tail_pct?: number;
  dual_short_max_notional_usd?: number;
  dual_short_notional_usdt?: number;
  /** 되돌림 진입 파라미터 (재판정 오버레이 적용 실효값). 반사실 차트 창 계산용. */
  dual_short_pullback_drop_pct?: number;
  dual_short_pullback_watch_min?: number;
  dual_short_pullback_min_peak_pct?: number;
  dual_short_pullback_stall_min?: number;
  dual_short_hold_min?: number;
  dual_short_window_s?: number;
  dual_short_dormant_lookback_min?: number;
  dual_short_dormant_max_range_pct?: number;
  dual_short_alert_types?: string[];
  upbit_skip_if_dual?: boolean;
  pump_ride_alert_types?: string[];
  /** 사이징 3종. 화면에서 재구현하지 말 것 -- 하드코딩했다가 yaml 이 바뀌어도
   *  안 따라갔다(2026-08-10: 상한 $25 인데 $90 으로 표시). */
  liq_cascade_risk_pct?: number;
  liq_cascade_tail_pct?: number;
  /** 0 또는 미설정 = 제한 없음. */
  liq_cascade_max_notional_usd?: number;
  leverage?: number;
  leverage_requested?: number;
  leverage_effective_min?: number;
  leverage_effective_max?: number;
  k?: number;
  auto_capital?: boolean;
  kill_drawdown_pct?: number;
  stop_pct?: number;
  /** 라이브 state config 비율 (0.10 = 10%). 2026-10-28 레인 표 스탑 칸. */
  stop?: number;
  mountain_stop?: number;
  bh_stop?: number;
  bh_dump_stop?: number;
  /** 산 홀드(분, 알림 기준). 라이브 `upbit_mountain_hold_min`. */
  mountain_hold_min?: number;
  /** 업비트 레인 홀드. **알림 기준** (차트 가로축). 반사실 라벨은 hold_after_min. */
  hold_exit_min?: number;
  /** 업비트 **진입 후 실보유**(분). 재판정 hold_after 와 연동. */
  hold_after_min?: number | null;
  /** 업비트 되돌림 **감시 상한**(분). 재판정 watch_min 과 연동. */
  watch_min?: number | null;
  /** 업숏 되돌림 실효셀(2026-08-27 STATE TOP1). 필터 카드용.
   *  lev 칸은 레버만 (2026-09-01 — 편승과 같음). */
  upbit_pullback_enabled?: boolean;
  upbit_pullback_auto?: boolean;
  upbit_pullback_drop_pct?: number | null;
  upbit_pullback_min_peak_pct?: number | null;
  upbit_pullback_stall_min?: number | null;
  /** 업숏 진입직전 재확인창 (2026-09-06). 바확인(confirm_s)과 별개.
   *  docs/stream-upbit-confirm-gate-prereg-2026-09-06.md */
  upbit_confirm_gate_enabled?: boolean;
  upbit_confirm_wait_s?: number | null;
  upbit_confirm_reject_pct?: number | null;
  /** 업비트 레인 진입지연(분). 되돌림 대기가 켜지면 옛 값 — hold_after_min 우선. */
  entry_delay_min?: number;
  max_signal_lag_min?: number;
  /** 빗썸 알림기준 홀드(차트 축). 반사실 라벨은 bh_hold_after_min. */
  bh_hold_exit_min?: number;
  /** 급락 스캘프 홀드 (분). durée 칸 u("되돌림/급락") 표시용. */
  bh_dump_hold_min?: number;
  /** 익절2 주문 ON (yaml bh_tp2_live). Strategy 칸 세 번째. */
  bh_tp2_live?: boolean;
  bh_hold_after_min?: number | null;
  bh_watch_min?: number | null;
  min_runup_pct?: number;
  max_segment_pct?: number;
  max_btc60m_pct?: number;
  min_concurrent_alerts?: number;
  risk_per_stop_pct_conc0?: number;
  risk_per_stop_pct_btc60?: number;
  /** 업숏 공평화 risk% (DB live_lane_settings). 사이징 칸 표시 SoT. */
  upbit_risk_pct?: number;
  upbit_tail_pct?: number;
};

/** §5-F 필터별 반사실 집계 한 줄. bucket='merit' 만 필터 품질을 말한다 --
 *  'capacity'(슬롯 부족·최소주문·지각)는 슬롯이 비었으면 잡았을 거래다. */
export type BinanceSkipReportRow = {
  exchange: string;
  skip_reason: string;
  bucket: string;
  n: number;
  avg_net_pct: number | null;
  sum_net_pct: number | null;
  win_rate: number | null;
  stop_rate: number | null;
};

export type BinanceSkipReport = {
  window_days: number;
  rows: BinanceSkipReportRow[];
};

/** segment_high 드릴다운 한 줄 (runup_60m_pct 또는 segment_pct 밴드).
 *  docs/stream-upbit-segment-high-runup-short-2026-08-06.md 부록 B. */
export type BinanceSegmentHighBand = {
  band: string;
  n: number;
  avg_net_pct: number | null;
  stop_rate: number | null;
};

/** segment_rise≥40% 를 segment_high 로 스킵한 것의 전용 반사실 카드. skip_report 는
 *  필터당 한 줄이라 runup/segment_pct 드릴다운을 못 담아서 따로 낸다.
 *  need_n 은 문서 §결론의 재판정 조건("40~50 밴드 n≥30") — t-검정 자체(ERA 같은
 *  단일종목 제외)는 백엔드가 안 하므로, 이 카드는 "판정 아님" 배너의 표본크기
 *  조건만 확인한다. 판정 확정은 문서를 다시 읽고 사람이 한다. */
export type BinanceSegmentHighReport = {
  window_days: number;
  n: number;
  avg_net_pct: number | null;
  sum_net_pct: number | null;
  win_rate: number | null;
  stop_rate: number | null;
  abort_scored: number;
  abort_fired: number;
  runup_bands: BinanceSegmentHighBand[];
  segment_bands: BinanceSegmentHighBand[];
  need_n: number;
};

/** §5-J flip 반사실 한 줄. delta = 실제(숏 레그+롱 레그) − flip 안 했을 때(숏만). */
export type BinanceFlipReportRow = {
  flip_type: string;
  n: number;
  flip_better_rate: number | null;
  avg_delta_pct: number | null;
  sum_delta_pct: number | null;
  avg_base_pct: number | null;
  avg_total_pct: number | null;
};

/** rows 가 비어도 백엔드가 반드시 내려준다 -- 배포된 flip 이 전부 꺼져 있어 0건이
 *  정상 상태이고, 키가 없으면 "쿼리가 깨진 것"과 구분이 안 되기 때문이다. */
/** §5-F 세 번째 거울: 일찍 끊은 거래를 안 끊었으면? delta = actual - base. */

const CAND_LONG_COLOR = '#ec7000';  // 빗숏과 같다. 둘 다 빗썸 알림에서 나온 레인이라
                                    // 오렌지로 묶는다. 롱이라 초록으로 갈랐더니
                                    // 원천 거래소가 안 보였다.
const PUMP_RIDE_COLOR = '#a371f7'; // 편승(2026-08-27). 롱·KR알림 공통이나 빗롱과 갈라
/** 편승 라이브스택·상세·재판정·건강도만 숨김 (2026-10-20). 레인 표는 남긴다. */
const PUMP_RIDE_STACK_VISIBLE = false;
                                    // 보라로 표시.
// 쌍숏(dual_short)은 단독 레인이 아니라 업숏·빗숏 종속이라 (2026-09-04 유저)
// 레인박스·재판정버튼·건당박스 어디에도 자기 색으로 안 뜬다. 피드 행은 lane()='ds'.
const LIQ_COLOR = '#f0b90b';   // 청산 캐스케이드 레인 (유저 지정: 노란색.
                               // Ex 칸 '바' 색과 같은 값이다 -- 갈라두면 같은 레인이
                               // 칸마다 다른 색으로 보인다)
// 폴백 상수. **평소엔 안 쓴다** -- cfg 가 값을 내려주면 그쪽이 이긴다. 예전에는
// 이 둘만 보고 그렸다가 yaml 이 바뀌어도 화면이 안 따라갔다(2026-08-10: 청산
// 노셔널 상한 $25 를 넣었는데 화면은 $90 을 계속 그렸다).
// 백엔드가 config 를 안 내려줄 때만 쓰는 폴백. **실값은 yaml 이 단일 출처**이고
// 화면은 cfg.liq_cascade_risk_pct 를 그대로 쓴다 -- 이 상수는 화면이 값을
// 재구현하지 않기 위한 최후 수단이다. 2026-08-14 배포값은 5.5(k=3 과 한 쌍).
const LIQ_TAIL_PCT_FALLBACK = 6.0;

/** 레인 표 스탑 칸 (2026-10-28, 실거래 칸 자리). 값은 라이브 state config
 *  (`stop`·`mountain_stop`·`bh_stop`·`bh_dump_stop` 비율, 모니터 config)에서,
 *  없으면 배포값 폴백. 업숏 되돌림/산 `10/25` · 빗숏 되돌림/급락(/익절2) `6/6(/6)`. */
function laneStopCell(
  key: string,
  tier: string | undefined,
  cfg: BinanceShortLiveConfig,
): { txt: string; title: string } {
  const pctOf = (v: number | null | undefined, fb: number) => (
    v != null && Number.isFinite(Number(v)) ? Math.round(Number(v) * 1000) / 10 : fb
  );
  if (key === 'upbit') {
    const ev = pctOf(cfg.stop, 10);
    const mt = pctOf(cfg.mountain_stop, 25);
    return {
      txt: `${ev}/${mt}`,
      title: `저녁 진입가 +${ev}% STOP_MARKET · 산 +${mt}% STOP_MARKET`
        + u(' · 산은 진입가 +15% 위에서 OI 진입 대비 −2% 이하면 시장가 (숏커버 스퀴즈)'),
    };
  }
  if (key === 'bh') {
    // 되돌림/급락(/익절2) — 보유·전략 칸과 같은 순서. 전부 BH_SCREEN_STOP.
    const book = pctOf(cfg.bh_stop, 6);
    if (cfg.bh_dump_stop == null) {
      return { txt: `${book}`, title: uDeep(`빗숏 진입가 +${book}% STOP_MARKET`) };
    }
    const dump = pctOf(cfg.bh_dump_stop, 6);
    if (cfg.bh_tp2_live !== false) {
      return {
        txt: `${book}/${dump}/${book}`,
        title: uDeep(`빗숏 되돌림 +${book}% · 급락 +${dump}% · 익절2 +${book}% STOP_MARKET`),
      };
    }
    return {
      txt: `${book}/${dump}`,
      title: uDeep(`빗숏 되돌림 +${book}% · 급락 스캘프 +${dump}% STOP_MARKET`),
    };
  }
  if (tier === 'v2' || tier === 'v3' || key === 'liq' || key === 'liq_v3') {
    return { txt: '–', title: u('가격 스탑 없음 · eq스탑(산시작 대비 잔고 −80%)은 규칙표') };
  }
  if (key === 'gyeop_long') {
    return { txt: '5', title: u('겹롱 진입가 −5% 이하면 시장가 (소프트)') };
  }
  if (key === 'pump_ride') {
    const v = pctOf(cfg.pump_ride_hard_stop_pct, 15);
    return { txt: `${v}`, title: `편승 하드 스탑 −${v}%` };
  }
  return { txt: '–', title: '' };
}

/** 레인 표 전략 칸 (2026-10-28 · 11-04 익절2). 그 레인이 지금 쓰는 서브전략 이름.
 *  순서는 보유·스탑 칸과 같다 (업숏 되돌림/산 · 빗숏 되돌림/급락/익절2).
 *  서브전략이 config 에 없으면(옛 페이로드) 기본 하나만. */
function laneStrategyCell(
  key: string,
  tier: string | undefined,
  cfg: BinanceShortLiveConfig,
): { txt: string; title: string } {
  if (key === 'upbit') {
    return cfg.mountain_hold_min != null
      ? { txt: u('되돌림/산'), title: u('되돌림: 고점 뒤 꺾임 숏 (KST 18–24) · 산: 그 밖, 산 모양이면 알림에 바로 숏') }
      : { txt: u('되돌림'), title: u('되돌림: 고점 뒤 꺾임 숏 (KST 18–24)') };
  }
  if (key === 'bh') {
    if (cfg.bh_dump_hold_min == null) {
      return { txt: u('되돌림'), title: u('되돌림: 고점 뒤 꺾임 숏') };
    }
    // bh_tp2_live 미포함(옛 페이로드) = ON 가정 — 11-03 이후 라이브 기본.
    // 명시 false 일 때만 되돌림/급락 둘.
    if (cfg.bh_tp2_live !== false) {
      return {
        txt: u('되돌림/급락/익절2'),
        title: u('되돌림: 고점 뒤 꺾임 숏 · 급락: 알림 직후 −3%/120s 스캘프 · 익절2: r10≤0∧lag≤30 · TP2%/S6/H90 · 명목300%'),
      };
    }
    return {
      txt: u('되돌림/급락'),
      title: u('되돌림: 고점 뒤 꺾임 숏 · 급락: 알림 직후 −3%/120s 스캘프'),
    };
  }
  if (tier === 'v2') return { txt: u('사다리'), title: u('누적청산 ≥100 → 조각 사다리 숏') };
  if (tier === 'v3') return { txt: u('얕은 사다리'), title: u('화이트 종목 · 문턱 20/35/50/65 · v2 진입방식') };
  if (key === 'pump_ride') return { txt: u('편승'), title: u('급등 편승 롱') };
  if (key === 'gyeop_long') return { txt: u('돌파 추격'), title: u('8분 저점 대비 +15% 첫 돌파 · 직전 15초 +8% 이상이면 롱') };
  return { txt: '–', title: '' };
}

const RegimeStat: React.FC<{
  weeks: BinanceRegimeWeek[] | null;
  liqVitality?: BinanceLiqVitality | null;
  /** 랩탑: equity 칸 우측 상단에 붙일 때. 박스 테두리는 바깥이 들고, 표만 그린다. */
  embedded?: boolean;
}> = ({ weeks, liqVitality, embedded }) => {
  const { t } = useDashboardLocale();
  const colorNet = (x?: number | null) => {
    if (x == null) return binanceMuted;
    if (x > 0) return '#3fb950';
    if (x < 0) return '#f85149';
    return binanceMuted;
  };
  const colorRatio = (r?: number | null) => {
    if (r == null) return binanceMuted;
    if (r >= 2.0) return '#3fb950';
    if (r >= 1.5) return '#f0b90b';
    return '#f85149';
  };
  // API 는 오래된 주부터 준다. 표는 **최신 주가 맨 위**.
  const rows = [...(weeks ?? [])].reverse();
  const th: React.CSSProperties = {
    textAlign: 'right', padding: '0 3px', color: binanceMuted, fontWeight: 500,
    lineHeight: 1.15, whiteSpace: 'nowrap', verticalAlign: 'bottom',
  };
  const td2: React.CSSProperties = {
    textAlign: 'left', padding: '0 3px', lineHeight: 1.15, whiteSpace: 'nowrap',
  };
  const LANES: { key: string; label: string; color: string }[] = [
    { key: 'upbit', label: t('binance.lane.upbit'), color: EXCHANGE_COLOR.upbit },
    { key: 'bh', label: t('binance.lane.bithumb'), color: EXCHANGE_COLOR.bithumb },
    { key: 'liq', label: t('binance.lane.liq'), color: LIQ_COLOR },
    { key: 'cand_long', label: t('binance.lane.candLong'), color: CAND_LONG_COLOR },
    { key: 'gyeop_long', label: t('binance.lane.gyeopLong'), color: EXCHANGE_COLOR.gyeop },
  ].filter((l) => !DASHBOARD_HIDDEN_LANE_KEYS.has(l.key));
  const cell = (w: BinanceRegimeWeek, key: string): BinanceRegimeLane | null => {
    const v = w.lanes?.[key];
    if (v) return v;
    return key === 'upbit' && (w.avg_net_pct != null || w.ratio != null) ? w : null;
  };
  const inner = (
    <>
      {t('binance.regime.title') ? (
        <div className="binance-stat-label">
          {t('binance.regime.title')}
        </div>
      ) : null}
      <div className="binance-regime-scroll">
        <table className="binance-regime-table" style={{
          borderCollapse: 'collapse', fontSize: '0.58rem',
        }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', width: '2.2em' }}>{t('binance.regime.col.week')}</th>
              {LANES.map((l) => (
                <th key={l.key} className="binance-regime-lane" style={{ ...th, color: l.color }}>
                  {l.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={`${w.year}-${w.week}`}>
                <td style={{ ...td2, textAlign: 'left', color: binanceMuted }}>w{w.week}</td>
                {LANES.map((l) => {
                  const v = cell(w, l.key);
                  if (!v) {
                    return <td key={l.key} style={{ ...td2, color: binanceMuted }}>-</td>;
                  }
                  if (v.avg_net_pct != null) {
                    const x = v.avg_net_pct;
                    const win = v.win_rate_pct;
                    const netTxt = `${x >= 0 ? '+' : ''}${x.toFixed(1)}`;
                    const meta = win != null
                      ? `${win.toFixed(0)}%·${v.n}`
                      : `${v.n}`;
                    return (
                      <td key={l.key} style={{ ...td2, color: colorNet(x) }}
                          title={`${netTxt}% · ${u('승률')} ${win != null ? `${win.toFixed(0)}%` : '–'} · n${v.n}`}>
                        {netTxt}
                        <span style={{ color: binanceMuted, fontWeight: 400, marginLeft: 2 }}>
                          {meta}
                        </span>
                      </td>
                    );
                  }
                  if (v.ratio == null) {
                    return <td key={l.key} style={{ ...td2, color: binanceMuted }}>-</td>;
                  }
                  return (
                    <td key={l.key} style={{ ...td2, color: colorRatio(v.ratio) }}>
                      {v.ratio.toFixed(2)}
                      <span style={{ color: binanceMuted, fontWeight: 400, marginLeft: 2 }}>
                        {v.n}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={LANES.length + 1} style={{ ...td2, textAlign: 'center', color: binanceMuted }}>–</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
  if (embedded) {
    return <div className="binance-regime-embed-inner" title={t('binance.regime.hint')}>{inner}</div>;
  }
  return (
    <div className="binance-stat-item binance-stat-item--regime" title={t('binance.regime.hint')}>
      {inner}
    </div>
  );
};

/** 폰 상태. 백엔드가 signal_receiver 의 박동 파일에서 만들어 내려준다. */
export type BinanceNotifier = {
  /** ok | listener | stale | down | unknown */
  status?: string;
  age_s?: number | null;
  listener_ok?: boolean | null;
  battery_exempt?: boolean | null;
  queue_pending?: number | null;
  app_version?: string | null;
  skew_s?: number | null;
  last_hb_utc?: string | null;
  last_alert_utc?: string | null;
  last_alert_lag_s?: number | null;
  lag_max_s?: number | null;
  lag_n?: number | null;
};

/**
 * 폰 한 줄. 자리는 레짐 표 **왼쪽**의 남는 칸이다(2026-08-19 유저 지시).
 *
 * 왜 이게 화면에 있어야 하나: 이 라인의 알림은 전부 안드로이드 폰 한 대를 지난다.
 * 그 폰이 삼성 Freecess 에 얼면 알림이 최대 48분 늦게 오거나 아예 안 오는데,
 * **화면에서는 조용한 시장과 구분되지 않았다** -- 폰을 USB 로 꽂아 logcat 을 보기
 * 전까지 알 수 없었다. 그래서 앱이 3분마다 알림과 무관한 경로로 박동을 보내고,
 * 이 줄은 그 박동의 나이만 말한다.
 *
 * 'listener' 를 따로 두는 이유: 앱은 멀쩡히 도는데 알림 접근 권한만 꺼진 상태가
 * **가장 조용한 고장**이다. 박동만 보면 정상으로 보인다.
 */
const PhoneStat: React.FC<{ nf?: BinanceNotifier }> = ({ nf }) => {
  const t = useDashboardT();
  const st = nf?.status ?? 'unknown';
  const ageMin = nf?.age_s != null ? Math.floor(nf.age_s / 60) : null;
  const ok = st === 'ok';
  const bad = st === 'stale' || st === 'down' || st === 'listener';
  const text = (() => {
    if (st === 'listener') return t('binance.stat.phone.listenerOff');
    if (st === 'stale' || st === 'down') {
      return `${t('binance.stat.phone.silent')} ${ageMin ?? '?'}m`;
    }
    if (st === 'ok') {
      return `${t('binance.stat.phone.ok')} · ${ageMin ?? 0}m`;
    }
    return '–';
  })();
  // 툴팁에 숫자를 전부 넣는다 -- 한 줄에는 판정만 두고, "왜 그렇게 봤나" 는
  // 여기서 확인한다. 배달지연 최댓값이 특히 중요하다: 이번 사고는 사흘에 걸쳐
  // 나빠졌고 한 건만 봐서는 안 보였다.
  const title = [
    t('binance.stat.phone.title'),
    nf?.app_version ? `v${nf.app_version}` : null,
    nf?.listener_ok === false ? t('binance.stat.phone.listenerOff') : null,
    nf?.battery_exempt === false ? t('binance.stat.phone.batteryOn') : null,
    nf?.queue_pending != null && nf.queue_pending > 0 ? `queue ${nf.queue_pending}` : null,
    nf?.last_alert_lag_s != null ? `lag ${nf.last_alert_lag_s}s` : null,
    nf?.lag_max_s != null ? `lag max ${nf.lag_max_s}s / ${nf.lag_n ?? 0}` : null,
    nf?.skew_s != null && Math.abs(nf.skew_s) > 5 ? `skew ${nf.skew_s}s` : null,
  ].filter(Boolean).join(' · ');
  return (
    <div className="binance-transfer-row binance-phone-row" title={title}>
      <span className="binance-stat-label">{t('binance.stat.phone')}</span>
      <span style={{
        color: bad ? '#f85149' : ok ? '#3fb950' : binanceMuted,
      }}>
        {text}
      </span>
    </div>
  );
};

/** 트레이더 프로세스. 상태 JSON 나이와 별개다 — 분석기는 모니터를 따로 띄우므로
 *  조회가 성공해도 루프 프로세스는 죽어 있을 수 있다. */
export type BinanceTrader = {
  alive?: boolean | null;
  state?: string;
  sub?: string;
  tick_age_s?: number | null;
};

/** analyser 수집·디스크. 백엔드 host 키. 트레이더 JSON 에는 없다. */
export type BinanceHostCollector = {
  id: string;
  age_s?: number | null;
  ok?: boolean;
  stale_s?: number;
};

export type BinanceHostDisk = {
  mount?: string;
  free_pct?: number;
  free_gb?: number;
  total_gb?: number;
  /** ok | low | critical */
  level?: string;
};

export type BinanceHost = {
  ok?: boolean;
  /** false 면 배치 테이블을 읽지 못했다. 스트림이 죽은 것과 구분한다. */
  db?: boolean;
  disk?: BinanceHostDisk | null;
  collectors?: BinanceHostCollector[];
};

function hostAge(age: number): string {
  if (age < 90) return `${age}s`;
  const m = Math.round(age / 60);
  if (m < 90) return `${m}m`;
  return `${Math.round(m / 60)}h`;
}

function hostCollectorName(t: ReturnType<typeof useDashboardT>, id: string): string {
  switch (id) {
    case 'mark': return t('binance.stat.host.mark');
    case 'agg': return t('binance.stat.host.agg');
    case 'spot': return t('binance.stat.host.spot');
    case 'oi': return t('binance.stat.host.oi');
    case 'liq': return t('binance.stat.host.liq');
    default: return id;
  }
}

/**
 * 수집·디스크 두 줄. 폰·트레이더 아래.
 * 마크가 멈춰도 알림 표는 그대로라, 여기 나이가 고장과 조용한 시장을 가른다.
 */
const HostStat: React.FC<{ host?: BinanceHost }> = ({ host }) => {
  const t = useDashboardT();
  const collectors = host?.collectors ?? [];
  const bad = collectors.filter((c) => !c.ok);
  const mark = collectors.find((c) => c.id === 'mark');
  const collectOk = host?.db === true && bad.length === 0 && collectors.length > 0;
  if (host == null) return null;
  const text = (() => {
    if (host.db === false || collectors.length === 0) return t('binance.stat.host.unknown');
    if (collectOk) {
      const age = mark?.age_s;
      return age != null
        ? `${t('binance.stat.host.ok')} · ${t('binance.stat.host.mark')} ${hostAge(age)}`
        : t('binance.stat.host.ok');
    }
    return bad.map((c) => {
      const name = hostCollectorName(t, c.id);
      return c.age_s == null ? `${name} ${t('binance.stat.host.down')}` : `${name} ${hostAge(c.age_s)}`;
    }).join(' · ');
  })();
  const title = [
    t('binance.stat.host.title'),
    ...collectors.map((c) => {
      const name = hostCollectorName(t, c.id);
      const age = c.age_s == null ? t('binance.stat.host.down') : hostAge(c.age_s);
      return `${name} ${age}`;
    }),
  ].join(' · ');
  const disk = host?.disk;
  const diskLevel = disk?.level ?? '';
  const diskBad = diskLevel === 'critical';
  const diskWarn = diskLevel === 'low';
  const diskText = disk?.free_pct == null
    ? '–'
    : t('binance.stat.host.free', { n: disk.free_pct.toFixed(1) });
  const diskTitle = [
    t('binance.stat.host.diskTitle'),
    disk?.free_gb != null && disk.total_gb != null
      ? `${disk.free_gb.toFixed(1)} / ${disk.total_gb.toFixed(1)} GB`
      : null,
    disk?.mount ?? null,
  ].filter(Boolean).join(' · ');
  return (
    <>
      <div className="binance-transfer-row binance-phone-row" title={title}>
        <span className="binance-stat-label">{t('binance.stat.host.collect')}</span>
        <span style={{
          color: collectOk ? '#3fb950' : '#f85149',
        }}>
          {text}
        </span>
      </div>
      <div className="binance-transfer-row binance-phone-row" title={diskTitle}>
        <span className="binance-stat-label">{t('binance.stat.host.disk')}</span>
        <span style={{
          color: disk == null ? binanceMuted : diskBad ? '#f85149' : diskWarn ? '#f0b90b' : '#3fb950',
        }}>
          {diskText}
        </span>
      </div>
    </>
  );
};


const TRADER_TICK_STALE_S = 180;

const TraderStat: React.FC<{ trader?: BinanceTrader; ageS: number | null; failed: boolean }> = ({ trader, ageS, failed }) => {
  const t = useDashboardT();
  const alive = trader?.alive;
  const tick = trader?.tick_age_s;
  const stalled = alive === true && tick != null && tick > TRADER_TICK_STALE_S;
  const dead = alive === false;
  const ageStale = ageS != null && ageS > 45;
  const aliveOk = alive === true && !stalled;
  const bad = failed || dead || stalled;
  const text = (() => {
    if (failed && alive == null) return t('binance.stat.trader.down');
    if (dead) {
      return ageS != null
        ? t('binance.stat.trader.dead', { n: ageS })
        : t('binance.stat.trader.deadNoAge');
    }
    if (stalled) {
      return ageS != null
        ? t('binance.stat.trader.stale', { n: ageS })
        : t('binance.stat.trader.aliveNoAge');
    }
    return null;
  })();
  return (
    <div className="binance-transfer-row binance-phone-row" title={t('binance.stat.trader.title')}>
      <span className="binance-stat-label">{t('binance.stat.trader')}</span>
      {aliveOk ? (
        <span>
          <span style={{ color: '#3fb950' }}>
            {t('binance.stat.trader.aliveNoAge')}
            {ageS != null ? ' · ' : ''}
          </span>
          {ageS != null ? (
            <span style={{ color: ageStale ? '#f85149' : '#3fb950' }}>
              {t('binance.stat.trader.age', { n: ageS })}
            </span>
          ) : null}
        </span>
      ) : (
        <span style={{
          color: bad ? '#f85149' : binanceMuted,
        }}>
          {text ?? '–'}
        </span>
      )}
    </div>
  );
};

/** 청산 캐스케이드 레인 -- 업비트 알림과 **독립**인 첫 레인.
 *  숏청산 60초 $50k -> 숏 -> 30분(2026-08-10 15->30). 스탑/익절 없음
 *  (60셀 스윕에서 전부 기준선 미달). 홀드는 yaml liq_cascade_hold_min 이 유일한
 *  출처다 -- 여기 숫자는 설명일 뿐 화면에 안 나간다. */
export type BinanceLiqOpen = {
  /** 이 포지션의 state 키. 수동 청산 버튼이 지목하는 값이다 (2026-08-17).
   *  이 레인은 같은 perp 로 동시에 두 건을 열지 않으므로(진입의 already_open
   *  스킵) perp -> sid 는 열린 것들 사이에서 유일하다. */
  sid?: string;
  perp?: string;
  entry_px?: number;
  qty?: number;
  notional?: number;
  entry_utc?: string;
  roll_notional_usd?: number | null;
  /** pullback | cascade_fade */
  entry_mode?: string | null;
  /** 'v2' = 사다리 · 'v3' = 얕은산 단건. 없으면 v1 (v1 은 2026-09-09 하드 OFF). */
  engine?: string;
  ladder_rung?: number;
  n_legs?: number;
  /** v3 산 안 재진입 다리 (1-based). */
  leg?: number;
  cum?: number;
};

/** 청캐 지금 상태 — 파도·모드·breadth (2026-09-04). */
/** v2(사다리) 실동작. 트레이더가 실제 로드한 liqv2_lane 모듈에서 읽어 내려준다
 *  — 화면에 상수를 박으면 배포가 어긋난 날 조용히 거짓말한다 (2026-09-06). */
export type BinanceLiqLadderV2 = {
  live: boolean;
  /** 엔진이 v2 인가 — **live 와 별개**. 레인을 꺼도 화면은 v2 를 그린다
   *  (live 로 가르면 끄는 순간 v1 표시로 되돌아간다, 2026-09-06). */
  engine_v2?: boolean;
  k: number;
  min_order: number;
  /** 2026-09-09: rung = 캐스케이드 누적 개별청산 // nliq_per_rung, 진입 ≥ min_cum_nliq */
  nliq_per_rung?: number;
  min_cum_nliq?: number;
  cascade_quiet_h?: number;
  watch_min: number;
  drop_pct: number;
  /** 조각 = 슬리브 등분, 신고가가 직전조각 대비 leg_step_pct↑ 마다 추가, 최대 max_legs. */
  max_legs: number;
  leg_step_pct?: number;
  /** 슬리브 i 증거금% = margin_cap_pct × sleeve_ratio^i / Σratio^j. */
  margin_cap_pct?: number;
  sleeve_ratio?: number;
  /** 깊은꺾임: 진입가가 캐스케이드 고점 −deep_cut_pct% 이상 아래면 슬리브 ×deep_mult. */
  deep_mult?: number;
  deep_cut_pct?: number;
  /** 고점확인 게이트. OFF면 트리거→WATCH, 통과 후 peak_boost_mult. */
  peak_confirm?: boolean;
  peak_boost_mult?: number;
  exit_min: number;
  extend_min: number;
  recover_pct: number;
  open: number;
  pending: number;
  /** 신규 종목 첫 조각. 서버 sleeve_notional_usd → _leg_notional. */
  notional_usdt?: number | null;
};

/** v3(얕은산 단건). 보유/k·조각 칸에 v2 와 개행으로 같이 그린다 (2026-10-07). */
export type BinanceLiqLadderV3 = {
  live: boolean;
  k: number;
  open: number;
  pending: number;
  /** 주문 sleeve_notional_usd. 단건. */
  leverage?: number;
  notional_usdt?: number | null;
  /** 단건 — 조각 분모는 항상 1 */
  max_legs?: number;
  hold_min?: number;
  extend_min?: number;
  gate_cum?: number;
  max_entries?: number;
};

export type BinanceLiqStats = {
  /** null 이면 v2 모듈이 없거나 꺼져 있다 — 그때만 아래 v1 값이 실동작이다 */
  ladder_v2?: BinanceLiqLadderV2 | null;
  /** null 이면 v3 모듈 없음 — 보유/k·조각은 v2 한 줄만 */
  ladder_v3?: BinanceLiqLadderV3 | null;
  enabled?: boolean;
  live?: boolean;
  fade_enabled?: boolean;
  pullback_enabled?: boolean;
  /** breadth≥min 파도 활성 */
  wave?: boolean;
  breadth?: number;
  breadth_min?: number;
  breadth_window_min?: number;
  /** 설정: auto | pullback | cascade_fade */
  entry_mode_pref?: 'auto' | 'pullback' | 'cascade_fade' | string;
  /** 미무장 감시가 따라야 할 모드 */
  entry_mode_want?: 'pullback' | 'cascade_fade' | string;
  entry_mode_source?: 'settings' | 'breadth' | string;
  /** 파도만 본 자동 목표 */
  entry_mode_auto?: 'pullback' | 'cascade_fade' | string;
  k?: number;
  k_eff?: number;
  open?: number;
  open_by_mode?: { pullback?: number; cascade_fade?: number; unset?: number };
  pending?: number;
  pending_by_mode?: { pullback?: number; cascade_fade?: number; unset?: number };
};

/** 후보-롱 레인(2026-08-13)의 열린 포지션. 빗썸 new_high 를 후보로 등록하고
 *  레벨 AND 속도가 확인되면 **롱만** 친다(숏 없음). 방향이 반대라 다른 풀과
 *  섞으면 안 되고, 트리거 삼값(lvl/vel/경과)이 곧 "왜 들어갔나" 다. */
export type BinanceCandLongOpen = {
  /** 수동 청산 버튼이 지목하는 state 키 (2026-08-17). */
  sid?: string;
  symbol?: string;
  perp?: string;
  entry_px?: number;
  qty?: number;
  stop_price?: number;
  notional?: number;
  entry_utc?: string;
  trigger_lvl_pct?: number | null;
  trigger_vel_pct?: number | null;
  trigger_elapsed_s?: number | null;
};

/** 편승(pump_ride, 2026-08-27). KR알림→BN confirm→조기트레일 롱. */
export type BinancePumpRideOpen = {
  sid?: string;
  symbol?: string;
  perp?: string;
  kr_name?: string;
  exchange?: string;
  lane?: string;
  side?: string;
  source?: string;
  alert_type?: string;
  signal_id?: string;
  entry_px?: number;
  peak_px?: number;
  qty?: number;
  stop_price?: number;
  notional?: number;
  entry_utc?: string;
  confirm_need_pct?: number | null;
  hold_min?: number | null;
};

/** 레인별 **실측** 진입 슬리피지(bp). 가정이 아니라 실제 체결가 - 참조가.
 *  fill_source='trades' 인 건만 센다 -- 'reference' 는 실체결가를 못 받아 참조가를
 *  그대로 쓴 것이라 슬리피지가 정의상 0 이고, 섞으면 "슬리피지 없음"이라는 반대
 *  결론이 나온다(2026-08-09 실제로 중앙값 0.00bp 로 보였다). */
export type BinanceSlippageReport = Record<string, {
  n: number;
  n_ref_only?: number;
  median_bp?: number;
  mean_bp?: number;
  worst_bp?: number;
  /** all 에만: 편도 평균 x2 + 수수료 10bp */
  roundtrip_est_bp?: number;
}>;

export type BinanceLiqReport = {
  days: number;
  n_trigger: number;
  /** 진입까지 간 건. n_trigger 와의 차이가 u("자본/슬롯이 막은 것") 이다 -- 그 구분이
   *  없으면 사전등록 판정(n>=400)을 굴릴 수 없다. */
  n_entered: number;
  n_settled: number;
  pnl_usdt: number;
  avg_net_pct?: number | null;
  wins: number;
  skips?: { reason: string; n: number }[];
  recent?: {
    perp: string; at: string; roll_notional_usd?: number | null;
    entered: boolean; skip_reason?: string | null;
    net_pct?: number | null; pnl_usdt?: number | null;
  }[];
};

/** 청산 캐스케이드 재판정 **권고**. 업비트 레인과 달리 자동 적용이 없다
 *  (2026-08-12 유저 결정) -- 값은 사람이 yaml 에서 바꾼다. 그래서 이 배너가
 *  결론이 사람에게 닿는 유일한 경로다. 절대 접어두지 말 것. */
export type BinanceLiqRetuneCell = {
  min_usd: number;
  rv60_min: number;
  n_liq: string;
  delay_s: number;
  hold_min: number;
  n: number;
  mean_pct: number;
  /** 판정 창 전체 수익(%p) = n x 건당. **줄세우기는 이것으로 한다.** 문턱·청산건수
   *  대역·rv60 은 트리거를 자르는 노브라 건당 평균은 자를수록 오른다 -- 건당으로
   *  줄을 세우면 1위가 늘 "가장 많이 버린 설정"이 되고 계좌는 오히려 준다. */
  total_pct?: number | null;
  median_pct: number;
  t: number;
  /** 플라시보(같은 심볼·기간 무작위 시각) 대비 초과. 이게 음수면 엣지가 아니라
   *  그 기간 그 종목들의 방향을 본 것이다. */
  excess_pct?: number | null;
  train_pct?: number | null;
  holdout_pct?: number | null;
  paired_t?: number | null;
};

/** 롤링 중앙값 + 부트스트랩 CI. 업비트 레인의 t_peak 과 **같은 추정자**를 쓴다
 *  -- 두 상자가 다른 자로 재면 나란히 놓고 못 읽는다. */
export type BinanceLiqTimingStat = {
  n: number;
  med?: number | null;
  ci_lo?: number | null;
  ci_hi?: number | null;
  /** 직전 같은 크기의 창. 롤링 창을 반으로 가르지 않는다 -- 해상도가 절반이 되면
   *  CI 가 배로 넓어져 2.4배 이동조차 구분이 안 됐다(2026-08-19 실측). */
  prev?: BinanceLiqTimingStat | null;
  /** 현재 창과 직전 창의 CI 가 안 겹친다 = 체제가 움직였다. 중앙값 차이만으로는
   *  판정하지 않는다 -- 한두 건이 창에 들고나는 것을 드리프트로 읽는다. */
  moved?: boolean;
};

/** 청산 레인의 **타이밍 드리프트**. 업비트는 u("알림 뒤 고점")(t_peak)이 축이었는데
 *  이 레인은 앵커가 트리거(+1초)라 그 축이 없다. 대신 진입 뒤 최선(t_best,
 *  숏이므로 최저가)·최악(t_worst, 최고가)까지의 분을 본다. 하루 1회 갱신. */
export type BinanceLiqTiming = {
  roll_n: number;
  n: number;
  n_window: number;
  /** 채점 지평(분). t_best 는 이 안에서만 찾으므로 계속 흘러내리는 심볼은 끝에
   *  붙는다 -- 그래서 절대 비교가 아니라 앞창/뒷창 비교로만 경고한다. */
  horizon_min: number;
  drift: boolean;
  warn?: string | null;
  note: string;
  t_best?: BinanceLiqTimingStat | null;
  t_worst?: BinanceLiqTimingStat | null;
  hold_min?: number | null;
  abort_check_min?: number | null;
  /** abort 가 최대 역행보다 **먼저** 판정한다 = 아직 안 끝난 것을 보고 끊는다. */
  abort_before_worst?: boolean | null;
};

/** 설정 정합성 · 배선 · 신선도 점검. 여기 걸리는 것들은 성적으로 안 드러난다 --
 *  force_order 를 한 틱도 못 받아도 표는 "발동 0건"으로 정상처럼 보인다. */
export type BinanceLiqChecks = {
  n: number;
  n_warn: number;
  note: string;
  rows: { key: string; ok: boolean; msg: string }[];
};

/** 청캐 되돌림 패키지 셀 — 배포/최적/격자1위 비교용 (2026-09-01). */
export type BinanceLiqPackageCell = {
  min_usd?: number;
  rv60_min?: number;
  drop?: number | null;
  peak?: number | null;
  watch?: number | null;
  hold?: number | null;
  mode?: string | null;
  hybrid?: boolean | null;
  exp_usd_max?: number | null;
  exp_total?: number | null;
  exp_n?: number | null;
  n?: number | null;
  total?: number | null;
  avg?: number | null;
  excess?: number | null;
  /** 배포 패키지 총합 대비 초과(%p). 패키지 표 vs배포 칸. */
  vs_deployed?: number | null;
  thirds?: number[] | null;
  /** 그 칸 최소 건손익(%p) — 꼬리 크기. (2026-09-03) */
  worst?: number | null;
  /** 손익>0 건수. n 과 같은 모집단(슬롯 통과분). */
  win?: number | null;
  /** win/n × 100. 백엔드가 계산해 내려준다. */
  win_pct?: number | null;
  /** 격자 전체 셀 중 승률 순위 (1-based·동률 동순위). 총합순 표 「비고」. */
  win_rank?: number | null;
  win_rank_total?: number | null;
  reason?: string | null;
  live_safe?: boolean | null;
  matches_deployed?: boolean | null;
};

export type BinanceLiqPackageRow = {
  label: string;
  role: string;
  cell?: BinanceLiqPackageCell | null;
  knobs?: string | null;
  note?: string | null;
};

export type BinanceLiqRetune = {
  computed_at: string;
  /** 판정 창 (기본 24시간 — live_retune_prefs lane=liq). */
  window_label?: string | null;
  window_hours?: number | null;
  window_days?: number | null;
  window_n: number;
  n_cells?: number | null;
  /** 다음 판정 창 시간 (DB). */
  cohort_n?: number | null;
  cohort_seed_default?: number | null;
  cohort_from_prefs?: boolean | null;
  cohort_unit?: 'count' | 'hours' | null;
  cohort_label?: string | null;
  cohort_min?: number | null;
  cohort_max?: number | null;
  /** 되돌림 격자가 **실제로** 트리거를 고르는 구간 (ISO). 홀드 지평만큼 뒤로
   *  밀린 값 — 입력 N = [now−N−lag, now−lag]. */
  pb_window_start?: string | null;
  pb_window_end?: string | null;
  pb_window_lag_min?: number | null;
  /** 실측 왕복 비용(bp). 가정값이 아니라 실체결 슬리피지에서 매번 다시 잰다. */
  cost_bp?: number | null;
  cost_source?: string | null;
  deployed?: BinanceLiqRetuneCell | null;
  best?: BinanceLiqRetuneCell | null;
  top?: BinanceLiqRetuneCell[] | null;
  hold_grid?: BinanceLiqRetuneCell[] | null;
  drift?: { train_best_hold: number; holdout_best_hold: number;
            moved: boolean; note: string } | null;
  /** 하루 1회 점검 (2026-08-19). 격자 권고와 **따로** 본다 -- 권고가 u("무변경")
   *  이어도 설정이 어긋났거나 타이밍이 밀렸을 수 있고, 둘은 같은 문장으로 못
   *  줄인다. 표본이 모자라 권고를 못 내는 날에도 이 둘은 나온다. */
  timing?: BinanceLiqTiming | null;
  checks?: BinanceLiqChecks | null;
  /** 되돌림 격자 최적 패키지 vs 배포 — 화면 메인 표. */
  packages?: BinanceLiqPackageRow[] | null;
  pullback_reject?: string | null;
  matches_deployed?: boolean | null;
  /** cascade-fade 격자 (파도 모집단 · tick). docs/stream-liq-cascade-fade-prereg-2026-09-04.md */
  packages_fade?: BinanceLiqPackageRow[] | null;
  cascade_fade_reject?: string | null;
  matches_deployed_fade?: boolean | null;
  /** 낙하칼 (수직낙하 즉시진입). **재판정 대상 아님 — 고정 규칙** (유저 2026-09-04).
   *  docs/stream-liq-knife-prereg-2026-09-04.md. 백엔드가 실효 config 를 실어주면
   *  값이 채워지고, 없으면 표는 뜨되 값은 '–'. */
  knife?: {
    enabled?: boolean | null;
    live?: boolean | null;
    thr_pct?: number | null;
    lag_s?: number | null;
    exit_min?: number | null;
    stop_pct?: number | null;
    n_recent?: number | null;
  } | null;
  /** 진입 직전 재확인창. **재판정 대상 아님 — 고정 규칙** (유저 2026-09-05).
   *  docs/stream-liq-confirm-gate-prereg-2026-09-05.md. */
  confirm_gate?: {
    enabled?: boolean | null;
    wait_s?: number | null;
    reject_pct?: number | null;
    n_recent?: number | null;
  } | null;
  needs_change: boolean;
  verdict: string;
  reason: string;
  elapsed_s?: number | null;
  run_when?: string | null;
  last_gate?: {
    at: string;
    ran: boolean;
    reason?: string | null;
    n_candidates?: number | null;
  } | null;
};

/** 빗숏·업숏 되돌림 재판정 셀 (2026-08-31, 상세 2026-09-01).
 *  노브 다섯 + 청캐/편승과 같은 성적 칸. 총합은 재생 프레임이라 라이브 손익이
 *  아니다 — 셀 비교로만 쓴다. */
export type BinanceBhRetuneCell = {
  drop?: number | null;
  watch?: number | null;
  peak?: number | null;
  stall?: number | null;
  hold?: number | null;
  n?: number | null;
  /** 판정 창 전체 수익(%p) = 슬롯을 채운 합. 줄세우기 축. */
  total?: number | null;
  avg?: number | null;
  win?: number | null;
  /** 격자 전체 셀 중 승률 순위 (1-based·동률 동순위). 총합순 표 「비고」. */
  win_rank?: number | null;
  win_rank_total?: number | null;
  worst?: number | null;
  /** 같은 분 다른 심볼 대조군 대비 건당 초과. */
  excess?: number | null;
  /** 배포 패키지 총합 대비 초과(%p). */
  vs_deployed?: number | null;
  ctrl_mean?: number | null;
  /** 창을 3등분한 각 구간 합. 하나라도 음수면 채택 관문에서 막힌다. */
  thirds?: number[] | null;
  reason?: string | null;
};

/** 청캐 v2(사다리) 색. 청캐 노랑(#f0b90b)의 **어두운 단** — 같은 레인의 갈래라
 *  다른 색을 주면 v1/v2 가 서로 다른 레인으로 읽힌다. 밝은 단(#ffd964)은
 *  일반시야 ΔE 8.7 로 v1 과 구분이 안 돼(검증기 hard FAIL) 어두운 쪽으로 갔다.
 *  검증: validate_palette #4d94ff,#ec7000,#f0b90b,#a37c00 --mode dark
 *        → CVD ΔE 13.0 · 일반시야 17.1 · 대비 4색 전부 3:1↑ 통과.
 *  (명도밴드만 FAIL — 업숏/빗숏/청캐 세 색은 피드·주간표·Ex 칸이 쓰는 레인
 *   정체색이라 이 그림 하나 때문에 바꾸지 않는다.) */
const LIQ_V2_COLOR = '#a37c00';
/** 안 쓴 자본. 계열색이 아니라 배경 톤이다. */
const MARGIN_FREE_COLOR = '#30363d';

type MarginSeg = {
  key: string;
  label: string;
  color: string;
  pct: number;
  note: string;
  live: boolean;
  /** 공유 풀: 칸 수. 주면 막대를 그만큼 쪼개 그린다 (한 칸 = 한 포지션).
   *  `color2` 가 있으면 칸마다 두 색을 반씩 — "이 칸은 둘 중 아무나" 라는 뜻. */
  slots?: number;
  color2?: string;
};

/** 증거금 배분 그림 (2026-09-08).
 *
 *  **비율이 아니라 자본 대비 절대 %** 다. 한 축(자본 100%)에 레인들이 실제로
 *  잡을 수 있는 증거금을 쌓아 보여준다 — 배분%(파이) 는 "몫"이라 합이 100 이
 *  되도록 보이지만, 이 시스템은 합이 100 을 넘을 수 있고(선진입 우선) 그게
 *  바로 "왜 못 들어갔나" 의 답이라 그 초과분이 보여야 한다.
 *
 *  세로선 = 자본 100%. 막대가 그 선을 넘으면 **동시에 다 못 들어간다**.
 */
function LaneMarginBar({
  segs, budgetPct,
}: { segs: MarginSeg[]; budgetPct: number }) {
  const used = segs.reduce((a, x) => a + (x.live ? x.pct : 0), 0);
  // **여백을 주지 않는다.** 예전엔 ×1.02 라 합이 100% 여도 오른쪽에 2% 가
  // 남아 '여유' 색으로 읽혔다 (2026-09-08 유저: "0%니까 표시되지 말아야지").
  const scale = Math.max(100, used, budgetPct);
  const over = used > 100.05;
  const freePct = Math.max(100 - used, 0);
  const shown = segs.filter((x) => x.live && x.pct > 0);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: '0.66rem', color: binanceMuted, marginBottom: 3,
      }}>
        <span style={{ color: '#f0b90b', fontWeight: 600 }}>{u('증거금 배분 (자본 대비)')}</span>
        <span style={{ color: over ? '#f85149' : binanceMuted }}>
          합 {used.toFixed(1)}%
          {over
            ? ` — 자본 초과, 동시에 다 못 들어간다`
            : ` · 여유 ${freePct.toFixed(1)}%`}
        </span>
      </div>
      {/* 막대 */}
      <div style={{ position: 'relative', height: 18 }}>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', gap: 2,
          // 여유가 0 이면 트랙 배경(=여유 색)을 아예 안 칠한다. 세그먼트 사이
          // 2px 틈으로만 비치는 건 마크 간격이지 '여유' 가 아니다.
          background: freePct > 0.05 ? MARGIN_FREE_COLOR : 'transparent',
          borderRadius: 4, overflow: 'hidden',
        }}>
          {shown.map((x) => {
            const n = Math.max(x.slots ?? 1, 1);
            const fill = x.color2
              ? `linear-gradient(135deg, ${x.color} 0 50%, ${x.color2} 50% 100%)`
              : x.color;
            return Array.from({ length: n }, (_, i) => (
              <div
                key={`${x.key}-${i}`}
                title={`${x.label}${n > 1 ? ` — ${i + 1}칸째` : ''} — 자본의 `
                  + `${(x.pct / n).toFixed(1)}% (${x.note})`}
                style={{
                  width: `${(x.pct / n / scale) * 100}%`,
                  background: fill,
                  minWidth: 2,
                }}
              />
            ));
          })}
        </div>
        {/* 자본 100% 선 */}
        <div
          title={u("자본 전부 (증거금 100%)")}
          style={{
            position: 'absolute', top: -2, bottom: -2,
            left: `${(100 / scale) * 100}%`,
            // 합이 100 이면 선이 오른쪽 끝에 딱 붙는다 — 2px 이 반쯤 잘리지
            // 않게 안쪽으로 당긴다.
            transform: 'translateX(-2px)',
            width: 2, background: '#e6edf3', opacity: 0.85,
          }}
        />
      </div>
      <div style={{
        position: 'relative', height: 12, fontSize: '0.58rem', color: binanceMuted,
      }}>
        <span style={{
          position: 'absolute', left: `${(100 / scale) * 100}%`,
          transform: 'translateX(-50%)', whiteSpace: 'nowrap',
        }}>
          자본 100%
        </span>
      </div>
      {/* 직접 라벨 = 표. 색만으로 정체를 말하지 않는다 */}
      <table style={{
        width: '100%', borderCollapse: 'collapse',
        fontSize: '0.62rem', color: binanceMuted, marginTop: 2,
      }}>
        <tbody>
          {segs.map((x) => (
            <tr key={x.key} style={{ opacity: x.live ? 1 : 0.45 }}>
              <td style={{ width: 12, padding: '1px 0' }}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                  background: x.live ? x.color : 'transparent',
                  border: x.live ? 'none' : `1px solid ${x.color}`,
                }}
                />
              </td>
              <td style={{ padding: '1px 4px', whiteSpace: 'nowrap' }}>{x.label}</td>
              <td style={{
                padding: '1px 4px', textAlign: 'right', whiteSpace: 'nowrap',
                color: x.live ? '#c9d1d9' : binanceMuted, fontWeight: 600,
              }}>
                {x.live ? `${x.pct.toFixed(1)}%` : 'OFF'}
              </td>
              <td style={{ padding: '1px 4px', width: '100%' }}>{x.note}</td>
            </tr>
          ))}
          <tr>
            <td style={{ width: 12, padding: '1px 0' }}>
              <span style={{
                display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                background: MARGIN_FREE_COLOR,
              }}
              />
            </td>
            <td style={{ padding: '1px 4px', whiteSpace: 'nowrap' }}>{u('여유')}</td>
            <td style={{
              padding: '1px 4px', textAlign: 'right',
              color: over ? '#f85149' : '#c9d1d9', fontWeight: 600,
            }}>
              {over ? `−${(used - 100).toFixed(1)}%` : `${freePct.toFixed(1)}%`}
            </td>
            <td style={{ padding: '1px 4px' }}>
              {over
                ? u('레인 상한 합이 자본을 넘는다 — 먼저 들어간 쪽이 먹고 나머지는 축소·스킵')
                : u('안 쓴 자본')}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** 레인 라이브 설정 (DB `live_lane_settings`, 2026-09-01). */
export type BinanceLaneSettingsLane = {
  id: string;
  label: string;
  live: boolean;
  /** 배분 파이에서 이 레인 몫 (%). budget_pct × alloc/100 = 레인 증거금%.
   *  합이 100%를 넘어도 됨 — 선진입 우선, 후진입은 증거금 부족 시 스킵. */
  alloc_pct: number;
  /** 역산 — 편집 칸 아님 */
  risk_pct: number;
  notional_pct: number;
  k: number;
  tail_pct: number;
  leverage: number;
  margin_pct_per_slot: number;
  margin_pct_lane: number;
  source?: string;
  /** 쌍숏: 배분을 따르는 부모 (upbit|bh). 파이 합산 제외. */
  follows?: string;
  /** 업숏 사이징 메타 (항상 rung 배수). API 호환용. */
  linear_segment?: boolean;
  /** 청캐만. auto=파도 자동 · pullback/cascade_fade=수동 고정 (2026-09-04). */
  entry_mode?: 'auto' | 'pullback' | 'cascade_fade';
  /** 청캐 사다리 엔진 ON (lanes.liq.ladder_v2). */
  ladder_v2?: boolean;
  /** 업숏 행에만 — 업숏·빗숏 **공유** 슬롯 수 (lanes.upbit.short_pool_k).
   *  두 레인에 걸친 값이라 업숏 행이 집이다. 0 이면 옛 레인별 k. */
  short_pool_k?: number;
  /** 청캐만 — 진입 때 다른 레인 전량 접기 (lanes.liq.flatten_others). */
  flatten_others?: boolean;
  /** 청캐 v1 (누적청산 <100) — 배분·k. */
  v1?: {
    k: number;
    alloc_pct: number;
    risk_pct?: number;
    notional_pct?: number;
    margin_pct_per_slot?: number;
    margin_pct_lane?: number;
  };
  /** v2 사이징 — 청캐(5번째+ linear) · 업숏(base/step/cap, 2026-09-07).
   *  업숏은 `k` 를 사이징에서 쓰지 않는다(안전 레일만)이라 optional. */
  v2?: {
    k?: number;
    /** 청캐 v2 — 사다리 보유중 업숏·빗숏 신규 차단 (lanes.liq.v2). */
    blocks_short_lanes?: boolean;
    /** 업숏·빗숏 v2 사이징 (청캐는 [옛] 호환으로만). API 가 항상 채운다. */
    base_notional_pct: number;
    step_notional_pct: number;
    /** 청캐 v2 (2026-09-10): 슬리브 i 증거금% = cap × ratio^i / Σratio^j.
     *  ratio 1 균등 · <1 앞무겁게 · >1 뒤무겁게. */
    margin_cap_pct: number;
    sleeve_ratio?: number;
    /** 러닝고점 대비 이만큼 꺾이면 진입 (0 = 트리거가 즉시). */
    drop_pct?: number;
    /** 청캐 v2 조각 — 슬리브를 max_legs 등분, 신고가가 직전조각 +leg_step_pct% 뛰면 다음 조각. */
    max_legs?: number;
    leg_step_pct?: number;
    /** 깊은꺾임: 진입가가 캐스케이드 고점 −deep_cut_pct% 이상 아래면 ×deep_mult. */
    deep_mult?: number;
    deep_cut_pct?: number;
    /** 고점확인 게이트 · 통과 후 사이징 배수 (지금 라이브=OFF). */
    peak_confirm?: boolean;
    peak_boost_mult?: number;
    preview?: {
      design_depth: number;
      sleeve_ratio?: number;
      shape?: string;
      first_sleeve_margin_pct?: number;
      last_sleeve_margin_pct?: number;
      per_sleeve_margin_pct?: number;
      per_sleeve_notional_pct?: number;
      sleeves?: { sleeve: number; margin_pct: number; notional_pct: number; cum_margin_pct: number; capped: boolean }[];
      notional_pcts: number[];
      margin_sum_pct: number;
      margin_cap_pct: number;
      hits_cap_at?: number | null;
      hits_cap: boolean;
    };
    /** 업숏 preview — rung 별 명목%/증거금% (analyser upbit_v2_preview). */
    upbit_preview?: {
      rungs: { rung: number; notional_pct: number; margin_pct: number; capped: boolean }[];
      margin_cap_pct: number;
      cap_notional_pct: number;
      concurrent_rung1: number;
    };
    /** 빗숏 preview — 같은 식 (DEFAULT 75/100/100). */
    bh_preview?: {
      rungs: { rung: number; notional_pct: number; margin_pct: number; capped: boolean }[];
      margin_cap_pct: number;
      cap_notional_pct: number;
      concurrent_rung1: number;
    };
  };
};

export type BinanceLaneSettings = {
  budget_pct: number;
  lanes: BinanceLaneSettingsLane[];
  alloc_sum_pct?: number;
  total_slots: number;
  total_margin_pct: number;
  budget_headroom_pct: number;
  updated_at?: string | null;
  updated_by?: string | null;
  has_db_row?: boolean;
  /** 업숏·빗숏 공유 슬롯 수 (yaml short_pool_k). 0 이면 옛 레인별 k.
   *  **yaml 이 정본이라 화면에서 못 고친다** — 읽어서 보여주기만 한다. */
  short_pool_k?: number;
  short_pool_lane_ids?: string[];
  /** 청캐 v2 사다리 보유중 업숏·빗숏 신규 차단. */
  liqv2_blocks_short_lanes?: boolean;
  /** 청캐 진입 때 다른 레인 강제청산(접기). 2026-09-08 부터 기본 false. */
  liq_flatten_others?: boolean;
};

/** 빗숏·업숏 되돌림 재판정. 청캐와 같은 골격 — 배포/채택/홀드격자/총합상위.
 *
 *  **applied 는 권고지 배포값이 아니다** — auto 가 꺼져 있으면 라이브는 이 행을
 *  읽지도 않는다. deployed 와 나란히 놓아야 "무엇을 바꾸라는 것인지" 가 읽힌다.
 *
 *  window_n = 직전 격자 채점 건수 · cohort_n = 다음 격자 모집단(DB·yaml). */
export type BinanceBhRetune = {
  computed_at: string;
  /** 직전 격자가 실제 채점한 알림 건수. */
  window_n: number;
  /** 다음 격자·자동 재판정에 쓸 모집단 건수 (live_retune_prefs DB). */
  cohort_n?: number | null;
  /** DB 행이 없을 때 코드 시드. */
  cohort_seed_default?: number | null;
  /** 대시보드에서 건수를 저장했는지. */
  cohort_from_prefs?: boolean | null;
  cohort_unit?: 'count' | 'hours' | null;
  cohort_label?: string | null;
  cohort_min?: number | null;
  cohort_max?: number | null;
  gate_n?: number | null;
  stale_h?: number | null;
  exchange_label?: string | null;
  /** monitor가 yaml에서 조립한 실행 조건 문구 */
  run_when?: string | null;
  elapsed_s?: number | null;
  last_gate?: {
    at: string;
    ran: boolean;
    reason?: string | null;
    n_candidates?: number | null;
  } | null;
  n_cells?: number | null;
  /** since 구간 알림 풀 크기. */
  n_alerts?: number | null;
  /** bh_pullback_auto / upbit_pullback_auto. false 면 판정은 돌지만 라이브는 yaml. */
  auto: boolean;
  applied?: BinanceBhRetuneCell | null;
  deployed?: BinanceBhRetuneCell | null;
  best?: BinanceBhRetuneCell | null;
  /** 총합순 상위. 관문과 무관한 참고 — 권고가 아니다. */
  top?: BinanceBhRetuneCell[] | null;
  /** 배포 축 고정 · 홀드만 훑은 표 (청캐 hold_grid 자리). */
  hold_grid?: BinanceBhRetuneCell[] | null;
  passed: boolean;
  /** 채택후보·총합상위가 지금 yaml 과 같을 때 — 청캐 matches_deployed 와 같다. */
  matches_deployed?: boolean | null;
  /** @deprecated 옛 행. 지금은 deployed.excess 를 본다. */
  excess_pct?: number | null;
  paired_t?: number | null;
  reason?: string | null;
  verdict: string;
  needs_change: boolean;
};

/** 편승 재판정 셀 하나 (확인문턱 × 홀드 × 슬롯 × trail_dd × hard_stop).
 *  총합은 **재생 프레임** 값이라 라이브 손익이 아니다 — 셀 사이의 비교로만 쓴다. */
export type BinancePumpRideRetuneCell = {
  confirm: number;
  hold: number;
  k: number;
  /** trail_dd (Phase B). 구 페이로드엔 없을 수 있다. */
  dd?: number | null;
  /** hard_stop (Phase B). 구 페이로드엔 없을 수 있다. */
  hs?: number | null;
  n: number;
  total: number;
  avg: number;
  med?: number | null;
  win: number;
  /** 격자 전체 셀 중 승률 순위 (1-based·동률 동순위). 총합순 표 「비고」. */
  win_rank?: number | null;
  win_rank_total?: number | null;
  worst?: number | null;
  /** 창을 3등분한 각 구간 합. 하나라도 음수면 채택 관문에서 막힌다. */
  thirds?: number[] | null;
  first_at?: string | null;
  last_at?: string | null;
  reason?: string | null;
  /** 배포 셀 총합 대비 초과(%p). */
  vs_deployed?: number | null;
};

/** 편승 레인 재판정 (2026-08-31). 청캐와 같은 계약 — **권고만 하고 yaml 은 안 고친다**. */
export type BinancePumpRideRetune = {
  computed_at: string;
  /** 모집단(감시) 수. 격자가 재생한 알림 수다. */
  window_n: number;
  /** 롤링 창 = 최근 몇 건으로 셀을 채점했나 (편승 20). */
  roll_n: number;
  n_cells: number;
  /** 다음 격자 모집단 건수 (live_retune_prefs lane=pr). */
  cohort_n?: number | null;
  cohort_seed_default?: number | null;
  cohort_from_prefs?: boolean | null;
  cohort_unit?: 'count' | 'hours' | null;
  cohort_label?: string | null;
  cohort_min?: number | null;
  cohort_max?: number | null;
  /** 지금 배포값의 성적. 비교 기준선이라 이게 없으면 판정보류다. */
  deployed?: BinancePumpRideRetuneCell | null;
  /** 관문(배포 우위·n≥8·3등분 양수·격자 모서리 금지·이웃 과반 초과)까지
   *  통과한 채택 후보. 없으면 null — 그게 정상이다. */
  best?: BinancePumpRideRetuneCell | null;
  /** 총합 1위 셀. **관문과 무관**하다 — 축을 넓힐 근거를 보여주는 참고칸이라
   *  채택 범위 밖(확인 3·4%, k3)도 여기엔 올라온다. */
  top?: BinancePumpRideRetuneCell | null;
  /** 격자 총합순 상위 5 (관문 무관). */
  top5?: BinancePumpRideRetuneCell[] | null;
  /** 지금 라이브(overlay) 노브가 best/top/top5 중 하나와 같을 때. */
  matches_deployed?: boolean | null;
  verdict: string;
  reason: string;
  needs_change: boolean;
  run_when?: string | null;
  /** 마지막 격자 실행 벽시계(초). */
  elapsed_s?: number | null;
  /** 최근 타이머 깨움 — 게이트 통과 여부 (스킵 vs 격자 실행). */
  last_gate?: {
    at: string;
    ran: boolean;
    reason?: string | null;
    n_candidates?: number | null;
  } | null;
};

export type BinanceLaneLiveStats = {
  n: number;
  n_settled?: number;
  wins?: number;
  avg_net_pct?: number | null;
  win_rate_pct?: number | null;
  pnl_usdt?: number;
  /** 현재 yaml 지문 — 이 id 와 같은 정산만 집계 */
  config_id?: string | null;
};

export type BinanceLiqVitality = {
  verdict: 'dead' | 'marginal' | 'alive' | 'unknown' | string;
  /** 'off'(꺼진 채 유지) | 'turn_off'(끄기 권고) | 'hold' | 'discuss'(켜기 논의)
   *  | 'keep'(켠 채 유지). **라이브 스위치를 보고 갈린다** — 같은 ALIVE 라도
   *  꺼져 있으면 discuss, 켜져 있으면 keep 이다 (2026-08-31). */
  live_recommend: 'off' | 'turn_off' | 'hold' | 'discuss' | 'keep' | string;
  n_roll: number;
  n_trigger?: number;
  avg_net_pct_roll?: number | null;
  med_net_pct_roll?: number | null;
  ratio_roll?: number | null;
  win_rate_pct_roll?: number | null;
  avg_net_pct_week?: number | null;
  ratio_week?: number | null;
  iso_year?: number | null;
  iso_week?: number | null;
  updated_at?: string | null;
};

export type BinanceRegimeLane = {
  n: number;
  mfe_med_pct?: number | null;
  mae_med_pct?: number | null;
  /** 순행중앙/역행중앙. 보조 지표. 화면 판정 축이 아님(2026-08-26). */
  ratio?: number | null;
  pct_mae_over_6?: number | null;
  /** 알림 뒤 고점까지 걸린 분(±60분 창). 진입지연을 지정하는 축이다. */
  t_peak_60_med?: number | null;
  t_peak_180_med?: number | null;
  /** 실효규칙 건당 순손익%(수수료·스탑 포함). **화면·on/off 축.** */
  avg_net_pct?: number | null;
  med_net_pct?: number | null;
  /** 진입 건 중 net>0 비율(%). 건당 옆에 같이 그린다. */
  win_rate_pct?: number | null;
};

/** u("지금 체제가 배포된 창과 맞는가"). 레짐비는 엣지 유무만 답하고, 창을 어디로
 *  옮겨야 하는지는 t_peak 이 답한다 -- 2026-08-10 에 +4분에서 +32분으로 밀린 것이
 *  코어 레인 붕괴의 정체였다. 손익보다 먼저 움직인다. */
export type BinanceLaneTiming = {
  /** 최근 N건 롤링 t_peak 중앙(분). 주 경계로 자르면 주 초에 n 이 한 자리로
   *  떨어져 한두 건에 10분씩 흔들린다 -- 그래서 롤링이다. */
  implied_entry_delay_min?: number | null;
  ci_lo?: number | null;
  ci_hi?: number | null;
  deployed_entry_delay_min?: number | null;
  /** 레인별 타이밍 (2026-08-11). 세 레인의 시계가 전부 다르다 -- 업비트는 자동
   *  재판정, 빗썸은 전역 지연+자기 홀드, 청산은 트리거+1초. t_peak 은 알림 뒤
   *  기준이라 청산 레인에는 정의되지 않는다(null). */
  lanes?: {
    lane: string; n_recent?: number; t_peak_med?: number | null;
    ci_lo?: number | null; ci_hi?: number | null;
    entry_delay_min?: number | null; hold_after_min?: number | null;
    live?: boolean;
    /** 청산 레인 전용 축(t_best/t_worst). t_peak 칸에 밀어넣지 않는다 -- 같은
     *  칸에 다른 뜻을 담으면 나란히 놓고 못 읽는다. 하루 1회 갱신. */
    liq_timing?: BinanceLiqTiming | null;
    /** 청캐 vitality(롤링 채점). yaml 자동 변경 없음 -- on/off 논의용. */
    vitality?: BinanceLiqVitality | null;
  }[];
  deployed_hold_exit_min?: number | null;
  deployed_hold_after_entry_min?: number | null;
  /** 판정: 지연 >= t_peak 이면 일치. **t_peak 과 같은 값이어야 하는 게 아니다** --
   *  중앙이 T 면 절반은 그 뒤에 고점을 찍으므로 T 에 들어가면 절반이 아직 오른다. */
  match?: boolean | null;
  /** 'early' = 고점 전 진입(위험) · 'late' = 되돌림 끝난 뒤 진입 의심 */
  warn?: 'early' | 'late' | null;
  n_recent?: number | null;
  series?: {
    year: number; week: number; n?: number | null;
    t_peak_60_med?: number | null; t_peak_180_med?: number | null;
    ratio?: number | null;
  }[] | null;
};

export type BinanceRegimeWeek = BinanceRegimeLane & {
  year: number;
  week: number;
  /** 레인별 값. 최상위 필드는 업비트와 같다(레인 차원 이전 형식 호환). */
  lanes?: Record<string, BinanceRegimeLane> | null;
};

export type BinanceAbortReport = {
  n: number;
  n_saved: number;
  avg_delta_pct: number | null;
  sum_delta_pct: number | null;
  avg_actual_pct: number | null;
  avg_base_pct: number | null;
  base_stop_rate: number | null;
  pending: number;
  /** 스탑 반사실(u("스탑 없이 30분 들었으면?")). abort 와 질문이 달라 합치지 않는다. */
  stop?: BinanceAbortReport | null;
  /** abort→롱 반사실(u("탈출규칙 없이 홀드했으면?")). 실주문이라 이게 없으면
   *  포워드 판정을 볼 곳이 없다(rekindle/shadow 를 껐던 이유와 같은 함정). */
  long?: BinanceAbortReport | null;
  /** 안 연 롱 반사실(u("열었으면 얼마였나")). actual=0 이 정상이고, delta<0 이면
   *  안 연 것이 손해였다는 뜻. */
  long_skip?: BinanceAbortReport | null;
  /** 청산 레인 반사실 4종 (2026-08-24). 여태 백엔드가 이 kind 들을 집계에서
   *  안 불러 화면이 0건이었다 -- DB 에는 20건이 쌓여 있었다.
   *  질문이 서로 달라 합치지 않는다:
   *    liq_abort  전제붕괴로 끊었다 -> "안 끊었으면?"
   *    liq_stall  정체로 끊었다     -> "더 뒀으면?"
   *    liq_rescue 연장했다          -> "안 늘렸으면?"
   *    liq_manual 사람이 껐다       -> "그냥 뒀으면?" */
  liq_abort?: BinanceAbortReport | null;
  liq_stall?: BinanceAbortReport | null;
  liq_rescue?: BinanceAbortReport | null;
  liq_manual?: BinanceAbortReport | null;
};

/** 신규상장 runup 면제(label_only) 반사실. abort/stop 반사실과 같은 모양이다 --
 *  "면제했으면 얼마였나". 규칙은 지금 주문을 내지 않으므로 delta 가 아니라
 *  면제 대상이었던 알림의 가상 손익 그 자체다. */
export type BinanceNewlistingReport = {
  n: number;
  pending: number;
  sum_pct: number | null;
  avg_pct: number | null;
  win_rate: number | null;
  stop_rate: number | null;
  days: number;
  mode: string;
  need_n: number;
};

export type BinanceFlipReport = {
  window_days: number;
  rows: BinanceFlipReportRow[];
  pending: number;
  /** 켜져 있는 flip 이름. 백엔드가 yaml 에서 읽어 내려준다 -- 프론트가 u("0건") 을
   *  설명할 때 하드코딩하면 yaml 이 바뀌는 순간 화면이 거짓말한다(실제로 "전부
   *  꺼짐" 이라고 적었는데 rekindle·shadow 는 켜져 있었다). */
  enabled_flips?: string[];
};

export type BinanceShortLiveFilter = {
  key: string;
  label: string;
  value: string;
  /** skip = 알림을 버림, size = 작게 넣음, collect = 저장만, sleeve = 별도 레인,
   *  exit = 청산 규칙, pending = 지금 적용 안 됨, 검토했고 보류 중인 후보,
   *  label_only = 규칙이 지금 돌면서 채점되지만 주문에는 손대지 않음(시뮬레이션).
   *    pending 과 다르다 -- pending 은 아예 안 돌리는 후보고, label_only 는
   *    매 알림마다 판정해서 라벨을 남기고 반사실 손익이 쌓인다. */
  effect: 'skip' | 'entry' | 'size' | 'collect' | 'sleeve' | 'exit' | 'pending'
    | 'label_only';
  /** 이 노브가 걸리는 레인. 백엔드 _FILTER_LANE 가 정한다 -- 어느 규칙이 어느
   *  레인에 걸리는지는 배포된 코드만 알기 때문. 없으면 공용으로 본다.
   *  pr = 편승(pump_ride). */
  lane?: 'common' | 'upbit' | 'bh' | 'liq' | 'pr';
  desc: string;
};

/** 옛 헤더 u('상세') 버튼(FilterModal)이 담던 것 — 재판정·설정 모달로 쪼개 넣는다
 *  (2026-09-04 유저). 부모가 한 번 만들어 각 모달에 그대로 넘긴다. */
type RetuneDetail = {
  filters?: BinanceShortLiveFilter[] | null;
  skipReport?: BinanceSkipReport | null;
  flipReport?: BinanceFlipReport | null;
  abortReport?: BinanceAbortReport | null;
  abortLegBreakdown?: Record<string, { n: number; sumDelta: number }>;
  newlistingReport?: BinanceNewlistingReport | null;
  segmentHighReport?: BinanceSegmentHighReport | null;
};

export type BinanceShortLiveRisk = {
  peak_equity_live?: number | null;
  kill_floor_usdt?: number | null;
  margin_to_kill_usdt?: number | null;
  per_trade_notional_usdt?: number | null;
};

export type BinancePnlBucket = {
  /** 주 버킷이면 월요일, 일 버킷이면 그 날짜 (KST) */
  key: string;
  n: number;
  wins: number;
  stops: number;
  pnl_usdt: number;
  avg_net_pct: number;
  win_rate_pct: number;
};

export type BinancePnlReport = {
  current_week_start?: string;
  current_week?: BinancePnlBucket | null;
  current_week_days?: BinancePnlBucket[];
  past_weeks?: BinancePnlBucket[];
};

/** 전체기간 자본 추이 한 점. x축은 시간이 아니라 거래 순번(n) -- 조용한 구간은
 *  평평한 선이 아니라 아예 없는 게 맞는 그림이라 시간축을 쓰지 않는다.
 *  n=0 이 baseline(현재 자본 - 실현손익 총합)이고, 그 뒤로 정산마다 한 점씩. */
export type BinanceEquityCurvePoint = {
  n: number;
  t: string | null;
  equity: number;
  symbol?: string;
  pnl_usdt?: number;
};

export type BinanceTransferReport = {
  in_n: number;
  in_usdt: number;
  out_n: number;
  out_usdt: number;
};

export type BinanceShortLiveResponse = {
  fetched_at?: string;
  equity_usdt?: number | null;
  /** 남은 증거금 (availableBalance). 신규 진입에 쓸 수 있는 금액. */
  available_usdt?: number | null;
  /** 구간 시작 잔고 대비 밀림% (2026-10-18). (equity/base − 1)×100.
   *  청캐 표 잔고밀림% 와 같은 축 — −50 이면 시작 대비 절반. */
  equity_drawdown_pct?: number | null;
  /** @deprecated 화면에서 제거. API 호환용으로만 남김. */
  margin_buffer_pct?: number | null;
  maint_margin_usdt?: number | null;
  /** 선물 지갑 TRANSFER 합계(USDT·USDC). 거래 손익·스왑 아님. 조회 실패·캐시 없으면 null. */
  transfer_report?: BinanceTransferReport | null;
  halted?: boolean;
  open_count?: number;
  pending_count?: number;
  n_settled?: number;
  n_stopped?: number;
  realized_usdt?: number;
  /** realized_usdt 는 두 레인 합산이다. 레인별로 보여주려면 이쪽을 쓴다. */
  realized_by_lane?: {
    upbit?: number; bh?: number; liq?: number; cand_long?: number;
    pump_ride?: number; dual_short?: number; gyeop_long?: number;
  };
  wins?: number;
  config?: BinanceShortLiveConfig;
  filters?: BinanceShortLiveFilter[];
  newlisting_report?: BinanceNewlistingReport | null;
  /** §5-F 필터별 반사실 집계. 분석 DB가 안 붙으면 백엔드가 통째로 생략한다. */
  skip_report?: BinanceSkipReport | null;
  /** segment_high(segment_rise≥40% 스킵) 전용 드릴다운. skip_report 의 부분집합을
   *  runup_60m_pct·segment_pct 밴드로 쪼갠 카드 — 부록 B. */
  segment_high_report?: BinanceSegmentHighReport | null;
  /** §5-J flip 반사실 집계. 0건이어도 내려온다(위 타입 주석 참조). */
  flip_report?: BinanceFlipReport | null;
  /** abort 반사실 집계. 마찬가지로 0건이어도 내려온다. */
  abort_report?: BinanceAbortReport | null;
  regime_report?: BinanceRegimeWeek[] | null;
  lane_timing?: BinanceLaneTiming | null;
  liq_report?: BinanceLiqReport | null;
  /** 청산 레인 재판정 권고(6시간마다). 자동 적용이 없으므로 화면이 유일한 전달
   *  경로다 -- 분석 DB 가 안 붙으면 백엔드가 null 로 내린다. */
  liq_retune?: BinanceLiqRetune | null;
  /** 청캐 촉매 시드 조사. failed·stale·missing 이면 헤더 성적 버튼이 빨갛다. */
  catalyst_seed?: Liqv2Catalyst['seed'];
  /** 편승 레인 재판정. 첫 실행 전이면 null — 버튼이 안 그려진다. */
  pump_ride_retune?: BinancePumpRideRetune | null;
  /** 빗숏 되돌림 재판정. 판정 행이 없으면 null — 버튼이 안 그려진다. */
  bh_retune?: BinanceBhRetune | null;
  /** 업숏 되돌림 재판정. 빗숏과 **같은 모양**이라 타입을 공유한다
   *  (되돌림% · 감시분 · peak% · stall분 · hold_after). */
  upbit_retune?: BinanceBhRetune | null;
  /** 쌍숏 되돌림 재판정 (2026-09-02). 업숏·빗숏과 같은 모양. */
  dual_retune?: BinanceBhRetune | null;
  /** 레인 ON/OFF·노셔널·예산 — DB `live_lane_settings` (yaml 폴백). */
  lane_settings?: BinanceLaneSettings | null;
  /** 레인별 라이브 원장 실거래 통계 -- 채점 칼럼. */
  lane_live_stats?: Record<string, BinanceLaneLiveStats> | null;
  /** @deprecated vitality 판정은 채점 칼럼에서 제거(2026-09-01). */
  liq_vitality?: BinanceLiqVitality | null;
  lane_vitality?: Record<string, BinanceLiqVitality> | null;
  slippage_report?: BinanceSlippageReport | null;
  open_liq?: BinanceLiqOpen[];
  /** 청캐 지금 상태(파도·모드·breadth). */
  liq_stats?: BinanceLiqStats | null;
  open_cl?: BinanceCandLongOpen[];
  open_pr?: BinancePumpRideOpen[];
  open_gl?: Array<{ sid?: string; perp?: string; qty?: number; entry_px?: number }>;
  /** 쌍숏 오픈 포지션 (2026-09-02). live:false 동안은 늘 빈 배열. */
  open_ds?: { symbol?: string; perp?: string; sid?: string }[];
  /** 편승 성적. */
  pump_ride_stats?: {
    open?: number;
    n_entries?: number;
    n_settled?: number;
    wins?: number;
    sum_net_pct?: number;
    realized_usdt?: number;
    n_candidates?: number;
  };
  /** 겹롱 레인표 gains·실현. */
  gyeop_long_stats?: {
    open?: number;
    n_entries?: number;
    n_settled?: number;
    wins?: number;
    realized_usdt?: number;
  };
  /** 청캐 레인표 u("승/정산") 전용 — 전 기간 원장 집계. liq_report(7일 창)·
   *  liq_stats(파도/모드 상태)와 별개다 (2026-09-04). */
  liq_lane_stats?: {
    open?: number;
    n_settled?: number;
    wins?: number;
    realized_usdt?: number;
    // 위 합에 들어 있는 v3 몫 (2026-10-27). v2 행 = 합 − v3.
    v3?: {
      open?: number;
      n_settled?: number;
      wins?: number;
      realized_usdt?: number;
    } | null;
  } | null;
  risk?: BinanceShortLiveRisk;
  skipped?: {
    unmapped?: number;
    unresolved?: number;
    late?: number;
    runup_low?: number;
    segment_high?: number;
    btc60_high?: number;
    halted?: number;
    non_upbit_source?: number;
    concurrent_low?: number;
    early20_weak?: number;
    dedup?: number;
    // capacity 계열 -- 규칙이 거른 게 아니라 자리/자본이 없던 것(mercury-binance dfdda48).
    slot_full?: number;
    min_notional?: number;
    liq_reserve?: number;
    liq_priority?: number;
  };
  open?: BinanceShortLiveOpen[];
  open_long?: BinanceShortLiveOpen[];
  open_bh?: BinanceShortLiveOpen[];
  /** 진입 대기 큐. pending_count 는 숫자뿐이라 무엇이 언제 들어가는지 못 그렸다. */
  pending?: BinanceShortLivePending[];
  watching?: BinanceShortLiveWatching[];
  long_stats?: BinanceShortLiveLongStats;
  bh_stats?: BinanceShortLiveBhStats;
  /** 후보-롱 성적. bh_stats 와 같은 모양(백엔드 cand_long_stats). */
  cand_long_stats?: {
    open?: number; n_entries?: number; n_settled?: number;
    wins?: number; sum_net_pct?: number; realized_usdt?: number;
    n_candidates?: number;
  };
  pnl_report?: BinancePnlReport;
  /** 전체기간(창 아님) 자본 추이 -- 거래 단위. 비어 있으면 아직 정산이 없거나
   *  백엔드가 구버전이라 필드 자체가 없는 것. */
  equity_curve?: BinanceEquityCurvePoint[];
  events?: BinanceShortLiveEvent[];
  issues?: string[];
  /** 트레이더 유닛이 running 인지. 페이로드 나이와 별개. */
  trader?: BinanceTrader;
  /** 폰(notifier-android) 상태. 이 라인의 인입은 전부 폰 한 대를 지나므로,
   *  폰이 멈추면 모든 레인이 같이 멈춘다. 백엔드 notifier_status() 참조. */
  notifier?: BinanceNotifier;
  /** analyser 디스크와 상시 수집 나이. 트레이더는 이 키를 만들지 않는다. */
  host?: BinanceHost;
};

const POLL_MS = 30_000;
/** 수동 진입·OPEN 행의 mark 꼬리 (2026-08-25). 풀 status 와 분리.
 *  조회는 perp, 부착은 행 key (같은 perp 다른 행에 안 샌다). */
const WATCH_LIVE_MS = 1_000;
const WATCH_LIVE_KEEP_MS = 15 * 60_000;
/** OPEN + 표에 보이는 차트 행. 동시에 많이 열려도 앞쪽(최신)부터. */
const WATCH_LIVE_MAX_PERPS = 24;
// 값 자체는 옛 키를 그대로 쓴다 -- 이름만 "필요한 알림만"으로 바뀌었지 저장하는
// 뜻은 같고, 키를 갈면 이미 꺼둔 사람의 설정이 조용히 되살아난다.
const NEEDED_ONLY_KEY = 'binance-alert-upbit-only';
// "진입(예정)" -- PENDING + 진입한 행(OPEN/EXIT)만. **기본 켜짐**(2026-08-20 유저
// 결정). 끈 적이 있는 사람만 '0' 이 저장돼 있어 꺼진 채로 남는다 -- neededOnly 와
// 같은 규칙이다.
const ENTERED_ONLY_KEY = 'binance-alert-entered-only';

const th: React.CSSProperties = {
  textAlign: 'left',
  color: '#f0b90b',
  padding: '6px 8px',
  borderBottom: '1px solid #f0b90b44',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  color: '#f5f0e6',
  padding: '6px 8px',
  borderBottom: '1px solid #f0b90b22',
  verticalAlign: 'top',
};

// 2026-08-09: 레인 6칸을 2칸으로 묶으면서 호출부가 주석 블록(설정 칸)에만 남았다.
// 그 블록은 되살릴 수 있으므로 지우지 않는다.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function Stat({ label, value, color, title }: {
  label: string; value: React.ReactNode; color?: string; title?: string;
}) {
  return (
    <div className="binance-stat-item" title={title}>
      <div className="binance-stat-label">{label}</div>
      <div className="binance-stat-value" style={{ color: color ?? '#f5f0e6' }}>{value}</div>
    </div>
  );
}


function effectMeta(
  t: (key: DashboardMessageKey, params?: Record<string, string | number>) => string,
): Record<string, { label: string; color: string }> {
  return {
    skip: { label: t('binance.filter.effect.skip'), color: '#d29922' },
    entry: { label: t('binance.filter.effect.entry'), color: '#3fb950' },
    size: { label: t('binance.filter.effect.size'), color: '#58a6ff' },
    collect: { label: t('binance.filter.effect.collect'), color: '#39c5cf' },
    sleeve: { label: t('binance.filter.effect.sleeve'), color: '#39c5cf' },
    exit: { label: t('binance.filter.effect.exit'), color: '#a371f7' },
    pending: { label: t('binance.filter.effect.pending'), color: '#8b949e' },
    label_only: { label: t('binance.filter.effect.label_only'), color: '#bc8cff' },
  };
}

/** 실현손익. 진행 중인 주는 일별로, 지나간 주는 주별로.
 *
 *  누적 realized 하나로는 "요즘 잘 되고 있나"를 알 수 없다. 이번 주는 아직 표본이
 *  얇아 주 단위로 뭉치면 아무것도 안 보이므로 일별로 편다. 숫자는 백테스트가
 *  아니라 실제 체결 손익(settle 의 pnl_usdt)이고, 합계는 realized 와 일치한다. */
function PnlModal({ report, onClose }: {
  report: BinancePnlReport;
  onClose: () => void;
}) {
  const t = useDashboardT();
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const days = report.current_week_days ?? [];
  // 이번 주는 일별로도 보고 주별로도 본다 -- 일별은 "요 며칠 어땠나", 주별은
  // "지난 주 대비 어떤가" 라 답하는 질문이 다르다. 그래서 진행 중인 주도 주별
  // 표에 같이 넣고 진행중으로 표시한다.
  const currentWeek = report.current_week ?? null;
  const weeks = [
    ...(currentWeek ? [currentWeek] : []),
    ...(report.past_weeks ?? []),
  ];
  const total = weeks.reduce((a, w) => a + w.pnl_usdt, 0);

  const cell: React.CSSProperties = {
    padding: '5px 8px', borderBottom: '1px solid #f0b90b22', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  };
  const head: React.CSSProperties = {
    ...cell, color: '#f0b90b', fontSize: '0.68rem',
    borderBottom: '1px solid #f0b90b44',
  };

  const Rows = ({ rows, liveKey }: { rows: BinancePnlBucket[]; liveKey?: string }) => (
    <>
      {rows.map((b) => (
        <tr key={b.key}>
          <td style={{ ...cell, textAlign: 'left' }}>
            {b.key}
            {b.key === liveKey && (
              <span style={{ color: '#f0b90b', marginLeft: 6, fontSize: '0.62rem' }}>
                {t('binance.pnlModal.inProgress')}
              </span>
            )}
          </td>
          <td style={cell}>{b.n}</td>
          <td style={cell}>{b.win_rate_pct.toFixed(1)}%</td>
          <td style={{ ...cell, color: b.stops > 0 ? '#d29922' : '#8b7d5a' }}>{b.stops}</td>
          <td style={{ ...cell, color: b.avg_net_pct >= 0 ? '#3fb950' : '#f85149' }}>
            {b.avg_net_pct >= 0 ? '+' : ''}{b.avg_net_pct.toFixed(3)}%
          </td>
          <td style={{
            ...cell, fontWeight: 600,
            color: b.pnl_usdt >= 0 ? '#3fb950' : '#f85149',
          }}>
            {b.pnl_usdt >= 0 ? '+' : ''}{b.pnl_usdt.toFixed(2)}
          </td>
        </tr>
      ))}
    </>
  );

  const Section = ({ title }: { title: string }) => (
    <tr>
      <td colSpan={6} style={{
        padding: '10px 8px 4px', color: '#f0b90b', fontSize: '0.66rem',
        letterSpacing: '0.04em',
      }}>{title}</td>
    </tr>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('binance.pnlModal.title')}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#12100c', border: '1px solid #f0b90b55', borderRadius: 8,
          maxWidth: 560, width: '100%', maxHeight: '82vh', overflowY: 'auto',
          padding: '18px 20px', color: '#f5f0e6',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 4,
        }}>
          <h3 style={{ margin: 0, color: '#f0b90b', fontSize: '0.95rem' }}>{t('binance.pnlModal.title')}</h3>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            style={{
              background: 'none', border: 'none', color: '#b7a882',
              fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1,
            }}
          >×</button>
        </div>
        <p style={{ margin: '0 0 10px', fontSize: '0.7rem', color: '#b7a882' }}>
          {t('binance.pnlModal.note')}
        </p>

        {days.length === 0 && weeks.length === 0 ? (
          <div style={{ color: '#b7a882', fontSize: '0.78rem' }}>{t('binance.pnlModal.empty')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>{t('binance.pnlModal.col.period')}</th>
                <th style={head}>{t('binance.pnlModal.col.count')}</th>
                <th style={head}>{t('binance.pnlModal.col.winRate')}</th>
                <th style={head}>{t('binance.pnlModal.col.stops')}</th>
                <th style={head}>{t('binance.pnlModal.col.perTrade')}</th>
                <th style={head}>PnL</th>
              </tr>
            </thead>
            <tbody>
              {days.length > 0 && (
                <>
                  <Section title={t('binance.pnlModal.section.thisWeekDaily', { start: report.current_week_start ?? '' })} />
                  <Rows rows={days} />
                </>
              )}
              {weeks.length > 0 && (
                <>
                  <Section title={t('binance.pnlModal.section.weekly')} />
                  <Rows rows={weeks} liveKey={report.current_week_start} />
                </>
              )}
              <tr>
                <td style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>{t('binance.pnlModal.total')}</td>
                <td style={cell} colSpan={4} />
                <td style={{
                  ...cell, fontWeight: 700,
                  color: total >= 0 ? '#3fb950' : '#f85149',
                }}>
                  {total >= 0 ? '+' : ''}{total.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * 필터별 반사실 수익률 (§5-F). 필터를 판정하는 유일한 질문 -- "이 노브가 거른
 * 알림들은 실제로 얼마였나" -- 에 답한다.
 *
 * sum > {u('0 이면 그 필터는 **돈을 버린 것**이고,')} < 0 이면 값을 한 것이다. 부호를
 * 거꾸로 읽기 쉬워서 합계 줄에 그대로 문장으로 적어둔다.
 *
 * capacity 버킷(slot_full·min_notional·late)은 따로 낸다. 슬롯이 비었으면 잡았을
 * 거래라 필터 품질과 무관하고, 합쳐서 보면 멀쩡한 필터가 나쁘게 보인다.
 */
function SkipReportTable({ report }: { report: BinanceSkipReport }) {
  const t = useDashboardT();
  // 거래소별로 나눠서 합산한다. 절대 합치지 않는다 -- 같은 skip 체인이 두 거래소에서
  // 부호가 반대로 나온다(문서 §5-I 30일 실측: upbit merit +22.37% / bithumb −114.96%).
  // 하나로 더하면 빗썸 표본(n=1,641)이 배포 라인인 업비트(n=91)를 완전히 덮어써서,
  // "업비트 필터가 돈을 버리고 있다"는 판정이 정반대로 뒤집힌다.
  // rows 가 null 로 올 수 있다(Go 의 nil 슬라이스). 표가 비는 것과 화면이 죽는 것은
  // 완전히 다른 문제라 여기서 막는다.
  const allRows = report.rows ?? [];
  const byExchange = Array.from(new Set(allRows.map((r) => r.exchange)))
    // 배포 라인(upbit, bh_sleeve) 먼저, 리서치(bithumb) 나중.
    .sort((a, b) => {
      const rank = (x: string) => (x === 'upbit' ? 0 : x === 'bh_sleeve' ? 1 : 2);
      return rank(a) - rank(b) || a.localeCompare(b);
    })
    .map((exchange) => {
      const rows = allRows
        .filter((r) => r.exchange === exchange)
        .sort((a, b) => {
          if (a.bucket !== b.bucket) return a.bucket === 'merit' ? -1 : 1;
          return (b.sum_net_pct ?? 0) - (a.sum_net_pct ?? 0);
        });
      const merit = rows.filter((r) => r.bucket === 'merit');
      return {
        exchange,
        rows,
        meritN: merit.reduce((a, r) => a + r.n, 0),
        meritSum: merit.reduce((a, r) => a + (r.sum_net_pct ?? 0), 0),
      };
    });
  const head: React.CSSProperties = {
    textAlign: 'right', padding: '3px 6px', color: binanceMuted,
    fontSize: '0.62rem', fontWeight: 500, whiteSpace: 'nowrap',
  };
  const cell: React.CSSProperties = {
    textAlign: 'right', padding: '3px 6px', fontSize: '0.7rem', whiteSpace: 'nowrap',
  };
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        borderBottom: `1px solid ${COUNTERFACTUAL_COLOR}44`,
        paddingBottom: 4, marginBottom: 8,
      }}>
        <span style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.8rem', fontWeight: 600 }}>
          {t('binance.skipReport.title')}
        </span>
        <span style={{ color: binanceMuted, fontSize: '0.66rem' }}>
          {t('binance.skipReport.window', { days: report.window_days })}
        </span>
      </div>
      {byExchange.map((g) => {
        const color = EXCHANGE_COLOR[g.exchange] ?? binanceMuted;
        return (
          <div key={g.exchange} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
              <span style={{ color, fontSize: '0.72rem', fontWeight: 600 }}>
                {g.exchange.toUpperCase()}
              </span>
              <span style={{ color: binanceMuted, fontSize: '0.62rem' }}>
                {t(g.exchange === 'bithumb'
                  ? 'binance.skipReport.lane.research'
                  : 'binance.skipReport.lane.deployed')}
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...head, textAlign: 'left' }}>{t('binance.skipReport.col.filter')}</th>
                    <th style={head}>{t('binance.skipReport.col.count')}</th>
                    <th style={head}>{t('binance.skipReport.col.avg')}</th>
                    <th style={head}>{t('binance.skipReport.col.sum')}</th>
                    <th style={head}>{t('binance.skipReport.col.winRate')}</th>
                    <th style={head}>{t('binance.skipReport.col.stopRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={`${r.exchange}|${r.skip_reason}|${r.bucket}`}
                      style={{ borderTop: '1px solid #ffffff10', opacity: r.bucket === 'merit' ? 1 : 0.6 }}>
                      <td style={{ ...cell, textAlign: 'left' }}>
                        {r.skip_reason}
                        {r.bucket !== 'merit' && (
                          <span style={{ color: binanceMuted, fontSize: '0.62rem', marginLeft: 5 }}>
                            {r.bucket === 'capacity'
                              ? t('binance.skipReport.capacity')
                              : r.bucket === 'newlisting_sim'
                                ? t('binance.skipReport.newlistingSim')
                                : r.bucket}
                          </span>
                        )}
                      </td>
                      <td style={cell}>{r.n}</td>
                      <td style={{ ...cell, color: COUNTERFACTUAL_COLOR }}>
                        {r.avg_net_pct == null ? '–'
                          : `${r.avg_net_pct >= 0 ? '+' : ''}${r.avg_net_pct.toFixed(3)}%`}
                      </td>
                      <td style={{ ...cell, color: COUNTERFACTUAL_COLOR, fontWeight: 600 }}>
                        {r.sum_net_pct == null ? '–'
                          : `${r.sum_net_pct >= 0 ? '+' : ''}${r.sum_net_pct.toFixed(2)}%`}
                      </td>
                      <td style={cell}>{r.win_rate == null ? '–' : `${r.win_rate.toFixed(0)}%`}</td>
                      <td style={cell}>{r.stop_rate == null ? '–' : `${r.stop_rate.toFixed(0)}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {g.meritN > 0 && (
              <p style={{ margin: '5px 0 0', fontSize: '0.66rem', color: binanceMuted }}>
                {t(g.meritSum > 0
                  ? 'binance.skipReport.verdict.costly'
                  : 'binance.skipReport.verdict.worth', {
                  n: g.meritN,
                  sum: `${g.meritSum >= 0 ? '+' : ''}${g.meritSum.toFixed(2)}`,
                })}
              </p>
            )}
          </div>
        );
      })}
      <p style={{ margin: '8px 0 0', fontSize: '0.66rem', color: binanceMuted, lineHeight: 1.6 }}>
        {t('binance.skipReport.note')}
      </p>
    </div>
  );
}

/**
 * flip 반사실 (§5-J) — 스킵 표의 거울. 스킵이 "안 한 거래는 얼마였나"라면 이쪽은
 * "한 거래를 안 했으면 얼마였나"다.
 *
 * **0건이어도 렌더한다.** 배포된 flip 규칙이 전부 꺼져 있어서 0건이 정상 상태이고,
 * 표가 통째로 사라지면 "flip 이 꺼져 있다"와 "집계가 깨졌다"를 화면에서 구분할 수
 * 없다. 빈 상태를 문장으로 말해주는 쪽이 정보량이 많다.
 */
function FlipReportTable({ report }: { report: BinanceFlipReport }) {
  const t = useDashboardT();
  const head: React.CSSProperties = {
    textAlign: 'right', padding: '3px 6px', color: binanceMuted,
    fontSize: '0.62rem', fontWeight: 500, whiteSpace: 'nowrap',
  };
  const cell: React.CSSProperties = {
    textAlign: 'right', padding: '3px 6px', fontSize: '0.7rem', whiteSpace: 'nowrap',
  };
  const pct = (v: number | null, digits = 2, sign = true) =>
    v == null ? '–' : `${sign && v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        borderBottom: `1px solid ${COUNTERFACTUAL_COLOR}44`,
        paddingBottom: 4, marginBottom: 8,
      }}>
        <span style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.8rem', fontWeight: 600 }}>
          {t('binance.flipReport.title')}
        </span>
        <span style={{ color: binanceMuted, fontSize: '0.66rem' }}>
          {t('binance.skipReport.window', { days: report.window_days })}
        </span>
      </div>
      {(report.rows ?? []).length === 0 ? (
        <p style={{ margin: 0, fontSize: '0.7rem', color: binanceMuted, lineHeight: 1.6 }}>
          {(report.enabled_flips?.length ?? 0) > 0
            ? t('binance.flipReport.emptyOn', { on: report.enabled_flips!.join(', ') })
            : t('binance.flipReport.emptyOff')}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>{t('binance.flipReport.col.type')}</th>
                <th style={head}>{t('binance.flipReport.col.count')}</th>
                <th style={head}>{t('binance.flipReport.col.betterRate')}</th>
                <th style={head}>{t('binance.flipReport.col.avgBase')}</th>
                <th style={head}>{t('binance.flipReport.col.avgTotal')}</th>
                <th style={head}>{t('binance.flipReport.col.avgDelta')}</th>
                <th style={head}>{t('binance.flipReport.col.sumDelta')}</th>
              </tr>
            </thead>
            <tbody>
              {(report.rows ?? []).map((r) => (
                <tr key={r.flip_type} style={{ borderTop: '1px solid #ffffff10' }}>
                  <td style={{ ...cell, textAlign: 'left' }}>{r.flip_type}</td>
                  <td style={cell}>{r.n}</td>
                  <td style={cell}>
                    {r.flip_better_rate == null ? '–' : `${r.flip_better_rate.toFixed(0)}%`}
                  </td>
                  <td style={cell}>{pct(r.avg_base_pct, 3)}</td>
                  <td style={cell}>{pct(r.avg_total_pct, 3)}</td>
                  <td style={{ ...cell, color: COUNTERFACTUAL_COLOR }}>
                    {pct(r.avg_delta_pct, 3)}
                  </td>
                  <td style={{ ...cell, color: COUNTERFACTUAL_COLOR, fontWeight: 600 }}>
                    {pct(r.sum_delta_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {report.pending > 0 && (
        <p style={{ margin: '5px 0 0', fontSize: '0.66rem', color: binanceMuted }}>
          {t('binance.flipReport.pending', { n: report.pending })}
        </p>
      )}
      <p style={{ margin: '6px 0 0', fontSize: '0.66rem', color: binanceMuted, lineHeight: 1.6 }}>
        {t('binance.flipReport.note')}
      </p>
    </div>
  );
}

/**
 * abort 반사실 — "일찍 끊은 거래를 안 끊었으면?".
 *
 * 6% 스탑은 그대로 두고 그 앞에서 나가는 규칙이라, 규칙이 옳았는지는 **끊지 않았을
 * 경로**와 비교해야만 알 수 있다. 계속 올라 스탑에 맞았을지(abort 가 살렸다),
 * 되돌아와 이익이었을지(abort 가 잘라먹었다).
 *
 * flip 표와 같은 이유로 0건이어도 그린다.
 */
function AbortReportTable({ report, legBreakdown }: {
  report: BinanceAbortReport;
  /** kind="abort" 이벤트를 leg 로 group-by 한 결과 (abortLegBreakdown 참조).
   *  없거나 비어 있으면 다리별 표는 그리지 않는다 -- report.n===0 과 같은 상태다. */
  legBreakdown?: Record<string, { n: number; sumDelta: number }>;
}) {
  const t = useDashboardT();
  const pct = (v: number | null, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`;
  const legRows = ABORT_LEG_ORDER
    .filter((leg) => (legBreakdown?.[leg]?.n ?? 0) > 0)
    .map((leg) => ({ leg, ...legBreakdown![leg] }));
  const legHead: React.CSSProperties = {
    textAlign: 'right', padding: '3px 6px', color: binanceMuted,
    fontSize: '0.62rem', fontWeight: 500, whiteSpace: 'nowrap',
  };
  const legCell: React.CSSProperties = {
    textAlign: 'right', padding: '3px 6px', fontSize: '0.7rem', whiteSpace: 'nowrap',
  };
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        borderBottom: `1px solid ${COUNTERFACTUAL_COLOR}44`,
        paddingBottom: 4, marginBottom: 8,
      }}>
        <span style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.8rem', fontWeight: 600 }}>
          {t('binance.abortReport.title')}
        </span>
      </div>
      {report.n === 0 ? (
        <p style={{ margin: 0, fontSize: '0.7rem', color: binanceMuted, lineHeight: 1.6 }}>
          {t('binance.abortReport.empty')}
        </p>
      ) : (
        <div style={{ fontSize: '0.72rem', lineHeight: 1.9 }}>
          <div>
            {t('binance.abortReport.saved', { saved: report.n_saved, n: report.n })}
            <span style={{ color: COUNTERFACTUAL_COLOR, fontWeight: 600, marginLeft: 6 }}>
              {t('binance.abortReport.sum', { sum: pct(report.sum_delta_pct) })}
            </span>
          </div>
          <div style={{ color: binanceMuted }}>
            {t('binance.abortReport.compare', {
              actual: pct(report.avg_actual_pct, 3),
              base: pct(report.avg_base_pct, 3),
            })}
          </div>
          <div style={{ color: binanceMuted }}>
            {t('binance.abortReport.stopRate', {
              rate: report.base_stop_rate == null ? '–' : `${report.base_stop_rate.toFixed(0)}%`,
            })}
          </div>
        </div>
      )}
      {/* 다리별 쪼개기 (2026-08-05, 문서 §3-4). 집계 API 는 다리 무관 합산만 주므로
          위 legRows 는 events 를 순회해 프론트에서 계산한 값이다 -- report.n 과는
          별개 소스라 report.n===0 인데도 legRows 가 있을 수 없고(같은 kind='abort'
          필터), report.n>0 인데 legRows 가 비면(구행뿐이거나 leg 도입 전) 표를
          숨긴다. */}
      {legRows.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #ffffff12' }}>
          <div style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.75rem',
            fontWeight: 600, marginBottom: 6 }}>
            {t('binance.abortReport.legTitle')}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...legHead, textAlign: 'left' }}>{t('binance.abortReport.legCol.leg')}</th>
                  <th style={legHead}>{t('binance.skipReport.col.count')}</th>
                  <th style={legHead}>{t('binance.skipReport.col.avg')}</th>
                  <th style={legHead}>{t('binance.skipReport.col.sum')}</th>
                </tr>
              </thead>
              <tbody>
                {legRows.map((r) => {
                  const meta = ABORT_LEG_META[r.leg];
                  const avg = r.n > 0 ? r.sumDelta / r.n : null;
                  return (
                    <tr key={r.leg} style={{ borderTop: '1px solid #ffffff10' }}>
                      <td style={{ ...legCell, textAlign: 'left' }} title={meta.hint}>
                        <span style={{
                          display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                          background: meta.color, marginRight: 6,
                        }} />
                        {meta.label}
                      </td>
                      <td style={legCell}>{r.n}</td>
                      <td style={{ ...legCell, color: COUNTERFACTUAL_COLOR }}>{pct(avg, 3)}</td>
                      <td style={{ ...legCell, color: COUNTERFACTUAL_COLOR, fontWeight: 600 }}>{pct(r.sumDelta)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* 0건이어도 그린다 -- flip 표와 같은 이유다. u("아직 스탑이 안 났다")와
          u("집계가 깨졌다")를 화면에서 구분할 수 있어야 한다. */}
      {report.stop && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #ffffff12' }}>
          <div style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.75rem',
            fontWeight: 600, marginBottom: 3 }}>
            {t('binance.stopReport.title')}
          </div>
          {report.stop.n === 0 ? (
            <p style={{ margin: 0, fontSize: '0.7rem', color: binanceMuted, lineHeight: 1.6 }}>
              {t('binance.stopReport.empty')}
            </p>
          ) : (
          <div style={{ fontSize: '0.72rem', lineHeight: 1.9 }}>
            <div>
              {t('binance.abortReport.saved', {
                saved: report.stop.n_saved, n: report.stop.n,
              })}
              <span style={{ color: COUNTERFACTUAL_COLOR, fontWeight: 600, marginLeft: 6 }}>
                {t('binance.abortReport.sum', {
                  sum: report.stop.sum_delta_pct == null ? '–'
                    : `${report.stop.sum_delta_pct >= 0 ? '+' : ''}${report.stop.sum_delta_pct.toFixed(2)}%`,
                })}
              </span>
            </div>
            <div style={{ color: binanceMuted }}>
              {t('binance.stopReport.compare', {
                actual: report.stop.avg_actual_pct == null ? '–'
                  : `${report.stop.avg_actual_pct >= 0 ? '+' : ''}${report.stop.avg_actual_pct.toFixed(3)}%`,
                base: report.stop.avg_base_pct == null ? '–'
                  : `${report.stop.avg_base_pct >= 0 ? '+' : ''}${report.stop.avg_base_pct.toFixed(3)}%`,
              })}
            </div>
          </div>
          )}
        </div>
      )}
      {/* abort→롱 반사실 -- u("탈출규칙 없이 홀드했으면?"). 위 abort/stop 과 질문이
          또 다르다(하드스탑은 유지, 신고가정체 탈출만 뺀다) -- score_one_long() 참조. */}
      {report.long && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #ffffff12' }}>
          <div style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.75rem',
            fontWeight: 600, marginBottom: 3 }}>
            {t('binance.longReport.title')}
          </div>
          {report.long.n === 0 ? (
            <p style={{ margin: 0, fontSize: '0.7rem', color: binanceMuted, lineHeight: 1.6 }}>
              {t('binance.longReport.empty')}
            </p>
          ) : (
          <div style={{ fontSize: '0.72rem', lineHeight: 1.9 }}>
            <div>
              {t('binance.abortReport.saved', {
                saved: report.long.n_saved, n: report.long.n,
              })}
              <span style={{ color: COUNTERFACTUAL_COLOR, fontWeight: 600, marginLeft: 6 }}>
                {t('binance.abortReport.sum', {
                  sum: report.long.sum_delta_pct == null ? '–'
                    : `${report.long.sum_delta_pct >= 0 ? '+' : ''}${report.long.sum_delta_pct.toFixed(2)}%`,
                })}
              </span>
            </div>
            <div style={{ color: binanceMuted }}>
              {t('binance.longReport.compare', {
                actual: report.long.avg_actual_pct == null ? '–'
                  : `${report.long.avg_actual_pct >= 0 ? '+' : ''}${report.long.avg_actual_pct.toFixed(3)}%`,
                base: report.long.avg_base_pct == null ? '–'
                  : `${report.long.avg_base_pct >= 0 ? '+' : ''}${report.long.avg_base_pct.toFixed(3)}%`,
              })}
            </div>
            {report.long.pending > 0 && (
              <div style={{ color: binanceMuted }}>
                {t('binance.abortReport.pending', { n: report.long.pending })}
              </div>
            )}
          </div>
          )}
        </div>
      )}
      {/* 안 연 롱 반사실 -- u("열었으면 얼마였나"). 모집단 = abort 는 발동했는데 롱이
          안 열린 자리(슬롯/최소명목가/leg=oi_cvd 제외). score_long_skips() 참조. */}
      {report.long_skip && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #ffffff12' }}>
          <div style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.75rem',
            fontWeight: 600, marginBottom: 3 }}>
            {t('binance.longSkipReport.title')}
          </div>
          {report.long_skip.n === 0 ? (
            <p style={{ margin: 0, fontSize: '0.7rem', color: binanceMuted, lineHeight: 1.6 }}>
              {t('binance.longSkipReport.empty')}
            </p>
          ) : (
          <div style={{ fontSize: '0.72rem', lineHeight: 1.9, color: binanceMuted }}>
            {t('binance.longSkipReport.compare', {
              n: report.long_skip.n,
              sum: report.long_skip.sum_delta_pct == null ? '–'
                : `${report.long_skip.sum_delta_pct >= 0 ? '+' : ''}${report.long_skip.sum_delta_pct.toFixed(2)}%`,
              base: report.long_skip.avg_base_pct == null ? '–'
                : `${report.long_skip.avg_base_pct >= 0 ? '+' : ''}${report.long_skip.avg_base_pct.toFixed(3)}%`,
            })}
          </div>
          )}
        </div>
      )}
      {/* 청산 레인 반사실 4종 (2026-08-24). 여태 백엔드가 이 kind 들을 집계에서
          안 불러 화면이 0건이었다 — DB 에는 20건이 쌓여 있었다(liq_abort 9 ·
          liq_rescue 6 · liq_manual 5). 행 단위 abort_cf 는 진작 배선돼 있었고
          조인도 정상인데 집계만 빠졌다(07-30 flip · 08-13 liq 와 같은 함정,
          에러 없이 조용히 빈다).
          질문이 서로 달라 한 줄로 합치지 않는다. 0 건인 줄은 안 그린다. */}
      {(() => {
        const pct = (v: number | null | undefined) => (v == null ? '–'
          : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`);
        const rows: Array<[string, string, BinanceAbortReport | null | undefined]> = [
          ['liq_abort', u('안 끊었으면?'), report.liq_abort],
          ['liq_stall', u('더 뒀으면?'), report.liq_stall],
          ['liq_rescue', u('안 늘렸으면?'), report.liq_rescue],
          ['liq_manual', u('그냥 뒀으면?'), report.liq_manual],
        ];
        const shown = rows.filter(([, , r]) => r && r.n > 0);
        if (shown.length === 0) return null;
        return (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #ffffff12' }}>
            <div style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.75rem',
              fontWeight: 600, marginBottom: 3 }}>
              청산 레인 반사실
            </div>
            {shown.map(([k, q, r]) => (
              <div key={k} style={{ fontSize: '0.72rem', lineHeight: 1.9,
                color: binanceMuted }}>
                {`${q} n${r!.n} · 규칙 ${pct(r!.avg_actual_pct)} vs 반사실 `
                  + `${pct(r!.avg_base_pct)} · 순이득 ${pct(r!.sum_delta_pct)}p `
                  + `(${r!.n_saved}/${r!.n} 이득)`}
              </div>
            ))}
          </div>
        );
      })()}
      {report.pending > 0 && (
        <p style={{ margin: '5px 0 0', fontSize: '0.66rem', color: binanceMuted }}>
          {t('binance.abortReport.pending', { n: report.pending })}
        </p>
      )}
      <p style={{ margin: '6px 0 0', fontSize: '0.66rem', color: binanceMuted, lineHeight: 1.6 }}>
        {t('binance.abortReport.note')}
      </p>
    </div>
  );
}

/** 적용 중인 필터 전체를 눈으로 확인하는 모달. 내용은 전부 백엔드가 내려주므로
 *  필터가 바뀌어도 프론트는 그대로 둔다. */
/** 청산 캐스케이드 재판정 배너.
 *
 *  이 레인은 업비트와 달리 재판정이 **자동으로 적용되지 않는다**(2026-08-12 유저
 *  결정). 그래서 이 배너가 "값을 바꿔야 한다" 는 결론이 사람에게 닿는 유일한
 *  경로다 -- 접거나 줄이지 말 것. 문장도 자르지 않는다(잘리는 건 대개 숫자다).
 *
 *  신뢰 조건은 백엔드가 이미 걸었다: 실측 비용 · 플라시보 초과 · 홀드아웃 ·
 *  배포값 대비 대응표본 t. 화면은 그 결과와 근거 숫자를 그대로 보여주기만 한다. */
/** 레인 라이브 설정 모달 — 배분%·예산 (DB, 2026-09-01). */
function laneSizingFromAlloc(
  budgetPct: number, allocPct: number, k: number, tail: number, lev: number,
) {
  const marginLane = budgetPct * allocPct / 100;
  const kSafe = Math.max(k, 1);
  const marginSlot = marginLane / kSafe;
  const notionalPct = marginSlot * lev;
  const riskPct = notionalPct * tail / 100;
  return {
    alloc_pct: allocPct,
    margin_pct_lane: marginLane,
    margin_pct_per_slot: marginSlot,
    notional_pct: notionalPct,
    risk_pct: riskPct,
  };
}

/** 레인 설정 모달 표시 순서 — 레인박스·백엔드 LANE_ORDER 와 같다. */
const LANE_SETTINGS_DISPLAY_ORDER = [
  'upbit', 'bh', 'dual_short', 'liq', 'pump_ride', 'gyeop_long',
] as const;

function sortLaneSettingsLanes(lanes: BinanceLaneSettingsLane[]): BinanceLaneSettingsLane[] {
  const rank = (id: string) => {
    const i = LANE_SETTINGS_DISPLAY_ORDER.indexOf(
      id as typeof LANE_SETTINGS_DISPLAY_ORDER[number],
    );
    return i === -1 ? 99 : i;
  };
  return [...lanes].sort((a, b) => rank(a.id) - rank(b.id));
}

/** 청캐 진입모드(자동/되돌림/fade) 토글을 설정 화면에 그릴까. 2026-09-04 유저:
 *  "당분간 사용하지 않을거야". 코드는 남겨두고 이 상수만 true 로 되돌리면 복구된다.
 *  백엔드 liq_entry_mode 는 그대로 — 숨겨도 지금 값으로 계속 돈다. */
const SHOW_LIQ_ENTRY_MODE = false;

function LaneSettingsModal({
  initial, onClose, onSaved, canSave, detail,
}: {
  initial: BinanceLaneSettings;
  onClose: () => void;
  onSaved: (next: BinanceLaneSettings) => void;
  canSave: boolean;
  /** 옛 u('상세') 버튼 내용 중 레인에 안 걸리는 것(공용 필터·skip 반사실)을
   *  이 모달 맨 아래에 낸다 (2026-09-04 유저). */
  detail?: RetuneDetail;
}) {
  const [budgetPct, setBudgetPct] = useState(initial.budget_pct);
  const [lanes, setLanes] = useState<BinanceLaneSettingsLane[]>(
    () => sortLaneSettingsLanes(
      initial.lanes
        .filter((l) => !DASHBOARD_HIDDEN_LANE_KEYS.has(l.id))
        .map((l) => ({ ...l })),
    ),
  );
  /** 배분% 입력 중 빈 칸 허용 — Number("")===0 이면 지워도 0이 되살아남. */
  const [allocDraft, setAllocDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const recalcLane = (
    l: BinanceLaneSettingsLane, budget: number,
  ): BinanceLaneSettingsLane => {
    if (l.id === 'liq') {
      const v1k = Math.max(1, Number(l.v1?.k ?? l.k) || 1);
      const v1alloc = l.live
        ? Math.max(0, Number(l.v1?.alloc_pct ?? l.alloc_pct) || 0)
        : 0;
      const sz = laneSizingFromAlloc(
        budget, v1alloc, v1k, l.tail_pct, l.leverage,
      );
      const lev = Math.max(l.leverage || 3, 1e-9);
      // 수치 기본값은 여기 없다 — API(`normalize_liq_v2`)가 항상 채워 보낸다.
      // 정본은 `liqv2_lane.py` + DB `lanes.liq.v2` 뿐 (2026-09-10 유저).
      // 편집 중(l.v2 존재)에만 이 프리뷰가 돈다.
      const rawK = Number(l.v2?.k);
      const v2 = {
        k: Number.isFinite(rawK) ? Math.max(0, Math.min(50, Math.round(rawK))) : 0,
        base_notional_pct: Math.max(0, Number(l.v2?.base_notional_pct) || 0),
        step_notional_pct: Math.max(0, Number(l.v2?.step_notional_pct) || 0),
        margin_cap_pct: Math.max(0.01, Number(l.v2?.margin_cap_pct) || 0.01),
        sleeve_ratio: Math.min(5, Math.max(0.05, Number(l.v2?.sleeve_ratio) || 1)),
        drop_pct: Math.min(50, Math.max(0, Number(l.v2?.drop_pct) || 0)),
        max_legs: Math.min(20, Math.max(1, Math.round(Number(l.v2?.max_legs) || 1))),
        leg_step_pct: Math.min(50, Math.max(0, Number(l.v2?.leg_step_pct) || 0)),
        deep_mult: Math.min(3, Math.max(1, Number(l.v2?.deep_mult) || 1)),
        deep_cut_pct: Math.min(0, Math.max(-50, Number(l.v2?.deep_cut_pct) || 0)),
      };
      // k=0: 캡 잔여. k>0: 옛 cap×ratio^i/Σ.
      const r = v2.sleeve_ratio;
      const flat = Math.abs(r - 1) < 1e-9;
      const round2 = (x: number) => Math.round(x * 100) / 100;
      const sleeves: { sleeve: number; margin_pct: number; notional_pct: number; cum_margin_pct: number; capped: boolean }[] = [];
      let cum = 0;
      if (v2.k <= 0) {
        sleeves.push({
          sleeve: 1,
          margin_pct: round2(v2.margin_cap_pct),
          notional_pct: round2(v2.margin_cap_pct * lev),
          cum_margin_pct: round2(v2.margin_cap_pct),
          capped: false,
        });
        cum = v2.margin_cap_pct;
      } else {
        const denom = flat ? v2.k : (r ** v2.k - 1) / (r - 1);
        for (let i = 0; i < v2.k; i += 1) {
          const got = flat ? v2.margin_cap_pct / v2.k
            : v2.margin_cap_pct * r ** i / denom;
          cum += got;
          sleeves.push({
            sleeve: i + 1,
            margin_pct: round2(got),
            notional_pct: round2(got * lev),
            cum_margin_pct: round2(cum),
            capped: false,
          });
        }
      }
      return {
        ...l,
        ...sz,
        k: v1k,
        alloc_pct: v1alloc,
        v1: { ...sz, k: v1k, alloc_pct: v1alloc },
        v2: {
          ...v2,
          preview: {
            design_depth: v2.k <= 0 ? 1 : v2.k,
            sleeve_ratio: round2(r),
            shape: v2.k <= 0 ? u('캡잔여(k=0)') : (flat ? u('균등') : (r < 1 ? u('앞무겁게') : u('뒤무겁게'))),
            first_sleeve_margin_pct: sleeves[0]?.margin_pct ?? 0,
            last_sleeve_margin_pct: sleeves[sleeves.length - 1]?.margin_pct ?? 0,
            per_sleeve_margin_pct: round2(v2.k > 0 ? v2.margin_cap_pct / v2.k : v2.margin_cap_pct),
            per_sleeve_notional_pct: round2((v2.k > 0 ? v2.margin_cap_pct / v2.k : v2.margin_cap_pct) * lev),
            sleeves,
            notional_pcts: sleeves.map((s) => s.notional_pct),
            margin_sum_pct: round2(cum),
            margin_cap_pct: v2.margin_cap_pct,
            hits_cap_at: v2.k <= 0 ? 1 : v2.k,
            hits_cap: true,
          },
        },
      };
    }
    if (!l.live) {
      return {
        ...l,
        alloc_pct: 0,
        risk_pct: 0,
        notional_pct: 0,
        margin_pct_per_slot: 0,
        margin_pct_lane: 0,
      };
    }
    const sz = laneSizingFromAlloc(budget, l.alloc_pct, l.k, l.tail_pct, l.leverage);
    return { ...l, ...sz };
  };

  // 공유 풀은 **한 번만** 센다 (백엔드 _pool_totals 와 같은 규칙). 두 레인
  // 캡을 더하면 이중계산 — 칸 2개면 어떤 조합이든 먹는 자본은 같다.
  const poolKForSum = Number(
    lanes.find((l) => l.id === 'upbit')?.short_pool_k ?? initial.short_pool_k ?? 0,
  ) || 0;
  const poolIdsForSum = new Set(initial.short_pool_lane_ids ?? ['upbit', 'bh']);
  const slotMarginOfLane = (l: BinanceLaneSettingsLane) => {
    const base = l.v2?.base_notional_pct;
    return base ? base / Math.max(l.leverage, 1) : (l.margin_pct_per_slot || 0);
  };
  const poolLanesLive = lanes.filter(
    (l) => l.live && !l.follows && poolIdsForSum.has(l.id),
  );
  const totalMargin = (poolKForSum > 0 && poolLanesLive.length > 0)
    ? lanes.reduce(
      (s, l) => s + (l.live && !l.follows && !poolIdsForSum.has(l.id)
        ? l.margin_pct_lane : 0), 0,
    ) + Math.max(...poolLanesLive.map(slotMarginOfLane)) * poolKForSum
    : lanes.reduce(
      (s, l) => s + (l.live && !l.follows ? l.margin_pct_lane : 0), 0,
    );
  // 업숏·빗숏 공유 슬롯 풀 (2026-09-08). 켜져 있으면 둘의 k 를 **더하지 않는다**
  // — 합계가 실제보다 크게 나온다. 업숏2 / 업숏1+빗숏1 / 빗숏2 가 전부 같은 칸.
  const poolHomeLane = lanes.find((l) => l.id === 'upbit');
  const poolK = Number(poolHomeLane?.short_pool_k ?? initial.short_pool_k ?? 0) || 0;
  const liqLaneForKnobs = lanes.find((l) => l.id === 'liq');
  const blocksShort = (liqLaneForKnobs?.v2?.blocks_short_lanes
    ?? initial.liqv2_blocks_short_lanes ?? true) !== false;
  const flattenOthers = (liqLaneForKnobs?.flatten_others
    ?? initial.liq_flatten_others ?? false) === true;
  const onPoolKChange = (n: number) => updateLane('upbit', {
    short_pool_k: Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0,
  });
  const setBlocksShort = (v: boolean) => {
    // v2 는 API(normalize_liq_v2)가 항상 채워 보낸다. 없으면 레인 미로드 — 무시.
    if (!liqLaneForKnobs?.v2) return;
    updateLane('liq', { v2: { ...liqLaneForKnobs.v2, blocks_short_lanes: v } });
  };
  const setFlattenOthers = (v: boolean) => updateLane('liq', { flatten_others: v });
  const poolIds = new Set(initial.short_pool_lane_ids ?? ['upbit', 'bh']);
  const poolOn = poolK > 0 && lanes.some((l) => l.live && poolIds.has(l.id));
  const totalSlots = poolOn
    ? lanes.reduce((s, l) => s + (l.live && !poolIds.has(l.id) ? l.k : 0), 0) + poolK
    : lanes.reduce((s, l) => s + (l.live ? l.k : 0), 0);
  const poolUsedLanes = lanes.filter((l) => l.live && poolIds.has(l.id));
  // 쌍숏(follows)은 부모 몫을 빌려 씀 — 합산에서 제외 (백엔드 pie 와 동일).
  const allocSum = (poolKForSum > 0 && poolLanesLive.length > 0)
    ? lanes.reduce(
      (s, l) => s + (l.live && !l.follows && !poolIdsForSum.has(l.id)
        ? l.alloc_pct : 0), 0,
    ) + Math.max(...poolLanesLive.map((l) => l.alloc_pct))
    : lanes.reduce(
      (s, l) => s + (l.live && !l.follows ? l.alloc_pct : 0), 0,
    );
  const overBudget = totalMargin > budgetPct + 0.05;

  // --- 증거금 배분 그림 (2026-09-08) --------------------------------------
  // 레인이 **실제로 잡을 수 있는 증거금 상한**을 쌓는다. base/step 모델(v2)이
  // 있으면 그 margin_cap 이 상한이고, 없으면 배분에서 나온 margin_pct_lane 이다.
  const laneById = (id: string) => lanes.find((l) => l.id === id);
  const capOf = (id: string) => {
    const l = laneById(id);
    if (!l) return 0;
    const cap = l.v2?.margin_cap_pct;
    return Number(cap ?? l.margin_pct_lane) || 0;
  };
  const upLane = laneById('upbit');
  const bhLane = laneById('bh');
  const liqLane = laneById('liq');
  const liqV1Margin = Number(
    liqLane?.v1?.margin_pct_lane ?? liqLane?.margin_pct_lane,
  ) || 0;
  const poolNote = poolK > 0
    ? `업숏·빗숏 공유 ${poolK}칸 중 이 레인 몫`
    : `k=${upLane?.k ?? '-'} 독립 슬롯`;
  // 한 칸(=포지션 하나)이 먹는 증거금. base/step 모델이면 첫단 명목/lev.
  const slotMarginOf = (id: string) => {
    const l = laneById(id);
    if (!l) return 0;
    if (l.v2) return l.v2.base_notional_pct / Math.max(l.leverage, 1);
    return Number(l.margin_pct_per_slot) || 0;
  };
  const upSlot = slotMarginOf('upbit');
  const bhSlot = slotMarginOf('bh');
  const bothPool = !!(upLane?.live && bhLane?.live);
  // **공유 풀은 한 덩어리로 그린다.** 두 레인의 캡을 각각 그리면 이중계산이다 —
  // 칸이 2개면 업숏2든 빗숏2든 업1빗1이든 자본을 먹는 양은 같다. 칸마다 두
  // 색을 반씩 칠해 "이 칸은 둘 중 아무나" 를 보여준다.
  const poolSlotMargin = bothPool ? Math.max(upSlot, bhSlot)
    : (upLane?.live ? upSlot : bhSlot);
  const marginSegs: MarginSeg[] = poolK > 0 ? [
    {
      key: 'pool',
      label: bothPool ? u('업숏·빗숏 (공유)') : (upLane?.live ? u('업숏') : u('빗숏')),
      color: (upLane?.live ? EXCHANGE_COLOR.upbit : EXCHANGE_COLOR.bithumb),
      color2: bothPool ? EXCHANGE_COLOR.bithumb : undefined,
      slots: poolK,
      pct: poolSlotMargin * poolK,
      live: !!(upLane?.live || bhLane?.live),
      note: `${poolK}칸 · 건당 ${poolSlotMargin.toFixed(1)}%`
        + (bothPool
          ? ` (업숏 ${upSlot.toFixed(1)} · 빗숏 ${bhSlot.toFixed(1)} 중 큰 쪽)`
          : '')
        + ` — 업숏${poolK} / 업숏1+빗숏1 / 빗숏${poolK} 가 같은 칸`,
    },
  ] : [
    {
      key: 'upbit',
      label: u('업숏'),
      color: EXCHANGE_COLOR.upbit,
      slots: Math.max(upLane?.k ?? 1, 1),
      pct: capOf('upbit'),
      live: !!upLane?.live,
      note: `${poolNote} · 건당 ${upSlot.toFixed(1)}%`,
    },
    {
      key: 'bh',
      label: u('빗숏'),
      color: EXCHANGE_COLOR.bithumb,
      slots: Math.max(bhLane?.k ?? 1, 1),
      pct: capOf('bh'),
      live: !!bhLane?.live,
      note: `k=${bhLane?.k ?? '-'} 독립 슬롯 · 건당 ${bhSlot.toFixed(1)}%`,
    },
    {
      key: 'liq_v1',
      label: u('청캐 v1'),
      color: LIQ_COLOR,
      slots: Math.max(liqLane?.v1?.k ?? liqLane?.k ?? 1, 1),
      pct: liqV1Margin,
      live: !!liqLane?.live,
      note: `누적청산 <100건 · k=${liqLane?.v1?.k ?? liqLane?.k ?? '-'}`
        + ` (2026-09-09 OFF — liq_v1_enabled)`
        + ` · 건당 ${(liqV1Margin / Math.max(liqLane?.v1?.k ?? liqLane?.k ?? 1, 1)).toFixed(1)}%`,
    },
  ];
  // 청캐 v2 는 **자리를 예약하지 않는다**. 캡(80%)은 상한일 뿐이고, 실제로는
  // 다른 레인이 쓰고 남은 만큼만 슬리브를 깎아 쌓는다(liqv2 free_margin_usd,
  // 2026-09-08). 그래서 막대에는 **지금 실제로 남는 자리**를 그린다 — 캡을
  // 그대로 그리면 "예약된 몫"으로 읽혀 합이 늘 자본을 넘어 보인다.
  const reservedPct = marginSegs.reduce((a, x) => a + (x.live ? x.pct : 0), 0);
  const v2Cap = Number(liqLane?.v2?.margin_cap_pct) || 0;
  const v2Room = Math.min(v2Cap, Math.max(100 - reservedPct, 0));
  marginSegs.push({
    key: 'liq_v2',
    label: u('청캐 v2 사다리'),
    color: LIQ_V2_COLOR,
    pct: v2Room,
    live: !!(liqLane?.live && liqLane?.ladder_v2),
    note: (liqLane?.v2?.peak_confirm
      ? `고점확인ON(arm+15%·X5%·Y30m) 후 · `
      : `고점확인OFF · 고점통과 시 사이징×${Number(liqLane?.v2?.peak_boost_mult ?? 1).toFixed(1)} · `)
      + `조각 ${liqLane?.v2?.max_legs ?? '-'}(폭 ${Number(liqLane?.v2?.leg_step_pct ?? 0).toFixed(1)}%) · `
      + `캡 ${v2Cap.toFixed(0)}%`
      + (Number(liqLane?.v2?.k) > 0
        ? ` · 겹침 k${liqLane?.v2?.k} · ratio ${Number(liqLane?.v2?.sleeve_ratio ?? 1).toFixed(2)}`
        : u(' · k=0(캡 잔여·실조각만 예약)'))
      + ` · 자리없으면 같은종목 연책조각(평단 좋은 쪽)`
      + ` · 지금 남는 자리 ${v2Room.toFixed(1)}% (예약 아님 — 남는 만큼 깎아서 쌓는다)`,
  });
  const overAlloc = allocSum > 100.05;

  const updateLane = (id: string, patch: Partial<BinanceLaneSettingsLane>) => {
    setLanes((prev) => prev.map((l) => {
      if (l.id !== id) return l;
      const merged = { ...l, ...patch };
      return recalcLane(merged, budgetPct);
    }));
  };

  const onBudgetChange = (v: number) => {
    setBudgetPct(v);
    setLanes((prev) => prev.map((l) => recalcLane(l, v)));
  };

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setErr(null);
    try {
      const hiddenLanes = initial.lanes.filter((l) => DASHBOARD_HIDDEN_LANE_KEYS.has(l.id));
      const body: BinanceLaneSettings = {
        budget_pct: budgetPct,
        lanes: [...lanes, ...hiddenLanes],
        alloc_sum_pct: allocSum,
        total_slots: totalSlots,
        total_margin_pct: totalMargin,
        budget_headroom_pct: budgetPct - totalMargin,
      };
      const next = await saveLaneSettings(body);
      onSaved(next);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const cell: React.CSSProperties = {
    padding: '4px 6px', borderBottom: '1px solid #f0b90b22',
    fontVariantNumeric: 'tabular-nums',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={u("레인 설정")}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#12100c',
          border: `1px solid ${(overBudget || overAlloc) ? '#f85149aa' : '#f0b90b55'}`,
          borderRadius: 8, padding: '14px 16px', maxWidth: 760, width: '100%',
          maxHeight: '86vh', overflowY: 'auto', color: '#e8dcc0',
          fontSize: '0.78rem', lineHeight: 1.5,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 8,
        }}>
          <strong style={{ color: '#f0b90b', fontSize: '0.95rem' }}>{u('레인 설정')}</strong>
          <button type="button" className="binance-alert-refresh" onClick={onClose}>{u('닫기')}</button>
        </div>
        <p style={{ margin: '0 0 10px', color: binanceMuted, fontSize: '0.7rem' }}>
          배분% = 증거금 예산에서 레인 몫. 실제 증거금 = 예산선 × 배분/100.
          합이 100%를 넘어도 됨 — 먼저 들어간 레인이 쓰고, 나중 레인은 모자라면 스킵.
          패스키 저장 → DB → 라이브 수 초 내 반영.
          청캐 모드는 미무장 감시에 바로 적용 · 자동(파도)이 같은 쪽으로 오면 SKIP.
          {initial.updated_at ? ` · 갱신 ${initial.updated_at}` : ''}
          {!initial.has_db_row ? u(' · DB 없음 — yaml·기본배분 표시') : ''}
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>
            증거금 예산선 (자본의 %, 배분 100%일 때 쓸 상한)
            <input
              type="number"
              min={1}
              max={200}
              step={0.5}
              value={budgetPct}
              onChange={(e) => onBudgetChange(Number(e.target.value))}
              style={{ marginLeft: 8, width: 72 }}
            />
          </label>
          <span style={{
            color: (overBudget || overAlloc) ? '#f85149' : binanceMuted,
            fontSize: '0.68rem',
          }}>
            배분 합 {allocSum.toFixed(1)}% · 슬롯 {totalSlots} · 증거금 {totalMargin.toFixed(1)}%
            {overAlloc
              ? u(' — 배분>100%(선진입 우선, 저장 가능)')
              : !overBudget
                ? ` · 여유 ${(budgetPct - totalMargin).toFixed(1)}%`
                : ''}
          </span>
        </div>
        {/* 정본은 **DB** (2026-09-08 유저: u("정본은 db야")). yaml 에서 지웠고
            아래 입력은 live_lane_settings 를 바로 고친다. 안 보이면
            u("업숏 k=2 인데 왜 빗숏까지 합쳐 2건에서 막히지") 가 된다. */}
        <div style={{
          marginBottom: 10, padding: '6px 8px', borderRadius: 4,
          border: '1px solid #30363d', background: '#0d1117',
          color: binanceMuted, fontSize: '0.66rem', lineHeight: 1.6,
        }}>
          <b style={{ color: '#f0b90b' }}>{u('공유 슬롯 · 청캐 우선 (DB 정본)')}</b>
          <div>
            · 업숏·빗숏 공유
            <input
              type="number"
              min={0}
              max={12}
              step={1}
              value={poolK}
              title={u("0 이면 옛 레인별 k 로 돌아간다")}
              onChange={(e) => onPoolKChange(Number(e.target.value))}
              style={{ width: 40, margin: '0 4px' }}
            />
            슬롯
            {poolK > 0
              ? ` — 업숏${poolK} / 업숏1+빗숏1 / 빗숏${poolK} 가 전부 같은 칸이다.`
                + u(' 아래 레인별 k 는 상한일 뿐 합이 아니다')
                + (poolUsedLanes.length > 0
                  ? ` (지금 ON: ${poolUsedLanes.map((l) => l.label).join('·')})`
                  : u(' (둘 다 OFF)'))
              : u(' — 끔. 업숏·빗숏이 각자 k 를 쓴다')}
          </div>
          <div>
            <label style={{ marginRight: 10 }}>
              <input
                type="checkbox"
                checked={blocksShort}
                onChange={(e) => setBlocksShort(e.target.checked)}
                style={{ marginRight: 4 }}
              />
              청캐 v2 보유중 업숏·빗숏 신규 차단
            </label>
            <label>
              <input
                type="checkbox"
                checked={flattenOthers}
                onChange={(e) => setFlattenOthers(e.target.checked)}
                style={{ marginRight: 4 }}
              />
              청캐 진입 시 타레인 강제청산
              {flattenOthers ? '' : u(' (끔 — 열린 건은 끝까지 유지)')}
            </label>
          </div>
        </div>
        <LaneMarginBar segs={marginSegs} budgetPct={budgetPct} />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
          <thead>
            <tr style={{ color: '#f0b90b' }}>
              <th style={cell}>{u('레인')}</th>
              <th style={cell}>ON</th>
              <th style={cell}>k</th>
              <th style={cell}>{u('배분 %')}</th>
              <th style={cell}>{u('증거금')}</th>
              <th style={cell}>{u('명목·risk')}</th>
            </tr>
          </thead>
          <tbody>
            {lanes.map((l) => (
              <React.Fragment key={l.id}>
              <tr>
                <td style={cell}>{l.label}</td>
                <td style={cell}>
                  <input
                    type="checkbox"
                    checked={l.live}
                    onChange={(e) => {
                      const on = e.target.checked;
                      if (!on) {
                        setAllocDraft((d) => {
                          if (d[l.id] === undefined) return d;
                          const next = { ...d };
                          delete next[l.id];
                          return next;
                        });
                      }
                      updateLane(l.id, { live: on });
                    }}
                  />
                </td>
                <td style={cell}>
                  {l.id === 'liq' ? (
                    <span style={{ color: binanceMuted, fontSize: '0.65rem' }}>v1/v2↓</span>
                  ) : (
                    <>
                      <input
                        type="number"
                        min={0}
                        max={12}
                        step={1}
                        value={l.k}
                        disabled={!l.live}
                        onChange={(e) => updateLane(l.id, { k: Number(e.target.value) })}
                        style={{ width: 40 }}
                      />
                      {/* 공유 풀이 켜져 있으면 이 k 는 실제 상한이 아니다 */}
                      {poolOn && poolIds.has(l.id) ? (
                        <span
                          title={`업숏·빗숏이 ${poolK}칸을 나눠 쓴다 (yaml short_pool_k). `
                            + u('이 k 는 이 레인 혼자의 상한일 뿐 합이 아니다.')}
                          style={{
                            marginLeft: 4, padding: '0 4px', borderRadius: 3,
                            border: '1px solid #f0b90b', color: '#f0b90b',
                            fontSize: '0.6rem', whiteSpace: 'nowrap',
                          }}
                        >
                          공유 {poolK}
                        </span>
                      ) : null}
                    </>
                  )}
                </td>
                <td style={cell}>
                  {l.id === 'liq' ? (
                    <span style={{ color: binanceMuted, fontSize: '0.65rem' }}>
                      {l.live ? `v1 ${Number(l.v1?.alloc_pct ?? l.alloc_pct).toFixed(0)}%` : '–'}
                    </span>
                  ) : (
                    <input
                      type="number"
                      min={0}
                      max={500}
                      step={1}
                      value={
                        !l.live ? 0
                          : (allocDraft[l.id] !== undefined ? allocDraft[l.id] : l.alloc_pct)
                      }
                      disabled={!l.live || !!l.follows}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setAllocDraft((d) => ({ ...d, [l.id]: raw }));
                        if (raw === '') {
                          updateLane(l.id, { alloc_pct: 0 });
                          return;
                        }
                        const n = Number(raw);
                        if (Number.isFinite(n)) updateLane(l.id, { alloc_pct: n });
                      }}
                      onBlur={() => {
                        setAllocDraft((d) => {
                          if (d[l.id] === undefined) return d;
                          const next = { ...d };
                          delete next[l.id];
                          return next;
                        });
                      }}
                      style={{ width: 48 }}
                    />
                  )}
                </td>
                <td style={cell}>
                  {l.live ? `${l.margin_pct_lane.toFixed(1)}%` : '–'}
                  {l.live && l.k > 1 && l.id !== 'liq' ? (
                    <span style={{ color: binanceMuted, fontSize: '0.65rem' }}>
                      {' '}({l.margin_pct_per_slot.toFixed(1)}%/슬롯)
                    </span>
                  ) : null}
                  {l.live && l.id === 'liq' ? (
                    <span style={{ color: binanceMuted, fontSize: '0.65rem' }}>
                      {' '}(v1 {l.margin_pct_per_slot.toFixed(1)}%/슬롯)
                    </span>
                  ) : null}
                </td>
                <td style={{ color: binanceMuted, fontSize: '0.65rem', ...cell }}>
                  {l.live
                    ? (l.id === 'liq'
                      ? `v1 명목 ${l.notional_pct.toFixed(0)}% · risk ${l.risk_pct.toFixed(2)}%`
                      : l.id === 'upbit'
                        ? (
                          <span style={{ fontSize: '0.62rem' }}>
                            첫단
                            <input
                              type="number"
                              readOnly
                              value={Number(l.notional_pct.toFixed(0))}
                              title={u("슬롯 명목% (배분÷k)")}
                              style={{ width: 48, margin: '0 4px', opacity: 0.85 }}
                            />
                            %×
                            <input
                              type="number"
                              readOnly
                              value={1}
                              title={u("10% 배수")}
                              style={{ width: 36, margin: '0 4px', opacity: 0.85 }}
                            />
                            · +
                            <input
                              type="number"
                              readOnly
                              value={1}
                              title={u("segment +10%p 당 +1×")}
                              style={{ width: 36, margin: '0 4px', opacity: 0.85 }}
                            />
                            ×/10% · 캡
                            <input
                              type="number"
                              readOnly
                              value={Number(l.margin_pct_lane.toFixed(0))}
                              title={u("업숏 레인 증거금 한도%")}
                              style={{ width: 44, margin: '0 4px', opacity: 0.85 }}
                            />
                            %증거금
                          </span>
                        )
                        : `명목 ${l.notional_pct.toFixed(0)}% · risk ${l.risk_pct.toFixed(2)}%`)
                    : '–'}
                </td>
              </tr>
              {l.id === 'liq' && l.live ? (
                <>
                  <tr>
                    <td style={{ ...cell, paddingLeft: 16, color: '#5c5648', textDecoration: 'line-through' }} colSpan={1}>
                      v1 (누적청산 &lt;100)
                    </td>
                    <td style={cell} colSpan={5}>
                      <span style={{ color: '#7d7362', fontSize: '0.62rem' }}>
                        하드 OFF (`liq_v1_enabled: false`, 2026-09-09) — 편집 불가.
                        캐스케이드 초입(누적청산 &lt;100)은 아무도 안 잡는다.
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ ...cell, paddingLeft: 16, color: binanceMuted, borderBottom: '1px solid #f0b90b33' }}>
                      v2 사다리 (누적청산 ≥100)
                    </td>
                    <td style={{ ...cell, borderBottom: '1px solid #f0b90b33' }}>
                      <label style={{ fontSize: '0.62rem', color: binanceMuted }}>
                        <input
                          type="checkbox"
                          checked={!!l.ladder_v2}
                          onChange={(e) => updateLane(l.id, { ladder_v2: e.target.checked })}
                          style={{ marginRight: 4 }}
                        />
                        엔진 ON
                      </label>
                    </td>
                    <td style={{ ...cell, borderBottom: '1px solid #f0b90b33' }}>
                      <input
                        type="number"
                        min={0}
                        max={50}
                        step={1}
                        value={l.v2?.k ?? ""}
                        disabled={!l.ladder_v2}
                        title={u("0 = 상한 없음(캡만). >0 이면 같은종목 동시 슬리브 상한")}
                        onChange={(e) => updateLane(l.id, {
                          v2: {
                            ...l.v2!,
                            k: Number(e.target.value),
                          },
                        })}
                        style={{ width: 40 }}
                      />
                      <span style={{ fontSize: '0.58rem', color: '#7d7362', marginLeft: 4 }}>
                        {Number(l.v2?.k) > 0 ? u('겹침k') : u('k=0 캡만')}
                      </span>
                    </td>
                    <td style={{ ...cell, borderBottom: '1px solid #f0b90b33' }} colSpan={3}>
                      <span style={{ fontSize: '0.62rem', color: binanceMuted, lineHeight: 1.7 }}>
                        마진캡
                        <input
                          type="number"
                          min={1}
                          max={200}
                          step={1}
                          value={l.v2?.margin_cap_pct ?? ""}
                          disabled={!l.ladder_v2}
                          onChange={(e) => updateLane(l.id, {
                            v2: {
                              ...l.v2!,
                              margin_cap_pct: Number(e.target.value),
                            },
                          })}
                          style={{ width: 44, margin: '0 4px' }}
                        />
                        % · 모양(ratio)
                        <input
                          type="number"
                          min={0.3}
                          max={3}
                          step={0.05}
                          value={l.v2?.sleeve_ratio ?? ""}
                          disabled={!l.ladder_v2}
                          onChange={(e) => updateLane(l.id, {
                            v2: {
                              ...l.v2!,
                              sleeve_ratio: Number(e.target.value),
                            },
                          })}
                          style={{ width: 48, margin: '0 4px' }}
                        />
                        <span style={{ color: '#7d7362' }}>
                          {u('<1 앞무겁게 · 1 균등 · >1 뒤무겁게')}
                        </span>
                        <br />
                        되돌림 진입
                        <input
                          type="number"
                          min={0}
                          max={20}
                          step={0.5}
                          value={l.v2?.drop_pct ?? ""}
                          disabled={!l.ladder_v2}
                          onChange={(e) => updateLane(l.id, {
                            v2: {
                              ...l.v2!,
                              drop_pct: Number(e.target.value),
                            },
                          })}
                          style={{ width: 44, margin: '0 4px' }}
                        />
                        <span style={{ color: '#7d7362' }}>
                          % (러닝고점 대비 이만큼 꺾이면 진입 · 0 = 트리거가 즉시)
                        </span>
                        <br />
                        조각 최대
                        <input
                          type="number"
                          min={1}
                          max={20}
                          step={1}
                          value={l.v2?.max_legs ?? ""}
                          disabled={!l.ladder_v2}
                          onChange={(e) => updateLane(l.id, {
                            v2: {
                              ...l.v2!,
                              max_legs: Number(e.target.value),
                            },
                          })}
                          style={{ width: 44, margin: '0 4px' }}
                        />
                        개 · 조각 폭
                        <input
                          type="number"
                          min={0}
                          max={50}
                          step={0.5}
                          value={l.v2?.leg_step_pct ?? ""}
                          disabled={!l.ladder_v2}
                          onChange={(e) => updateLane(l.id, {
                            v2: {
                              ...l.v2!,
                              leg_step_pct: Number(e.target.value),
                            },
                          })}
                          style={{ width: 44, margin: '0 4px' }}
                        />
                        <span style={{ color: '#7d7362' }}>
                          % (슬리브 등분 · 신고가가 직전조각 +이만큼일 때 다음 조각)
                        </span>
                        <br />
                        깊은꺾임 배수
                        <input
                          type="number"
                          min={1}
                          max={3}
                          step={0.25}
                          value={l.v2?.deep_mult ?? ""}
                          disabled={!l.ladder_v2}
                          onChange={(e) => updateLane(l.id, {
                            v2: { ...l.v2!, deep_mult: Number(e.target.value) },
                          })}
                          style={{ width: 44, margin: '0 4px' }}
                        />
                        × · 고점 대비
                        <input
                          type="number"
                          min={-50}
                          max={0}
                          step={1}
                          value={l.v2?.deep_cut_pct ?? ""}
                          disabled={!l.ladder_v2}
                          onChange={(e) => updateLane(l.id, {
                            v2: { ...l.v2!, deep_cut_pct: Number(e.target.value) },
                          })}
                          style={{ width: 48, margin: '0 4px' }}
                        />
                        <span style={{ color: '#7d7362' }}>
                          % (진입가가 캐스케이드 고점 대비 이만큼 아래면 슬리브 ×배수 · 1× = OFF)
                        </span>
                        {l.v2?.preview?.sleeves?.length ? (
                          <>
                            <br />
                            {l.v2.preview.shape} · 슬리브 {l.v2.preview.first_sleeve_margin_pct?.toFixed(2)}
                            {' → '}{l.v2.preview.last_sleeve_margin_pct?.toFixed(2)}% 증거금
                            {u(' · 합 ')}{l.v2.preview.margin_sum_pct.toFixed(0)}% (= 캡)
                            <br />
                            <span style={{ color: '#7d7362' }}>
                              {l.v2.preview.sleeves.slice(0, 8).map((s) => s.margin_pct.toFixed(1)).join('·')}
                              {l.v2.preview.sleeves.length > 8 ? '…' : ''}% 증거금 (명목 ×lev)
                            </span>
                          </>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                </>
              ) : null}
              {l.id === 'liq' && SHOW_LIQ_ENTRY_MODE ? (
                <tr>
                  <td style={{ ...cell, borderBottom: '1px solid #f0b90b33' }} colSpan={6}>
                    <span style={{ marginRight: 8, color: binanceMuted }}>{u('진입모드')}</span>
                    {([
                      ['auto', u('자동')],
                      ['pullback', u('되돌림')],
                      ['cascade_fade', 'fade'],
                    ] as const).map(([val, lab]) => {
                      const on = (l.entry_mode || 'auto') === val;
                      return (
                        <button
                          key={val}
                          type="button"
                          className="binance-alert-refresh"
                          onClick={() => updateLane(l.id, { entry_mode: val })}
                          style={{
                            fontSize: '0.65rem',
                            padding: '2px 8px',
                            marginRight: 4,
                            borderColor: on ? '#f0b90b' : undefined,
                            color: on ? '#f0b90b' : binanceMuted,
                          }}
                        >
                          {lab}
                        </button>
                      );
                    })}
                    <span style={{
                      marginLeft: 6, color: binanceMuted, fontSize: '0.62rem',
                    }}>
                      {(l.entry_mode || 'auto') === 'auto'
                        ? u('파도(breadth)에 따라 전환')
                        : u('미무장 감시 고정 · 자동이 같은 쪽이면 SKIP')}
                    </span>
                  </td>
                </tr>
              ) : null}
              {l.id === 'upbit' && l.live ? (
                <tr>
                  <td style={{ ...cell, paddingLeft: 16, color: binanceMuted, borderBottom: '1px solid #f0b90b33' }}>
                    사이징 v2
                  </td>
                  <td style={{ ...cell, borderBottom: '1px solid #f0b90b33' }} colSpan={5}>
                    <span style={{ fontSize: '0.62rem', color: binanceMuted }}>
                      rung1
                      <input
                        type="number"
                        min={0}
                        max={400}
                        step={5}
                        value={l.v2?.base_notional_pct ?? 175}
                        onChange={(e) => updateLane(l.id, {
                          v2: {
                            ...(l.v2 || { step_notional_pct: 100, margin_cap_pct: 100 }),
                            base_notional_pct: Number(e.target.value),
                          },
                        })}
                        style={{ width: 52, margin: '0 4px' }}
                      />
                      %명목 +
                      <input
                        type="number"
                        min={0}
                        max={200}
                        step={5}
                        value={l.v2?.step_notional_pct ?? 100}
                        onChange={(e) => updateLane(l.id, {
                          v2: {
                            ...(l.v2 || { base_notional_pct: 175, margin_cap_pct: 100 }),
                            step_notional_pct: Number(e.target.value),
                          },
                        })}
                        style={{ width: 48, margin: '0 4px' }}
                      />
                      /rung · 캡
                      <input
                        type="number"
                        min={1}
                        max={200}
                        step={5}
                        value={l.v2?.margin_cap_pct ?? 100}
                        onChange={(e) => updateLane(l.id, {
                          v2: {
                            ...(l.v2 || { base_notional_pct: 175, step_notional_pct: 100 }),
                            margin_cap_pct: Number(e.target.value),
                          },
                        })}
                        style={{ width: 48, margin: '0 4px' }}
                      />
                      %증거금
                      {l.v2?.upbit_preview ? (
                        <span>
                          {' '}· {l.v2.upbit_preview.rungs
                            .map((r) => `${r.notional_pct.toFixed(0)}${r.capped ? '캡' : ''}`)
                            .join(' / ')}
                          {' '}· rung1 동시 {l.v2.upbit_preview.concurrent_rung1.toFixed(1)}개
                        </span>
                      ) : null}
                    </span>
                    <div style={{ fontSize: '0.58rem', color: binanceMuted, marginTop: 2 }}>
                      명목 = 자본 × (rung1% + (rung−1)×step%). 캡이 넘치면 스킵이 아니라
                      남은 만큼 축소 진입. <b>{u('k 는 사이징에 안 쓴다')}</b> — 동시 포지션 개수 레일.
                    </div>
                  </td>
                </tr>
              ) : null}
              {l.id === 'bh' ? (
                <tr>
                  <td style={{ ...cell, paddingLeft: 16, color: binanceMuted, borderBottom: '1px solid #f0b90b33' }}>
                    사이징 v2
                  </td>
                  <td style={{ ...cell, borderBottom: '1px solid #f0b90b33' }} colSpan={5}>
                    <span style={{ fontSize: '0.62rem', color: binanceMuted }}>
                      rung1
                      <input
                        type="number"
                        min={0}
                        max={400}
                        step={5}
                        value={l.v2?.base_notional_pct ?? 75}
                        onChange={(e) => updateLane(l.id, {
                          v2: {
                            ...(l.v2 || { step_notional_pct: 100, margin_cap_pct: 100 }),
                            base_notional_pct: Number(e.target.value),
                          },
                        })}
                        style={{ width: 52, margin: '0 4px' }}
                      />
                      %명목 +
                      <input
                        type="number"
                        min={0}
                        max={200}
                        step={5}
                        value={l.v2?.step_notional_pct ?? 100}
                        onChange={(e) => updateLane(l.id, {
                          v2: {
                            ...(l.v2 || { base_notional_pct: 75, margin_cap_pct: 100 }),
                            step_notional_pct: Number(e.target.value),
                          },
                        })}
                        style={{ width: 48, margin: '0 4px' }}
                      />
                      /rung · 캡
                      <input
                        type="number"
                        min={1}
                        max={200}
                        step={5}
                        value={l.v2?.margin_cap_pct ?? 100}
                        onChange={(e) => updateLane(l.id, {
                          v2: {
                            ...(l.v2 || { base_notional_pct: 75, step_notional_pct: 100 }),
                            margin_cap_pct: Number(e.target.value),
                          },
                        })}
                        style={{ width: 48, margin: '0 4px' }}
                      />
                      %증거금
                      {(l.v2?.bh_preview || l.v2?.upbit_preview) ? (
                        <span>
                          {' '}· {(l.v2.bh_preview || l.v2.upbit_preview)!.rungs
                            .map((r) => `${r.notional_pct.toFixed(0)}${r.capped ? '캡' : ''}`)
                            .join(' / ')}
                        </span>
                      ) : null}
                    </span>
                    <div style={{ fontSize: '0.58rem', color: binanceMuted, marginTop: 2 }}>
                      패키지 ON 때만 적용 (`bh_ladder_package`). 후보 75/100/100 —
                      업숏 175 복사 금지. 주문은 별도 OFF.
                    </div>
                  </td>
                </tr>
              ) : null}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {err ? (
          <p style={{ color: '#f85149', marginTop: 10, fontSize: '0.72rem' }}>{err}</p>
        ) : null}
        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="binance-alert-refresh" onClick={onClose}>{u('취소')}</button>
          <button
            type="button"
            className="binance-alert-refresh"
            disabled={!canSave || busy}
            onClick={() => { save(); }}
          >
            {busy ? u('저장 중…') : u('패스키로 저장')}
          </button>
        </div>

        {detail ? <LaneDetail scope="common" {...detail} /> : null}
      </div>
    </div>
  );
}

/** 재판정 표 행 — 패스키로 DB overlay 적용. */
function RetuneApplyBtn({
  kind, cell, label, matchesDeployed, onApplied,
}: {
  kind: string;
  cell: Record<string, unknown> | null | undefined;
  label?: string;
  matchesDeployed?: boolean;
  onApplied?: (kind: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  if (!passkeySupported()) return null;
  const disabled = busy || !cell || matchesDeployed;
  return (
    <button
      type="button"
      className="binance-alert-refresh"
      disabled={disabled}
      title={matchesDeployed ? u('이미 반영과 동일') : u('패스키로 DB 적용 (라이브 즉시)')}
      style={{ fontSize: '0.62rem', padding: '2px 5px', minWidth: 36, minHeight: 22 }}
      onClick={async (e) => {
        e.stopPropagation();
        if (!cell || busy) return;
        setBusy(true);
        try {
          const r = await applyRetuneRow(kind, cell, label);
          alert(r.message ?? u('적용됐다'));
          onApplied?.(kind);
        } catch (err) {
          alert(err instanceof Error ? err.message : String(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? '…' : u('적용')}
    </button>
  );
}

/** 재판정 모달 안 — 이 레인 타이머·게이트 (KST). */
function RetuneScheduleBlock({
  schedule,
  computedAt,
  lastGate,
  runKind,
  lastElapsedS,
  runWhenOverride,
  cohortN,
}: {
  schedule: RetuneScheduleDef;
  computedAt?: string | null;
  lastGate?: {
    at: string;
    ran: boolean;
    reason?: string | null;
    n_candidates?: number | null;
  } | null;
  runKind: RetuneRunKind;
  lastElapsedS?: number | null;
  runWhenOverride?: string | null;
  /** 수동 실행 시 함께 저장·적용할 격자 건수. */
  cohortN?: number | null;
}) {
  const m = scheduleRowMeta(schedule, computedAt, lastGate, runWhenOverride);
  const [running, setRunning] = React.useState(false);
  const [runSource, setRunSource] = React.useState<'auto' | 'manual' | null>(null);
  const [runBusy, setRunBusy] = React.useState(false);
  const [runMsg, setRunMsg] = React.useState<string | null>(null);
  const [runElapsed, setRunElapsed] = React.useState<number | null>(null);
  const [lastFail, setLastFail] = React.useState<RetuneRunFail | null>(null);

  const applyStatus = React.useCallback((st: {
    running?: boolean;
    source?: 'auto' | 'manual' | null;
    elapsed_s?: number | null;
    last_fail?: RetuneRunFail | null;
  } | undefined) => {
    setRunning(Boolean(st?.running));
    setRunSource(st?.running ? (st?.source ?? null) : null);
    if (st?.running && st.elapsed_s != null) {
      setRunElapsed(st.elapsed_s);
    } else if (!st?.running) {
      setRunElapsed(null);
    }
    setLastFail(st?.running ? null : (st?.last_fail ?? null));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const poll = () => {
      fetchRetuneRunStatus().then((s) => {
        if (cancelled) return;
        applyStatus(s.status?.[runKind]);
      }).catch(() => { /* fail-open — 다음 폴링에서 재시도 */ });
    };
    poll();
    const id = window.setInterval(poll, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [runKind, applyStatus]);

  React.useEffect(() => {
    if (!running || runElapsed == null) return undefined;
    const id = window.setInterval(() => {
      setRunElapsed((e) => (e == null ? e : e + 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [running, runElapsed]);

  const elapsedDisplay = running && runElapsed != null
    ? `${formatRetuneElapsed(runElapsed)} (진행 중)`
    : formatRetuneElapsed(lastElapsedS);

  const onManualRun = async () => {
    setRunBusy(true);
    setRunMsg(null);
    setLastFail(null);
    try {
      const r = await runRetuneJob(runKind, cohortN ?? undefined);
      setRunning(Boolean(r.running));
      setRunSource(r.running ? 'manual' : null);
      setRunMsg(r.message || (r.started ? u('백그라운드 실행 시작') : u('이미 실행 중')));
    } catch (e) {
      setRunMsg(e instanceof Error ? e.message : u('실패'));
    } finally {
      setRunBusy(false);
    }
  };

  const runBtnLabel = runBusy
    ? '…'
    : running
      ? (runSource === 'auto' ? u('자동 실행 중') : u('실행 중'))
      : u('수동 실행');

  return (
    <div className="binance-retune-schedule" style={{ marginBottom: 10 }} aria-label={u("재판정 스케줄")}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 8, marginBottom: 4,
      }}>
        <div className="binance-retune-schedule-title" style={{ marginBottom: 0 }}>
          재판정 스케줄 (한국시간)
        </div>
        {passkeySupported() ? (
          <button
            type="button"
            className="binance-alert-refresh"
            disabled={runBusy || running}
            onClick={onManualRun}
            title={running && runSource === 'auto'
              ? u('타이머 재판정이 돌고 있다 — 끝날 때까지 수동 실행 불가')
              : `게이트 무시·강제 실행 (${schedule.runDurationShort})`}
            style={{ fontSize: '0.62rem', padding: '2px 8px', flexShrink: 0 }}
          >
            {runBtnLabel}
          </button>
        ) : null}
      </div>
      {runMsg ? (
        <div style={{ fontSize: '0.62rem', color: '#b7a882', marginBottom: 4 }}>{runMsg}</div>
      ) : null}
      {!running && lastFail ? (
        <div style={{
          fontSize: '0.62rem', color: '#f85149', marginBottom: 4,
          border: '1px solid #f8514955', borderRadius: 3, padding: '4px 6px',
        }}>
          <div style={{ fontWeight: 600 }}>
            마지막 수동 실행 실패
            {lastFail.at ? ` · ${formatKstIso(lastFail.at, { withLabel: true })}` : ''}
            {lastFail.exit_status ? ` · exit ${lastFail.exit_status}` : ''}
          </div>
          {lastFail.error ? (
            <div style={{ color: '#f0b90b', wordBreak: 'break-all', marginTop: 2 }}>
              {lastFail.error}
            </div>
          ) : null}
          {lastFail.log_tail ? (
            <details style={{ marginTop: 2 }}>
              <summary style={{ cursor: 'pointer', color: '#b7a882' }}>{u('로그 꼬리')}</summary>
              <pre style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '2px 0 0',
                maxHeight: 160, overflow: 'auto', color: '#b7a882',
                fontSize: '0.58rem',
              }}>{lastFail.log_tail}</pre>
            </details>
          ) : null}
        </div>
      ) : null}
      <table className="binance-retune-schedule-table">
        <tbody>
          <tr>
            <td className="binance-retune-schedule-lane">{u('타이머 깨움')}</td>
            <td className="binance-retune-schedule-wake">{m.wakeKst}</td>
          </tr>
          <tr>
            <td className="binance-retune-schedule-lane">{u('실행 조건')}</td>
            <td>{m.runWhen}</td>
          </tr>
          <tr>
            <td className="binance-retune-schedule-lane">{u('실행 소요')}</td>
            <td>{elapsedDisplay}</td>
          </tr>
          <tr>
            <td className="binance-retune-schedule-lane">{u('마지막 실행')}</td>
            <td>{m.lastWakeKst !== '–' ? m.lastWakeKst : m.lastJudgmentKst}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** 재판정 모달 — 격자 건수/창 시간 입력 (live_retune_prefs). */
function RetuneCohortControls({
  lane,
  cohortN,
  seedDefault,
  fromPrefs,
  unit = 'count',
  label,
  min = 10,
  max = 500,
  lagMin,
  onSaved,
  onChange,
}: {
  lane: RetunePrefsLane;
  cohortN: number;
  seedDefault?: number | null;
  fromPrefs?: boolean | null;
  unit?: 'count' | 'hours' | null;
  label?: string | null;
  min?: number | null;
  max?: number | null;
  /** unit='hours' 일 때 채점 구간이 지금보다 얼마나 뒤인가(분). 홀드 지평. */
  lagMin?: number | null;
  onSaved?: () => void;
  onChange?: (n: number | null) => void;
}) {
  const lo = min ?? 10;
  const hi = max ?? 500;
  const title = label || (unit === 'hours' ? u('판정 창(시간)') : u('격자 건수'));
  const [input, setInput] = React.useState(String(cohortN));
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);
  React.useEffect(() => { setInput(String(cohortN)); }, [cohortN]);
  const parsed = React.useMemo(() => {
    const n = parseInt(input, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [input]);
  React.useEffect(() => { onChange?.(parsed); }, [parsed, onChange]);

  // unit='hours': 입력하는 즉시 실제 채점 구간을 보여준다. 되돌림 격자는
  // 트리거를 홀드 지평(lagMin)만큼 지나야 채점하므로 구간이 그만큼 뒤로 밀린다
  // — 입력 N = [now−N−lag, now−lag]. (백엔드 pullback_pick 과 같은 식.)
  const windowPreview = React.useMemo(() => {
    if (unit !== 'hours' || parsed == null || lagMin == null) return null;
    const end = new Date(Date.now() - lagMin * 60000);
    const start = new Date(end.getTime() - parsed * 3600000);
    return { start, end };
  }, [unit, parsed, lagMin]);

  const onSave = async () => {
    if (parsed == null || parsed < lo || parsed > hi) {
      setMsg(`${lo}~${hi} 사이`);
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await saveRetuneCohort(lane, parsed);
      if (!res.ok) throw new Error(res.error || u('저장 실패'));
      setMsg(unit === 'hours'
        ? `창 ${parsed}시간 저장 — 다음 재판정에 적용`
        : `격자 ${parsed}건 저장 — 다음 자동·수동 재판정에 적용`);
      onSaved?.();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : u('저장 실패'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
      marginBottom: 10, fontSize: '0.72rem',
    }}>
      <label style={{ color: '#b7a882' }}>
        {title}
        <input
          type="number"
          min={lo}
          max={hi}
          step={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          style={{
            marginLeft: 6, width: 64, padding: '2px 6px',
            background: '#0d0b08', border: '1px solid #f0b90b44',
            borderRadius: 4, color: '#e8dcc0',
          }}
        />
      </label>
      {passkeySupported() ? (
        <button
          type="button"
          className="binance-alert-refresh"
          disabled={busy || parsed == null}
          onClick={onSave}
          title={u("자동·수동 재판정 모두 이 값을 쓴다")}
          style={{ fontSize: '0.62rem', padding: '2px 8px' }}
        >
          {busy ? '…' : u('저장')}
        </button>
      ) : null}
      {seedDefault != null && (
        <span style={{ color: '#b7a882', fontSize: '0.64rem' }}>
          {fromPrefs ? u('DB 저장됨') : `시드 ${seedDefault}`}
        </span>
      )}
      {msg ? (
        <span style={{
          color: msg.includes(u('실패')) || msg.includes(u('사이'))
            ? '#f85149' : '#3fb950',
          fontSize: '0.64rem',
        }}>{msg}</span>
      ) : null}
      {windowPreview ? (
        <span style={{
          width: '100%', color: '#b7a882', fontSize: '0.64rem', lineHeight: 1.5,
        }}>
          채점 구간 {formatKstDate(windowPreview.start, false)} ~{' '}
          {formatKstDate(windowPreview.end, false)} KST
          {' · '}지금 터진 트리거는 홀드 {Math.round((lagMin ?? 0) / 60)}h 뒤에 채점됨
        </span>
      ) : null}
    </div>
  );
}

/** 총합순 표의 「비고」 칸 — 격자 전체 셀 중 이 셀의 승률 순위. 표에 보이는
 *  몇 줄이 아니라 백엔드가 격자 전체에서 매겨 내려준다 (win_rank, 2026-09-04 유저). */
function winRankNote(
  c?: { win_rank?: number | null } | null,
): string {
  return c != null && c.win_rank != null ? `승률 ${c.win_rank}위` : '–';
}

/** 빗숏·업숏 되돌림 재판정 모달 — BhRetuneModal 과 같은 자리. */
/** 편승 재판정 모달 (2026-08-31). 청캐 모달과 같은 자리·같은 계약이다.
 *
 *  **총합을 기대수익으로 읽으면 안 된다.** 격자는 화면 건당 박스와 같은 재생
 *  프레임이라 슬리피지·증거금·주문 실패가 빠져 있고, 같은 기간 원장에서 이
 *  레인은 적자였다. 그래서 캡션에 그 한 줄을 박아 둔다 — 08-31 아침에
 *  "박스 vs 원장" 을 섞어 읽은 사고가 정확히 이 모양이었다. */
function BhRetuneModal({ r, onClose, lane = u('빗숏'), onApplied, detail, liveCfg }: {
  r: BinanceBhRetune;
  onClose: () => void;
  /** 업숏도 같은 모양이라 이 모달을 공유한다. */
  lane?: string;
  onApplied?: (kind: string) => void;
  /** 옛 u('상세') 버튼 내용 — 이 레인 몫을 모달 맨 아래에 낸다. */
  detail?: RetuneDetail;
  /** 업숏 모달「지금 도는 설정」아래 쌍숏 줄 — 라이브 watch payload. */
  liveCfg?: BinanceShortLiveConfig | null;
}) {
  const pbKind = lane === u('업숏') ? 'upbit_pb'
    : lane === u('쌍숏') ? 'dual_short' : 'bh_pb';
  const schedule = lane === u('업숏') ? UPBIT_PB_SCHEDULE
    : lane === u('쌍숏') ? DUAL_SHORT_SCHEDULE : BH_PB_SCHEDULE;
  const cohortDefault = r.cohort_n ?? r.window_n ?? 20;
  const [cohortParsed, setCohortParsed] = React.useState<number | null>(cohortDefault);
  const onCohortChange = React.useCallback((n: number | null) => {
    setCohortParsed(n);
  }, []);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cell: React.CSSProperties = {
    padding: '5px 8px', borderBottom: '1px solid #f0b90b22', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  };
  const head: React.CSSProperties = {
    ...cell, color: '#f0b90b', fontSize: '0.68rem',
    borderBottom: '1px solid #f0b90b44',
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 1) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;
  const g = (v: number | null | undefined, suffix = '') =>
    v == null ? '–' : `${v}${suffix}`;
  const knobs = (c?: BinanceBhRetuneCell | null) =>
    c == null ? '–'
      : `${g(c.drop, '%')}/${g(c.watch, 'm')}/peak${g(c.peak, '%')}/`
        + `stall${g(c.stall, 'm')}/${g(c.hold, 'm')}`;
  const bhAxesKey = (c?: BinanceBhRetuneCell | null) => {
    if (!c || c.drop == null || c.watch == null || c.hold == null
        || c.peak == null || c.stall == null) return null;
    return `${Number(c.drop)}/${c.watch}/${c.hold}/${Number(c.peak)}/${c.stall}`;
  };
  const bhAxesMatch = (a?: BinanceBhRetuneCell | null,
                       b?: BinanceBhRetuneCell | null) => {
    const ka = bhAxesKey(a);
    const kb = bhAxesKey(b);
    return ka != null && ka === kb;
  };

  // 채택후보: best 가 있으면 그걸, 없으면 applied(옛 계약) — 둘 다 없으면 관문 미통과.
  const best = r.best ?? (r.passed ? r.applied : null);
  const rows: { label: string; role: string; c?: BinanceBhRetuneCell | null }[] = [
    { label: u('배포'), role: 'deployed', c: r.deployed },
    { label: u('최적 패키지'), role: 'recommended', c: best },
    { label: u('총합1위'), role: 'top', c: r.top?.[0] ?? null },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${lane} 되돌림 재판정`}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#12100c',
          border: `1px solid ${r.needs_change ? '#f85149aa' : '#f0b90b55'}`,
          borderRadius: 8, padding: '14px 16px', maxWidth: 760, width: '100%',
          minWidth: 0, boxSizing: 'border-box',
          maxHeight: '86vh', overflowX: 'hidden', overflowY: 'auto', color: '#e8dcc0',
          fontSize: '0.78rem', lineHeight: 1.5,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: 4,
        }}>
          <strong style={{
            color: r.needs_change ? '#f85149' : '#f0b90b',
            fontSize: '0.95rem',
          }}>
            {lane} 재판정 · {r.verdict}
          </strong>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #f0b90b55', color: '#e8dcc0',
              borderRadius: 4, cursor: 'pointer', padding: '2px 8px',
            }}
          >{u('닫기')}</button>
        </div>

        {lane === u('업숏') ? <UpbitPbDailyPanel liveCfg={liveCfg} /> : null}
        {lane === u('업숏') ? <UpbitRescueDailyPanel /> : null}
        {lane === u('빗숏') ? <BhPbDailyPanel liveCfg={liveCfg} /> : null}

        <RetuneScheduleBlock
          schedule={schedule}
          computedAt={r.computed_at}
          lastGate={r.last_gate}
          runKind={pbKind}
          runWhenOverride={r.run_when}
          lastElapsedS={r.elapsed_s}
          cohortN={cohortParsed}
        />

        <RetuneCohortControls
          lane={pbKind as RetunePrefsLane}
          cohortN={cohortDefault}
          seedDefault={r.cohort_seed_default}
          fromPrefs={r.cohort_from_prefs}
          unit={r.cohort_unit}
          label={r.cohort_label}
          min={r.cohort_min}
          max={r.cohort_max}
          onChange={onCohortChange}
          onSaved={() => onApplied?.('cohort')}
        />

        <p style={{ margin: '0 0 10px', fontSize: '0.7rem', color: '#b7a882', lineHeight: 1.6 }}>
          {r.auto
            ? u('자동적용 ON — 통과하면 라이브가 이 값을 집어간다.')
            : u('자동 적용하지 않는다. 표 「적용」= 패스키로 DB 반영(다음 사이클에 라이브).')}
          {' · '}되돌림 격자(꺾임·감시·peak·stall·홀드) 최적 패키지.
          {' · '}격자 {r.cohort_n ?? r.window_n}건
          {r.window_n !== (r.cohort_n ?? r.window_n)
            ? ` (직전 채점 ${r.window_n}건)` : ''}
          {r.n_alerts != null ? ` · 알림풀 ${r.n_alerts}` : ''}
          {r.n_cells != null ? ` · 셀 ${r.n_cells}` : ''}
        </p>

        {r.reason ? (
          <div style={{
            border: `1px solid ${r.needs_change ? '#f8514955' : '#f0b90b33'}`,
            borderRadius: 6, padding: '8px 10px', marginBottom: 12,
            fontSize: '0.75rem', lineHeight: 1.7,
          }}>
            {r.reason}
          </div>
        ) : null}

        <div style={{
          margin: '0 0 6px', color: '#f0b90b', fontSize: '0.66rem',
          letterSpacing: '0.04em',
        }}>
          되돌림 격자 총합 (관문 통과=최적 패키지)
        </div>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%', fontSize: '0.75rem' }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>{u('셀')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('설정')}</th>
              <th style={head}>n</th>
              <th style={head} title={u("판정 창 전체 수익(%p) = 슬롯을 채운 합")}>{u('총합')}</th>
              <th style={head}>{u('건당')}</th>
              <th style={head} title={u("손익>0 건수 / n")}>{u('승률')}</th>
              <th style={head} title={u("그 칸 최소 건손익(%p)")}>{u('최악')}</th>
              <th style={head} title={u("배포 패키지 총합 대비 초과")}>{u('vs배포')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('3등분')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('비고')}</th>
              <th style={head} />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ label, role, c }) => {
              const isRec = role === 'recommended';
              const isDep = role === 'deployed';
              return (
              <tr key={label}>
                <td style={{
                  ...cell, textAlign: 'left',
                  color: isRec ? '#f0b90b' : '#b7a882',
                  fontWeight: isDep || isRec ? 700 : 400,
                }}>
                  {label}
                  {bhAxesMatch(c, r.deployed) && !isDep ? (
                      <span style={{ color: '#b7a882', marginLeft: 5, fontSize: '0.64rem' }}>
                        =배포
                      </span>
                    ) : null}
                </td>
                {c ? (
                  <>
                    <td style={{ ...cell, textAlign: 'left' }}>{knobs(c)}</td>
                    <td style={cell}>{c.n ?? '–'}</td>
                    <td style={{ ...cell, color: sign(c.total), fontWeight: 700 }}>
                      {num(c.total)}
                    </td>
                    <td style={{ ...cell, color: sign(c.avg) }}>{num(c.avg, 2)}</td>
                    <td style={cell}>
                      {c.win != null ? `${num(c.win, 0)}%` : '–'}
                    </td>
                    <td style={{ ...cell, color: sign(c.worst) }}>{num(c.worst, 2)}</td>
                    <td style={{ ...cell, color: sign(c.vs_deployed) }}>
                      {num(c.vs_deployed, 2)}
                    </td>
                    <td style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                      {c.thirds ? c.thirds.map((x) => num(x, 0)).join(' / ') : '–'}
                    </td>
                    <td style={{ ...cell, textAlign: 'left', color: '#b7a882', fontSize: '0.68rem' }}>
                      {c.win_rank != null ? (
                        <span style={{ color: '#f0b90b' }}>승률 {c.win_rank}위</span>
                      ) : null}
                      {c.excess != null
                        ? `${c.win_rank != null ? ' · ' : ''}대조 ${num(c.excess, 2)}`
                        : (c.win_rank != null ? null : (isDep ? u('성적 없음') : '–'))}
                    </td>
                    <td style={{ ...cell, textAlign: 'center' }}>
                      <RetuneApplyBtn
                        kind={pbKind}
                        cell={c as Record<string, unknown>}
                        label={label}
                        matchesDeployed={
                          isDep || bhAxesMatch(c, r.deployed)
                        }
                        onApplied={onApplied}
                      />
                    </td>
                  </>
                ) : (
                  <td colSpan={10} style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                    {label === u('최적 패키지') ? u('관문을 넘은 셀 없음 (정상)') : '–'}
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {r.top && r.top.length > 1 ? (
          <>
            <div style={{
              margin: '14px 0 4px', color: '#f0b90b', fontSize: '0.66rem',
              letterSpacing: '0.04em',
            }}>
              참고 · <b>{u('총합순')}</b> 상위 (수천 셀 중 최고라 뽑기 편향이 있다 — 권고가 아니다)
            </div>
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>{u('설정')}</th>
                  <th style={head}>n</th>
                  <th style={head}>{u('총합')}</th>
                  <th style={head}>{u('건당')}</th>
                  <th style={head}>{u('승률')}</th>
                  <th style={head} title={u("배포 패키지 총합 대비 초과")}>{u('vs배포')}</th>
                  <th style={{ ...head, textAlign: 'left' }}>{u('3등분')}</th>
                  <th style={{ ...head, textAlign: 'left' }} title={u("격자 전체 셀 중 승률 순위")}>{u('비고')}</th>
                  <th style={head} />
                </tr>
              </thead>
              <tbody>
                {r.top.map((c, i) => (
                  <tr key={i}>
                    <td style={{ ...cell, textAlign: 'left' }}>{knobs(c)}</td>
                    <td style={cell}>{c.n ?? '–'}</td>
                    <td style={{ ...cell, color: sign(c.total), fontWeight: 700 }}>
                      {num(c.total)}
                    </td>
                    <td style={{ ...cell, color: sign(c.avg) }}>{num(c.avg, 2)}</td>
                    <td style={cell}>{c.win != null ? `${num(c.win, 0)}%` : '–'}</td>
                    <td style={{ ...cell, color: sign(c.vs_deployed) }}>
                      {num(c.vs_deployed, 2)}
                    </td>
                    <td style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                      {c.thirds ? c.thirds.map((x) => num(x, 0)).join(' / ') : '–'}
                    </td>
                    <td style={{ ...cell, textAlign: 'left', color: '#b7a882', fontSize: '0.68rem' }}>
                      {winRankNote(c)}
                      {c.excess != null ? ` · 대조 ${num(c.excess, 2)}` : ''}
                    </td>
                    <td style={{ ...cell, textAlign: 'center' }}>
                      <RetuneApplyBtn
                        kind={pbKind}
                        cell={c as Record<string, unknown>}
                        label={`총합#${i + 1}`}
                        matchesDeployed={bhAxesMatch(c, r.deployed)}
                        onApplied={onApplied}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        ) : null}

        <div style={{ marginTop: 12, color: '#b7a882', fontSize: '0.72rem' }}>
          <div>
            축은 되돌림% × 감시분 × peak% × stall분 × hold_after 다섯. 판정축은 슬롯 k 를
            채운 창 합계이고, **같은 분 다른 심볼 대조군 대비**로 잰다.
          </div>
          <div style={{ marginTop: 4 }}>
            관문: 배포 우위 · n≥6 · 3등분 전부 양수 · 대조 excess&gt;0 · 감시 고원 ·
            스파이크 금지 · 이웃 과반 초과 · min_gain.
          </div>
          <div style={{ marginTop: 4, color: '#d29922' }}>
            ※ 총합은 <strong>{u('재생 프레임')}</strong> 값이다 — 라이브 손익이 아니다.
            실적은 원장(live_ledger) · 위 일일채점을 볼 것.
          </div>
          <div style={{ marginTop: 4, color: r.auto ? undefined : '#d29922' }}>
            {r.auto
              ? u('자동적용 ON — 통과하면 라이브가 이 값을 집어간다.')
              : u('※ 자동적용 OFF — 라이브는 이 행을 읽지 않는다. 통과해도 권고다.')}
          </div>
        </div>

        {detail && (lane === u('업숏') || lane === u('빗숏')) ? (
          <LaneDetail scope={lane === u('업숏') ? 'upbit' : 'bh'} {...detail} />
        ) : null}
      </div>
    </div>
  );
}

/** 날짜 열은 오른쪽이 최신이라 가로 스크롤은 맨 끝에서 연다. */
/** 성적 모달 표 공통. 2026-08-07 부터 오늘(로컬)까지 MM-DD. */
function scoreDayAxis(): string[] {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const cur = new Date(2026, 7, 7);
  const out: string[] = [];
  while (cur <= end) {
    const mm = String(cur.getMonth() + 1).padStart(2, '0');
    const dd = String(cur.getDate()).padStart(2, '0');
    out.push(`${mm}-${dd}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const ScoreScrollCtx = React.createContext<{
  registerBar: (el: HTMLDivElement) => () => void;
  registerClip: (el: HTMLDivElement) => () => void;
} | null>(null);

/** 성적 모달 표들의 가로 위치를 한 값으로 묶는다. 날짜 행은 스크롤 상자가 아니다. */
function ScoreScrollProvider({ children }: { children: React.ReactNode }) {
  const bars = React.useRef<Set<HTMLDivElement>>(new Set());
  const clips = React.useRef<Set<HTMLDivElement>>(new Set());
  const left = React.useRef(0);
  const userMoved = React.useRef(false);
  const lock = React.useRef(false);
  const apply = React.useCallback((x: number) => {
    left.current = x;
    clips.current.forEach((clip) => {
      const inner = clip.firstElementChild as HTMLElement | null;
      if (inner) inner.style.transform = `translateX(${-x}px)`;
    });
    lock.current = true;
    bars.current.forEach((bar) => {
      if (Math.abs(bar.scrollLeft - x) > 1) bar.scrollLeft = x;
    });
    lock.current = false;
  }, []);
  const registerBar = React.useCallback((el: HTMLDivElement) => {
    bars.current.add(el);
    const pin = () => {
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      apply(userMoved.current ? Math.min(left.current, max) : max);
    };
    pin();
    const onScroll = () => {
      if (lock.current) return;
      if (Math.abs(el.scrollLeft - left.current) <= 1) return;
      userMoved.current = true;
      apply(el.scrollLeft);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      bars.current.delete(el);
    };
  }, [apply]);
  const registerClip = React.useCallback((el: HTMLDivElement) => {
    clips.current.add(el);
    const inner = el.firstElementChild as HTMLElement | null;
    if (inner) inner.style.transform = `translateX(${-left.current}px)`;
    let origin: { x: number; y: number; left: number; horizontal: boolean | null } | null = null;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      let max = 0;
      bars.current.forEach((bar) => {
        max = Math.max(max, bar.scrollWidth - bar.clientWidth);
      });
      userMoved.current = true;
      apply(Math.max(0, Math.min(max, left.current + e.deltaX)));
    };
    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      origin = { x: t.clientX, y: t.clientY, left: left.current, horizontal: null };
    };
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!origin || !t) return;
      const dx = origin.x - t.clientX;
      const dy = origin.y - t.clientY;
      if (origin.horizontal == null) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        origin.horizontal = Math.abs(dx) > Math.abs(dy);
      }
      if (!origin.horizontal) return;
      e.preventDefault();
      let max = 0;
      bars.current.forEach((bar) => {
        max = Math.max(max, bar.scrollWidth - bar.clientWidth);
      });
      userMoved.current = true;
      apply(Math.max(0, Math.min(max, origin.left + dx)));
    };
    const onTouchEnd = () => { origin = null; };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      clips.current.delete(el);
    };
  }, [apply]);
  const value = React.useMemo(
    () => ({ registerBar, registerClip }),
    [registerBar, registerClip],
  );
  return (
    <ScoreScrollCtx.Provider value={value}>
      <style>{`
        .score-grid {
          overflow-x: auto;
          overflow-y: hidden;
        }
        .score-grid thead th:last-child:not([colspan]),
        .score-grid tbody td:last-child:not([colspan]) {
          position: sticky;
          right: 0;
          z-index: 3;
          background: #0f0f0f;
        }
        .score-date-clip {
          overflow: hidden;
          min-width: 0;
          touch-action: pan-y;
        }
        .score-date-scroll {
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          min-width: 0;
        }
        .score-date-scroll::-webkit-scrollbar { height: 0; display: none; }
        .score-date-scroll.score-date-bar {
          overflow-x: scroll;
          overflow-y: hidden;
          height: 14px;
          scrollbar-width: auto;
          scrollbar-color: #b7a882 #1c1914;
        }
        .score-date-scroll.score-date-bar::-webkit-scrollbar { display: block; height: 14px; }
        .score-date-scroll.score-date-bar::-webkit-scrollbar-thumb {
          background: #b7a882;
          border-radius: 4px;
        }
        .score-date-scroll.score-date-bar::-webkit-scrollbar-track { background: #1c1914; }
      `}</style>
      {children}
    </ScoreScrollCtx.Provider>
  );
}

function useScrollEnd(token: number) {
  const ctx = React.useContext(ScoreScrollCtx);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || token <= 0) return;
    if (ctx) return ctx.registerBar(el);
    const pin = () => { el.scrollLeft = el.scrollWidth; };
    pin();
    const id = requestAnimationFrame(pin);
    return () => cancelAnimationFrame(id);
  }, [token, ctx]);
  return ref;
}

/** 성적 모달의 라이브스택·원장·건강도 표는 각각 접는다. 재판정 화면은 항상 펼친다. */
function useScoreFold(enabled?: boolean) {
  const [open, setOpen] = React.useState(true);
  const active = !!enabled;
  const folded = active && !open;
  return {
    folded,
    mark: active ? (open ? '▾ ' : '▸ ') : '',
    onToggle: active ? () => setOpen((v) => !v) : undefined,
    scrollToken: (n: number) => (folded ? 0 : n),
  };
}

/** 성적 모달에서는 레인 상자를 벗기고, 바깥 라이브스택 박스의 구간으로 둔다. */
function scoreStackShell(
  stackOnly: boolean | undefined,
  alone: React.CSSProperties,
  accent?: string,
): React.CSSProperties {
  if (!stackOnly) return alone;
  return {
    marginTop: 14, paddingTop: 10,
    borderTop: `1px solid ${accent ?? '#3a352c'}`,
  };
}

function ScoreFoldTitle({
  fold, children,
}: {
  fold: { mark: string; onToggle?: () => void };
  children: React.ReactNode;
}) {
  if (!fold.onToggle) return <span>{children}</span>;
  return (
    <button
      type="button"
      onClick={fold.onToggle}
      aria-expanded={fold.mark.startsWith('▾')}
      style={{
        background: 'none', border: 'none', padding: 0,
        color: 'inherit', font: 'inherit', cursor: 'pointer', fontWeight: 600,
      }}
    >{fold.mark}{children}</button>
  );
}

/** 실현%eq 심볼 바탕 — 되돌림 동색 · 산/급락 은색 · 전승별책(wr100) 금(우선). */
const WR100_GOLD = '#f0b90b';
const WR100_GOLD_BG = 'rgba(240, 185, 11, 0.28)';
const STRATEGY_COPPER = '#cd7f32';
const STRATEGY_COPPER_BG = 'rgba(205, 127, 50, 0.32)';
const STRATEGY_SILVER = '#c0c0c0';
const STRATEGY_SILVER_BG = 'rgba(192, 192, 192, 0.30)';
/** 익절2 별책(bh_tp2) — 금·은·동과 구분되는 청록. 본계 합산 안 함. */
const STRATEGY_TEAL = '#39c5cf';
const STRATEGY_TEAL_BG = 'rgba(57, 197, 207, 0.26)';

type SizedTone = 'gold' | 'copper' | 'silver' | 'teal';
type ToneSum = { copper: number; silver: number; gold: number; teal?: number };
type SizedPerpIn = { perp: string; eq_pct: number; kind?: string | null };

type ScoreStackCell = {
  text: string;
  color: string;
  sub?: string;
  /** 실현%eq 심볼 short name (USDT/KRW- 제거 · 대문자) — 금 우선 */
  subGoldNames?: ReadonlySet<string>;
  /** kind 반영 줄 (있으면 이걸 그림) */
  subItems?: { text: string; tone: SizedTone }[];
  /** 계 칸: 동색+은색+금색(+청록 익절2)=합 */
  toneSum?: ToneSum;
};

const SCORE_BLANK: ScoreStackCell = { text: '-', color: '#3a352c' };

function perpShortName(perp: string): string {
  return (perp || '').replace(/^KRW-/i, '').replace(/USDT$/i, '').toUpperCase();
}

function wr100GoldByDay(
  days?: { day: string; sized_perps?: { perp: string }[] | null }[] | null,
): Map<string, Set<string>> {
  const m = new Map<string, Set<string>>();
  for (const d of days ?? []) {
    const s = new Set<string>();
    for (const p of d.sized_perps ?? []) {
      const n = perpShortName(p.perp);
      if (n) s.add(n);
    }
    if (s.size) m.set(d.day, s);
  }
  return m;
}

function strategyTone(kind?: string | null): SizedTone {
  const k = (kind || '').toLowerCase();
  if (k === 'mountain' || k === 'bh_dump' || k === 'dump') return 'silver';
  if (k === 'bh_tp2' || k === 'tp2') return 'teal';
  return 'copper'; // book · pullback · screen · 기본=되돌림
}

function sizedPerpItems(
  ps: SizedPerpIn[] | null | undefined,
  goldNames?: ReadonlySet<string> | null,
): { text: string; tone: SizedTone }[] {
  if (!(ps && ps.length)) return [];
  return ps.map((p) => {
    const name = (p.perp || '').replace(/^KRW-/i, '').replace(/USDT$/i, '');
    const v = Number(p.eq_pct);
    const text = !Number.isFinite(v)
      ? name
      : `${name} ${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
    const st = strategyTone(p.kind);
    const tone: SizedTone = st !== 'teal' && goldNames?.has(perpShortName(p.perp))
      ? 'gold'
      : st;
    return { text, tone };
  });
}

function toneChipStyle(tone: SizedTone): React.CSSProperties {
  if (tone === 'gold') {
    return {
      background: WR100_GOLD_BG, color: WR100_GOLD,
      borderRadius: 2, padding: '0 2px',
    };
  }
  if (tone === 'silver') {
    return {
      background: STRATEGY_SILVER_BG, color: STRATEGY_SILVER,
      borderRadius: 2, padding: '0 2px',
    };
  }
  if (tone === 'teal') {
    return {
      background: STRATEGY_TEAL_BG, color: STRATEGY_TEAL,
      borderRadius: 2, padding: '0 2px',
    };
  }
  return {
    background: STRATEGY_COPPER_BG, color: STRATEGY_COPPER,
    borderRadius: 2, padding: '0 2px',
  };
}

function renderSizedToneLines(
  items: { text: string; tone: SizedTone }[],
): React.ReactNode {
  if (!items.length) return null;
  return (
    <div style={{ fontSize: '0.55rem', color: '#7d7362', lineHeight: 1.2 }}>
      {items.map((it, i) => (
        <div key={`${it.text}-${i}`} style={toneChipStyle(it.tone)}>{it.text}</div>
      ))}
    </div>
  );
}

/** 본계 sized_perps 를 금(전승)·은(산/급락)·동(되돌림)으로 서로소 합산. */
function sumSizedByTone(
  days: { day: string; sized_perps?: SizedPerpIn[] | null }[] | null | undefined,
  goldBy: Map<string, Set<string>>,
): ToneSum {
  let copper = 0;
  let silver = 0;
  let gold = 0;
  let teal = 0;
  for (const d of days ?? []) {
    const g = goldBy.get(d.day);
    for (const p of d.sized_perps ?? []) {
      const v = Number(p.eq_pct);
      if (!Number.isFinite(v)) continue;
      const t = strategyTone(p.kind);
      if (t === 'teal') teal += v; // 익절2 별책은 금 표시보다 우선 (본계 행과 별개 건)
      else if (g?.has(perpShortName(p.perp))) gold += v;
      else if (t === 'silver') silver += v;
      else copper += v;
    }
  }
  return { copper, silver, gold, teal };
}

/** 계 칸 아래: 동/은/금만 (합은 위 본문 %eq). */
function renderToneSumBreakdown(
  parts: ToneSum,
): React.ReactNode {
  const fmt = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
  const teal = parts.teal ?? 0;
  const total = parts.copper + parts.silver + parts.gold + teal;
  const rows: { tone: SizedTone; v: number }[] = [
    { tone: 'copper', v: parts.copper },
    { tone: 'silver', v: parts.silver },
    { tone: 'gold', v: parts.gold },
    ...(Math.abs(teal) > 1e-9 ? [{ tone: 'teal' as SizedTone, v: teal }] : []),
  ];
  return (
    <div
      title={`실현%eq 기여 단순합 · 동 ${fmt(parts.copper)} · 은 ${fmt(parts.silver)} · 금 ${fmt(parts.gold)}${Math.abs(teal) > 1e-9 ? ` · 청록(익절2) ${fmt(teal)}` : ''} = ${fmt(total)}`}
      style={{
        fontSize: '0.55rem', color: '#7d7362', lineHeight: 1.25,
        fontWeight: 400,
      }}
    >
      {rows.map((r) => (
        <div key={r.tone} style={toneChipStyle(r.tone)}>{fmt(r.v)}</div>
      ))}
    </div>
  );
}

function scoreSignedCell(
  v: number | null | undefined, suffix: string, digits: number,
): ScoreStackCell {
  if (v == null || !Number.isFinite(Number(v))) return SCORE_BLANK;
  const n = Number(v);
  return {
    text: `${n >= 0 ? '+' : ''}${n.toFixed(digits)}${suffix}`,
    color: n >= 0 ? '#3fb950' : '#f85149',
  };
}

/** 상세 모달 공통 지표. 없는 값은 '-'. */
function scoreMetricCell(
  key: string,
  d: {
    has_data?: boolean;
    n?: number;
    avg_net_pct?: number;
    win_rate_pct?: number;
    worst_pct?: number;
    sum_pct?: number;
    mdd?: number;
    realized_eq_pct?: number | null;
    worst_eq_pct?: number | null;
  } | undefined,
  winCut: number,
): ScoreStackCell {
  const miss = !d || d.has_data === false;
  // 지표 키는 한국어 고정(모듈·채점 경로). 화면 문구만 u()/scoreStackLabelText.
  // key === u('건당') 금지 — 모듈 로드 시 키는 한국어인데 /en·/fr 의 u() 값이
  // 달라져 셀이 전부 공백이 된다 (2026-11-04).
  if (key === 'n') return miss ? SCORE_BLANK : { text: String(d!.n ?? 0), color: '#b7a882' };
  if (key === '건당') return miss ? SCORE_BLANK : scoreSignedCell(d!.avg_net_pct, '%', 2);
  if (key === '승률') {
    if (miss || d!.win_rate_pct == null) return SCORE_BLANK;
    const w = d!.win_rate_pct;
    return { text: `${w.toFixed(0)}%`, color: w >= winCut ? '#3fb950' : '#f85149' };
  }
  if (key === '최악') return miss ? SCORE_BLANK : scoreSignedCell(d!.worst_pct, '%', 2);
  if (key === '합계') return miss ? SCORE_BLANK : scoreSignedCell(d!.sum_pct, '%', 1);
  if (key === 'MDD') return miss ? SCORE_BLANK : scoreSignedCell(d!.mdd, '%', 1);
  if (key === '실현%eq') return scoreSignedCell(d?.realized_eq_pct, '%eq', 1);
  if (key === '잔고밀림') return scoreSignedCell(d?.worst_eq_pct, '%eq', 1);
  return SCORE_BLANK;
}

const SCORE_STACK_LABELS = [
  'n', '건당', '승률', '최악', '합계', 'MDD', '실현%eq', '잔고밀림', '강제청산', '진입정지',
] as const;

/** 빗숏 라이브스택 본지표 — 각 아래에 되돌림·급락 서브행. */
const BH_STACK_PARENT_LABELS = [
  'n', '건당', '승률', '최악', '합계', 'MDD',
  '실현%eq', '잔고밀림', '강제청산', '진입정지',
] as const;

/** `↳pb:n` · `↳dump:승률` … 화면 라벨은 항상 ↳되돌림/↳급락. */
const BH_SCORE_STACK_LABELS: readonly string[] = BH_STACK_PARENT_LABELS.flatMap((m) => [
  m, `↳pb:${m}`, `↳dump:${m}`, `↳tp2:${m}`,
]);

function scoreStackLabelText(label: string): string {
  if (label.startsWith('↳pb:')) return u('↳되돌림');
  if (label.startsWith('↳dump:')) return u('↳급락');
  if (label.startsWith('↳tp2:')) return u('↳익절2');
  return u(label);
}

function ScoreDateFrame({
  accent, axis, scrollRef, labels, cellAt, totAt,
}: {
  accent: string;
  axis: string[];
  scrollRef: React.Ref<HTMLDivElement>;
  labels: readonly string[];
  cellAt: (label: string, day: string) => React.ReactNode;
  totAt: (label: string) => React.ReactNode;
}) {
  const ctx = React.useContext(ScoreScrollCtx);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const labelKey = labels.join('\n');
  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !ctx) return undefined;
    const nodes = root.querySelectorAll<HTMLDivElement>('.score-date-clip');
    const cleanups: (() => void)[] = [];
    nodes.forEach((el) => cleanups.push(ctx.registerClip(el)));
    return () => cleanups.forEach((fn) => fn());
  }, [ctx, axis.length, labelKey]);
  const dayW = '4.5rem';
  const labW = '7.2rem';
  const base: React.CSSProperties = {
    padding: '4px 6px',
    fontSize: '0.64rem',
    fontVariantNumeric: 'tabular-nums',
    boxSizing: 'border-box',
    background: '#0f0f0f',
    height: '100%',
  };
  const labStyle = (head: boolean, dim?: boolean): React.CSSProperties => ({
    ...base, textAlign: 'left',
    color: head ? accent : (dim ? '#6e6658' : '#8b949e'),
    fontWeight: head ? 600 : 400,
    borderRight: `2px solid ${accent}44`,
    ...(dim ? { paddingLeft: 14 } : null),
  });
  const dayStyle = (head: boolean): React.CSSProperties => ({
    ...base, textAlign: 'right',
    color: head ? accent : undefined,
    fontWeight: head ? 600 : 400,
    borderLeft: `1px solid ${accent}22`,
    width: dayW, minWidth: dayW, maxWidth: dayW,
    verticalAlign: 'top',
  });
  const totStyle = (head: boolean, dim?: boolean): React.CSSProperties => ({
    ...dayStyle(head),
    // 서브행(↳되돌림/급락) 계 칸은 본지표처럼 볼드 안 함.
    fontWeight: head ? 600 : (dim ? 400 : 700),
    color: head ? accent : undefined,
    borderLeft: `2px solid ${accent}55`,
  });
  const rowGrid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${labW} minmax(0, 1fr) ${dayW}`,
    alignItems: 'stretch',
  };
  const days = (nodes: React.ReactNode) => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${axis.length}, ${dayW})`,
      width: `calc(${axis.length} * ${dayW})`,
      height: '100%',
    }}>{nodes}</div>
  );
  const body = [
    { key: '__h', head: true, label: u('지표'), tot: u('계') as React.ReactNode, dim: false },
    ...labels.map((label) => ({
      key: label,
      head: false,
      label: scoreStackLabelText(label),
      tot: totAt(label),
      dim: label.startsWith('↳'),
    })),
  ];
  return (
    <div ref={rootRef} style={{ marginTop: 4 }}>
      {body.map((row) => (
        <div key={row.key} style={{
          ...rowGrid,
          borderTop: row.head ? 'none' : `1px solid ${accent}${row.dim ? '10' : '18'}`,
        }}>
          <div style={labStyle(row.head, row.dim)}>{row.label}</div>
          <div className="score-date-clip">
            {days(row.head
              ? axis.map((day) => (
                <div key={day} style={dayStyle(true)}>{day}</div>
              ))
              : axis.map((day) => (
                <div key={day} style={dayStyle(false)}>{cellAt(row.key, day)}</div>
              )))}
          </div>
          <div style={totStyle(row.head, row.dim)}>{row.tot}</div>
        </div>
      ))}
      <div style={rowGrid}>
        <div />
        <div ref={scrollRef} className="score-date-scroll score-date-bar">
          <div style={{ width: `calc(${axis.length} * ${dayW})`, height: 1 }} />
        </div>
        <div />
      </div>
    </div>
  );
}

function ScoreLaneStackTable({
  accent, axis, scrollRef, at, labels = SCORE_STACK_LABELS,
}: {
  accent: string;
  axis: string[];
  scrollRef: React.Ref<HTMLDivElement>;
  at: (key: string, day: string | null) => ScoreStackCell;
  labels?: readonly string[];
}) {
  const show = (c: ScoreStackCell) => (
    (c.toneSum || c.subItems?.length || c.sub) ? (
      <span style={{ display: 'inline-block', maxWidth: '100%', color: c.color }}>
        <div>{c.text}</div>
        {c.toneSum
          ? renderToneSumBreakdown(c.toneSum)
          : c.subItems?.length
            ? renderSizedToneLines(c.subItems)
            : (
              <div style={{
                fontSize: '0.55rem', color: '#7d7362', lineHeight: 1.2, whiteSpace: 'pre-line',
              }}>{c.sub}</div>
            )}
      </span>
    ) : <span style={{ color: c.color }}>{c.text}</span>
  );
  return (
    <ScoreDateFrame
      accent={accent}
      axis={axis}
      scrollRef={scrollRef}
      labels={labels}
      cellAt={(label, day) => show(at(label, day))}
      totAt={(label) => show(at(label, null))}
    />
  );
}

/** 빗숏 통합책 + 되돌림/급락 서브행 셀. 본지표·서브 전부 동일 키로 채점. */
function bhStackMetricAt(
  key: string,
  day: string | null,
  book: UpbitPbDaily | null | undefined,
  pullback: UpbitPbDaily | null | undefined,
  dump: UpbitPbDaily | null | undefined,
  goldBy: Map<string, Set<string>>,
  toneSum: ToneSum,
  tp2?: UpbitPbDaily | null,
): ScoreStackCell {
  const pick = (src: UpbitPbDaily | null | undefined) => {
    if (!src) return undefined;
    if (day == null) return { ...src, has_data: (src.n ?? 0) > 0 };
    return src.days?.find((d) => d.day === day);
  };
  const eqCell = (
    metric: string,
    src: UpbitPbDaily | null | undefined,
    totTone?: ToneSum,
  ): ScoreStackCell => {
    const dayRow = day ? src?.days?.find((d) => d.day === day) : undefined;
    const row = pick(src);
    if (metric === '강제청산') {
      if (!row || (row as { liq?: boolean }).liq == null) {
        return SCORE_BLANK;
      }
      const liq = (row as { liq?: boolean }).liq;
      if (!liq) return { text: '·', color: '#3a352c' };
      const sub = ((row as { liq_perps?: string[] }).liq_perps ?? []).join('\n');
      return { text: 'Y', color: '#f85149', sub: sub || undefined };
    }
    if (metric === '진입정지') {
      const n = (row as { halt_n?: number } | undefined)?.halt_n ?? 0;
      if (n <= 0) return { text: '·', color: '#3a352c' };
      const sub = ((row as { halt_perps?: string[] } | undefined)?.halt_perps ?? []).join('\n');
      return { text: String(n), color: '#f0b90b', sub: sub || undefined };
    }
    const cell = scoreMetricCell(metric, row, 55);
    if (metric !== '실현%eq' || cell.text === '-') return cell;
    if (day == null) {
      return totTone ? { ...cell, toneSum: totTone } : cell;
    }
    if (!dayRow) return cell;
    const items = sizedPerpItems(dayRow.sized_perps, goldBy.get(day));
    if (!items.length) return cell;
    return {
      ...cell,
      sub: items.map((x) => x.text).join('\n'),
      subItems: items,
    };
  };

  const sub = key.match(/^↳(pb|dump|tp2):(.+)$/);
  if (sub) {
    const metric = sub[2];
    if (sub[1] === 'tp2') {
      // 별책 — 금/은/동 합산 칸 없음 (청록 심볼만).
      return eqCell(metric, tp2);
    }
    const src = sub[1] === 'pb' ? pullback : dump;
    const stratTone = day == null
      ? sumSizedByTone(src?.days, goldBy)
      : undefined;
    return eqCell(metric, src, stratTone);
  }
  return eqCell(key, book, toneSum);
}

function PumpRideDailyPanel({ stackOnly }: {
  stackOnly?: boolean;
} = {}) {
  const [data, setData] = React.useState<PumpRideDaily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchPumpRideDaily(0)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);
  const fold = useScoreFold(!!stackOnly);
  const scrollEnd = useScrollEnd(fold.scrollToken(
    stackOnly ? (data ? scoreDayAxis().length : 0) : (data?.days.length ?? 0),
  ));

  const cell: React.CSSProperties = {
    padding: '4px 8px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: '1px solid #f0b90b22',
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;

  const badge = (() => {
    if (!data) return null;
    if (data.verdict === 'alarm') {
      return { text: `경보 — ${(data.alarms ?? []).join('·')}`, bg: '#f8514922', fg: '#f85149' };
    }
    if (data.verdict === 'hold') return { text: u('판정 보류'), bg: '#b7a88222', fg: '#b7a882' };
    return { text: u('정상'), bg: '#3fb95022', fg: '#3fb950' };
  })();

  const stickyLab = (borderRight: string, color: string): React.CSSProperties => ({
    ...cell, textAlign: 'left', color,
    position: 'sticky', left: 0, background: '#0f0f0f',
    borderLeft: 'none', borderRight,
  });

  const winOk = (w: number) =>
    w >= (data?.config?.constitution ? 55 : 45);
  const scoreRows = (
    label: string,
    src: PumpRideDaily | null | undefined,
    accent: string,
  ) => {
    if (!src || !src.days?.length) return null;
    const lg = src;
    return (
      <>
        <tr>
          <td colSpan={(data?.days.length ?? 0) + 2} style={{
            padding: '10px 8px 4px', color: accent,
            borderTop: `2px solid ${accent}55`, fontSize: '0.64rem',
          }}>
            {label}
          </td>
        </tr>
        {([
          ['n', (d: PumpRideDailyDay) => String(d.n), () => '#b7a882',
            () => ({ t: String(lg.n), c: '#e6dcc3' })],
          [u('건당'), (d: PumpRideDailyDay) => `${num(d.avg_net_pct)}%`,
            (d: PumpRideDailyDay) => sign(d.avg_net_pct),
            () => ({ t: `${num(lg.avg_net_pct)}%`, c: sign(lg.avg_net_pct) })],
          [u('중앙'), (d: PumpRideDailyDay) => `${num(d.med_net_pct)}%`,
            (d: PumpRideDailyDay) => sign(d.med_net_pct),
            () => ({ t: `${num(lg.med_net_pct)}%`, c: sign(lg.med_net_pct) })],
          [u('승률'), (d: PumpRideDailyDay) => `${d.win_rate_pct.toFixed(0)}%`,
            (d: PumpRideDailyDay) => (winOk(d.win_rate_pct) ? '#3fb950' : '#f85149'),
            () => ({
              t: `${lg.win_rate_pct.toFixed(0)}%`,
              c: winOk(lg.win_rate_pct) ? '#3fb950' : '#f85149',
            })],
          [u('최악'), (d: PumpRideDailyDay) => `${num(d.worst_pct)}%`,
            (d: PumpRideDailyDay) => sign(d.worst_pct),
            () => ({ t: `${num(lg.worst_pct)}%`, c: sign(lg.worst_pct) })],
          [u('합계'), (d: PumpRideDailyDay) => num(d.sum_pct, 1),
            (d: PumpRideDailyDay) => sign(d.sum_pct),
            () => ({ t: num(lg.sum_pct, 1), c: sign(lg.sum_pct) })],
          ['MDD', (d: PumpRideDailyDay) => num(d.mdd, 1),
            (d: PumpRideDailyDay) => sign(d.mdd),
            () => ({ t: num(lg.mdd, 1), c: sign(lg.mdd) })],
        ] as [string, (d: PumpRideDailyDay) => string, (d: PumpRideDailyDay) => string,
          () => { t: string; c: string }][])
          .filter(([lab]) => !(stackOnly && lab === u('중앙')))
          .map(([lab, val, col, tot]) => {
            const g = tot();
            return (
              <tr key={`${label}-${lab}`} style={{ borderTop: `1px solid ${accent}18` }}>
                <td style={stickyLab(`2px solid ${accent}44`, '#8b949e')}>{lab}</td>
                {(lg.days ?? []).map((d) => (
                  <td key={d.day} style={{
                    ...cell, color: d.has_data ? col(d) : '#3a352c',
                  }}>
                    {d.has_data ? val(d) : '–'}
                  </td>
                ))}
                <td style={{
                  ...cell, color: g.c, fontWeight: 700,
                  borderLeft: `2px solid ${accent}55`,
                }}>{lg.n > 0 ? g.t : ''}</td>
              </tr>
            );
          })}
      </>
    );
  };

  return (
    <div style={scoreStackShell(stackOnly, {
      marginTop: 4, marginBottom: 14, padding: '10px 12px', border: '1px solid #f0b90b33',
      borderRadius: 6, background: '#0f0f0f',
    }, PUMP_RIDE_COLOR)}>
      {!stackOnly && data?.config ? (() => {
        const c = data.config!;
        const n = (v: number | undefined | null, d = 0) =>
          v == null ? '?' : Number(v).toFixed(d);
        const rule = [
          c.confirm_pct != null ? `확인 +${n(c.confirm_pct, 1)}%` : null,
          c.hold_min != null ? `홀드 ${n(c.hold_min)}분` : null,
          c.watch_max != null ? `감시 ${n(c.watch_max)}분` : null,
          c.trail_arm != null ? `trail arm ${n(c.trail_arm, 1)}%` : null,
          c.trail_dd != null ? `dd ${n(c.trail_dd, 1)}%` : null,
          c.hard_pct != null ? `hard ${n(c.hard_pct, 0)}%` : null,
          c.evolve10 ? 'evolve10' : null,
          c.constitution
            ? (`체질 ${c.rung_policy || 'path'}`
              + (c.path_w != null && c.path_p != null
                ? ` W${n(c.path_w)} P${n(c.path_p, 1)}` : ''))
            : null,
        ].filter(Boolean).join(' · ');
        const size = [
          c.slot_k != null ? `채점 k=${n(c.slot_k)}` : null,
          c.k != null ? `라이브 k=${n(c.k)}` : null,
          c.alloc_pct != null ? `배분 ${n(c.alloc_pct, 0)}%` : null,
          c.risk_pct != null ? `risk ${n(c.risk_pct, 2)}%` : null,
          c.leverage != null ? `lev ${n(c.leverage, 0)}` : null,
        ].filter(Boolean).join(' · ');
        const on = c.live === true;
        return (
          <div style={{
            marginBottom: 8, padding: '6px 8px', borderRadius: 4,
            background: '#f0b90b0d', border: '1px solid #f0b90b22',
            fontSize: '0.63rem', lineHeight: 1.55, color: '#b7a882',
          }}>
            <div style={{ marginBottom: 2 }}>
              <span style={{ color: on ? '#3fb950' : '#f85149', fontWeight: 600 }}>
                {on ? u('편승 주문 ON') : u('편승 주문 OFF')}
              </span>
              <span style={{ color: '#7d7362' }}> {u('· 지금 도는 설정')}</span>
              {c.constitution ? (
                <span style={{ color: '#79c0ff' }}> {u('· 체질 채점')}</span>
              ) : null}
            </div>
            <div><span style={{ color: '#7d7362' }}>{u('규칙')} </span>{rule || '–'}</div>
            <div><span style={{ color: '#7d7362' }}>{u('사이징')} </span>{size || '–'}</div>
          </div>
        );
      })() : null}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: stackOnly ? PUMP_RIDE_COLOR : '#f0b90b', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <ScoreFoldTitle fold={fold}>{stackOnly ? u('편승') : u('편승 — 일일 채점 · 레인 건강도')}</ScoreFoldTitle>
        {!stackOnly && (
        <span style={{ color: '#7d7362', fontSize: '0.62rem', fontWeight: 400 }}>
          {data?.cell ?? '–'} · k={data?.health?.slot_k ?? data?.config?.slot_k ?? '–'}
        </span>
        )}
        {!stackOnly && badge && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: '0.64rem',
            background: badge.bg, color: badge.fg,
          }}>{badge.text}</span>
        )}
        {!stackOnly && (
        <span style={{ marginLeft: 'auto', color: '#7d7362', fontSize: '0.62rem' }}>
          {data?.last_scored_at
            ? `최근 채점 ${new Date(data.last_scored_at).toLocaleString('ko-KR', { hour12: false })}`
            : ''}
        </span>
        )}
      </div>

      {!fold.folded && (<>
      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {!stackOnly && data && data.n === 0 && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>
          채점된 진입이 없다. (일일 잡이 아직 안 돌았거나 틱 공백)
        </div>
      )}

      {stackOnly && data && (
        <ScoreLaneStackTable
          accent={PUMP_RIDE_COLOR}
          axis={scoreDayAxis()}
          scrollRef={scrollEnd}
          at={(key, day) => {
            const src = data.live_full ?? data;
            const row = day == null
              ? { ...src, has_data: (src.n ?? 0) > 0 }
              : (src.days ?? []).find((d) => d.day === day);
            return scoreMetricCell(key, row, data.config?.constitution ? 55 : 45);
          }}
        />
      )}

      {!stackOnly && data && data.days.length > 0 && (
        <>
          <div ref={scrollEnd} style={{ overflowX: 'auto', marginTop: 4 }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.64rem' }}>
              <thead>
                <tr style={{ color: '#f0b90b' }}>
                  <th style={stickyLab('2px solid #f0b90b44', '#f0b90b')}>{u('지표')}</th>
                  {data.days.map((d) => (
                    <th key={d.day} style={cell}>{d.day}</th>
                  ))}
                  <th style={{
                    ...cell, borderLeft: '2px solid #f0b90b55', fontWeight: 700,
                  }}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {!stackOnly && scoreRows(
                  data.config?.constitution
                    ? `셀 채점 — 체질 ${data.config.rung_policy || 'path'}`
                      + (data.config.path_w != null && data.config.path_p != null
                        ? ` W${data.config.path_w} P${data.config.path_p}` : '')
                    : u('셀 채점 — 배포 confirm/hold/k'),
                  data, '#f0b90b')}
                {scoreRows(u('라이브스택 — k=∞'), data.live_full ?? undefined, '#79c0ff')}
                {!stackOnly && scoreRows(u('실제 원장 — live_ledger · 편승'), data.ledger ?? undefined, '#3fb950')}

                {!stackOnly && <tr>
                  <td colSpan={data.days.length + 2} style={{
                    padding: '10px 8px 4px', color: '#f0b90b',
                    borderTop: '2px solid #f0b90b55', fontSize: '0.64rem',
                    fontWeight: 700,
                  }}>
                    레인 건강도
                    <span style={{ color: '#7d7362', fontWeight: 400 }}>
                      {u(' · 규칙이 아니라 레인 자체 · 확인 깔때기·타이밍·사다리')}
                    </span>
                  </td>
                </tr>}
                {!stackOnly && ([
                  [u('감시'), (d: PumpRideDailyDay) => String(d.n_funnel ?? 0)],
                  [u('확인률'), (d: PumpRideDailyDay) => (
                    d.has_health ? `${d.confirm_ok_pct.toFixed(0)}%` : ''
                  )],
                  [u('진입률'), (d: PumpRideDailyDay) => (
                    d.has_health && d.n_confirm_ok > 0
                      ? `${d.enter_ok_pct.toFixed(0)}%` : ''
                  )],
                  [u('lag중앙'), (d: PumpRideDailyDay) => (
                    d.has_health && d.lag_med_min > 0
                      ? `${d.lag_med_min.toFixed(0)}m` : ''
                  )],
                  [u('rung1 건당'), (d: PumpRideDailyDay) => (
                    d.rung1_avg == null ? '' : `${num(d.rung1_avg)}%`
                  )],
                  [u('rung1 승률'), (d: PumpRideDailyDay) => (
                    d.rung1_win == null ? '' : `${d.rung1_win.toFixed(0)}%`
                  )],
                  [u('rung2+ 건당'), (d: PumpRideDailyDay) => (
                    d.rung2_avg == null ? '' : `${num(d.rung2_avg)}%`
                  )],
                  [u('rung2+ 승률'), (d: PumpRideDailyDay) => (
                    d.rung2_win == null ? '' : `${d.rung2_win.toFixed(0)}%`
                  )],
                ] as [string, (d: PumpRideDailyDay) => string][]).map(([lab, val]) => (
                  <tr key={lab} style={{ borderTop: '1px solid #f0b90b18' }}>
                    <td style={stickyLab('2px solid #f0b90b44', '#b7a882')}>{lab}</td>
                    {data.days.map((d) => (
                      <td key={d.day} style={{
                        ...cell, color: d.n_funnel > 0 ? '#b7a882' : '#3a352c',
                      }}>
                        {d.n_funnel === 0 && lab !== u('감시') ? '–' : val(d)}
                      </td>
                    ))}
                    <td style={{
                      ...cell, color: '#b7a882', fontWeight: 700,
                      borderLeft: '2px solid #f0b90b55',
                    }}>
                      {lab === u('감시') && data.health
                        ? String(data.health.n_funnel)
                        : lab === u('확인률') && data.health
                          ? `${data.health.confirm_ok_pct.toFixed(0)}%`
                          : lab === u('진입률') && data.health && data.health.n_confirm_ok > 0
                            ? `${data.health.enter_ok_pct.toFixed(0)}%`
                            : lab === u('lag중앙') && data.health && data.health.lag_med_min > 0
                              ? `${data.health.lag_med_min.toFixed(0)}m`
                              : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!stackOnly && data.health?.confirm_edge_ok != null && (
            <div style={{
              marginTop: 6, fontSize: '0.64rem',
              color: data.health.confirm_edge_ok ? '#3fb950' : '#f85149',
            }}>
              {data.health.confirm_edge_ok
                ? u('확인 구조 유지 — 감시창 안 BN 확인이 성립한다')
                : u('⚠ 확인 구조 붕괴 — 확인률이 경보선 미만. 편승 전제가 깨진 신호')}
            </div>
          )}
          {!stackOnly && data.health?.timing_ok != null && (
            <div style={{
              marginTop: data.health.confirm_edge_ok != null ? 2 : 6,
              fontSize: '0.64rem',
              color: data.health.timing_ok ? '#3fb950' : '#f85149',
            }}>
              {data.health.timing_ok
                ? u('타이밍 유지 — 확인 lag가 감시창 안에 있다')
                : u('⚠ 타이밍 밀림 — 확인이 늦다. 9월 드리프트 축')}
            </div>
          )}
          {!stackOnly && data.health && (
            <div style={{ marginTop: 6, color: '#7d7362', fontSize: '0.62rem' }}>
              창 {data.window_days}일 · 감시 {data.health.n_funnel}
              {' · '}확인 {data.health.n_confirm_ok}
              {' · '}진입 {data.health.n_taken}
              {' · '}확인률 {data.health.confirm_ok_pct.toFixed(0)}%
              {' · '}lag {data.health.lag_med_min.toFixed(0)}m
            </div>
          )}
          {!stackOnly && (
          <div style={{ marginTop: 8, color: '#7d7362', fontSize: '0.62rem' }}>
            경보선 n≥10 · 승률 45% / 건당 +0.5% / MDD −50 · 확인률 25% · lag 90m
            (사전등록 2026-09-19). 격자 총합과 섞어 읽지 말 것.
          </div>
          )}
        </>
      )}
      </>)}
    </div>
  );
}

function PumpRideRetuneModal({ r, onClose, onApplied, detail }: {
  r: BinancePumpRideRetune;
  onClose: () => void;
  onApplied?: (kind: string) => void;
  /** 옛 u('상세') 버튼 내용 — 편승 필터를 모달 맨 아래에 낸다. */
  detail?: RetuneDetail;
}) {
  const cohortDefault = r.cohort_n ?? r.window_n ?? 50;
  const [cohortParsed, setCohortParsed] = React.useState<number | null>(cohortDefault);
  const onCohortChange = React.useCallback((n: number | null) => {
    setCohortParsed(n);
  }, []);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cell: React.CSSProperties = {
    padding: '5px 8px', borderBottom: '1px solid #f0b90b22', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  };
  const head: React.CSSProperties = {
    ...cell, color: '#f0b90b', fontSize: '0.68rem',
    borderBottom: '1px solid #f0b90b44',
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 1) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;

  const rows: { label: string; c?: BinancePumpRideRetuneCell | null }[] = [
    { label: u('배포'), c: r.deployed },
    { label: u('채택후보'), c: r.best },
    { label: u('총합1위'), c: r.top },
  ];
  const top5 = r.top5?.length ? r.top5 : (r.top ? [r.top] : []);
  const cellKey = (c: BinancePumpRideRetuneCell) =>
    `${c.confirm}-${c.hold}-${c.k}-${c.dd ?? ''}-${c.hs ?? ''}`;
  const depKey = r.deployed ? cellKey(r.deployed) : null;
  const bestKey = r.best ? cellKey(r.best) : null;
  const fmtExit = (v: number | null | undefined) =>
    v == null ? '–' : Number(v).toFixed(2);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={u("편승 재판정")}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#12100c', border: '1px solid #f0b90b55', borderRadius: 8,
          padding: '14px 16px', maxWidth: 860, width: '100%',
          maxHeight: '86vh', overflowY: 'auto', color: '#e8dcc0',
          fontSize: '0.78rem', lineHeight: 1.5,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: 8,
        }}>
          <strong style={{ color: '#f0b90b' }}>
            편승 재판정 · {r.verdict}
          </strong>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #f0b90b55', color: '#e8dcc0',
              borderRadius: 4, cursor: 'pointer', padding: '2px 8px',
            }}
          >{u('닫기')}</button>
        </div>

        <PumpRideDailyPanel />

        <div style={{
          marginBottom: 10, fontSize: '0.66rem', color: '#7d7362', lineHeight: 1.45,
        }}>
          격자 총합은 재생 프레임(원장≠). path ON이면 체질 CF 채점 —
          전건 스프레이와 섞지 않는다. 주문은 별도 스위치.
        </div>

        <RetuneScheduleBlock
          schedule={PUMP_RIDE_SCHEDULE}
          computedAt={r.computed_at}
          lastGate={r.last_gate}
          runKind="pr"
          runWhenOverride={r.run_when}
          lastElapsedS={r.elapsed_s}
          cohortN={cohortParsed}
        />

        <RetuneCohortControls
          lane="pr"
          cohortN={cohortDefault}
          seedDefault={r.cohort_seed_default}
          fromPrefs={r.cohort_from_prefs}
          unit={r.cohort_unit}
          label={r.cohort_label}
          min={r.cohort_min}
          max={r.cohort_max}
          onChange={onCohortChange}
          onSaved={() => onApplied?.('cohort')}
        />

        <div style={{ marginBottom: 10 }}>{r.reason}</div>

        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>{u('셀')}</th>
              <th style={head}>{u('확인')}</th>
              <th style={head}>{u('홀드')}</th>
              <th style={head}>k</th>
              <th style={head} title="trail drawdown">dd</th>
              <th style={head} title="hard stop">hs</th>
              <th style={head}>n</th>
              <th style={head}>{u('총합')}</th>
              <th style={head}>{u('건당')}</th>
              <th style={head} title={u("배포 셀 총합 대비 초과")}>{u('vs배포')}</th>
              <th style={head}>{u('승률')}</th>
              <th style={head}>{u('최악')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('3등분')}</th>
              <th style={head} />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ label, c }) => (
              <tr key={label}>
                <td style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>{u(label)}</td>
                {c ? (
                  <>
                    <td style={cell}>{c.confirm}%</td>
                    <td style={cell}>{c.hold}m</td>
                    <td style={cell}>{c.k}</td>
                    <td style={cell}>{fmtExit(c.dd)}</td>
                    <td style={cell}>{fmtExit(c.hs)}</td>
                    <td style={cell}>{c.n}</td>
                    <td style={{ ...cell, color: sign(c.total), fontWeight: 700 }}>
                      {num(c.total)}
                    </td>
                    <td style={{ ...cell, color: sign(c.avg) }}>{num(c.avg, 2)}</td>
                    <td style={{ ...cell, color: sign(c.vs_deployed) }}>
                      {num(c.vs_deployed, 2)}
                    </td>
                    <td style={cell}>{c.win?.toFixed(1)}%</td>
                    <td style={{ ...cell, color: sign(c.worst) }}>{num(c.worst, 2)}</td>
                    <td style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                      {c.thirds ? c.thirds.map((x) => num(x, 0)).join(' / ') : '–'}
                    </td>
                    <td style={{ ...cell, textAlign: 'center' }}>
                      <RetuneApplyBtn
                        kind="pr"
                        cell={c as Record<string, unknown>}
                        label={label}
                        matchesDeployed={label === u('배포')}
                        onApplied={onApplied}
                      />
                    </td>
                  </>
                ) : (
                  <td colSpan={13} style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                    {label === u('채택후보') ? u('관문을 넘은 셀 없음 (정상)') : '–'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {top5.length > 0 ? (
          <>
            <div style={{
              margin: '14px 0 6px', color: '#f0b90b', fontSize: '0.66rem',
              letterSpacing: '0.04em',
            }}>
              격자 총합 top5 (롤링 {r.roll_n}건 · 관문 무관)
            </div>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>#</th>
                  <th style={head}>{u('확인')}</th>
                  <th style={head}>{u('홀드')}</th>
                  <th style={head}>k</th>
                  <th style={head} title="trail drawdown">dd</th>
                  <th style={head} title="hard stop">hs</th>
                  <th style={head}>n</th>
                  <th style={head}>{u('총합')}</th>
                  <th style={head}>{u('건당')}</th>
                  <th style={head} title={u("배포 셀 총합 대비 초과")}>{u('vs배포')}</th>
                  <th style={head}>{u('승률')}</th>
                  <th style={head}>{u('최악')}</th>
                  <th style={{ ...head, textAlign: 'left' }}>{u('3등분')}</th>
                  <th style={{ ...head, textAlign: 'left' }} title={u("격자 전체 셀 중 승률 순위")}>{u('비고')}</th>
                  <th style={head} />
                </tr>
              </thead>
              <tbody>
                {top5.map((c, i) => {
                  const k = cellKey(c);
                  const isDep = depKey != null && k === depKey;
                  const isBest = bestKey != null && k === bestKey;
                  const tag = isDep ? u('배포') : isBest ? u('채택') : null;
                  return (
                    <tr key={k}>
                      <td style={{
                        ...cell, textAlign: 'left',
                        color: isDep || isBest ? '#f0b90b' : '#b7a882',
                        fontWeight: isDep || isBest ? 700 : 400,
                      }}>
                        {i + 1}
                        {tag ? (
                          <span style={{ marginLeft: 6, fontSize: '0.64rem' }}>
                            {tag}
                          </span>
                        ) : null}
                      </td>
                      <td style={cell}>{c.confirm}%</td>
                      <td style={cell}>{c.hold}m</td>
                      <td style={cell}>{c.k}</td>
                      <td style={cell}>{fmtExit(c.dd)}</td>
                      <td style={cell}>{fmtExit(c.hs)}</td>
                      <td style={cell}>{c.n}</td>
                      <td style={{ ...cell, color: sign(c.total), fontWeight: 700 }}>
                        {num(c.total)}
                      </td>
                      <td style={{ ...cell, color: sign(c.avg) }}>{num(c.avg, 2)}</td>
                      <td style={{ ...cell, color: sign(c.vs_deployed) }}>
                        {num(c.vs_deployed, 2)}
                      </td>
                      <td style={cell}>{c.win?.toFixed(1)}%</td>
                      <td style={{ ...cell, color: sign(c.worst) }}>{num(c.worst, 2)}</td>
                      <td style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                        {c.thirds ? c.thirds.map((x) => num(x, 0)).join(' / ') : '–'}
                      </td>
                      <td style={{ ...cell, textAlign: 'left', color: '#b7a882', fontSize: '0.68rem' }}>
                        {winRankNote(c)}
                      </td>
                      <td style={{ ...cell, textAlign: 'center' }}>
                        <RetuneApplyBtn
                          kind="pr"
                          cell={c as Record<string, unknown>}
                          label={`top${i + 1}`}
                          matchesDeployed={isDep}
                          onApplied={onApplied}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        ) : null}

        <div style={{ marginTop: 10, color: '#b7a882', fontSize: '0.72rem' }}>
          <div>
            격자 {r.n_cells}셀 · 모집단 감시 {r.window_n}건 · 롤링 {r.roll_n}건
          </div>
          <div style={{ marginTop: 4 }}>
            축은 확인 × 홀드 × k × dd × hs. evolve 는 끈 상태 고정.
            「배포」칸은 <strong>{u('지금 라이브')}</strong> 노브(적용 overlay 포함)다.
          </div>
          <div style={{ marginTop: 4 }}>
            관문: 배포 우위 · n≥8 · 3등분 전부 양수 · 격자 모서리 금지 · 이웃 과반 초과.
            &quot;총합1위&quot;는 관문과 무관한 참고칸이라 채택 범위 밖도 올라온다.
          </div>
          <div style={{ marginTop: 4, color: '#d29922' }}>
            ※ 총합은 <strong>{u('재생 프레임')}</strong> 값이다 — 라이브 손익이 아니다.
            셀 사이의 비교로만 쓰고, 실적은 원장(live_ledger)을 볼 것.
          </div>
          <div style={{ marginTop: 4 }}>
            이 판정은 <strong>{u('권고만')}</strong> 한다. 적용은 표 「적용」으로 DB 에 반영.
          </div>
        </div>

        {detail ? <LaneDetail scope="pr" {...detail} /> : null}
      </div>
    </div>
  );
}

/** 업숏 되돌림 Top1 일일 채점 패널 — 청캐 사다리 패널과 같은 자리·표 양식.
 *
 *  `analyser_upbit_pb_daily.py` 가 매일 틱에서 재채점해 `upbit_pb_daily` 에
 *  쌓은 걸 보여준다. 판정선은 서버(파이썬 상수)가 단일 출처
 *  (docs/stream-upbit-pb-daily-prereg-2026-09-06.md). */
/** 겹롱 라이브스택. 편승과 같은 색·같은 날짜표. 합은 가격%. */
function GyeopLongStackPanel() {
  const [data, setData] = React.useState<GyeopLongDaily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchGyeopLongDaily(0)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);
  const fold = useScoreFold(true);
  const axis = scoreDayAxis();
  const scrollEnd = useScrollEnd(fold.scrollToken(axis.length));
  return (
    <div style={scoreStackShell(true, {}, PUMP_RIDE_COLOR)}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: PUMP_RIDE_COLOR, fontSize: '0.72rem', fontWeight: 600,
      }}>
        <ScoreFoldTitle fold={fold}>{u('겹롱')}</ScoreFoldTitle>
      </div>
      {!fold.folded && (
        <>
          {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
          {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
          {data && (
            <ScoreLaneStackTable
              accent={PUMP_RIDE_COLOR}
              axis={axis}
              scrollRef={scrollEnd}
              at={(key, day) => {
                const row = day == null
                  ? { ...data, has_data: (data.n ?? 0) > 0 }
                  : (data.days ?? []).find((d) => d.day === day);
                if (key === '강제청산') {
                  if (!row?.liq) return { text: '·', color: '#3a352c' };
                  const sub = (row.liq_perps ?? []).join('\n');
                  return { text: 'Y', color: '#f85149', sub: sub || undefined };
                }
                if (key === '진입정지') {
                  const n = row?.halt_n ?? 0;
                  if (n <= 0) return { text: '·', color: '#3a352c' };
                  const sub = (row?.halt_perps ?? []).join('\n');
                  return { text: String(n), color: '#f0b90b', sub: sub || undefined };
                }
                const cell = scoreMetricCell(key, row, 80);
                if (key !== '실현%eq' || day == null || cell.text === '-') return cell;
                const dayRow = (data.days ?? []).find((d) => d.day === day);
                const sub = (dayRow?.sized_perps ?? []).map((p) => {
                  const name = (p.perp || '').replace(/USDT$/i, '');
                  const v = Number(p.eq_pct);
                  return Number.isFinite(v)
                    ? `${name} ${v >= 0 ? '+' : ''}${v.toFixed(1)}` : name;
                }).join('\n');
                return sub ? { ...cell, sub } : cell;
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function UpbitPbDailyPanel({ liveCfg, stackOnly }: {
  liveCfg?: BinanceShortLiveConfig | null;
  stackOnly?: boolean;
} = {}) {
  const [data, setData] = React.useState<UpbitPbDaily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchUpbitPbDaily(0)  // 적재분 전체 · 좌우 스크롤
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);
  const fold = useScoreFold(!!stackOnly);
  const scrollEnd = useScrollEnd(fold.scrollToken(
    stackOnly
      ? (data ? scoreDayAxis().length : 0)
      : (data?.days.length ?? 0) + (data?.season?.book?.days?.length ?? 0),
  ));

  const cell: React.CSSProperties = {
    padding: '4px 8px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: `1px solid ${stackOnly ? EXCHANGE_COLOR.upbit : '#f0b90b'}22`,
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;

  const badge = (() => {
    if (!data) return null;
    if (data.verdict === 'alarm') {
      return { text: `경보 — ${(data.alarms ?? []).join('·')}`, bg: '#f8514922', fg: '#f85149' };
    }
    if (data.verdict === 'hold') return { text: u('판정 보류'), bg: '#b7a88222', fg: '#b7a882' };
    return { text: u('정상'), bg: '#3fb95022', fg: '#3fb950' };
  })();

  return (
    <div style={scoreStackShell(stackOnly, {
      marginTop: 4, marginBottom: 14, padding: '10px 12px', border: '1px solid #f0b90b33',
      borderRadius: 6, background: '#0f0f0f',
    }, EXCHANGE_COLOR.upbit)}>
      {/* 실동작 설정을 제목 위에 — 청캐 사다리 패널과 같은 계약 (2026-09-06).
          그 아래 쌍숏은 업숏·빗숏 종속 경로(레인 아님) — 같은 박스에 적는다. */}
      {!stackOnly && (() => {
        const c = data?.config;
        const ds = liveCfg;
        if (!c && !ds) return null;
        const n = (v: number | undefined | null, d = 0) =>
          v == null ? '?' : Number(v).toFixed(d);
        const box: React.CSSProperties = {
          marginBottom: 8, padding: '6px 8px', borderRadius: 4,
          background: '#f0b90b0d', border: '1px solid #f0b90b22',
          fontSize: '0.63rem', lineHeight: 1.55, color: '#b7a882',
        };
        const upbitBlock = (c || liveCfg) ? (() => {
          const holdExit = liveCfg?.hold_exit_min ?? c?.hold_exit_min;
          const rule = c ? [
            c.drop_pct != null ? `꺾임 ${n(c.drop_pct, 1)}%` : null,
            c.watch_min != null ? `감시 ${n(c.watch_min)}분` : null,
            c.peak_pct != null ? `peak ${n(c.peak_pct, 1)}%` : null,
            c.stall_min != null ? `stall ${n(c.stall_min)}분` : null,
            holdExit != null ? `홀드 ${n(holdExit)}분` : null,
            c.confirm_s != null ? `바확인 ${n(c.confirm_s)}s` : null,
            liveCfg?.stop_pct != null
              ? (liveCfg.stop_pct <= 0 ? u('스탑 OFF') : `스탑 ${n(liveCfg.stop_pct, 0)}%`)
              : null,
          ].filter(Boolean).join(' · ') : '';
          // 라이브 재확인창은 monitor config(liveCfg) — 일일채점 config 가 아님.
          const gateOn = liveCfg?.upbit_confirm_gate_enabled === true;
          const gate = liveCfg ? [
            gateOn ? u('재확인창 ON') : u('재확인창 OFF'),
            liveCfg.upbit_confirm_wait_s != null
              ? `${n(liveCfg.upbit_confirm_wait_s)}s` : null,
            liveCfg.upbit_confirm_reject_pct != null
              ? `역행≥${n(liveCfg.upbit_confirm_reject_pct, 1)}%` : null,
          ].filter(Boolean).join(' · ') : '';
          const size = c ? [
            (c.base_pct != null && c.step_pct != null && c.margin_cap_pct != null)
              ? `${n(c.base_pct, 0)}/${n(c.step_pct, 0)}/캡${n(c.margin_cap_pct, 0)}`
              : null,
            c.k != null ? `k=${n(c.k)}` : null,
          ].filter(Boolean).join(' · ') : '';
          const on = c?.live === true;
          return (
            <>
              <div style={{ marginBottom: 2 }}>
                <b style={{ color: on ? '#3fb950' : '#f85149' }}>
                  {on ? u('● 라이브 ON') : u('○ 라이브 OFF')}
                </b>
                <span style={{ color: '#7d7362' }}>{u(' — 지금 도는 설정')}</span>
              </div>
              {rule ? <div><span style={{ color: '#7d7362' }}>{u('규칙')} </span>{rule}</div> : null}
              {gate ? (
                <div>
                  <span style={{ color: '#7d7362' }}>{u('확인창')} </span>
                  <span style={{ color: gateOn ? '#3fb950' : '#f85149' }}>{gate}</span>
                </div>
              ) : null}
              {size ? <div><span style={{ color: '#7d7362' }}>{u('사이징')} </span>{size}</div> : null}
            </>
          );
        })() : null;
        const dsBlock = ds ? (() => {
          const on = ds.dual_short_live === true;
          const rule = [
            ds.dual_short_pullback_drop_pct != null
              ? `꺾임 ${n(ds.dual_short_pullback_drop_pct, 1)}%` : null,
            ds.dual_short_pullback_watch_min != null
              ? `감시 ${n(ds.dual_short_pullback_watch_min)}분` : null,
            ds.dual_short_pullback_min_peak_pct != null
              ? `peak ${n(ds.dual_short_pullback_min_peak_pct, 1)}%` : null,
            ds.dual_short_pullback_stall_min != null
              ? `stall ${n(ds.dual_short_pullback_stall_min)}분` : null,
            ds.dual_short_hold_min != null
              ? `홀드 ${n(ds.dual_short_hold_min)}분` : null,
            ds.dual_short_dormant_lookback_min != null
              ? `dormant ${n(ds.dual_short_dormant_lookback_min)}분`
                + (ds.dual_short_dormant_max_range_pct != null
                  ? `≤${n(ds.dual_short_dormant_max_range_pct, 1)}%` : '')
              : null,
            ds.dual_short_window_s != null
              ? `양쪽알림 ${n(ds.dual_short_window_s)}s` : null,
          ].filter(Boolean).join(' · ');
          const size = [
            ds.dual_short_k != null ? `동시슬롯 k=${n(ds.dual_short_k)}` : null,
            u('사이징=부모슬롯 따름'),
            ds.dual_short_risk_pct != null
              ? `risk ${n(ds.dual_short_risk_pct, 2)}%` : null,
            ds.dual_short_leverage != null
              ? `lev ${n(ds.dual_short_leverage, 0)}` : null,
            ds.dual_short_notional_usdt != null
              ? `명목≈$${n(ds.dual_short_notional_usdt, 0)}` : null,
          ].filter(Boolean).join(' · ');
          return (
            <div style={{
              marginTop: c ? 8 : 0,
              paddingTop: c ? 6 : 0,
              borderTop: c ? '1px solid #f0b90b22' : undefined,
            }}>
              <div style={{ marginBottom: 2 }}>
                <b style={{ color: on ? '#3fb950' : '#f85149' }}>
                  {on ? u('● 쌍숏 ON') : u('○ 쌍숏 OFF')}
                </b>
                <span style={{ color: '#7d7362' }}>
                  {u(' — 업숏·빗숏 종속 (레인 아님)')}
                </span>
              </div>
              <div><span style={{ color: '#7d7362' }}>{u('규칙')} </span>{rule || '–'}</div>
              <div><span style={{ color: '#7d7362' }}>{u('사이징')} </span>{size || '–'}</div>
            </div>
          );
        })() : null;
        return (
          <div style={box}>
            {upbitBlock}
            {dsBlock}
          </div>
        );
      })()}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: stackOnly ? EXCHANGE_COLOR.upbit : '#f0b90b', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <ScoreFoldTitle fold={fold}>{stackOnly ? u('업숏') : u('업숏 되돌림 Top1 — 일일 채점')}</ScoreFoldTitle>
        {!stackOnly && (
        <span style={{ color: '#7d7362', fontSize: '0.62rem', fontWeight: 400 }}>
          {data?.cell ?? '1.5/120/2/180/20'} · k={data?.health?.slot_k ?? data?.config?.slot_k ?? 3}
        </span>
        )}
        {!stackOnly && badge && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: '0.64rem',
            background: badge.bg, color: badge.fg,
          }}>{badge.text}</span>
        )}
        {!stackOnly && (
        <span style={{ marginLeft: 'auto', color: '#7d7362', fontSize: '0.62rem' }}>
          {data?.last_scored_at
            ? `최근 채점 ${new Date(data.last_scored_at).toLocaleString('ko-KR', { hour12: false })}`
            : ''}
        </span>
        )}
      </div>

      {!fold.folded && (<>
      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {!stackOnly && data && data.n === 0 && !(data.season?.book?.days?.length) && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>
          채점된 진입이 없다. (일일 잡이 아직 안 돌았거나 틱 공백)
        </div>
      )}

      {stackOnly && data && (() => {
        const goldBy = wr100GoldByDay(data.season?.wr100?.days);
        const book = data.season?.combo ?? data.season?.book;
        const toneSum = sumSizedByTone(book?.days, goldBy);
        return (
        <ScoreLaneStackTable
          accent={EXCHANGE_COLOR.upbit}
          axis={scoreDayAxis()}
          scrollRef={scrollEnd}
          at={(key, day) => {
            const dayRow = day ? book?.days?.find((d) => d.day === day) : undefined;
            const row = day == null
              ? (book ? { ...book, has_data: (book.n ?? 0) > 0 } : undefined)
              : dayRow;
            const cell = scoreMetricCell(key, row, 65);
            if (key !== '실현%eq' || cell.text === '-') return cell;
            if (day == null) {
              return { ...cell, toneSum };
            }
            if (!dayRow) return cell;
            const items = sizedPerpItems(dayRow.sized_perps, goldBy.get(day));
            if (!items.length) return cell;
            return {
              ...cell,
              sub: items.map((x) => x.text).join('\n'),
              subItems: items,
            };
          }}
        />
        );
      })()}
      {!stackOnly && data && (data.n > 0 || (data.season?.book?.days?.length ?? 0) > 0) && (() => {
        const book = data.season?.combo ?? data.season?.book ?? null;
        const seen = new Set<string>();
        const axis: string[] = [];
        for (const day of (book?.days ?? []).map((d) => d.day)
          .concat(data.days.map((d) => d.day))
          .concat((data.ledger?.days ?? []).map((d) => d.day))) {
          if (seen.has(day)) continue;
          seen.add(day);
          axis.push(day);
        }
        axis.sort();
        if (stackOnly) {
          axis.splice(0, axis.length, ...scoreDayAxis());
        }
        const cellBy = new Map(data.days.map((d) => [d.day, d]));
        const ledBy = new Map((data.ledger?.days ?? []).map((d) => [d.day, d]));
        const bookBy = new Map((book?.days ?? []).map((d) => [d.day, d]));
        const dayW = '4.5rem';
        const labW = '7.2rem';
        const tableW = `calc(${labW} + ${(axis.length + 1)} * ${dayW})`;
        const dcell: React.CSSProperties = {
          ...cell, padding: '4px 6px',
          width: dayW, minWidth: dayW, maxWidth: dayW, boxSizing: 'border-box',
        };
        const lab = (color: string, edge = '#f0b90b44'): React.CSSProperties => ({
          ...dcell, textAlign: 'left', color,
          width: labW, minWidth: labW, maxWidth: labW,
          position: 'sticky', left: 0, background: '#0f0f0f',
          borderLeft: 'none', borderRight: `2px solid ${edge}`, zIndex: 1,
        });
        const span = axis.length + 2;
        const band = (
          key: string, color: string, pad: string, border: string | undefined,
          node: React.ReactNode,
        ) => (
          <tr key={key}>
            <td colSpan={span} style={{
              padding: 0,
              borderTop: border,
            }}>
              <div style={{
                position: 'sticky', left: 0, zIndex: 2,
                width: '100cqi', boxSizing: 'border-box',
                padding: pad, color, fontSize: '0.64rem',
                background: '#0f0f0f', whiteSpace: 'normal',
              }}>{node}</div>
            </td>
          </tr>
        );
        const eqTone = (v?: number | null) =>
          v == null ? '#3a352c' : (v <= -50 ? '#f85149' : sign(v));
        const wr100Gold = wr100GoldByDay(data.season?.wr100?.days);
        const cellRows: [string, (d: UpbitPbDailyDay) => string, (d: UpbitPbDailyDay) => string,
          () => { t: string; c: string }][] = [
          ['n', (d) => String(d.n), () => '#b7a882',
            () => ({ t: String(data.n), c: '#e6dcc3' })],
          [u('건당'), (d) => `${num(d.avg_net_pct)}%`, (d) => sign(d.avg_net_pct),
            () => ({ t: `${num(data.avg_net_pct)}%`, c: sign(data.avg_net_pct) })],
          [u('중앙'), (d) => `${num(d.med_net_pct)}%`, (d) => sign(d.med_net_pct),
            () => ({ t: `${num(data.med_net_pct)}%`, c: sign(data.med_net_pct) })],
          [u('승률'), (d) => `${d.win_rate_pct.toFixed(0)}%`,
            (d) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
            () => ({
              t: `${data.win_rate_pct.toFixed(0)}%`,
              c: data.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
            })],
          [u('최악'), (d) => `${num(d.worst_pct)}%`, (d) => sign(d.worst_pct),
            () => ({ t: `${num(data.worst_pct)}%`, c: sign(data.worst_pct) })],
          [u('합계'), (d) => num(d.sum_pct, 1), (d) => sign(d.sum_pct),
            () => ({ t: num(data.sum_pct, 1), c: sign(data.sum_pct) })],
          ['MDD', (d) => num(d.mdd, 1), (d) => sign(d.mdd),
            () => ({ t: num(data.mdd, 1), c: sign(data.mdd) })],
        ];
        const stackRows: [string, (d: UpbitPbSeasonDay) => string, (d: UpbitPbSeasonDay) => string,
          (b: UpbitPbSeasonBook) => { t: string; c: string }][] = book ? [
          ['n', (d) => String(d.n), () => '#b7a882',
            (b) => ({ t: String(b.n), c: '#e6dcc3' })],
          [u('건당'), (d) => `${num(d.avg_net_pct)}%`, (d) => sign(d.avg_net_pct),
            (b) => ({ t: `${num(b.avg_net_pct)}%`, c: sign(b.avg_net_pct) })],
          [u('중앙'), (d) => `${num(d.med_net_pct)}%`, (d) => sign(d.med_net_pct),
            (b) => ({ t: `${num(b.med_net_pct)}%`, c: sign(b.med_net_pct) })],
          [u('승률'), (d) => `${d.win_rate_pct.toFixed(0)}%`,
            (d) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
            (b) => ({
              t: `${b.win_rate_pct.toFixed(0)}%`,
              c: b.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
            })],
          [u('최악'), (d) => `${num(d.worst_pct)}%`, (d) => sign(d.worst_pct),
            (b) => ({ t: `${num(b.worst_pct)}%`, c: sign(b.worst_pct) })],
          [u('합계'), (d) => `${num(d.sum_pct, 1)}%`, (d) => sign(d.sum_pct),
            (b) => ({ t: `${num(b.sum_pct, 1)}%`, c: sign(b.sum_pct) })],
          ['MDD', (d) => `${num(d.mdd, 1)}%`, (d) => sign(d.mdd),
            (b) => ({ t: `${num(b.mdd, 1)}%`, c: sign(b.mdd) })],
        ] : [];
        const head = stackOnly ? EXCHANGE_COLOR.upbit : '#f0b90b';
        const accent = stackOnly ? EXCHANGE_COLOR.upbit : '#58a6ff';
        return (
        <>
          <div ref={scrollEnd} className={stackOnly ? 'score-grid' : undefined} style={{ overflowX: 'auto', marginTop: 4, containerType: 'inline-size' }}>
            <table style={{
              borderCollapse: 'collapse', fontSize: '0.64rem',
              tableLayout: 'fixed', width: tableW, minWidth: tableW,
            }}>
              <thead>
                <tr style={{ color: head }}>
                  <th style={lab(head, `${head}44`)}>{u('지표')}</th>
                  {axis.map((day) => (
                    <th key={day} style={dcell}>{day}</th>
                  ))}
                  <th style={{ ...dcell, borderLeft: `2px solid ${head}55`, fontWeight: 700 }}
                    title={u("창 전체 집계 (일별 합이 아님 — 서버 창 요약)")}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {!stackOnly && band('cell-band', '#f0b90b', '6px 8px 4px', undefined, (
                  <>
                    규칙채점 — {data.cell ?? '1.5/120/2/180/20'} · k={data.health?.slot_k ?? data.config?.slot_k ?? 3}
                    <span style={{ color: '#7d7362', fontWeight: 400 }}>
                      {' · '}전기간 {axis.length}일
                    </span>
                  </>
                ))}
                {!stackOnly && cellRows.map(([label, val, col, tot]) => {
                  const g = tot();
                  return (
                  <tr key={`cell-${label}`} style={{ borderTop: '1px solid #f0b90b18' }}>
                    <td style={lab('#e6dcc3')}>{u(label)}</td>
                    {axis.map((day) => {
                      const d = cellBy.get(day);
                      return (
                        <td key={day} style={{ ...dcell, color: d?.has_data ? col(d) : '#3a352c' }}>
                          {d?.has_data ? val(d) : '–'}
                        </td>
                      );
                    })}
                    <td style={{
                      ...dcell, color: g.c, fontWeight: 700,
                      borderLeft: '2px solid #f0b90b55',
                    }}>{g.t}</td>
                  </tr>
                  );
                })}

                {band('stack-band', accent, '10px 8px 4px', `2px solid ${accent}55`, (
                  <>
                    {u('라이브스택 k=2 — 150/0/캡100 · 홀드 210분 · 스탑 +10%')}
                    <span style={{ color: '#7d7362', fontWeight: 400 }}>
                      {u(' · seg≥20 · KST 18–24 · 촉매제외 · −10%/4시간 · 15분에 더 높으면 미진입 · 산은 알림에 바로')}
                      {data.season?.span ? ` · ${data.season.span}` : ''}
                    </span>
                  </>
                ))}
                {book && stackRows.filter(([label]) => !(stackOnly && label === u('중앙'))).map(([label, val, col, tot]) => {
                  const g = tot(book);
                  return (
                  <tr key={`stack-${label}`} style={{ borderTop: `1px solid ${accent}18` }}>
                    <td style={lab('#8b949e', `${accent}44`)}>{u(label)}</td>
                    {axis.map((day) => {
                      const d = bookBy.get(day);
                      return (
                        <td key={day} style={{ ...dcell, color: d?.has_data ? col(d) : '#3a352c' }}>
                          {d?.has_data ? val(d) : '–'}
                        </td>
                      );
                    })}
                    <td style={{
                      ...dcell, color: g.c, fontWeight: 700,
                      borderLeft: `2px solid ${accent}55`,
                    }}>{g.t}</td>
                  </tr>
                  );
                })}
                {book && (
                  <>
                    <tr style={{ borderTop: `1px solid ${accent}33` }}>
                      <td style={lab('#8b949e', `${accent}44`)} title={u("가격% × 실제 명목% (75/50/캡100 · k=2). 되돌림 동색 · 산 은색 · 전승별책 금.")}>
                        실현%eq
                      </td>
                      {axis.map((day) => {
                        const d = bookBy.get(day);
                        const items = sizedPerpItems(d?.sized_perps, wr100Gold.get(day));
                        return (
                        <td key={day} style={{
                          ...dcell, color: d?.realized_eq_pct == null ? '#3a352c' : sign(d?.realized_eq_pct),
                        }}>
                          {d?.realized_eq_pct == null ? '–' : (
                            <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                              <div>{`${num(d.realized_eq_pct, 1)}%eq`}</div>
                              {renderSizedToneLines(items)}
                            </span>
                          )}
                        </td>
                        );
                      })}
                      <td style={{
                        ...dcell, fontWeight: 700, borderLeft: `2px solid ${accent}55`,
                        color: sign(book.realized_eq_pct),
                      }}>
                        <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                          <div>{`${num(book.realized_eq_pct, 1)}%eq`}</div>
                          {renderToneSumBreakdown(sumSizedByTone(book.days, wr100Gold))}
                        </span>
                      </td>
                    </tr>
                    <tr style={{ borderTop: `1px solid ${accent}33` }}>
                      <td style={lab('#8b949e', `${accent}44`)} title={u("구간 시작 잔고 대비 잔고밀림% (2026-10-18)")}>
                        잔고밀림%
                      </td>
                      {axis.map((day) => {
                        const d = bookBy.get(day);
                        return (
                        <td key={day} style={{ ...dcell, color: eqTone(d?.worst_eq_pct) }}>
                          {d?.worst_eq_pct == null ? '–' : `${num(d.worst_eq_pct, 1)}%eq`}
                        </td>
                        );
                      })}
                      <td style={{
                        ...dcell, fontWeight: 700, borderLeft: `2px solid ${accent}55`,
                        color: eqTone(book.worst_eq_pct),
                      }}>{`${num(book.worst_eq_pct, 1)}%eq`}</td>
                    </tr>
                  </>
                )}

                {!stackOnly && data.ledger && (
                  <>
                    {band('ledger-band', '#3fb950', '10px 8px 4px', '2px solid #3fb95055', (
                      <>
                        {u('실제 원장 — live_ledger · 업숏 정산 (KST)')}
                        {data.ledger.n > 0 ? (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}n {data.ledger.n}
                            {' · '}건당 {num(data.ledger.avg_net_pct)}%
                            {' · '}합 {num(data.ledger.sum_pct, 1)}
                            {' · '}승 {data.ledger.win_rate_pct.toFixed(0)}%
                            {' · '}MDD {num(data.ledger.mdd, 1)}
                          </span>
                        ) : (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}정산 없음
                          </span>
                        )}
                      </>
                    ))}
                    {(() => {
                      const lg = data.ledger!;
                      const rows: [string, (d: UpbitPbDailyDay) => string, (d: UpbitPbDailyDay) => string,
                        () => { t: string; c: string }][] = [
                        ['n', (d) => String(d.n), () => '#8b949e',
                          () => ({ t: String(lg.n), c: '#8b949e' })],
                        [u('건당'), (d) => `${num(d.avg_net_pct)}%`, (d) => sign(d.avg_net_pct),
                          () => ({ t: `${num(lg.avg_net_pct)}%`, c: sign(lg.avg_net_pct) })],
                        [u('중앙'), (d) => `${num(d.med_net_pct)}%`, (d) => sign(d.med_net_pct),
                          () => ({ t: `${num(lg.med_net_pct)}%`, c: sign(lg.med_net_pct) })],
                        [u('승률'), (d) => `${d.win_rate_pct.toFixed(0)}%`,
                          (d) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
                          () => ({
                            t: `${lg.win_rate_pct.toFixed(0)}%`,
                            c: lg.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
                          })],
                        [u('최악'), (d) => `${num(d.worst_pct)}%`, (d) => sign(d.worst_pct),
                          () => ({ t: `${num(lg.worst_pct)}%`, c: sign(lg.worst_pct) })],
                        [u('합계'), (d) => num(d.sum_pct, 1), (d) => sign(d.sum_pct),
                          () => ({ t: num(lg.sum_pct, 1), c: sign(lg.sum_pct) })],
                        ['MDD', (d) => num(d.mdd, 1), (d) => sign(d.mdd),
                          () => ({ t: num(lg.mdd, 1), c: sign(lg.mdd) })],
                      ];
                      return rows.map(([label, val, col, tot]) => {
                        const g = tot();
                        return (
                        <tr key={`ledger-${label}`} style={{ borderTop: '1px solid #3fb95018' }}>
                          <td style={lab('#8b949e', '#3fb95044')}>{u(label)}</td>
                          {axis.map((day) => {
                            const d = ledBy.get(day);
                            return (
                              <td key={day} style={{
                                ...dcell, color: d?.has_data ? col(d) : '#3a352c',
                              }}>
                                {d?.has_data ? val(d) : '–'}
                              </td>
                            );
                          })}
                          <td style={{
                            ...dcell, color: g.c, fontWeight: 700,
                            borderLeft: '2px solid #3fb95055',
                          }}>{lg.n > 0 ? g.t : ''}</td>
                        </tr>
                        );
                      });
                    })()}
                  </>
                )}

                {!stackOnly && band('health-band', '#f0b90b', '10px 8px 4px', '2px solid #f0b90b55',
                  u('레인 건강도 — 전제·꼬리·집중 (성적과 다른 질문)'))}
                {!stackOnly && ([
                  [u('lag중앙'), (d: UpbitPbDailyDay) => (
                    d.has_data ? `${d.lag_med_min.toFixed(0)}m` : '–'
                  ), () => (data.health
                    ? { t: `${data.health.lag_med_min.toFixed(0)}m`, c: '#e6dcc3' }
                    : null)],
                  [u('peak도달'), (d: UpbitPbDailyDay) => (
                    d.has_health ? `${d.peak_ok_pct.toFixed(0)}%` : ''
                  ), () => (data.health
                    ? { t: `${data.health.peak_ok_pct.toFixed(0)}%`, c: '#e6dcc3' }
                    : null)],
                  [u('drop성립'), (d: UpbitPbDailyDay) => (
                    d.has_health && d.n_peak_ok > 0 ? `${d.drop_ok_pct.toFixed(0)}%` : ''
                  ), () => (data.health && data.health.n_peak_ok > 0
                    ? { t: `${data.health.drop_ok_pct.toFixed(0)}%`, c: '#e6dcc3' }
                    : null)],
                  [u('스탑%'), (d: UpbitPbDailyDay) => (
                    d.has_data ? `${d.stop_pct.toFixed(0)}%` : '–'
                  ), () => (data.health
                    ? { t: `${data.health.stop_pct.toFixed(0)}%`, c: '#e6dcc3' }
                    : null)],
                  [u('최악비중'), (d: UpbitPbDailyDay) => (
                    d.has_data ? `${d.worst_share_pct.toFixed(0)}%` : '–'
                  ), () => (data.health
                    ? { t: `${data.health.worst_share_pct.toFixed(0)}%`, c: '#e6dcc3' }
                    : null)],
                  [u('상위1비중'), (d: UpbitPbDailyDay) => (
                    d.has_data && d.top1_share_pct > 0 ? `${d.top1_share_pct.toFixed(0)}%` : ''
                  ), () => (data.health && data.health.top1_share_pct > 0
                    ? { t: `${data.health.top1_share_pct.toFixed(0)}%`, c: '#e6dcc3' }
                    : null)],
                ] as [string, (d: UpbitPbDailyDay) => string,
                  () => { t: string; c: string } | null][]).map(([label, val, tot]) => {
                  const g = tot();
                  return (
                  <tr key={label} style={{ borderTop: '1px solid #f0b90b18' }}>
                    <td style={lab('#b7a882')}>{u(label)}</td>
                    {axis.map((day) => {
                      const d = cellBy.get(day);
                      return (
                        <td key={day} style={{ ...dcell, color: '#b7a882' }}>
                          {d ? val(d) : ''}
                        </td>
                      );
                    })}
                    <td style={{
                      ...dcell, color: g?.c ?? '#3a352c', fontWeight: 700,
                      borderLeft: '2px solid #f0b90b55',
                    }}>{g?.t ?? ''}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!stackOnly && data.health?.pullback_edge_ok != null && (
            <div style={{
              marginTop: 6, fontSize: '0.64rem',
              color: data.health.pullback_edge_ok ? '#3fb950' : '#f85149',
            }}>
              {data.health.pullback_edge_ok
                ? u('되돌림 구조 유지 — peak 후 drop 진입이 성립한다')
                : u('⚠ 되돌림 구조 붕괴 — peak는 나오는데 되돌림 진입이 안 된다. Top1 전제가 깨진 신호')}
            </div>
          )}
          {!stackOnly && data.health?.timing_ok != null && (
            <div style={{
              marginTop: data.health.pullback_edge_ok != null ? 2 : 6,
              fontSize: '0.64rem',
              color: data.health.timing_ok ? '#3fb950' : '#f85149',
            }}>
              {data.health.timing_ok
                ? u('타이밍 유지 — 진입 중앙이 감시창 안에 있다')
                : u('⚠ 타이밍 밀림 — 진입 중앙이 감시창 뒤로 밀린다. 예전에 레인을 죽인 신호')}
            </div>
          )}

          {!stackOnly && data.health && (
            <div style={{ marginTop: 6, fontSize: '0.64rem', color: '#b7a882' }}>
              창 {data.window_days}일(전기간) · funnel {data.health.n_funnel}
              {' · '}peak도달 {data.health.peak_ok_pct.toFixed(0)}%
              {' · '}drop성립 {data.health.drop_ok_pct.toFixed(0)}%
              {' · '}스탑 {data.health.stop_pct.toFixed(0)}%
              {' · '}최악비중 {data.health.worst_share_pct.toFixed(0)}%
              {' · '}상위1 {data.health.top1_share_pct.toFixed(0)}%
              {' · '}lag {data.health.lag_med_min.toFixed(0)}m
            </div>
          )}

          {!stackOnly && (
          <div style={{ marginTop: 8, color: '#7d7362', fontSize: '0.62rem' }}>
            기준선 9/1~9/5 mark@1s · unbounded 건당 +3.74%(n50·합+187·승78) ·
            k=3 건당 +3.30%(n42·합+138.6·승74·MDD−16) · OOS +4.55/−3.6 ·
            경보선 승률 60% / 건당 +1.5% / MDD −35 ·
            전제선 drop성립 50% · lag중앙 90m (watch 120의 75%).
            {' '}라이브스택 150/0/캡100 · k=2 · seg≥20 · KST 18–24 · 촉매제외 · −10%/4시간 · 15분에 더 높으면 미진입 · 산은 종일, 알림에 바로, 스탑 없음.
            연장 슬리브 전용 표는 그 아래.
          </div>
          )}
        </>
        );
      })()}
      </>)}
    </div>
  );
}

/** 빗숏 라이브스택. 옛 셀 채점 표는 없다.
 *  docs/stream-bh-screen-book-2026-10-10.md */
function BhPbDailyPanel({ liveCfg, stackOnly }: {
  liveCfg?: BinanceShortLiveConfig | null;
  stackOnly?: boolean;
} = {}) {
  const [data, setData] = React.useState<BhPbDaily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchBhPbDaily(0)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);
  const fold = useScoreFold(!!stackOnly);
  const scrollEnd = useScrollEnd(fold.scrollToken(
    stackOnly ? (data ? scoreDayAxis().length : 0) : (data?.fits?.book?.days?.length ?? 0),
  ));

  const cell: React.CSSProperties = {
    padding: '4px 8px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: `1px solid ${stackOnly ? EXCHANGE_COLOR.bithumb : '#f0b90b'}22`,
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;
  const judged = (data?.fits?.book?.n ?? 0) > 0 ? data!.fits!.book! : data;
  const badge = (() => {
    if (!judged) return null;
    if (judged.verdict === 'alarm') {
      return { text: `경보 — ${(judged.alarms ?? []).join('·')}`, bg: '#f8514922', fg: '#f85149' };
    }
    if (judged.verdict === 'hold') return { text: u('판정 보류'), bg: '#b7a88222', fg: '#b7a882' };
    return { text: u('정상'), bg: '#3fb95022', fg: '#3fb950' };
  })();
  const c = data?.config;
  const n = (v: number | undefined | null, d = 0) =>
    v == null ? '?' : Number(v).toFixed(d);
  const pkgOn = liveCfg?.bh_ladder_package === true;
  const liveOn = c?.live === true || liveCfg?.bh_newhigh_live === true;

  return (
    <div style={scoreStackShell(stackOnly, {
      marginTop: 4, marginBottom: 14, padding: '10px 12px', border: '1px solid #f0b90b33',
      borderRadius: 6, background: '#0f0f0f',
    }, EXCHANGE_COLOR.bithumb)}>
      {!stackOnly && (
      <div style={{
        marginBottom: 8, padding: '6px 8px', borderRadius: 4,
        background: '#f0b90b0d', border: '1px solid #f0b90b22',
        fontSize: '0.63rem', lineHeight: 1.55, color: '#b7a882',
      }}>
        <div style={{ marginBottom: 2 }}>
          <b style={{ color: liveOn ? '#3fb950' : '#f85149' }}>
            {liveOn ? u('● 라이브 ON') : u('○ 라이브 OFF')}
          </b>
          <span style={{ color: '#7d7362' }}>{u(' — 지금 도는 설정')}</span>
          <span style={{ marginLeft: 10, color: pkgOn ? '#3fb950' : '#f85149' }}>
            {pkgOn ? u('패키지 ON') : u('패키지 OFF')}
          </span>
        </div>
        <div>
          <span style={{ color: '#7d7362' }}>{u('규칙')} </span>
          꺾임 2.5% · 감시 120분 · peak 2% · 홀드 90분 · 스탑 6%
          {' · '}고점뒤 15초~15분 · 얕은고점 제외
          {' · '}빠른고점(알림10분·직전10분+3·고점+4.5~+6) 제외
          {' · '}청산은 ≥19건·$9만 또는 고점 +7.5%·되돌림 ≤35%
          <span style={{ color: '#7d7362' }}>{u(' · 주문=라이브스택')}</span>
        </div>
        {c ? (
          <div>
            <span style={{ color: '#7d7362' }}>{u('사이징')} </span>
            {[
              c.k != null ? `k=${n(c.k)}` : null,
              c.alloc_pct != null ? `배분 ${n(c.alloc_pct, 0)}%` : null,
              u('75/50/캡150'),
            ].filter(Boolean).join(' · ')}
          </div>
        ) : null}
      </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: stackOnly ? EXCHANGE_COLOR.bithumb : '#f0b90b', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <ScoreFoldTitle fold={fold}>{stackOnly ? u('빗숏') : u('빗숏 라이브스택')}</ScoreFoldTitle>
        {!stackOnly && badge && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: '0.64rem',
            background: badge.bg, color: badge.fg,
          }}>{badge.text}</span>
        )}
        {!stackOnly && (
        <span style={{ marginLeft: 'auto', color: '#7d7362', fontSize: '0.62rem' }}>
          {data?.fits?.book?.last_scored_at
            ? `최근 채점 ${new Date(data.fits.book.last_scored_at).toLocaleString('ko-KR', { hour12: false })}`
            : ''}
        </span>
        )}
      </div>

      {!fold.folded && (<>
      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {!stackOnly && data && data.n === 0 && !(data.fits?.book?.n) && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>
          채점된 진입이 없다. (일일 잡 `mercury-bh-pb-daily` 미실행 또는 마크 공백)
        </div>
      )}

      {stackOnly && data && (() => {
        const goldBy = wr100GoldByDay(data.fits?.wr100?.days);
        const book = (data.fits?.book?.n ?? 0) > 0 ? data.fits!.book! : null;
        const pullback = data.fits?.pullback;
        const dump = data.fits?.dump;
        const toneSum = sumSizedByTone(book?.days, goldBy);
        return (
        <ScoreLaneStackTable
          accent={EXCHANGE_COLOR.bithumb}
          axis={scoreDayAxis()}
          scrollRef={scrollEnd}
          labels={BH_SCORE_STACK_LABELS}
          at={(key, day) => bhStackMetricAt(
            key, day, book, pullback, dump, goldBy, toneSum, data.fits?.tp2,
          )}
        />
        );
      })()}
      {!stackOnly && data && (data.n > 0 || (data.fits?.book?.n ?? 0) > 0) && (() => {
        const seen = new Set<string>();
        const axis: string[] = [];
        for (const day of [
          ...(data.fits?.book?.days ?? []),
          ...(data.ledger?.days ?? []),
        ].map((d) => d.day)) {
          if (seen.has(day)) continue;
          seen.add(day);
          axis.push(day);
        }
        axis.sort();
        if (stackOnly) {
          axis.splice(0, axis.length, ...scoreDayAxis());
        }
        const dayW = '4.5rem';
        const labW = '7.2rem';
        const tableW = `calc(${labW} + ${(axis.length + 1)} * ${dayW})`;
        const dcell: React.CSSProperties = {
          ...cell, padding: '4px 6px',
          width: dayW, minWidth: dayW, maxWidth: dayW, boxSizing: 'border-box',
        };
        const lab = (color: string, edge: string): React.CSSProperties => ({
          ...dcell, textAlign: 'left', color,
          width: labW, minWidth: labW, maxWidth: labW,
          position: 'sticky', left: 0, background: '#0f0f0f',
          borderLeft: 'none', borderRight: edge, zIndex: 1,
        });
        const span = axis.length + 2;
        const byDay = (src?: UpbitPbDaily | null) =>
          new Map((src?.days ?? []).map((d) => [d.day, d]));
        const bhBook = (data.fits?.book?.n ?? 0) > 0 ? data.fits!.book! : data;
        const bhHealth = bhBook.health;
        const bhHealthBy = byDay(bhBook);
        const scoreRows = (
          key: string,
          src: UpbitPbDaily,
          color: string,
          by: Map<string, UpbitPbDailyDay>,
        ) => ([
          ['n', (d: UpbitPbDailyDay) => String(d.n), () => '#b7a882',
            { t: String(src.n), c: '#e6dcc3' }],
          [u('건당'), (d: UpbitPbDailyDay) => `${num(d.avg_net_pct)}%`,
            (d: UpbitPbDailyDay) => sign(d.avg_net_pct),
            { t: `${num(src.avg_net_pct)}%`, c: sign(src.avg_net_pct) }],
          [u('중앙'), (d: UpbitPbDailyDay) => `${num(d.med_net_pct)}%`,
            (d: UpbitPbDailyDay) => sign(d.med_net_pct),
            { t: `${num(src.med_net_pct)}%`, c: sign(src.med_net_pct) }],
          [u('승률'), (d: UpbitPbDailyDay) => `${d.win_rate_pct.toFixed(0)}%`,
            (d: UpbitPbDailyDay) => (d.win_rate_pct >= 55 ? '#3fb950' : '#f85149'),
            {
              t: `${src.win_rate_pct.toFixed(0)}%`,
              c: src.win_rate_pct >= 55 ? '#3fb950' : '#f85149',
            }],
          [u('최악'), (d: UpbitPbDailyDay) => `${num(d.worst_pct)}%`,
            (d: UpbitPbDailyDay) => sign(d.worst_pct),
            { t: `${num(src.worst_pct)}%`, c: sign(src.worst_pct) }],
          [u('합계'), (d: UpbitPbDailyDay) => `${num(d.sum_pct, 1)}%`,
            (d: UpbitPbDailyDay) => sign(d.sum_pct),
            { t: `${num(src.sum_pct, 1)}%`, c: sign(src.sum_pct) }],
          ['MDD', (d: UpbitPbDailyDay) => `${num(d.mdd, 1)}%`,
            (d: UpbitPbDailyDay) => sign(d.mdd),
            { t: `${num(src.mdd, 1)}%`, c: sign(src.mdd) }],
        ] as [string, (d: UpbitPbDailyDay) => string, (d: UpbitPbDailyDay) => string,
          { t: string; c: string }][])
          .filter(([label]) => !(stackOnly && label === u('중앙')))
          .map(([label, val, col, g]) => (
            <tr key={`${key}-${label}`} style={{ borderTop: `1px solid ${color}18` }}>
              <td style={lab('#8b949e', `2px solid ${color}44`)}>{u(label)}</td>
              {axis.map((day) => {
                const d = by.get(day);
                return (
                  <td key={day} style={{ ...dcell, color: d?.has_data ? col(d) : '#3a352c' }}>
                    {d?.has_data ? val(d) : '–'}
                  </td>
                );
              })}
              <td style={{
                ...dcell, color: g.c, fontWeight: 700, borderLeft: `2px solid ${color}55`,
              }}>{src.n > 0 ? g.t : ''}</td>
            </tr>
          ));
        const eqTone = (v?: number | null) =>
          v == null ? '#3a352c' : (v <= -50 ? '#f85149' : sign(v));
        const wr100Gold = wr100GoldByDay(data.fits?.wr100?.days);
        const eqRows = (key: string, src: UpbitPbDaily, color: string, by: Map<string, UpbitPbDailyDay>, noTone = false) => (
          <>
            <tr key={`${key}-eq`} style={{ borderTop: `1px solid ${color}33` }}>
              <td style={lab('#8b949e', `2px solid ${color}44`)} title={u("가격% × 명목% (75/50/캡150 · k=2). 되돌림 동색 · 급락 은색 · 전승별책 금.")}>
                실현%eq
              </td>
              {axis.map((day) => {
                const d = by.get(day);
                const items = sizedPerpItems(d?.sized_perps, wr100Gold.get(day));
                return (
                  <td key={day} style={{
                    ...dcell, color: d?.realized_eq_pct == null ? '#3a352c' : sign(d.realized_eq_pct),
                  }}>
                    {d?.realized_eq_pct == null ? '–' : (
                      <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                        <div>{`${num(d.realized_eq_pct, 1)}%eq`}</div>
                        {renderSizedToneLines(items)}
                      </span>
                    )}
                  </td>
                );
              })}
              <td style={{
                ...dcell, fontWeight: 700, borderLeft: `2px solid ${color}55`,
                color: sign(src.realized_eq_pct),
              }}>
                {src.realized_eq_pct == null ? '' : (
                  <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                    <div>{`${num(src.realized_eq_pct, 1)}%eq`}</div>
                    {!noTone && renderToneSumBreakdown(sumSizedByTone(src.days, wr100Gold))}
                  </span>
                )}
              </td>
            </tr>
            <tr key={`${key}-dd`} style={{ borderTop: `1px solid ${color}33` }}>
              <td style={lab('#8b949e', `2px solid ${color}44`)}
                title={u("창 시작 100 + 보유 중 마크 틱 MTM (params.worst_eq_mtm). 정산만 보면 0으로 가려진다.")}>
                잔고밀림%
              </td>
              {axis.map((day) => {
                const d = by.get(day);
                return (
                  <td key={day} style={{ ...dcell, color: eqTone(d?.worst_eq_pct) }}>
                    {d?.worst_eq_pct == null ? '–' : `${num(d.worst_eq_pct, 1)}%eq`}
                  </td>
                );
              })}
              <td style={{
                ...dcell, fontWeight: 700, borderLeft: `2px solid ${color}55`,
                color: eqTone(src.worst_eq_pct),
              }}>{src.worst_eq_pct == null ? '' : `${num(src.worst_eq_pct, 1)}%eq`}</td>
            </tr>
          </>
        );
        const head = stackOnly ? EXCHANGE_COLOR.bithumb : '#f0b90b';
        const accent = stackOnly ? EXCHANGE_COLOR.bithumb : '#79c0ff';
        return (
        <>
          <div ref={scrollEnd} className={stackOnly ? 'score-grid' : undefined} style={{ overflowX: 'auto', marginTop: 4, containerType: 'inline-size' }}>
            <table style={{
              borderCollapse: 'collapse', fontSize: '0.64rem',
              tableLayout: 'fixed', width: tableW, minWidth: tableW,
            }}>
              <thead>
                <tr style={{ color: head }}>
                  <th style={lab(head, `2px solid ${head}44`)}>{u('지표')}</th>
                  {axis.map((day) => (
                    <th key={day} style={dcell}>{day}</th>
                  ))}
                  <th style={{ ...dcell, borderLeft: `2px solid ${head}55`, fontWeight: 700 }}
                    title={u("창 전체 집계 (일별 합이 아님)")}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.fits?.book?.n ?? 0) > 0 && (
                  <>
                    {pinnedScoreTitle(span, accent, `2px solid ${accent}55`, '10px 8px 4px', (
                      <>
                        라이브스택=주문
                        <span style={{ color: '#7d7362', fontWeight: 400 }}>
                          {' · '}{liveOn ? u('주문 ON') : u('주문 OFF')}
                          {' · '}고점뒤 15초~15분 · 얕은고점 제외
                          {' · '}빠른고점 제외
                        </span>
                      </>
                    ))}
                    {scoreRows('bh-book', data.fits!.book!, accent, byDay(data.fits?.book))}
                    {eqRows('bh-book', data.fits!.book!, accent, byDay(data.fits?.book))}
                    {(data.fits?.pullback?.n ?? 0) > 0 && (
                      <>
                        {pinnedScoreTitle(span, '#c9a227', `2px solid #c9a22755`, '8px 8px 2px', (
                          <>
                            {u('↳ 되돌림')}
                            <span style={{ color: '#7d7362', fontWeight: 400 }}>
                              {' · '}{u('book만 · 통합계와 겹치면 급락 우선')}
                            </span>
                          </>
                        ))}
                        {scoreRows('bh-pb', data.fits!.pullback!, '#c9a227', byDay(data.fits?.pullback))}
                        {eqRows('bh-pb', data.fits!.pullback!, '#c9a227', byDay(data.fits?.pullback))}
                      </>
                    )}
                    {(data.fits?.dump?.n ?? 0) > 0 && (
                      <>
                        {pinnedScoreTitle(span, '#a8b2c1', `2px solid #a8b2c155`, '8px 8px 2px', (
                          <>
                            {u('↳ 급락')}
                            <span style={{ color: '#7d7362', fontWeight: 400 }}>
                              {' · '}{u('bh_dump만 · 주문 OFF')}
                            </span>
                          </>
                        ))}
                        {scoreRows('bh-dump', data.fits!.dump!, '#a8b2c1', byDay(data.fits?.dump))}
                        {eqRows('bh-dump', data.fits!.dump!, '#a8b2c1', byDay(data.fits?.dump))}
                      </>
                    )}
                    {(data.fits?.tp2?.n ?? 0) > 0 && (
                      <>
                        {pinnedScoreTitle(span, STRATEGY_TEAL, `2px solid ${STRATEGY_TEAL}55`, '8px 8px 2px', (
                          <>
                            {u('↳ 익절2 별책')}
                            <span style={{ color: '#7d7362', fontWeight: 400 }}>
                              {' · '}{u('r10≤0 ∧ lag≤30 · 익절2%/스탑6%/홀드90 · 스킵 구제(나이·촉매 제외) · 명목300% · 주문 ON · 자리배분 없음')}
                            </span>
                          </>
                        ))}
                        {scoreRows('bh-tp2', data.fits!.tp2!, STRATEGY_TEAL, byDay(data.fits?.tp2))}
                        {eqRows('bh-tp2', data.fits!.tp2!, STRATEGY_TEAL, byDay(data.fits?.tp2), true)}
                      </>
                    )}
                  </>
                )}

                {!stackOnly && data.ledger && (
                  <>
                    {pinnedScoreTitle(span, '#d2a8ff', '2px solid #d2a8ff55', '10px 8px 4px', (
                      <>
                        {u('실제 원장 — live_ledger · 빗숏 정산 (KST)')}
                        <span style={{ color: '#7d7362', fontWeight: 400 }}>
                          {data.ledger.n > 0 ? '' : u(' · 체결 없음')}
                        </span>
                      </>
                    ))}
                    {scoreRows('bh-ledger', data.ledger, '#d2a8ff', byDay(data.ledger))}
                  </>
                )}

                {!stackOnly && bhHealth && (
                  <>
                    {pinnedScoreTitle(span, '#e3b341', '2px solid #e3b34155', '10px 8px 4px',
                      u('레인 건강도'))}
                    {([
                      [u('진입중앙'), (d: UpbitPbDailyDay) => (
                        d.n > 0 ? `${d.lag_med_min.toFixed(0)}m` : ''
                      ), { t: `${bhHealth.lag_med_min.toFixed(0)}m`, c: '#e6dcc3' }],
                      [u('스탑'), (d: UpbitPbDailyDay) => (
                        d.n > 0 ? `${d.stop_pct.toFixed(0)}%` : ''
                      ), { t: `${bhHealth.stop_pct.toFixed(0)}%`, c: '#e6dcc3' }],
                      [u('최악비중'), (d: UpbitPbDailyDay) => (
                        d.n > 0 ? `${d.worst_share_pct.toFixed(0)}%` : ''
                      ), { t: `${bhHealth.worst_share_pct.toFixed(0)}%`, c: '#e6dcc3' }],
                      [u('상위1'), (d: UpbitPbDailyDay) => (
                        d.n > 0 && d.top1_share_pct > 0 ? `${d.top1_share_pct.toFixed(0)}%` : ''
                      ), bhHealth.top1_share_pct > 0
                        ? { t: `${bhHealth.top1_share_pct.toFixed(0)}%`, c: '#e6dcc3' }
                        : { t: '–', c: '#7d7362' }],
                    ] as [string, (d: UpbitPbDailyDay) => string, { t: string; c: string }][])
                      .map(([label, val, g]) => (
                        <tr key={`bh-health-${label}`} style={{ borderTop: '1px solid #e3b34118' }}>
                          <td style={lab('#8b949e', '2px solid #e3b34144')}>{u(label)}</td>
                          {axis.map((day) => {
                            const d = bhHealthBy.get(day);
                            return (
                              <td key={day} style={{ ...dcell, color: '#b7a882' }}>
                                {d ? val(d) : ''}
                              </td>
                            );
                          })}
                          <td style={{
                            ...dcell, color: g.c, fontWeight: 700,
                            borderLeft: '2px solid #e3b34155',
                          }}>{g.t}</td>
                        </tr>
                      ))}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {!stackOnly && bhHealth?.timing_ok != null && (
            <div style={{
              marginTop: 6, fontSize: '0.64rem',
              color: bhHealth.timing_ok ? '#3fb950' : '#f85149',
            }}>
              {bhHealth.timing_ok
                ? u('✓ 진입 타이밍 정상 — 중앙이 60분 안')
                : u('⚠ 진입이 늦다 — 중앙이 60분을 넘는다')}
            </div>
          )}
          {!stackOnly && bhHealth?.tail_ok != null && (
            <div style={{
              marginTop: 2, fontSize: '0.64rem',
              color: bhHealth.tail_ok ? '#3fb950' : '#f85149',
            }}>
              {bhHealth.tail_ok
                ? u('✓ 스탑 비중 정상 — taken 중 25% 안')
                : u('⚠ 스탑이 늘었다 — taken 중 25%를 넘는다')}
            </div>
          )}
          {!stackOnly && (
          <div style={{ marginTop: 8, color: '#7d7362', fontSize: '0.62rem' }}>
            판정선 n≥10 · 건당≥+5 · 승≥65 · MDD&gt;−25.
            건강도 진입중앙≤60분 · 스탑≤25%.
          </div>
          )}
        </>
        );
      })()}
      </>)}
    </div>
  );
}

/** 업숏 구조홀드(연장) 전용 — 채점·라이브스택·원장·건강도.
 *  docs/stream-upbit-rescue-depth-gate-2026-09-07.md */
function UpbitRescueDailyPanel() {
  const [data, setData] = React.useState<UpbitRescueDaily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchUpbitRescueDaily(0)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);
  const num = (x: number, d = 2) => (Number.isFinite(x) ? x.toFixed(d) : '–');
  const sign = (x: number) => (x > 0 ? '#3fb950' : x < 0 ? '#f85149' : '#8b949e');
  const cell: React.CSSProperties = {
    padding: '3px 6px', textAlign: 'right', fontSize: '0.72rem',
    borderLeft: '1px solid #f0b90b22', whiteSpace: 'nowrap',
  };
  if (err) {
    return (
      <div style={{ margin: '12px 0', color: '#f85149', fontSize: '0.75rem' }}>
        연장 일일채점: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ margin: '12px 0', color: '#7d7362', fontSize: '0.72rem' }}>
        연장 일일채점 로딩…
      </div>
    );
  }
  const metricRows = (
    labelPrefix: string,
    days: UpbitRescueDailyDay[] | UpbitPbDailyDay[],
    src: {
      n: number; avg_net_pct: number; med_net_pct: number; win_rate_pct: number;
      worst_pct: number; sum_pct: number; mdd: number;
    },
    border: string,
  ) => ([
    ['n', (d: { n: number }) => String(d.n), () => '#8b949e',
      () => ({ t: String(src.n), c: '#8b949e' })],
    [u('건당'), (d: { avg_net_pct: number }) => `${num(d.avg_net_pct)}%`,
      (d: { avg_net_pct: number }) => sign(d.avg_net_pct),
      () => ({ t: `${num(src.avg_net_pct)}%`, c: sign(src.avg_net_pct) })],
    [u('중앙'), (d: { med_net_pct: number }) => `${num(d.med_net_pct)}%`,
      (d: { med_net_pct: number }) => sign(d.med_net_pct),
      () => ({ t: `${num(src.med_net_pct)}%`, c: sign(src.med_net_pct) })],
    [u('승률'), (d: { win_rate_pct: number }) => `${d.win_rate_pct.toFixed(0)}%`,
      (d: { win_rate_pct: number }) => (d.win_rate_pct >= 50 ? '#3fb950' : '#f85149'),
      () => ({
        t: `${src.win_rate_pct.toFixed(0)}%`,
        c: src.win_rate_pct >= 50 ? '#3fb950' : '#f85149',
      })],
    [u('최악'), (d: { worst_pct: number }) => `${num(d.worst_pct)}%`,
      (d: { worst_pct: number }) => sign(d.worst_pct),
      () => ({ t: `${num(src.worst_pct)}%`, c: sign(src.worst_pct) })],
    [u('합계'), (d: { sum_pct: number }) => num(d.sum_pct, 1),
      (d: { sum_pct: number }) => sign(d.sum_pct),
      () => ({ t: num(src.sum_pct, 1), c: sign(src.sum_pct) })],
    ['MDD', (d: { mdd: number }) => num(d.mdd, 1),
      (d: { mdd: number }) => sign(d.mdd),
      () => ({ t: num(src.mdd, 1), c: sign(src.mdd) })],
  ] as [string, (d: any) => string, (d: any) => string, () => { t: string; c: string }][])
    .map(([label, val, col, tot]) => {
      const g = tot();
      return (
        <tr key={`${labelPrefix}-${label}`} style={{ borderTop: `1px solid ${border}22` }}>
          <td style={{
            ...cell, textAlign: 'left', color: '#8b949e',
            position: 'sticky', left: 0, background: '#0f0f0f',
            borderLeft: 'none', borderRight: `2px solid ${border}44`,
          }}>{u(label)}</td>
          {days.map((d) => (
            <td key={d.day} style={{ ...cell, color: d.has_data ? col(d) : '#3a352c' }}>
              {d.has_data ? val(d) : '–'}
            </td>
          ))}
          <td style={{
            ...cell, color: g.c, fontWeight: 700,
            borderLeft: `2px solid ${border}55`,
          }}>{src.n > 0 ? g.t : ''}</td>
        </tr>
      );
    });

  return (
    <div style={{
      margin: '16px 0 8px', padding: '10px 0 4px',
      borderTop: '1px solid #d2a8ff33',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 6, gap: 8, flexWrap: 'wrap',
      }}>
        <div style={{ color: '#d2a8ff', fontSize: '0.78rem', fontWeight: 600 }}>
          구조홀드(연장) 일일 채점
          <span style={{ color: '#7d7362', fontWeight: 400, marginLeft: 8 }}>
            depth_gate {data.depth_gate_pct}% · giveup {data.giveup_bp}bp
            {' · '}step {data.step_min ?? 15}분 · {data.cell}
          </span>
        </div>
        <div style={{ color: '#7d7362', fontSize: '0.68rem' }}>
          {data.verdict === 'alarm'
            ? `⚠ ${ (data.alarms || []).join('·') || u('경보') }`
            : data.verdict === 'ok' ? u('판정 ok') : u('표본 대기')}
          {data.last_scored
            ? ` · 채점 ${new Date(data.last_scored).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}`
            : ''}
        </div>
      </div>

      {data.n === 0 && !(data.days?.some((d) => d.has_data)) ? (
        <div style={{ color: '#7d7362', fontSize: '0.72rem' }}>
          아직 적재 없음 — `analyser_upbit_rescue_daily.py --persist` 대기
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
              <thead>
                <tr>
                  <th style={{
                    ...cell, textAlign: 'left', color: '#d2a8ff',
                    position: 'sticky', left: 0, background: '#0f0f0f',
                    borderLeft: 'none', borderRight: '2px solid #d2a8ff44',
                  }}>{u('지표')}</th>
                  {data.days.map((d) => (
                    <th key={d.day} style={cell}>{d.day}</th>
                  ))}
                  <th style={{
                    ...cell, borderLeft: '2px solid #d2a8ff55', fontWeight: 700,
                  }}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {([
                  ['n', (d: UpbitRescueDailyDay) => String(d.n), () => '#b7a882',
                    () => ({ t: String(data.n), c: '#e6dcc3' })],
                  [u('건당'), (d: UpbitRescueDailyDay) => `${num(d.avg_net_pct)}%`,
                    (d: UpbitRescueDailyDay) => sign(d.avg_net_pct),
                    () => ({ t: `${num(data.avg_net_pct)}%`, c: sign(data.avg_net_pct) })],
                  [u('중앙'), (d: UpbitRescueDailyDay) => `${num(d.med_net_pct)}%`,
                    (d: UpbitRescueDailyDay) => sign(d.med_net_pct),
                    () => ({ t: `${num(data.med_net_pct)}%`, c: sign(data.med_net_pct) })],
                  [u('승률'), (d: UpbitRescueDailyDay) => `${d.win_rate_pct.toFixed(0)}%`,
                    (d: UpbitRescueDailyDay) => (d.win_rate_pct >= 50 ? '#3fb950' : '#f85149'),
                    () => ({
                      t: `${data.win_rate_pct.toFixed(0)}%`,
                      c: data.win_rate_pct >= 50 ? '#3fb950' : '#f85149',
                    })],
                  [u('최악'), (d: UpbitRescueDailyDay) => `${num(d.worst_pct)}%`,
                    (d: UpbitRescueDailyDay) => sign(d.worst_pct),
                    () => ({ t: `${num(data.worst_pct)}%`, c: sign(data.worst_pct) })],
                  [u('합계'), (d: UpbitRescueDailyDay) => num(d.sum_pct, 1),
                    (d: UpbitRescueDailyDay) => sign(d.sum_pct),
                    () => ({ t: num(data.sum_pct, 1), c: sign(data.sum_pct) })],
                  ['MDD', (d: UpbitRescueDailyDay) => num(d.mdd, 1),
                    (d: UpbitRescueDailyDay) => sign(d.mdd),
                    () => ({ t: num(data.mdd, 1), c: sign(data.mdd) })],
                  [u('Δ합'), (d: UpbitRescueDailyDay) => num(d.delta_sum, 1),
                    (d: UpbitRescueDailyDay) => sign(d.delta_sum),
                    () => ({ t: num(data.delta_sum, 1), c: sign(data.delta_sum) })],
                ] as [string, (d: UpbitRescueDailyDay) => string, (d: UpbitRescueDailyDay) => string,
                  () => { t: string; c: string }][])
                  .map(([label, val, col, tot]) => {
                    const g = tot();
                    return (
                      <tr key={`rescue-cell-${label}`} style={{ borderTop: '1px solid #d2a8ff18' }}>
                        <td style={{
                          ...cell, textAlign: 'left', color: '#e6dcc3',
                          position: 'sticky', left: 0, background: '#0f0f0f',
                          borderLeft: 'none', borderRight: '2px solid #d2a8ff44',
                        }}>{u(label)}</td>
                        {data.days.map((d) => (
                          <td key={d.day} style={{ ...cell, color: d.has_data ? col(d) : '#3a352c' }}>
                            {d.has_data ? val(d) : '–'}
                          </td>
                        ))}
                        <td style={{
                          ...cell, color: g.c, fontWeight: 700,
                          borderLeft: '2px solid #d2a8ff55',
                        }}>{g.t}</td>
                      </tr>
                    );
                  })}

                {data.live_full && data.live_full.days?.length > 0 && (
                  <>
                    <tr>
                      <td colSpan={data.days.length + 2} style={{
                        padding: '10px 8px 4px', color: '#58a6ff',
                        borderTop: '2px solid #58a6ff55', fontSize: '0.64rem',
                      }}>
                        라이브스택 연장경로 — rescue*/depth_gate
                        {data.live_full.n > 0 ? (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}n {data.live_full.n}
                            {' · '}건당 {num(data.live_full.avg_net_pct)}%
                            {' · '}합 {num(data.live_full.sum_pct, 1)}
                            {' · '}MDD {num(data.live_full.mdd, 1)}
                          </span>
                        ) : (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}아직 없음 (pb live 재채점 대기)
                          </span>
                        )}
                      </td>
                    </tr>
                    {metricRows('live', data.live_full.days, data.live_full, '#58a6ff')}
                  </>
                )}

                {data.ledger && (
                  <>
                    <tr>
                      <td colSpan={data.days.length + 2} style={{
                        padding: '10px 8px 4px', color: '#3fb950',
                        borderTop: '2px solid #3fb95055', fontSize: '0.64rem',
                      }}>
                        {u('실제 원장 — live_ledger · exit_reason rescue:*')}
                        {data.ledger.n > 0 ? (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}n {data.ledger.n}
                            {' · '}건당 {num(data.ledger.avg_net_pct)}%
                            {' · '}합 {num(data.ledger.sum_pct, 1)}
                          </span>
                        ) : (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}배포 이후 연장 정산 각인 대기
                          </span>
                        )}
                      </td>
                    </tr>
                    {data.ledger.days?.length > 0
                      ? metricRows('ledger', data.ledger.days, data.ledger, '#3fb950')
                      : null}
                  </>
                )}

                {data.health && (
                  <>
                    <tr>
                      <td colSpan={data.days.length + 2} style={{
                        padding: '10px 8px 4px', color: '#f0b90b',
                        borderTop: '2px solid #f0b90b55', fontSize: '0.64rem',
                      }}>
                        건강도 — 연장 깔때기·한계기여
                        <span style={{ color: '#7d7362', fontWeight: 400 }}>
                          {' · '}진입 {data.health.n_entered}
                          {' · '}후보 {data.health.n_would_arm}
                          {' · '}게이트 {data.health.n_depth_gate}
                          {' · '}arm률 {data.health.arm_rate_pct.toFixed(0)}%
                          {' · '}gate률 {data.health.gate_rate_pct.toFixed(0)}%
                          {' · '}Δ합 {num(data.health.delta_sum, 1)}
                          {' · '}개선 {data.health.improve_n}/악화 {data.health.worsen_n}
                          {data.health.edge_ok == null
                            ? ''
                            : data.health.edge_ok
                              ? u(' · 한계기여 OK')
                              : u(' · ⚠ 한계기여 −')}
                        </span>
                      </td>
                    </tr>
                    {([
                      [u('후보 n'), (d: UpbitRescueDailyDay) => (
                        d.has_health ? String(d.n_would_arm) : ''
                      ), () => '#8b949e'],
                      [u('게이트 n'), (d: UpbitRescueDailyDay) => (
                        d.has_health ? String(d.n_depth_gate) : ''
                      ), () => '#8b949e'],
                      [u('Δ합'), (d: UpbitRescueDailyDay) => (
                        d.has_data ? num(d.delta_sum, 1) : '–'
                      ), (d: UpbitRescueDailyDay) => sign(d.delta_sum)],
                    ] as [string, (d: UpbitRescueDailyDay) => string,
                      (d: UpbitRescueDailyDay) => string][])
                      .map(([label, val, col]) => (
                        <tr key={`rescue-h-${label}`} style={{ borderTop: '1px solid #f0b90b18' }}>
                          <td style={{
                            ...cell, textAlign: 'left', color: '#e6dcc3',
                            position: 'sticky', left: 0, background: '#0f0f0f',
                            borderLeft: 'none', borderRight: '2px solid #f0b90b44',
                          }}>{u(label)}</td>
                          {data.days.map((d) => (
                            <td key={d.day} style={{ ...cell, color: col(d) }}>
                              {val(d)}
                            </td>
                          ))}
                          <td style={{
                            ...cell, borderLeft: '2px solid #f0b90b55', color: '#8b949e',
                          }} />
                        </tr>
                      ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 8, color: '#7d7362', fontSize: '0.62rem' }}>
            채점 = 홀드만료 arm 후보에 depth_gate·giveup·max 적용.
            라이브스택 행 = pb live 중 연장경로만. 원장은 배포 후 rescue:* 각인분.
            기준 CF 9월: gate7 합+147.5 · Δ합+8.8 · 최악−9.8 (기준 giveup만 +136/−2.6/−15.8).
            {' '}step {data.step_min ?? 15}분은 연장 중 recover/giveup 확인 주기
            (게이트와 별개 · 1분은 기각).
          </div>
        </>
      )}
    </div>
  );
}

/** 청캐 v1(되돌림) 고정셀 일일 채점 — 사다리(v2) 패널과 같은 자리·표 양식.
 *
 *  `analyser_liq_ladder_daily.py --v1-daily --persist` → `liq_v1_daily`.
 *  판정선은 서버 상수 (docs/stream-liq-v1-daily-prereg-2026-09-06.md).
 *  **2026-09-09: 렌더에서 뺐다** (v1 하드 OFF). 되살릴 때 다시 붙인다. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LiqV1DailyPanel() {
  const [data, setData] = React.useState<LiqV1Daily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchLiqV1Daily(10)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);

  const cell: React.CSSProperties = {
    padding: '4px 8px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: '1px solid #f0b90b22',
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;

  const badge = (() => {
    if (!data) return null;
    if (data.verdict === 'alarm') {
      return { text: `경보 — ${(data.alarms ?? []).join('·')}`, bg: '#f8514922', fg: '#f85149' };
    }
    if (data.verdict === 'hold') return { text: u('판정 보류'), bg: '#b7a88222', fg: '#b7a882' };
    return { text: u('정상'), bg: '#3fb95022', fg: '#3fb950' };
  })();

  return (
    <div style={{
      marginTop: 14, padding: '10px 12px', border: '1px solid #f0b90b33',
      borderRadius: 6, background: '#0f0f0f',
    }}>
      {(() => {
        const c = data?.config;
        if (!c) return null;
        const n = (v: number | undefined, d = 0) =>
          v == null ? '?' : v.toFixed(d);
        const rule = [
          c.peak_pct != null ? `고점 ${n(c.peak_pct, 1)}%` : null,
          c.drop_pct != null ? `꺾임 ${n(c.drop_pct, 1)}%` : null,
          c.watch_min != null ? `감시 ${n(c.watch_min)}분` : null,
          c.hold_min != null ? `홀드 ${n(c.hold_min)}분` : null,
          u('누적청산 <100건 (v1 OFF)'),
          c.cost_pct != null ? `비용 ${n(c.cost_pct, 3)}%` : null,
        ].filter(Boolean).join(' · ');
        const size = [
          c.slot_k != null ? `채점슬롯 k=${n(c.slot_k)}` : null,
          c.k != null ? `라이브 k=${n(c.k)}` : null,
          c.alloc_pct != null ? `배분 ${n(c.alloc_pct, 0)}%` : null,
          c.leverage != null ? `lev ${n(c.leverage, 0)}` : null,
          c.notional_pct != null ? `슬롯명목 자본의 ${n(c.notional_pct, 0)}%` : null,
          c.risk_pct != null ? `risk ${n(c.risk_pct, 2)}%` : null,
        ].filter(Boolean).join(' · ');
        const on = c.live === true;
        return (
          <div style={{
            marginBottom: 8, padding: '6px 8px', borderRadius: 4,
            background: '#f0b90b0d', border: '1px solid #f0b90b22',
            fontSize: '0.63rem', lineHeight: 1.55, color: '#b7a882',
          }}>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: on ? '#3fb950' : '#f85149' }}>
                {on ? u('● 라이브 ON') : u('○ 라이브 OFF')}
              </b>
              <span style={{ color: '#7d7362' }}>
                {c.v1_enabled === false
                  ? u(' — v1 게이트 OFF (liq_v1_enabled, 2026-09-09). 이 채점은 참고용.')
                  : ` — v1 고정셀 ${data?.cell ?? '6.0/2.0/15/15'}`}
              </span>
            </div>
            {rule ? <div><span style={{ color: '#7d7362' }}>{u('규칙')} </span>{rule}</div> : null}
            {size ? <div><span style={{ color: '#7d7362' }}>{u('사이징')} </span>{size}</div> : null}
          </div>
        );
      })()}

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 6, gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{ color: '#f0b90b', fontSize: '0.75rem' }}>
          청캐 v1(되돌림) — 일일 채점
        </span>
        {badge ? (
          <span style={{
            fontSize: '0.65rem', padding: '2px 6px', borderRadius: 3,
            background: badge.bg, color: badge.fg,
          }}>{badge.text}</span>
        ) : null}
      </div>

      {err ? (
        <div style={{ color: '#f85149', fontSize: '0.7rem' }}>{err}</div>
      ) : !data ? (
        <div style={{ color: '#7d7362', fontSize: '0.7rem' }}>{u('불러오는 중…')}</div>
      ) : (data.n === 0 && !(data.live_full && data.live_full.n > 0)) ? (
        <div style={{ color: '#7d7362', fontSize: '0.7rem' }}>
          최근 {data.window_days}일에 채점된 v1 진입이 없다. (일일 잡이 아직 안 돌았거나
          캐스케이드 누적청산이 100건에 못 미쳤다.)
        </div>
      ) : (
        <>
          <div style={{
            fontSize: '0.7rem', color: '#b7a882', marginBottom: 8, lineHeight: 1.5,
          }}>
            n={data.n} · 건당 {num(data.avg_net_pct)}% · 승률 {data.win_rate_pct.toFixed(0)}%
            {' · '}합 {num(data.sum_pct, 1)} · MDD {num(data.mdd, 1)}
            {' · '}최악 {num(data.worst_pct)}%
          </div>
          <div style={{ overflowX: 'auto', containerType: 'inline-size' }}>
            <table style={{
              borderCollapse: 'collapse', fontSize: '0.64rem', width: '100%',
            }}>
              <thead>
                <tr style={{ color: '#f0b90b' }}>
                  <th style={{
                    ...cell, textAlign: 'left', position: 'sticky', left: 0,
                    background: '#0f0f0f', borderLeft: 'none',
                    borderRight: '2px solid #f0b90b44',
                  }}>{u('지표')}</th>
                  {data.days.map((d) => (
                    <th key={d.day} style={cell}>{d.day}</th>
                  ))}
                  <th style={{
                    ...cell, borderLeft: '2px solid #f0b90b55', fontWeight: 700,
                  }}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {([
                  // 셀·스택·원장 동일 지표열 (업숏과 같음, 2026-09-07):
                  // n / 건당 / 중앙 / 승률 / 최악 / 합계 / MDD
                  ['n', (d: LiqV1DailyDay) => String(d.n), () => '#b7a882',
                    () => ({ t: String(data.n), c: '#e6dcc3' })],
                  [u('건당'), (d: LiqV1DailyDay) => `${num(d.avg_net_pct)}%`,
                    (d: LiqV1DailyDay) => sign(d.avg_net_pct),
                    () => ({ t: `${num(data.avg_net_pct)}%`, c: sign(data.avg_net_pct) })],
                  [u('중앙'), (d: LiqV1DailyDay) => `${num(d.med_net_pct)}%`,
                    (d: LiqV1DailyDay) => sign(d.med_net_pct),
                    () => ({ t: `${num(data.med_net_pct)}%`, c: sign(data.med_net_pct) })],
                  [u('승률'), (d: LiqV1DailyDay) => `${d.win_rate_pct.toFixed(0)}%`,
                    (d: LiqV1DailyDay) => (d.win_rate_pct >= 50 ? '#3fb950' : '#f85149'),
                    () => ({
                      t: `${data.win_rate_pct.toFixed(0)}%`,
                      c: data.win_rate_pct >= 50 ? '#3fb950' : '#f85149',
                    })],
                  [u('최악'), (d: LiqV1DailyDay) => `${num(d.worst_pct)}%`,
                    (d: LiqV1DailyDay) => sign(d.worst_pct),
                    () => ({ t: `${num(data.worst_pct)}%`, c: sign(data.worst_pct) })],
                  [u('합계'), (d: LiqV1DailyDay) => num(d.sum_pct, 1),
                    (d: LiqV1DailyDay) => sign(d.sum_pct),
                    () => ({ t: num(data.sum_pct, 1), c: sign(data.sum_pct) })],
                  ['MDD', (d: LiqV1DailyDay) => num(d.mdd, 1),
                    (d: LiqV1DailyDay) => sign(d.mdd),
                    () => ({ t: num(data.mdd, 1), c: sign(data.mdd) })],
                ] as [string, (d: LiqV1DailyDay) => string, (d: LiqV1DailyDay) => string,
                  () => { t: string; c: string }][])
                  .map(([label, val, col, tot]) => {
                    const g = tot();
                    return (
                    <tr key={`cell-${label}`} style={{ borderTop: '1px solid #f0b90b18' }}>
                      <td style={{
                        ...cell, textAlign: 'left', color: '#e6dcc3',
                        position: 'sticky', left: 0, background: '#0f0f0f',
                        borderLeft: 'none', borderRight: '2px solid #f0b90b44',
                      }}>{u(label)}</td>
                      {data.days.map((d) => (
                        <td key={d.day} style={{
                          ...cell, color: d.has_data ? col(d) : '#3a352c',
                        }}>
                          {d.has_data ? val(d) : '–'}
                        </td>
                      ))}
                      <td style={{
                        ...cell, color: g.c, fontWeight: 700,
                        borderLeft: '2px solid #f0b90b55',
                      }}>{data.n > 0 ? g.t : ''}</td>
                    </tr>
                    );
                  })}

                {data.live_full && data.live_full.days?.length > 0 && (
                  <>
                    {pinnedScoreTitle(data.days.length + 2, '#58a6ff', '2px solid #58a6ff55', '10px 8px 4px', (
                      <>
                        라이브스택 k=∞ — 구조홀드(−5%·상한240)
                        {data.live_full.n > 0 ? (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}n {data.live_full.n}
                            {' · '}건당 {num(data.live_full.avg_net_pct)}%
                            {' · '}합 {num(data.live_full.sum_pct, 1)}
                            {' · '}승 {data.live_full.win_rate_pct.toFixed(0)}%
                            {' · '}MDD {num(data.live_full.mdd, 1)}
                          </span>
                        ) : (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}아직 적재 없음 (일일 잡 대기)
                          </span>
                        )}
                      </>
                    ))}
                    {(() => {
                      const lf = data.live_full!;
                      return ([
                        ['n', (d: LiqV1DailyDay) => String(d.n), () => '#8b949e',
                          () => ({ t: String(lf.n), c: '#8b949e' })],
                        [u('건당'), (d: LiqV1DailyDay) => `${num(d.avg_net_pct)}%`,
                          (d: LiqV1DailyDay) => sign(d.avg_net_pct),
                          () => ({ t: `${num(lf.avg_net_pct)}%`, c: sign(lf.avg_net_pct) })],
                        [u('중앙'), (d: LiqV1DailyDay) => `${num(d.med_net_pct)}%`,
                          (d: LiqV1DailyDay) => sign(d.med_net_pct),
                          () => ({ t: `${num(lf.med_net_pct)}%`, c: sign(lf.med_net_pct) })],
                        [u('승률'), (d: LiqV1DailyDay) => `${d.win_rate_pct.toFixed(0)}%`,
                          (d: LiqV1DailyDay) => (d.win_rate_pct >= 50 ? '#3fb950' : '#f85149'),
                          () => ({
                            t: `${lf.win_rate_pct.toFixed(0)}%`,
                            c: lf.win_rate_pct >= 50 ? '#3fb950' : '#f85149',
                          })],
                        [u('최악'), (d: LiqV1DailyDay) => `${num(d.worst_pct)}%`,
                          (d: LiqV1DailyDay) => sign(d.worst_pct),
                          () => ({ t: `${num(lf.worst_pct)}%`, c: sign(lf.worst_pct) })],
                        [u('합계'), (d: LiqV1DailyDay) => num(d.sum_pct, 1),
                          (d: LiqV1DailyDay) => sign(d.sum_pct),
                          () => ({ t: num(lf.sum_pct, 1), c: sign(lf.sum_pct) })],
                        ['MDD', (d: LiqV1DailyDay) => num(d.mdd, 1),
                          (d: LiqV1DailyDay) => sign(d.mdd),
                          () => ({ t: num(lf.mdd, 1), c: sign(lf.mdd) })],
                      ] as [string, (d: LiqV1DailyDay) => string, (d: LiqV1DailyDay) => string,
                        () => { t: string; c: string }][])
                        .map(([label, val, col, tot]) => {
                          const g = tot();
                          return (
                          <tr key={`live-${label}`} style={{ borderTop: '1px solid #58a6ff18' }}>
                            <td style={{
                              ...cell, textAlign: 'left', color: '#8b949e',
                              position: 'sticky', left: 0, background: '#0f0f0f',
                              borderLeft: 'none', borderRight: '2px solid #58a6ff44',
                            }}>{u(label)}</td>
                            {(lf.days ?? []).map((d) => (
                              <td key={d.day} style={{
                                ...cell, color: d.has_data ? col(d) : '#3a352c',
                              }}>
                                {d.has_data ? val(d) : '–'}
                              </td>
                            ))}
                            <td style={{
                              ...cell, color: g.c, fontWeight: 700,
                              borderLeft: '2px solid #58a6ff55',
                            }}>{lf.n > 0 ? g.t : ''}</td>
                          </tr>
                          );
                        });
                    })()}
                  </>
                )}

                {data.ledger && data.ledger.days?.length > 0 && (
                  <>
                    {pinnedScoreTitle(data.days.length + 2, '#3fb950', '2px solid #3fb95055', '10px 8px 4px', (
                      <>
                        {u('실제 원장 — live_ledger · 청캐 정산 (KST)')}
                        {data.ledger.n === 0 && (
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}정산 없음
                          </span>
                        )}
                      </>
                    ))}
                    {(() => {
                      const lg = data.ledger!;
                      return ([
                        ['n', (d: LiqV1DailyDay) => String(d.n), () => '#8b949e',
                          () => ({ t: String(lg.n), c: '#8b949e' })],
                        [u('건당'), (d: LiqV1DailyDay) => `${num(d.avg_net_pct)}%`,
                          (d: LiqV1DailyDay) => sign(d.avg_net_pct),
                          () => ({ t: `${num(lg.avg_net_pct)}%`, c: sign(lg.avg_net_pct) })],
                        [u('중앙'), (d: LiqV1DailyDay) => `${num(d.med_net_pct)}%`,
                          (d: LiqV1DailyDay) => sign(d.med_net_pct),
                          () => ({ t: `${num(lg.med_net_pct)}%`, c: sign(lg.med_net_pct) })],
                        [u('승률'), (d: LiqV1DailyDay) => `${d.win_rate_pct.toFixed(0)}%`,
                          (d: LiqV1DailyDay) => (d.win_rate_pct >= 50 ? '#3fb950' : '#f85149'),
                          () => ({
                            t: `${lg.win_rate_pct.toFixed(0)}%`,
                            c: lg.win_rate_pct >= 50 ? '#3fb950' : '#f85149',
                          })],
                        [u('최악'), (d: LiqV1DailyDay) => `${num(d.worst_pct)}%`,
                          (d: LiqV1DailyDay) => sign(d.worst_pct),
                          () => ({ t: `${num(lg.worst_pct)}%`, c: sign(lg.worst_pct) })],
                        [u('합계'), (d: LiqV1DailyDay) => num(d.sum_pct, 1),
                          (d: LiqV1DailyDay) => sign(d.sum_pct),
                          () => ({ t: num(lg.sum_pct, 1), c: sign(lg.sum_pct) })],
                        ['MDD', (d: LiqV1DailyDay) => num(d.mdd, 1),
                          (d: LiqV1DailyDay) => sign(d.mdd),
                          () => ({ t: num(lg.mdd, 1), c: sign(lg.mdd) })],
                      ] as [string, (d: LiqV1DailyDay) => string, (d: LiqV1DailyDay) => string,
                        () => { t: string; c: string }][])
                        .map(([label, val, col, tot]) => {
                          const g = tot();
                          return (
                          <tr key={`ledger-${label}`} style={{ borderTop: '1px solid #3fb95018' }}>
                            <td style={{
                              ...cell, textAlign: 'left', color: '#8b949e',
                              position: 'sticky', left: 0, background: '#0f0f0f',
                              borderLeft: 'none', borderRight: '2px solid #3fb95044',
                            }}>{u(label)}</td>
                            {(lg.days ?? []).map((d) => (
                              <td key={d.day} style={{
                                ...cell, color: d.has_data ? col(d) : '#3a352c',
                              }}>
                                {d.has_data ? val(d) : '–'}
                              </td>
                            ))}
                            <td style={{
                              ...cell, color: g.c, fontWeight: 700,
                              borderLeft: '2px solid #3fb95055',
                            }}>{lg.n > 0 ? g.t : ''}</td>
                          </tr>
                          );
                        });
                    })()}
                  </>
                )}

                {pinnedScoreTitle(data.days.length + 2, '#f0b90b', '2px solid #f0b90b55', '10px 8px 4px',
                  u('레인 건강도 — 규칙이 아니라 청캐 레인 자체'))}
                {([
                  [u('트리거'), (d: LiqV1DailyDay) => String(d.n_trigger ?? 0)],
                  [u('1st 건당'), (d: LiqV1DailyDay) => (d.ord1_avg == null ? '–' : `${num(d.ord1_avg)}%`)],
                  [u('1st 승률'), (d: LiqV1DailyDay) => (d.ord1_win == null ? '–' : `${d.ord1_win.toFixed(0)}%`)],
                  [u('3rd+ 건당'), (d: LiqV1DailyDay) => (d.ord3_avg == null ? '–' : `${num(d.ord3_avg)}%`)],
                  [u('3rd+ 승률'), (d: LiqV1DailyDay) => (d.ord3_win == null ? '–' : `${d.ord3_win.toFixed(0)}%`)],
                ] as [string, (d: LiqV1DailyDay) => string][]).map(([label, val]) => (
                  <tr key={label} style={{ borderTop: '1px solid #f0b90b18' }}>
                    <td style={{
                      ...cell, textAlign: 'left', color: '#b7a882',
                      position: 'sticky', left: 0, background: '#0f0f0f',
                      borderLeft: 'none', borderRight: '2px solid #f0b90b44',
                    }}>{u(label)}</td>
                    {data.days.map((d) => (
                      <td key={d.day} style={{ ...cell, color: '#b7a882' }}>
                        {val(d) === '0' && label !== u('트리거') ? '' : val(d)}
                      </td>
                    ))}
                    <td style={{
                      ...cell, color: '#b7a882', fontWeight: 700,
                      borderLeft: '2px solid #f0b90b55',
                    }}>
                      {label === u('트리거') && data.health
                        ? String(data.health.n_trigger)
                        : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** 청캐 낙하칼 고정셀 일일 채점 — 사다리(liqv2) 패널과 같은 자리·표 양식.
 *
 *  `analyser_liq_ladder_daily.py --knife-daily --persist` → `liq_knife_daily`.
 *  판정선은 서버 상수. 라이브 셀 thr3/lag120/exit8/stop0 (2026-09-07).
 *  **2026-09-09: 렌더에서 뺐다** (낙하칼 OFF 레인). 되살릴 때 다시 붙인다. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LiqKnifeDailyPanel() {
  const [data, setData] = React.useState<LiqKnifeDaily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchLiqKnifeDaily(10)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);

  const cell: React.CSSProperties = {
    padding: '4px 8px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: '1px solid #f0b90b22',
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;

  const badge = (() => {
    if (!data) return null;
    if (data.verdict === 'alarm') {
      return { text: `경보 — ${(data.alarms ?? []).join('·')}`, bg: '#f8514922', fg: '#f85149' };
    }
    if (data.verdict === 'hold') return { text: u('판정 보류'), bg: '#b7a88222', fg: '#b7a882' };
    return { text: u('정상'), bg: '#3fb95022', fg: '#3fb950' };
  })();

  return (
    <div style={{
      marginTop: 14, padding: '10px 12px', border: '1px solid #f0b90b33',
      borderRadius: 6, background: '#0f0f0f',
    }}>
      {(() => {
        const c = data?.config;
        if (!c) return null;
        const n = (v: number | undefined, d = 0) =>
          v == null ? '?' : v.toFixed(d);
        const rule = [
          c.thr_pct != null ? `낙폭 ≥${n(c.thr_pct, 1)}%` : null,
          c.lag_s != null ? `대기 ${n(c.lag_s)}s~` : null,
          c.max_age_s != null ? `${n(c.max_age_s)}s` : null,
          c.exit_min != null ? `홀드 ${n(c.exit_min)}분` : null,
          c.stop_pct != null
            ? (c.stop_pct > 0 ? `스탑 +${n(c.stop_pct, 1)}%` : u('스탑 없음'))
            : null,
          u('누적청산 <100건 (v1 OFF)'),
          c.cost_pct != null ? `비용 ${n(c.cost_pct, 3)}%` : null,
        ].filter(Boolean).join(' · ');
        const size = [
          c.slot_k != null ? `채점슬롯 k=${n(c.slot_k)}` : null,
          c.k != null ? `라이브 k=${n(c.k)}` : null,
          c.alloc_pct != null ? `배분 ${n(c.alloc_pct, 0)}%` : null,
          c.leverage != null ? `lev ${n(c.leverage, 0)}` : null,
          c.notional_pct != null ? `슬롯명목 자본의 ${n(c.notional_pct, 0)}%` : null,
          c.risk_pct != null ? `risk ${n(c.risk_pct, 2)}%` : null,
        ].filter(Boolean).join(' · ');
        const on = c.live === true;
        const knifeOff = c.enabled === false;
        return (
          <div style={{
            marginBottom: 8, padding: '6px 8px', borderRadius: 4,
            background: '#f0b90b0d', border: '1px solid #f0b90b22',
            fontSize: '0.63rem', lineHeight: 1.55, color: '#b7a882',
          }}>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: on ? '#3fb950' : '#f85149' }}>
                {on ? u('● 라이브 ON') : u('○ 라이브 OFF')}
              </b>
              {knifeOff ? (
                <span style={{ color: '#d29922', marginLeft: 6 }}>
                  (liq_knife_enabled=false)
                </span>
              ) : null}
              <span style={{ color: '#7d7362' }}>
                {u(' — 지금 도는 설정 · 셀 ')}{data?.cell ?? 'thr3/lag120/exit8/stop0'}
              </span>
            </div>
            <div><span style={{ color: '#7d7362' }}>{u('규칙')} </span>{rule}</div>
            {size ? <div><span style={{ color: '#7d7362' }}>{u('사이징')} </span>{size}</div> : null}
          </div>
        );
      })()}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: '#f0b90b', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <span>{u('청캐 낙하칼 — 일일 채점')}</span>
        {badge && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: '0.64rem',
            background: badge.bg, color: badge.fg,
          }}>{badge.text}</span>
        )}
        <span style={{ marginLeft: 'auto', color: '#7d7362', fontSize: '0.62rem' }}>
          {data?.last_scored_at
            ? `최근 채점 ${new Date(data.last_scored_at).toLocaleString('ko-KR', { hour12: false })}`
            : ''}
        </span>
      </div>

      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {data && data.n === 0 && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>
          최근 {data.window_days}일에 채점된 낙하칼 진입이 없다. (일일 잡이 아직 안 돌았거나
          thr 문턱을 넘는 낙하가 없었다)
        </div>
      )}

      {data && data.n > 0 && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 16px', fontSize: '0.7rem' }}>
            <span style={{ color: '#b7a882' }}>n <b style={{ color: '#e6dcc3' }}>{data.n}</b></span>
            <span style={{ color: '#b7a882' }}>{u('건당')} <b style={{ color: sign(data.avg_net_pct) }}>{num(data.avg_net_pct)}%</b></span>
            <span style={{ color: '#b7a882' }}>{u('중앙')} <b style={{ color: sign(data.med_net_pct) }}>{num(data.med_net_pct)}%</b></span>
            <span style={{ color: '#b7a882' }}>{u('승률')} <b style={{ color: data.win_rate_pct >= 50 ? '#3fb950' : '#f85149' }}>{data.win_rate_pct.toFixed(0)}%</b></span>
            <span style={{ color: '#b7a882' }}>{u('합계')} <b style={{ color: sign(data.sum_pct) }}>{num(data.sum_pct, 1)}</b></span>
            <span style={{ color: '#b7a882' }}>{u('최악')} <b style={{ color: sign(data.worst_pct) }}>{num(data.worst_pct)}%</b></span>
            <span style={{ color: '#b7a882' }}>MDD <b style={{ color: sign(data.mdd) }}>{num(data.mdd, 1)}</b></span>
            <span style={{ color: '#b7a882' }}>{u('스탑')} <b style={{ color: '#e6dcc3' }}>{data.n_stopped}</b></span>
            <span style={{ color: '#b7a882' }}>{u('홀드')} <b style={{ color: '#e6dcc3' }}>{(data.avg_hold_min ?? 0).toFixed(1)}m</b></span>
          </div>

          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.64rem' }}>
              <thead>
                <tr style={{ color: '#f0b90b' }}>
                  <th style={{
                    ...cell, textAlign: 'left', position: 'sticky', left: 0,
                    background: '#0f0f0f', borderLeft: 'none',
                    borderRight: '2px solid #f0b90b44',
                  }}>{u('지표')}</th>
                  {data.days.map((d) => (
                    <th key={d.day} style={cell}>{d.day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {([
                  [u('진입 n'), (d: LiqKnifeDailyDay) => String(d.n), () => '#b7a882'],
                  [u('건당'), (d: LiqKnifeDailyDay) => `${num(d.avg_net_pct)}%`,
                    (d: LiqKnifeDailyDay) => sign(d.avg_net_pct)],
                  [u('중앙'), (d: LiqKnifeDailyDay) => `${num(d.med_net_pct)}%`,
                    (d: LiqKnifeDailyDay) => sign(d.med_net_pct)],
                  [u('승률'), (d: LiqKnifeDailyDay) => `${d.win_rate_pct.toFixed(0)}%`,
                    (d: LiqKnifeDailyDay) => (d.win_rate_pct >= 50 ? '#3fb950' : '#f85149')],
                  [u('최악'), (d: LiqKnifeDailyDay) => `${num(d.worst_pct)}%`,
                    (d: LiqKnifeDailyDay) => sign(d.worst_pct)],
                  [u('합계'), (d: LiqKnifeDailyDay) => num(d.sum_pct, 1),
                    (d: LiqKnifeDailyDay) => sign(d.sum_pct)],
                  [u('스탑'), (d: LiqKnifeDailyDay) => String(d.n_stopped), () => '#b7a882'],
                  [u('홀드'), (d: LiqKnifeDailyDay) => d.avg_hold_min.toFixed(1), () => '#b7a882'],
                ] as [string, (d: LiqKnifeDailyDay) => string, (d: LiqKnifeDailyDay) => string][])
                  .map(([label, val, col]) => (
                    <tr key={label} style={{ borderTop: '1px solid #f0b90b18' }}>
                      <td style={{
                        ...cell, textAlign: 'left', color: '#e6dcc3',
                        position: 'sticky', left: 0, background: '#0f0f0f',
                        borderLeft: 'none', borderRight: '2px solid #f0b90b44',
                      }}>{u(label)}</td>
                      {data.days.map((d) => (
                        <td key={d.day} style={{ ...cell, color: d.has_data ? col(d) : '#3a352c' }}>
                          {d.has_data ? val(d) : '–'}
                        </td>
                      ))}
                    </tr>
                  ))}

                <tr>
                  <td colSpan={data.days.length + 1} style={{
                    padding: '10px 8px 4px', color: '#f0b90b',
                    borderTop: '2px solid #f0b90b55', fontSize: '0.64rem',
                  }}>
                    레인 건강도 — 규칙이 아니라 청캐 레인 자체
                  </td>
                </tr>
                {([
                  [u('트리거'), (d: LiqKnifeDailyDay) => String(d.n_trigger)],
                  [u('무장'), (d: LiqKnifeDailyDay) => String(d.n_armed)],
                ] as [string, (d: LiqKnifeDailyDay) => string][]).map(([label, val]) => (
                  <tr key={label} style={{ borderTop: '1px solid #f0b90b18' }}>
                    <td style={{
                      ...cell, textAlign: 'left', color: '#b7a882',
                      position: 'sticky', left: 0, background: '#0f0f0f',
                      borderLeft: 'none', borderRight: '2px solid #f0b90b44',
                    }}>{u(label)}</td>
                    {data.days.map((d) => (
                      <td key={d.day} style={{ ...cell, color: '#b7a882' }}>
                        {val(d) === '0' && label !== u('트리거') ? '' : val(d)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 8, color: '#7d7362', fontSize: '0.62rem' }}>
            고정셀 thr3 · lag120s · exit8m · stop0 (구조홀드 미모형) ·
            최근 6일 스모크 n24 · 건당 −0.33 · 승률 50 · 합 −7.8 · MDD −27.9 ·
            경보선 승률 50% / 건당 +1.0% / MDD −30 · n&lt;8 보류
          </div>
        </>
      )}
    </div>
  );
}

/** 청캐 v3 — 사다리(v2)와 같은 양식: 45일 · 계 · 원장 · 레인 건강도. */
function LiqV3DailyPanel({ stackOnly }: {
  stackOnly?: boolean;
} = {}) {
  const [data, setData] = React.useState<LiqV3Daily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchLiqV3Daily()
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);
  const fold = useScoreFold(!!stackOnly);
  const scrollEnd = useScrollEnd(fold.scrollToken(
    stackOnly ? (data ? scoreDayAxis().length : 0) : (data?.days.length ?? 0),
  ));

  const dayW = '4.5rem';
  const labW = '7.2rem';
  const colDays = stackOnly ? scoreDayAxis() : (data?.days ?? []).map((d) => d.day);
  const nDayCols = colDays.length + 1;
  const tableW = `calc(${labW} + ${nDayCols} * ${dayW})`;
  const cell: React.CSSProperties = {
    padding: '4px 6px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: '1px solid #f0b90b22',
    width: dayW, minWidth: dayW, maxWidth: dayW,
    boxSizing: 'border-box',
  };
  const tableFixed: React.CSSProperties = {
    borderCollapse: 'collapse', fontSize: '0.64rem',
    tableLayout: 'fixed', width: tableW, minWidth: tableW,
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;
  const stickyLab = (borderRight: string, color = '#e6dcc3'): React.CSSProperties => ({
    ...cell, textAlign: 'left', color,
    width: labW, minWidth: labW, maxWidth: labW,
    position: 'sticky', left: 0, background: '#0f0f0f',
    borderLeft: 'none', borderRight, zIndex: 1,
  });

  const badge = (() => {
    if (!data) return null;
    if (data.verdict === 'alarm') {
      return { text: `경보 — ${(data.alarms ?? []).join('·')}`, bg: '#f8514922', fg: '#f85149' };
    }
    if (data.verdict === 'hold') return { text: u('판정 보류'), bg: '#b7a88222', fg: '#b7a882' };
    return { text: u('정상'), bg: '#3fb95022', fg: '#3fb950' };
  })();

  const scoreRows = (
    title: string,
    titleColor: string,
    src: { n: number; avg_net_pct: number; med_net_pct: number; win_rate_pct: number;
      worst_pct: number; sum_pct: number; mdd: number; days: LiqV3DailyDay[];
      n_extended?: number },
    emptyNote?: string,
  ) => (
    <>
      {pinnedScoreTitle((data?.days?.length ?? 0) + 2, titleColor, `2px solid ${titleColor}55`, '10px 8px 4px', (
        <>
          {title}
          {src.n === 0 && emptyNote ? (
            <span style={{ color: '#7d7362', fontWeight: 400 }}>{emptyNote}</span>
          ) : src.n_extended != null && src.n > 0 ? (
            <span style={{ color: '#7d7362', fontWeight: 400 }}>
              {` · 연장 ${src.n_extended}건`}
            </span>
          ) : null}
        </>
      ))}
      {([
        ['n', (d: LiqV3DailyDay) => String(d.n), () => '#b7a882',
          () => ({ t: String(src.n), c: '#e6dcc3' })],
        [u('건당'), (d: LiqV3DailyDay) => `${num(d.avg_net_pct)}%`,
          (d: LiqV3DailyDay) => sign(d.avg_net_pct),
          () => ({ t: `${num(src.avg_net_pct)}%`, c: sign(src.avg_net_pct) })],
        [u('중앙'), (d: LiqV3DailyDay) => `${num(d.med_net_pct)}%`,
          (d: LiqV3DailyDay) => sign(d.med_net_pct),
          () => ({ t: `${num(src.med_net_pct)}%`, c: sign(src.med_net_pct) })],
        [u('승률'), (d: LiqV3DailyDay) => `${d.win_rate_pct.toFixed(0)}%`,
          (d: LiqV3DailyDay) => (d.win_rate_pct >= 50 ? '#3fb950' : '#f85149'),
          () => ({
            t: `${src.win_rate_pct.toFixed(0)}%`,
            c: src.win_rate_pct >= 50 ? '#3fb950' : '#f85149',
          })],
        [u('최악'), (d: LiqV3DailyDay) => `${num(d.worst_pct)}%`,
          (d: LiqV3DailyDay) => sign(d.worst_pct),
          () => ({ t: `${num(src.worst_pct)}%`, c: sign(src.worst_pct) })],
        [u('합계'), (d: LiqV3DailyDay) => `${num(d.sum_pct, 1)}%`,
          (d: LiqV3DailyDay) => sign(d.sum_pct),
          () => ({ t: `${num(src.sum_pct, 1)}%`, c: sign(src.sum_pct) })],
        ['MDD', (d: LiqV3DailyDay) => `${num(d.mdd, 1)}%`,
          (d: LiqV3DailyDay) => sign(d.mdd),
          () => ({ t: `${num(src.mdd, 1)}%`, c: sign(src.mdd) })],
      ] as [string, (d: LiqV3DailyDay) => string, (d: LiqV3DailyDay) => string,
        () => { t: string; c: string }][])
        .filter(([label]) => !(stackOnly && label === u('중앙')))
        .map(([label, val, col, tot]) => {
          const g = tot();
          return (
            <tr key={`${title}-${label}`} style={{ borderTop: '1px solid #f0b90b18' }}>
              <td style={stickyLab(`2px solid ${titleColor}44`, '#b7a882')}>{u(label)}</td>
              {colDays.map((day) => {
                const d = (src.days ?? []).find((x) => x.day === day);
                return (
                <td key={day} style={{
                  ...cell, color: d?.has_data ? col(d) : '#3a352c',
                }}>
                  {d?.has_data ? val(d) : '–'}
                </td>
                );
              })}
              <td style={{
                ...cell, color: g.c, fontWeight: 700,
                borderLeft: `2px solid ${titleColor}55`,
              }}>{src.n > 0 ? g.t : ''}</td>
            </tr>
          );
        })}
    </>
  );

  return (
    <div style={scoreStackShell(stackOnly, {
      marginTop: 14, padding: '10px 12px', border: '1px solid #f0b90b33',
      borderRadius: 6, background: '#0f0f0f',
    }, LIQ_COLOR)}>
      {!stackOnly && (() => {
        const c = data?.config;
        if (!c) return null;
        const n = (v: number | undefined, d = 0) =>
          v == null ? '?' : v.toFixed(d);
        // 「N (심볼들)」 — v3 화이트는 자동 툴이 고른 지금 목록 (2026-11-02)
        const wlList = (c.whitelist ?? []).map((p) => p.replace(/USDT$/, ''));
        const wl = wlList.length ? `${wlList.length} (${wlList.join(' · ')})` : '?';
        const rule = [
          c.gate_cum != null ? `cum≥${n(c.gate_cum)}` : null,
          c.reenter_every != null ? `+${n(c.reenter_every)} 재진입` : null,
          c.max_entries != null ? `≤${n(c.max_entries)}회/산` : null,
          c.tp_pct != null ? `TP ${n(c.tp_pct, 1)}%` : null,
          c.hold_min != null && c.extend_min != null
            ? `H${n(c.hold_min)}→${n(c.extend_min)}`
            : null,
          c.recover_pct != null ? `회복 +${n(c.recover_pct, 1)}%` : null,
          u('조각 없음 · cum≥100→v2'),
          c.cost_pct != null ? `비용 ${n(c.cost_pct, 3)}%` : null,
        ].filter(Boolean).join(' · ');
        const size = [
          c.k != null ? `k=${n(c.k)}` : null,
          c.margin_cap_pct != null ? `캡 ${n(c.margin_cap_pct, 0)}%` : null,
          c.notional_pct != null ? `명목 ${n(c.notional_pct, 0)}%` : null,
          c.leverage != null ? `lev ${n(c.leverage, 0)}` : null,
        ].filter(Boolean).join(' · ');
        const on = c.live === true;
        return (
          <div style={{
            marginBottom: 8, padding: '6px 8px', borderRadius: 4,
            background: '#f0b90b0d', border: '1px solid #f0b90b22',
            fontSize: '0.63rem', lineHeight: 1.55, color: '#b7a882',
          }}>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: on ? '#3fb950' : '#f85149' }}>
                {on ? u('● 라이브 ON') : u('○ 라이브 OFF')}
              </b>
              <span style={{ color: '#7d7362' }}>
                {u(' — 셀 ')}{data?.cell ?? c.cell ?? 'gate20/+15×4 H60→480 tp5/rec2'}
              </span>
            </div>
            <div><span style={{ color: '#7d7362' }}>{u('화이트')} </span>{wl}</div>
            <div><span style={{ color: '#7d7362' }}>{u('규칙')} </span>{rule}</div>
            {size ? <div><span style={{ color: '#7d7362' }}>{u('사이징')} </span>{size}</div> : null}
          </div>
        );
      })()}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: stackOnly ? LIQ_COLOR : '#f0b90b', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <ScoreFoldTitle fold={fold}>{stackOnly ? u('청캐 v3') : u('청캐 v3 (얕은산) — 일일 채점')}</ScoreFoldTitle>
        {!stackOnly && badge && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: '0.64rem',
            background: badge.bg, color: badge.fg,
          }}>{badge.text}</span>
        )}
        {!stackOnly && (
        <span style={{ marginLeft: 'auto', color: '#7d7362', fontSize: '0.62rem' }}>
          {data?.last_scored_at
            ? `최근 채점 ${new Date(data.last_scored_at).toLocaleString('ko-KR', { hour12: false })}`
            : ''}
        </span>
        )}
      </div>

      {!fold.folded && (<>
      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {!stackOnly && data && data.n === 0 && !(data.ledger && data.ledger.days?.length) && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>
          최근 {data.window_days}일에 채점된 v3 진입이 없다. (일일 잡 미가동 · 화이트 산 없음)
        </div>
      )}

      {stackOnly && data && (
        <ScoreLaneStackTable
          accent={LIQ_COLOR}
          axis={scoreDayAxis()}
          scrollRef={scrollEnd}
          at={(key, day) => {
            const eq = data.equity;
            const eqBy = Object.fromEntries((eq?.days ?? []).map((d) => [d.day, d]));
            if (day == null) {
              const cell = scoreMetricCell(key, {
                has_data: data.n > 0,
                n: data.n,
                avg_net_pct: data.avg_net_pct,
                win_rate_pct: data.win_rate_pct,
                worst_pct: data.worst_pct,
                sum_pct: data.sum_pct,
                mdd: data.mdd,
                realized_eq_pct: eq?.realized_eq_pct,
                worst_eq_pct: eq?.worst_eq_pct,
              }, 50);
              return cell;
            }
            const src = data.days.find((d) => d.day === day);
            const e = eqBy[day];
            const cell = scoreMetricCell(key, src ? {
              ...src,
              realized_eq_pct: e?.realized_eq_pct ?? null,
              worst_eq_pct: e?.worst_eq_pct ?? null,
            } : (e ? {
              has_data: false,
              realized_eq_pct: e.realized_eq_pct,
              worst_eq_pct: e.worst_eq_pct,
            } : undefined), 50);
            if (key !== '실현%eq' || cell.text === '-') return cell;
            const sub = (e?.sized_perps ?? []).map((p) => {
              const name = (p.perp || '').replace(/USDT$/i, '');
              const v = Number(p.eq_pct);
              return Number.isFinite(v) ? `${name} ${v >= 0 ? '+' : ''}${v.toFixed(1)}` : name;
            }).join('\n');
            return sub ? { ...cell, sub } : cell;
          }}
        />
      )}
      {!stackOnly && data && (data.n > 0 || (data.ledger && data.ledger.days?.length > 0)) && (
        <>
          <div ref={scrollEnd} className={stackOnly ? 'score-grid' : undefined} style={{ overflowX: 'auto', marginTop: 8, containerType: 'inline-size' }}>
            <table style={{ ...tableFixed, marginTop: 0 }}>
              <thead>
                <tr style={{ color: '#f0b90b' }}>
                  <th style={stickyLab('2px solid #f0b90b44', '#f0b90b')}>{u('지표')}</th>
                  {colDays.map((day) => (
                    <th key={day} style={cell}>{day}</th>
                  ))}
                  <th style={{
                    ...cell, borderLeft: '2px solid #f0b90b55', fontWeight: 700,
                  }}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {scoreRows(
                  u('규칙 성적 — 셀 (얕은산 S10)'),
                  '#f0b90b',
                  data,
                )}

                {(() => {
                  // v2 와 같이 실현%eq · 잔고밀림%만 (강제청산·촉매 행은 생략).
                  const eq = data.equity;
                  if (!eq || !(eq.n_sized > 0)) return null;
                  const by = Object.fromEntries((eq.days || []).map((d) => [d.day, d]));
                  const eqTone = (v?: number | null) =>
                    v == null ? '#3a352c' : (v <= -50 ? '#f85149' : sign(v));
                  const fmtSized = (ps?: { perp: string; eq_pct: number }[] | null) =>
                    !(ps && ps.length) ? null : ps.map((p) => {
                      const name = (p.perp || '').replace(/USDT$/i, '');
                      const v = Number(p.eq_pct);
                      if (!Number.isFinite(v)) return name;
                      return `${name} ${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
                    }).join('\n');
                  const lab = stickyLab('2px solid #f0b90b44', '#8b949e');
                  const totBr = { borderLeft: '2px solid #f0b90b55' } as const;
                  const eqAnchor = eq.equity_usd > 0
                    ? `$${Number(eq.equity_usd).toFixed(0)}`
                    : null;
                  return (
                    <>
                      <tr style={{ borderTop: '1px solid #f0b90b33' }}>
                        <td style={lab} title={u("사이징 반영 실현 (칸 net 합계와 다름)")}>
                          {eqAnchor ? `실현%eq · ${eqAnchor}` : u('실현%eq')}
                        </td>
                        {colDays.map((day) => {
                          const e = by[day];
                          const v = e?.realized_eq_pct;
                          const sub = fmtSized(e?.sized_perps);
                          return (
                            <td key={day} style={{
                              ...cell, color: v == null ? '#3a352c' : sign(v),
                            }}>
                              {v == null ? '–' : (
                                <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                                  <div>{`${num(v, 1)}%eq`}</div>
                                  {sub ? (
                                    <div style={{
                                      fontSize: '0.55rem', color: '#7d7362',
                                      lineHeight: 1.2, whiteSpace: 'pre-line',
                                    }}>{sub}</div>
                                  ) : null}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td style={{
                          ...cell, fontWeight: 700, ...totBr,
                          color: eq.realized_eq_pct == null ? '#8b949e' : sign(eq.realized_eq_pct),
                        }}>
                          {eq.realized_eq_pct == null ? '' : `${num(eq.realized_eq_pct, 1)}%eq`}
                        </td>
                      </tr>
                      <tr style={{ borderTop: '1px solid #f0b90b33' }}>
                        <td style={lab} title={u("구간 시작 잔고 대비 잔고밀림% (2026-10-18)")}>
                          잔고밀림%
                        </td>
                        {colDays.map((day) => {
                          const e = by[day];
                          const v = e?.worst_eq_pct;
                          return (
                            <td key={day} style={{ ...cell, color: eqTone(v) }}>
                              {v == null ? '–' : `${num(v, 1)}%eq`}
                            </td>
                          );
                        })}
                        <td style={{
                          ...cell, fontWeight: 700, ...totBr, color: eqTone(eq.worst_eq_pct),
                        }}>
                          {eq.worst_eq_pct == null ? '' : `${num(eq.worst_eq_pct, 1)}%eq`}
                        </td>
                      </tr>
                    </>
                  );
                })()}

                {!stackOnly && data.ledger && data.ledger.days?.length > 0 && scoreRows(
                  u('실제 원장 — live_ledger · v3 · 날짜=발동 KST (스택과 동일)'),
                  '#3fb950',
                  data.ledger,
                  data.ledger.n === 0 ? u(' · 정산 없음 (라이브 OFF)') : u(' · %eq 앵커는 패널 잔고행'),
                )}

                {!stackOnly && pinnedScoreTitle(data.days.length + 2, '#f0b90b', '2px solid #f0b90b55', '10px 8px 4px', (
                  <>
                    레인 건강도
                    <span style={{ color: '#7d7362', fontWeight: 400 }}>
                      {u(' · 규칙이 아니라 레인 자체 · 화이트 트리거 · leg(cum 문턱)별')}
                    </span>
                  </>
                ))}
                {!stackOnly && ([
                  [u('트리거'), (d: LiqV3DailyDay) => String(d.n_trigger ?? 0)],
                  [u('leg1≥20 건당'), (d: LiqV3DailyDay) => (d.leg1_avg == null ? '–' : `${num(d.leg1_avg)}%`)],
                  [u('leg1 승률'), (d: LiqV3DailyDay) => (d.leg1_win == null ? '–' : `${d.leg1_win.toFixed(0)}%`)],
                  [u('leg2≥35 건당'), (d: LiqV3DailyDay) => (d.leg2_avg == null ? '–' : `${num(d.leg2_avg)}%`)],
                  [u('leg2 승률'), (d: LiqV3DailyDay) => (d.leg2_win == null ? '–' : `${d.leg2_win.toFixed(0)}%`)],
                  [u('leg3≥50 건당'), (d: LiqV3DailyDay) => (d.leg3_avg == null ? '–' : `${num(d.leg3_avg)}%`)],
                  [u('leg3 승률'), (d: LiqV3DailyDay) => (d.leg3_win == null ? '–' : `${d.leg3_win.toFixed(0)}%`)],
                  [u('leg4≥65 건당'), (d: LiqV3DailyDay) => (d.leg4_avg == null ? '–' : `${num(d.leg4_avg)}%`)],
                  [u('leg4 승률'), (d: LiqV3DailyDay) => (d.leg4_win == null ? '–' : `${d.leg4_win.toFixed(0)}%`)],
                ] as [string, (d: LiqV3DailyDay) => string][]).map(([label, val]) => (
                  <tr key={label} style={{ borderTop: '1px solid #f0b90b18' }}>
                    <td style={stickyLab('2px solid #f0b90b44', '#b7a882')}>{u(label)}</td>
                    {data.days.map((d) => (
                      <td key={d.day} style={{
                        ...cell, color: (d.n_trigger ?? 0) > 0 ? '#b7a882' : '#3a352c',
                      }}>
                        {(d.n_trigger ?? 0) === 0
                          ? '–'
                          : val(d) === '0' && label !== u('트리거') ? '' : val(d)}
                      </td>
                    ))}
                    <td style={{
                      ...cell, borderLeft: '2px solid #f0b90b55',
                    }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!stackOnly && data.health?.reenter_edge_ok != null && (
            <div style={{
              marginTop: 6, fontSize: '0.64rem',
              color: data.health.reenter_edge_ok ? '#3fb950' : '#f85149',
            }}>
              {data.health.reenter_edge_ok
                ? u('재진입 유지 — leg2+ 승률 ≥ leg1')
                : u('⚠ 재진입 역전 — leg2+ 가 leg1보다 나쁘다')}
            </div>
          )}
          {!stackOnly && data.health && (
            <div style={{ marginTop: 4, color: '#7d7362', fontSize: '0.62rem' }}>
              창 {data.window_days}일 · 화이트 트리거 {data.health.n_trigger}
              {' · '}진입 {data.health.n_entry}
              {' · '}트리거/일 {data.health.trigger_per_day.toFixed(1)}
            </div>
          )}
          {!stackOnly && (
          <div style={{ marginTop: 8, color: '#7d7362', fontSize: '0.62rem' }}>
            경보선 n≥8 · 승률 50% / 건당 +1.0% / MDD −30 · v2와 같은 45일 창 ·
            원장은 liqv3_settle만 (라이브 OFF면 빈칸).
          </div>
          )}
        </>
      )}
      </>)}
    </div>
  );
}

/** 청캐 통합 일일채점 — v2 live_plain(고점OFF) + v3. */
function LiqUnifiedDailyPanel() {
  const [data, setData] = React.useState<LiqUnifiedDaily | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchLiqUnifiedDaily()
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);

  const dayW = '4.5rem';
  const labW = '7.2rem';
  const nDayCols = (data?.days?.length ?? 0) + 1;
  const tableW = `calc(${labW} + ${nDayCols} * ${dayW})`;
  const cell: React.CSSProperties = {
    padding: '4px 6px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: '1px solid #79c0ff22',
    width: dayW, minWidth: dayW, maxWidth: dayW,
    boxSizing: 'border-box',
  };
  const tableFixed: React.CSSProperties = {
    borderCollapse: 'collapse', fontSize: '0.64rem',
    tableLayout: 'fixed', width: tableW, minWidth: tableW,
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;
  const stickyLab = (borderRight: string, color = '#e6dcc3'): React.CSSProperties => ({
    ...cell, textAlign: 'left', color,
    width: labW, minWidth: labW, maxWidth: labW,
    position: 'sticky', left: 0, background: '#0f0f0f',
    borderLeft: 'none', borderRight, zIndex: 1,
  });
  const eqTone = (v?: number | null) =>
    v == null ? '#3a352c' : (v <= -50 ? '#f85149' : sign(v));
  const fmtSized = (ps?: { perp: string; eq_pct: number }[] | null) =>
    !(ps && ps.length) ? null : ps.map((p) => {
      const name = (p.perp || '').replace(/USDT$/i, '');
      const v = Number(p.eq_pct);
      if (!Number.isFinite(v)) return name;
      return `${name} ${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
    }).join('\n');

  return (
    <div style={{
      marginTop: 14, padding: '10px 12px', border: '1px solid #79c0ff44',
      borderRadius: 6, background: '#0f0f0f',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: '#79c0ff', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <span>{u('통합 일일채점 — live_plain(고점OFF) + v3')}</span>
        <span style={{ marginLeft: 'auto', color: '#7d7362', fontSize: '0.62rem' }}>
          {data?.last_scored_at
            ? `최근 채점 ${new Date(data.last_scored_at).toLocaleString('ko-KR', { hour12: false })}`
            : `창 ${data?.window_days ?? 45}일`}
        </span>
      </div>
      <div style={{ color: '#7d7362', fontSize: '0.62rem', marginBottom: 6 }}>
        건당 net% 단순합 · 실현%eq·잔고밀림은 엔진별 캡/k 사이징 합산
        {data ? ` · v2 ${data.n_v2} + v3 ${data.n_v3} = ${data.n}` : ''}
        {data && data.n_extended > 0 ? ` · 연장 ${data.n_extended}` : ''}
      </div>

      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {data && data.n === 0 && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>
          최근 {data.window_days}일에 합칠 채점이 없다.
        </div>
      )}

      {data && data.n > 0 && data.days?.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 4 }}>
          <table style={{ ...tableFixed, marginTop: 0 }}>
            <thead>
              <tr style={{ color: '#79c0ff' }}>
                <th style={stickyLab('2px solid #79c0ff44', '#79c0ff')}>{u('지표')}</th>
                {data.days.map((d) => (
                  <th key={d.day} style={cell}>{d.day}</th>
                ))}
                <th style={{
                  ...cell, borderLeft: '2px solid #79c0ff55', fontWeight: 700,
                }}>{u('계')}</th>
              </tr>
            </thead>
            <tbody>
              {([
                ['n', (d: Liqv2LadderDay) => String(d.n), () => '#8b949e',
                  () => ({ t: String(data.n), c: '#8b949e' })],
                [u('건당'), (d: Liqv2LadderDay) => `${num(d.avg_net_pct)}%`,
                  (d: Liqv2LadderDay) => sign(d.avg_net_pct),
                  () => ({ t: `${num(data.avg_net_pct)}%`, c: sign(data.avg_net_pct) })],
                [u('중앙'), (d: Liqv2LadderDay) => `${num(d.med_net_pct)}%`,
                  (d: Liqv2LadderDay) => sign(d.med_net_pct),
                  () => ({ t: `${num(data.med_net_pct)}%`, c: sign(data.med_net_pct) })],
                [u('승률'), (d: Liqv2LadderDay) => `${d.win_rate_pct.toFixed(0)}%`,
                  (d: Liqv2LadderDay) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
                  () => ({
                    t: `${data.win_rate_pct.toFixed(0)}%`,
                    c: data.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
                  })],
                [u('최악'), (d: Liqv2LadderDay) => `${num(d.worst_pct)}%`,
                  (d: Liqv2LadderDay) => sign(d.worst_pct),
                  () => ({ t: `${num(data.worst_pct)}%`, c: sign(data.worst_pct) })],
                [u('합계'), (d: Liqv2LadderDay) => `${num(d.sum_pct, 1)}%`,
                  (d: Liqv2LadderDay) => sign(d.sum_pct),
                  () => ({ t: `${num(data.sum_pct, 1)}%`, c: sign(data.sum_pct) })],
                ['MDD', (d: Liqv2LadderDay) => `${num(d.mdd, 1)}%`,
                  (d: Liqv2LadderDay) => sign(d.mdd),
                  () => ({ t: `${num(data.mdd, 1)}%`, c: sign(data.mdd) })],
              ] as [string, (d: Liqv2LadderDay) => string, (d: Liqv2LadderDay) => string,
                () => { t: string; c: string }][])
                .map(([label, val, col, tot]) => {
                  const g = tot();
                  return (
                    <tr key={`udaily-${label}`} style={{ borderTop: '1px solid #79c0ff18' }}>
                      <td style={stickyLab('2px solid #79c0ff44', '#8b949e')}>{u(label)}</td>
                      {data.days.map((d) => (
                        <td key={d.day} style={{
                          ...cell, color: d.has_data ? col(d) : '#3a352c',
                        }}>
                          {d.has_data ? val(d) : '–'}
                        </td>
                      ))}
                      <td style={{
                        ...cell, color: g.c, fontWeight: 700,
                        borderLeft: '2px solid #79c0ff55',
                      }}>{g.t}</td>
                    </tr>
                  );
                })}

              {(() => {
                const eq = data.equity;
                if (!eq || !(eq.n_sized > 0)) return null;
                const by = Object.fromEntries((eq.days || []).map((d) => [d.day, d]));
                const lab = stickyLab('2px solid #79c0ff44', '#8b949e');
                const totBr = { borderLeft: '2px solid #79c0ff55' } as const;
                const eqAnchor = eq.equity_usd > 0
                  ? `$${Number(eq.equity_usd).toFixed(0)}`
                  : null;
                return (
                  <>
                    <tr style={{ borderTop: '1px solid #79c0ff33' }}>
                      <td style={lab} title={u("사이징 반영 실현 (칸 net 합계와 다름)")}>
                        {eqAnchor ? `실현%eq · ${eqAnchor}` : u('실현%eq')}
                      </td>
                      {data.days.map((d) => {
                        const e = by[d.day];
                        const v = e?.realized_eq_pct;
                        const sub = fmtSized(e?.sized_perps);
                        return (
                          <td key={d.day} style={{
                            ...cell, color: v == null ? '#3a352c' : sign(v),
                          }}>
                            {v == null ? '–' : (
                              <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                                <div>{`${num(v, 1)}%eq`}</div>
                                {sub ? (
                                  <div style={{
                                    fontSize: '0.55rem', color: '#7d7362',
                                    lineHeight: 1.2, whiteSpace: 'pre-line',
                                  }}>{sub}</div>
                                ) : null}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{
                        ...cell, fontWeight: 700, ...totBr,
                        color: eq.realized_eq_pct == null ? '#8b949e' : sign(eq.realized_eq_pct),
                      }}>
                        {eq.realized_eq_pct == null ? '' : `${num(eq.realized_eq_pct, 1)}%eq`}
                      </td>
                    </tr>
                    <tr style={{ borderTop: '1px solid #79c0ff33' }}>
                      <td style={lab} title={u("구간 시작 잔고 대비 잔고밀림% (2026-10-18)")}>
                        잔고밀림%
                      </td>
                      {data.days.map((d) => {
                        const e = by[d.day];
                        const v = e?.worst_eq_pct;
                        return (
                          <td key={d.day} style={{ ...cell, color: eqTone(v) }}>
                            {v == null ? '–' : `${num(v, 1)}%eq`}
                          </td>
                        );
                      })}
                      <td style={{
                        ...cell, fontWeight: 700, ...totBr, color: eqTone(eq.worst_eq_pct),
                      }}>
                        {eq.worst_eq_pct == null ? '' : `${num(eq.worst_eq_pct, 1)}%eq`}
                      </td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** 청캐 통합 실제 원장 — v2·v3 아래. lane=liq 전체(v1·v2·v3). */
function LiqUnifiedLedgerPanel() {
  const [data, setData] = React.useState<LiqUnifiedLedger | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    fetchLiqUnifiedLedger()
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);

  const dayW = '4.5rem';
  const labW = '7.2rem';
  const nDayCols = (data?.days?.length ?? 0) + 1;
  const tableW = `calc(${labW} + ${nDayCols} * ${dayW})`;
  const cell: React.CSSProperties = {
    padding: '4px 6px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: '1px solid #3fb95022',
    width: dayW, minWidth: dayW, maxWidth: dayW,
    boxSizing: 'border-box',
  };
  const tableFixed: React.CSSProperties = {
    borderCollapse: 'collapse', fontSize: '0.64rem',
    tableLayout: 'fixed', width: tableW, minWidth: tableW,
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;
  const stickyLab = (borderRight: string, color = '#e6dcc3'): React.CSSProperties => ({
    ...cell, textAlign: 'left', color,
    width: labW, minWidth: labW, maxWidth: labW,
    position: 'sticky', left: 0, background: '#0f0f0f',
    borderLeft: 'none', borderRight, zIndex: 1,
  });
  const eqTone = (v?: number | null) =>
    v == null ? '#3a352c' : (v <= -50 ? '#f85149' : sign(v));
  const fmtSized = (ps?: { perp: string; eq_pct: number }[] | null) =>
    !(ps && ps.length) ? null : ps.map((p) => {
      const name = (p.perp || '').replace(/USDT$/i, '');
      const v = Number(p.eq_pct);
      if (!Number.isFinite(v)) return name;
      return `${name} ${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
    }).join('\n');

  return (
    <div style={{
      marginTop: 14, padding: '10px 12px', border: '1px solid #3fb95044',
      borderRadius: 6, background: '#0f0f0f',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: '#3fb950', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <span>{u('통합 실제 원장 — live_ledger · 청캐 전체 · 날짜=발동 KST')}</span>
        <span style={{ marginLeft: 'auto', color: '#7d7362', fontSize: '0.62rem' }}>
          v1·v2·v3 · 스택과 동일 축 · 잔고밀림%=시작대비 · 창 {data?.window_days ?? 45}일
        </span>
      </div>

      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {data && data.n === 0 && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>
          최근 {data.window_days}일에 청캐 정산이 없다.
        </div>
      )}

      {data && data.days?.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 8, containerType: 'inline-size' }}>
          <table style={{ ...tableFixed, marginTop: 0 }}>
            <thead>
              <tr style={{ color: '#3fb950' }}>
                <th style={stickyLab('2px solid #3fb95044', '#3fb950')}>{u('지표')}</th>
                {data.days.map((d) => (
                  <th key={d.day} style={cell}>{d.day}</th>
                ))}
                <th style={{
                  ...cell, borderLeft: '2px solid #3fb95055', fontWeight: 700,
                }}>{u('계')}</th>
              </tr>
            </thead>
            <tbody>
              {pinnedScoreTitle(data.days.length + 2, '#3fb950', undefined, '4px 8px 4px', (
                <>
                  {u('실제 원장 — lane=liq · 날짜=발동')}
                  {data.n === 0 ? (
                    <span style={{ color: '#7d7362', fontWeight: 400 }}> {u('· 정산 없음')}</span>
                  ) : (
                    <span style={{ color: '#7d7362', fontWeight: 400 }}>
                      {` · n ${data.n} · 건당 ${num(data.avg_net_pct)}% · 합 ${num(data.sum_pct, 1)}`}
                    </span>
                  )}
                </>
              ))}
              {([
                ['n', (d: Liqv2LadderDay) => String(d.n), () => '#8b949e',
                  () => ({ t: String(data.n), c: '#8b949e' })],
                [u('건당'), (d: Liqv2LadderDay) => `${num(d.avg_net_pct)}%`,
                  (d: Liqv2LadderDay) => sign(d.avg_net_pct),
                  () => ({ t: `${num(data.avg_net_pct)}%`, c: sign(data.avg_net_pct) })],
                [u('중앙'), (d: Liqv2LadderDay) => `${num(d.med_net_pct)}%`,
                  (d: Liqv2LadderDay) => sign(d.med_net_pct),
                  () => ({ t: `${num(data.med_net_pct)}%`, c: sign(data.med_net_pct) })],
                [u('승률'), (d: Liqv2LadderDay) => `${d.win_rate_pct.toFixed(0)}%`,
                  (d: Liqv2LadderDay) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
                  () => ({
                    t: `${data.win_rate_pct.toFixed(0)}%`,
                    c: data.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
                  })],
                [u('최악'), (d: Liqv2LadderDay) => `${num(d.worst_pct)}%`,
                  (d: Liqv2LadderDay) => sign(d.worst_pct),
                  () => ({ t: `${num(data.worst_pct)}%`, c: sign(data.worst_pct) })],
                [u('합계'), (d: Liqv2LadderDay) => `${num(d.sum_pct, 1)}%`,
                  (d: Liqv2LadderDay) => sign(d.sum_pct),
                  () => ({ t: `${num(data.sum_pct, 1)}%`, c: sign(data.sum_pct) })],
                ['MDD', (d: Liqv2LadderDay) => `${num(d.mdd, 1)}%`,
                  (d: Liqv2LadderDay) => sign(d.mdd),
                  () => ({ t: `${num(data.mdd, 1)}%`, c: sign(data.mdd) })],
              ] as [string, (d: Liqv2LadderDay) => string, (d: Liqv2LadderDay) => string,
                () => { t: string; c: string }][])
                .map(([label, val, col, tot]) => {
                  const g = tot();
                  return (
                    <tr key={`uni-${label}`} style={{ borderTop: '1px solid #3fb95018' }}>
                      <td style={stickyLab('2px solid #3fb95044', '#8b949e')}>{u(label)}</td>
                      {data.days.map((d) => (
                        <td key={d.day} style={{
                          ...cell, color: d.has_data ? col(d) : '#3a352c',
                        }}>
                          {d.has_data ? val(d) : '–'}
                        </td>
                      ))}
                      <td style={{
                        ...cell, color: g.c, fontWeight: 700,
                        borderLeft: '2px solid #3fb95055',
                      }}>{data.n > 0 ? g.t : ''}</td>
                    </tr>
                  );
                })}

              {(() => {
                const eq = data.equity;
                if (!eq || !(eq.n_sized > 0)) return null;
                const by = Object.fromEntries((eq.days || []).map((d) => [d.day, d]));
                const lab = stickyLab('2px solid #3fb95044', '#8b949e');
                const totBr = { borderLeft: '2px solid #3fb95055' } as const;
                const eqAnchor = eq.equity_usd > 0
                  ? `$${Number(eq.equity_usd).toFixed(0)}`
                  : null;
                return (
                  <>
                    <tr style={{ borderTop: '1px solid #3fb95033' }}>
                      <td style={lab} title={u("사이징 반영 실현")}>
                        {eqAnchor ? `실현%eq · ${eqAnchor}` : u('실현%eq')}
                      </td>
                      {data.days.map((d) => {
                        const e = by[d.day];
                        const v = e?.realized_eq_pct;
                        const sub = fmtSized(e?.sized_perps);
                        return (
                          <td key={d.day} style={{
                            ...cell, color: v == null ? '#3a352c' : sign(v),
                          }}>
                            {v == null ? '–' : (
                              <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                                <div>{`${num(v, 1)}%eq`}</div>
                                {sub ? (
                                  <div style={{
                                    fontSize: '0.55rem', color: '#7d7362',
                                    lineHeight: 1.2, whiteSpace: 'pre-line',
                                  }}>{sub}</div>
                                ) : null}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{
                        ...cell, fontWeight: 700, ...totBr,
                        color: eq.realized_eq_pct == null ? '#8b949e' : sign(eq.realized_eq_pct),
                      }}>
                        {eq.realized_eq_pct == null ? '' : `${num(eq.realized_eq_pct, 1)}%eq`}
                      </td>
                    </tr>
                    <tr style={{ borderTop: '1px solid #3fb95033' }}>
                      <td style={lab} title={u("구간 시작 잔고 대비 잔고밀림% (2026-10-18)")}>
                        잔고밀림%
                      </td>
                      {data.days.map((d) => {
                        const e = by[d.day];
                        const v = e?.worst_eq_pct;
                        return (
                          <td key={d.day} style={{ ...cell, color: eqTone(v) }}>
                            {v == null ? '–' : `${num(v, 1)}%eq`}
                          </td>
                        );
                      })}
                      <td style={{
                        ...cell, fontWeight: 700, ...totBr, color: eqTone(eq.worst_eq_pct),
                      }}>
                        {eq.worst_eq_pct == null ? '' : `${num(eq.worst_eq_pct, 1)}%eq`}
                      </td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** 청캐 사다리(liqv2) 일일 채점 패널 — 재판정 화면 안.
 *
 *  `analyser_liq_ladder_daily.py` 가 매일 틱에서 재채점해 `liq_ladder_daily`
 *  에 쌓은 걸 보여준다. 화면에서 다시 계산하지 않는다 — 판정선은 서버(파이썬
 *  상수)가 단일 출처다 (docs/stream-liq-ladder-prereg-2026-09-05.md §6).
 *  기준선 9/1~9/5: 건당 +8.83% · 승률 88% · MDD −18.5. */
function Liqv2LadderPanel({ onData, stackOnly }: {
  onData?: (d: Liqv2Ladder) => void;
  stackOnly?: boolean;
}) {
  const [data, setData] = React.useState<Liqv2Ladder | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  // 모달 헤더가 v1 재판정 대신 v2 를 따라가야 해서 위로 올려 준다. 여기서
  // 한 번만 받아 오고 모달이 또 부르지 않게 한다 (2026-09-06).
  const onDataRef = React.useRef(onData);
  onDataRef.current = onData;
  React.useEffect(() => {
    let alive = true;
    fetchLiqv2Ladder()
      .then((d) => { if (alive) { setData(d); onDataRef.current?.(d); } })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);
  const fold = useScoreFold(!!stackOnly);
  const scrollEnd = useScrollEnd(fold.scrollToken(
    stackOnly ? (data ? scoreDayAxis().length : 0) : (data?.days.length ?? 0),
  ));

  // 날짜·계 열 폭 = '+109.1%eq' 기준 (비교용으로 표마다 같게 고정).
  // 예전 +318.1%eq(5.4rem) 는 넓었다 — 실현%eq 가 세 자리여도 이 폭이면 된다.
  const dayW = '4.5rem'; // '+109.1%eq' 한 칸
  const labW = '7.2rem'; // u('누적<50 건당') 등 지표명
  const colDays = stackOnly ? scoreDayAxis() : (data?.days ?? []).map((d) => d.day);
  const nDayCols = colDays.length + 1; // days + 계
  const tableW = `calc(${labW} + ${nDayCols} * ${dayW})`;
  const plainInk = stackOnly ? LIQ_COLOR : '#79c0ff';
  const cell: React.CSSProperties = {
    padding: '4px 6px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    borderLeft: `1px solid ${stackOnly ? LIQ_COLOR : '#f0b90b'}22`,
    width: dayW, minWidth: dayW, maxWidth: dayW,
    boxSizing: 'border-box',
  };
  const tableFixed: React.CSSProperties = {
    borderCollapse: 'collapse',
    fontSize: '0.64rem',
    tableLayout: 'fixed',
    width: tableW,
    minWidth: tableW,
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;
  const stickyLab = (borderRight: string, color = '#e6dcc3'): React.CSSProperties => ({
    ...cell, textAlign: 'left', color,
    width: labW, minWidth: labW, maxWidth: labW,
    position: 'sticky', left: 0, background: '#0f0f0f',
    borderLeft: 'none', borderRight, zIndex: 1,
  });
  // 잔고밀림/강제청산 셀 — 그 시각 오픈 심볼 (미실현 손실 큰 순, 최대 3)
  const fmtPerps = (ps?: string[] | null, n = 3) =>
    !(ps && ps.length)
      ? ''
      : ps.slice(0, n).map((p) => p.replace(/USDT$/i, '')).join('·');
  const withPerps = (main: React.ReactNode, ps?: string[] | null) => {
    const lab = fmtPerps(ps);
    return (
      <span title={lab || undefined} style={{ display: 'inline-block', maxWidth: '100%' }}>
        <div>{main}</div>
        {lab ? (
          <div style={{
            fontSize: '0.55rem', color: '#7d7362', lineHeight: 1.15,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{lab}</div>
        ) : null}
      </span>
    );
  };
  // 실현%eq 셀 — 심볼별 기여 (개행, 말줄임 없음).
  const fmtSizedScores = (
    ps?: { perp: string; eq_pct: number }[] | null,
  ) => {
    if (!(ps && ps.length)) return [] as string[];
    return ps.map((p) => {
      const name = (p.perp || '').replace(/USDT$/i, '');
      const v = Number(p.eq_pct);
      if (!Number.isFinite(v)) return name;
      return `${name} ${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
    });
  };
  const withSizedScores = (
    main: React.ReactNode,
    ps?: { perp: string; eq_pct: number }[] | null,
  ) => {
    const lines = fmtSizedScores(ps);
    return (
      <span style={{ display: 'inline-block', maxWidth: '100%' }}>
        <div>{main}</div>
        {lines.length ? (
          <div style={{
            fontSize: '0.55rem', color: '#7d7362', lineHeight: 1.2,
            whiteSpace: 'pre-line',
          }}>{lines.join('\n')}</div>
        ) : null}
      </span>
    );
  };
  // 강제청산 Y = 실제 터짐 OR 촉매 진입정지로 막아서 안 터짐(반사실)
  const liqMark = (e?: {
    liq?: boolean; liq_defended?: boolean;
    liq_perps?: string[]; liq_defended_perps?: string[];
  } | null) => {
    if (!e) return { y: false, perps: undefined as string[] | undefined, tip: undefined as string | undefined };
    if (e.liq) {
      return { y: true, perps: e.liq_perps, tip: u('강제청산') };
    }
    if (e.liq_defended) {
      return {
        y: true, perps: e.liq_defended_perps,
        tip: u('진입정지로 방어 — 촉매 OFF면 강제청산'),
      };
    }
    return { y: false, perps: undefined, tip: undefined };
  };
  const catDays = () => {
    const rows = (data?.catalyst as Liqv2Catalyst | undefined)?.days ?? [];
    return Object.fromEntries(rows.map((d) => [d.day, d]));
  };
  const catCfNet = (d?: { cf_saved_usdt?: number; cf_killed_usdt?: number } | null) =>
    (d?.cf_saved_usdt || 0) - (d?.cf_killed_usdt || 0);
  const catTotals = () => {
    const rows = (data?.catalyst as Liqv2Catalyst | undefined)?.days ?? [];
    return {
      n: rows.reduce((a, d) => a + (d.n_triggers || 0), 0),
      // 회피 − 죽인엣지 (흑자였을 체결도 합산)
      net: rows.reduce((a, d) => a + catCfNet(d), 0),
      cfN: rows.reduce((a, d) => a + (d.cf_n || 0), 0),
    };
  };
  const fmtCfNet = (v: number) => {
    if (v === 0) return '·';
    return `$${v > 0 ? '+' : ''}${v.toFixed(0)}`;
  };
  /** 슬롯 꽉 참 → 연책조각 정책. 약어(OV/best_avg) 쓰지 않음. */
  const overflowPolicyKo = (policy?: string | null) => {
    if (!policy) return null;
    if (policy === 'best_avg') {
      return u('자리없으면 같은종목 연책조각(평단 좋은 쪽)');
    }
    if (policy === 'worst_avg') {
      return u('자리없으면 같은종목 연책조각(평단 나쁜 쪽)');
    }
    if (policy === 'newest' || policy === 'oldest') {
      return `자리없으면 같은종목 연책조각(${policy === 'newest' ? u('최신') : u('가장 오래된')} 책)`;
    }
    return `자리없으면 같은종목 연책조각`;
  };
  /** 라이브스택 표 제목 우측 규칙 한 줄 — params/config 우선, 하드코딩 폴백만. */
  const liveStackRuleLine = (
    cfg: Liqv2LadderConfig | undefined,
    fallback: {
      k: number; legs: number; step: number; cap: number;
      ratio: number; boost: number; recover: number;
      overflow?: string | null;
    },
  ) => {
    const k = cfg?.k ?? cfg?.slot_k ?? fallback.k;
    const legs = cfg?.max_legs ?? fallback.legs;
    const step = cfg?.leg_step_pct ?? fallback.step;
    const cap = cfg?.margin_cap_pct ?? fallback.cap;
    const ratio = cfg?.sleeve_ratio ?? fallback.ratio;
    const boost = cfg?.peak_boost_mult ?? fallback.boost;
    const recover = cfg?.recover_pct ?? fallback.recover;
    const bits = [
      `k${Number(k).toFixed(0)}`,
      `조각${Number(legs).toFixed(0)}`,
      `폭${Number(step).toFixed(0)}`,
      `캡${Number(cap).toFixed(0)}`,
      `ratio${Number(ratio).toFixed(1)}`,
    ];
    if (boost != null && Number(boost) > 1 + 1e-9) {
      bits.push(`고점통과×${Number(boost).toFixed(1)}`);
    }
    if (recover != null && Number(recover) > 0) {
      bits.push(`연장중 +${Number(recover).toFixed(0)}% 회복청산`);
    }
    const ov = overflowPolicyKo(cfg?.overflow_policy ?? fallback.overflow);
    if (ov) bits.push(ov);
    if (cfg?.disaster_med_mode || cfg?.disaster_med_rule) {
      bits.push(
        typeof cfg.disaster_med_rule === 'string' && cfg.disaster_med_rule
          && cfg.disaster_med_rule !== 'OFF'
          ? `재해모드 ${cfg.disaster_med_rule}`
          : u('재해모드 ON'),
      );
    } else if (cfg?.disaster_hedge || (cfg?.disaster_hedge_rule
      && cfg.disaster_hedge_rule !== 'OFF')) {
      bits.push(
        typeof cfg.disaster_hedge_rule === 'string' && cfg.disaster_hedge_rule
          ? `재해헤지 ${cfg.disaster_hedge_rule}`
          : u('재해헤지 ON'),
      );
    }
    // eq_stop: params 미반영(재채점 중)이어도 라이브 기본 ON 표시. 명시 OFF만 숨김.
    if (cfg?.eq_stop !== false && cfg?.eq_stop_rule !== 'OFF') {
      bits.push(
        typeof cfg?.eq_stop_rule === 'string' && cfg.eq_stop_rule
          ? `eq_stop ${cfg.eq_stop_rule}`
          : 'eq_stop eq≤−80·until_flat',
      );
    }
    if (cfg?.catalyst_hold === false) {
      bits.push(u('촉매OFF'));
    }
    return bits.join('·');
  };
  /** 스택 표 공통 잔고·촉매 행 — 실현%eq · 잔고밀림% · 강제청산 · …
   *  잔고밀림$ 행 폐기 (2026-10-07) — % 만. */
  const eqPathRows = (
    eq: Liqv2Equity | undefined,
    days: { day: string }[],
    accent: string,
  ) => {
    if (!eq || eq.n_sized <= 0) return null;
    const by = Object.fromEntries((eq.days || []).map((d) => [d.day, d]));
    const eqTone = (v?: number | null) =>
      v == null ? '#3a352c' : (v <= -50 ? '#f85149' : sign(v));
    const catBy = catDays();
    const tot = catTotals();
    const lab = stickyLab(`2px solid ${accent}44`, '#8b949e');
    const totBr = { borderLeft: `2px solid ${accent}55` } as const;
    return (
      <>
        <tr style={{ borderTop: `1px solid ${accent}33` }}>
          <td style={lab} title={u("사이징 반영 실현 (칸 net 합계와 다름)")}>
            실현%eq
          </td>
          {days.map((d) => {
            const e = by[d.day];
            const v = e?.realized_eq_pct;
            return (
              <td key={d.day} style={{ ...cell, color: v == null ? '#3a352c' : sign(v) }}>
                {v == null ? '–' : withSizedScores(`${num(v, 1)}%eq`, e?.sized_perps)}
              </td>
            );
          })}
          <td style={{
            ...cell, fontWeight: 700, ...totBr,
            color: eq.realized_eq_pct == null ? '#8b949e' : sign(eq.realized_eq_pct),
          }}>
            {eq.realized_eq_pct == null ? '' : `${num(eq.realized_eq_pct, 1)}%eq`}
          </td>
        </tr>
        <tr style={{ borderTop: `1px solid ${accent}33` }}>
          <td style={lab} title={u("구간 시작 잔고 대비 잔고밀림% (화면과 같은 축, 2026-10-18).")}>
            잔고밀림%
          </td>
          {days.map((d) => {
            const e = by[d.day];
            const v = e?.worst_eq_pct;
            return (
              <td key={d.day} style={{ ...cell, color: eqTone(v) }}>
                {v == null ? '–' : `${num(v, 1)}%eq`}
              </td>
            );
          })}
          <td style={{ ...cell, fontWeight: 700, ...totBr, color: eqTone(eq.worst_eq_pct) }}>
            {eq.worst_eq_pct == null ? '' : `${num(eq.worst_eq_pct, 1)}%eq`}
          </td>
        </tr>
        <tr style={{ borderTop: `1px solid ${accent}18` }}>
          <td style={lab}>{u('강제청산')}</td>
          {days.map((d) => {
            const e = by[d.day];
            const m = liqMark(e);
            return (
              <td key={d.day} style={{
                ...cell, color: m.y ? '#f85149' : '#3a352c', fontWeight: m.y ? 700 : 400,
              }} title={m.tip}>
                {e == null ? '–' : (m.y ? withPerps('Y', m.perps) : '·')}
              </td>
            );
          })}
          <td style={{
            ...cell, fontWeight: 700, ...totBr,
            color: (eq.liq || eq.liq_defended) ? '#f85149' : '#8b949e',
          }}>
            {(() => { const m = liqMark(eq); return m.y ? <span title={m.tip}>{withPerps('Y', m.perps)}</span> : '·'; })()}
          </td>
        </tr>
        <tr style={{ borderTop: `1px solid ${accent}18` }}>
          <td style={lab} title={u("홀드 창 안 liq_trigger 수")}>{u('진입정지')}</td>
          {days.map((d) => {
            const c = catBy[d.day];
            const n = c?.n_triggers ?? 0;
            return (
              <td key={d.day} style={{
                ...cell, color: n > 0 ? '#f0b90b' : '#3a352c',
              }} title={(c?.perps || []).join('·') || undefined}>
                {n > 0 ? withPerps(String(n), c?.perps) : '·'}
              </td>
            );
          })}
          <td style={{
            ...cell, fontWeight: 700, ...totBr,
            color: tot.n > 0 ? '#f0b90b' : '#8b949e',
          }}>{tot.n > 0 ? tot.n : '·'}</td>
        </tr>
        <tr style={{ borderTop: `1px solid ${accent}18` }}>
          <td style={lab} title={u("원장 CF 순효과 = 회피 − 죽인엣지 (흑자였을 체결 포함)")}>CF$</td>
          {days.map((d) => {
            const c = catBy[d.day];
            const v = catCfNet(c);
            return (
              <td key={d.day} style={{
                ...cell,
                color: v > 0 ? '#3fb950' : v < 0 ? '#f85149' : '#3a352c',
              }} title={(c?.perps || []).join('·') || undefined}>
                {v !== 0 ? withPerps(fmtCfNet(v), c?.perps) : '·'}
              </td>
            );
          })}
          <td style={{
            ...cell, fontWeight: 700, ...totBr,
            color: tot.net > 0 ? '#3fb950' : tot.net < 0 ? '#f85149' : '#8b949e',
          }}>{fmtCfNet(tot.net)}</td>
        </tr>
      </>
    );
  };


  const badge = (() => {
    if (!data) return null;
    if (data.verdict === 'alarm') {
      return { text: `경보 — ${(data.alarms ?? []).join('·')}`, bg: '#f8514922', fg: '#f85149' };
    }
    if (data.verdict === 'hold') return { text: u('판정 보류'), bg: '#b7a88222', fg: '#b7a882' };
    return { text: u('정상'), bg: '#3fb95022', fg: '#3fb950' };
  })();

  return (
    <div style={scoreStackShell(stackOnly, {
      marginTop: 14, padding: '10px 12px', border: '1px solid #f0b90b33',
      borderRadius: 6, background: '#0f0f0f',
    }, LIQ_COLOR)}>
      {/* 실동작 설정을 제목 위에 적는다 — 규칙 값은 마지막 채점행, 사이징은
          live_lane_settings 에서 온다. 상수를 화면에 박아 두면 배포가 어긋난
          날 화면이 조용히 거짓말한다 (2026-09-06 유저 지시). */}
      {!stackOnly && (() => {
        const c = data?.config;
        if (!c) return null;
        const n = (v: number | undefined, d = 0) =>
          v == null ? '?' : v.toFixed(d);
        const rule = [
          c.min_cum_nliq != null
            ? `누적청산 ${n(c.min_cum_nliq)}건+ 감시` : null,
          c.watch_min != null ? `감시창 ${n(c.watch_min)}분` : null,
          c.drop_pct != null
            ? (c.drop_pct > 0
              ? `러닝고점 −${n(c.drop_pct, 1)}% 꺾이면 첫 조각`
              : u('트리거가에 첫 조각'))
            : null,
          c.max_legs != null
            ? `신고가 +${c.leg_step_pct != null ? n(c.leg_step_pct, 1) : '?'}%마다 조각 (최대 ${n(c.max_legs)})`
            : null,
          c.cascade_quiet_h != null ? `캐스케이드 ${n(c.cascade_quiet_h)}h 경계` : null,
          c.exit_min != null && c.extend_min != null
            ? `청산 ${n(c.exit_min)}분(지면 ${n(c.extend_min)}분 연장)` : null,
          u('스탑 없음'),
          c.cost_pct != null ? `비용 ${n(c.cost_pct, 3)}%` : null,
        ].filter(Boolean).join(' · ');
        const ratioShape = c.sleeve_ratio == null || Math.abs(c.sleeve_ratio - 1) < 1e-9
          ? u('균등') : (c.sleeve_ratio < 1 ? u('앞무겁게') : u('뒤무겁게'));
        const sched = c.sleeve_margin_pcts;
        const size = [
          // v2 (2026-09-10): 슬리브 i 증거금% = cap × ratio^i / Σratio^j.
          c.margin_cap_pct != null
            ? `마진캡 ${n(c.margin_cap_pct)}%` : null,
          c.sleeve_ratio != null
            ? `모양 ratio ${n(c.sleeve_ratio, 2)} (${ratioShape})` : null,
          sched && sched.length
            ? `슬리브 ${sched[0].toFixed(1)}→${sched[sched.length - 1].toFixed(1)}% 증거금`
            : (c.sleeve_margin_pct != null
              ? `슬리브당 ${n(c.sleeve_margin_pct, 1)}% 증거금` : null),
          c.deep_mult != null && c.deep_mult > 1
            ? `깊은꺾임 ×${n(c.deep_mult, 2)} (진입가 고점${
              c.deep_cut_pct != null ? ` ${n(c.deep_cut_pct, 0)}` : ' −12'}% 아래)`
            : null,
          c.peak_boost_mult != null && c.peak_boost_mult > 1
            ? `고점통과 후 사이징 ×${n(c.peak_boost_mult, 1)}` : null,
          overflowPolicyKo(c.overflow_policy),
          c.leverage != null ? `lev ${n(c.leverage, 0)}` : null,
          c.same_symbol_max != null ? `같은종목 겹침 ${n(c.same_symbol_max)}` : null,
          c.alloc_pct != null ? `배분 ${n(c.alloc_pct, 0)}%` : null,
        ].filter(Boolean).join(' · ');
        const on = c.live === true && c.ladder_v2 === true;
        return (
          <div style={{
            marginBottom: 8, padding: '6px 8px', borderRadius: 4,
            background: '#f0b90b0d', border: '1px solid #f0b90b22',
            fontSize: '0.63rem', lineHeight: 1.55, color: '#b7a882',
          }}>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: on ? '#3fb950' : '#f85149' }}>
                {on ? u('● 라이브 ON') : u('○ 라이브 OFF')}
              </b>
            </div>
            <div><span style={{ color: '#7d7362' }}>{u('규칙')} </span>{rule}</div>
            <div><span style={{ color: '#7d7362' }}>{u('사이징')} </span>{size}</div>
            {(() => {
              // 지금 라이브 = 고점확인OFF (live_plain). ON 스택은 반사실.
              const eq = data?.equity as Liqv2Equity | undefined;
              const eqOff = data?.live_plain?.equity as Liqv2Equity | undefined;
              const eqOn = data?.live_full?.equity as Liqv2Equity | undefined;
              const e = eqOff || eqOn || eq;
              if (!e || !(e.n_sized > 0 || e.worst_eq_pct != null)) return null;
              return (
                <div>
                  <span style={{ color: '#7d7362' }}>{u('잔고경로(고점확인OFF)')} </span>
                  {`심볼클러스터마다 $100 리셋`
                    + ` · 자본 $${e.equity_usd?.toFixed?.(0) ?? e.equity_usd}`
                    + (e.margin_cap_pct != null ? ` · 캡${e.margin_cap_pct}%` : '')
                    + ` · k${e.k}`
                    + (e.worst_eq_pct != null
                      ? ` · 잔고밀림 ${e.worst_eq_pct >= 0 ? '+' : ''}${e.worst_eq_pct.toFixed(1)}%eq`
                      : '')
                    + (e.liq || e.liq_defended
                      ? ` · 강제청산 Y${e.liq_defended && !e.liq ? u('(진입정지)') : ''}${
                          ((e.liq ? e.liq_perps : e.liq_defended_perps)?.length)
                            ? ` (${(e.liq ? e.liq_perps! : e.liq_defended_perps!).slice(0, 3).map((p) => p.replace(/USDT$/i, '')).join('·')})`
                            : ''
                        }`
                      : '')}
                </div>
              );
            })()}
          </div>
        );
      })()}

      {!stackOnly && (() => {
        const cat = data?.catalyst as Liqv2Catalyst | undefined;
        if (!cat) return null;
        const now = cat.active_now ?? [];
        const cf = cat.cf;
        const seed = cat.seed;
        const seedBad = seed && seed.status && seed.status !== 'ok';
        // 채점 params 가 마스터 스위치. yaml OFF 면 창이 있어도 진입정지 안 함.
        const holdOff = data?.live_plain?.config?.catalyst_hold === false
          || data?.config?.catalyst_hold === false;
        const kindKo: Record<string, string> = {
          chain_shutdown: u('체인종료'),
          hard_fork_dw: u('포크+DW'),
          token_migrate: u('마이그레이션'),
          major_listing: u('대형상장'),
        };
        const seedLabel = (() => {
          if (!seedBad || !seed) return null;
          if (seed.status === 'failed') return u('● 촉매 시드 조사 실패 — 새 촉매를 자동으로 못 찾음');
          if (seed.status === 'stale') return u('● 촉매 시드 조사 지연 — 마지막 성공 후 30h 초과');
          return u('● 촉매 시드 상태 없음 — 조사 잡 확인');
        })();
        return (
          <>
          {seedBad && seedLabel && (
            <div style={{
              marginBottom: 8, padding: '8px 10px', borderRadius: 4,
              background: '#f8514922', border: '1px solid #f85149aa',
              fontSize: '0.7rem', lineHeight: 1.5, color: '#f85149', fontWeight: 600,
            }}>
              <div>{seedLabel}</div>
              <div style={{ fontWeight: 400, color: '#e6a1a1', marginTop: 2, fontSize: '0.62rem' }}>
                {seed.detail || '—'}
                {seed.ran_at
                  ? ` · 마지막 ${new Date(seed.ran_at).toLocaleString('ko-KR', { hour12: false })}`
                  : ''}
                {seed.model ? ` · ${seed.model}` : ''}
              </div>
            </div>
          )}
          <div style={{
            marginBottom: 8, padding: '6px 8px', borderRadius: 4,
            background: holdOff ? '#3fb9500d' : (now.length ? '#f8514912' : '#3fb9500d'),
            border: `1px solid ${holdOff ? '#3fb95022' : (now.length ? '#f8514933' : '#3fb95022')}`,
            fontSize: '0.63rem', lineHeight: 1.55, color: '#b7a882',
          }}>
            <div style={{ marginBottom: 2 }}>
              <b style={{ color: holdOff ? '#3fb950' : (now.length ? '#f85149' : '#3fb950') }}>
                {holdOff
                  ? u('○ 촉매 진입정지 OFF — 재해헤지 · LSK 등 신규 허용')
                  : now.length
                  ? `● 촉매 진입정지 ${now.length}종목 (신규 전면금지)`
                  : u('○ 촉매 진입정지 — 지금 창 없음')}
              </b>
              <span style={{ color: '#7d7362', marginLeft: 6 }}>
                {cat.mode === 'live_block' ? 'live_block' : (cat.mode || '')}
                {cat.last_scored_at
                  ? ` · 동기 ${new Date(cat.last_scored_at).toLocaleString('ko-KR', { hour12: false })}`
                  : ''}
              </span>
            </div>
            {now.length > 0 && (
              <div>
                {now.map((h) => (
                  <div key={h.seed_key}>
                    <span style={{ color: '#e6dcc3' }}>{h.perp.replace(/USDT$/, '')}</span>
                    {' · '}
                    {kindKo[h.kind] || h.kind}
                    {' · '}
                    {h.from_kst || '?'}→{h.to_kst || '?'}
                    {h.note ? <span style={{ color: '#7d7362' }}> — {h.note}</span> : null}
                  </div>
                ))}
              </div>
            )}
            {cf && (
              <div style={{ marginTop: 2, color: '#7d7362' }}>
                {(() => {
                  const net = (cf.pnl_saved_usdt ?? 0) - (cf.pnl_killed_usdt ?? 0);
                  return `CF $${net > 0 ? '+' : ''}${net.toFixed(0)}`;
                })()}
                {cat.forward != null ? ` · 전방트리거 ${cat.forward.n_triggers}건` : ''}
                {u(' · 상세는 아래 표 진입정지·CF$ 행')}
              </div>
            )}
            {!now.length && (cat.holds?.length ?? 0) > 0 && (
              <div style={{ color: '#7d7362' }}>
                등록 {(cat.holds || []).filter((h) => h.active).length}건
                {' · '}
                {(cat.holds || []).filter((h) => h.active).map((h) =>
                  h.perp.replace(/USDT$/, '')).join(' · ') || '—'}
              </div>
            )}
          </div>
          </>
        );
      })()}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: stackOnly ? LIQ_COLOR : '#f0b90b', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <ScoreFoldTitle fold={fold}>{stackOnly ? u('청캐 v2') : u('청캐 사다리 (liqv2) — 일일 채점')}</ScoreFoldTitle>
        {!stackOnly && badge && (
          <span style={{
            padding: '1px 6px', borderRadius: 3, fontSize: '0.64rem',
            background: badge.bg, color: badge.fg,
          }}>{badge.text}</span>
        )}
        {!stackOnly && (
        <span style={{ marginLeft: 'auto', color: '#7d7362', fontSize: '0.62rem' }}>
          {data?.last_scored_at
            ? `최근 채점 ${new Date(data.last_scored_at).toLocaleString('ko-KR', { hour12: false })}`
            : ''}
        </span>
        )}
      </div>

      {!fold.folded && (<>
      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !data && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {!stackOnly && data && data.n === 0 && !(data.live_full && data.live_full.n > 0)
        && !(data.cell_plain && data.cell_plain.n > 0)
        && !(data.live_plain && data.live_plain.n > 0) && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>
          최근 {data.window_days}일에 채점된 사다리 진입이 없다. (일일 잡이 아직 안 돌았거나
          캐스케이드 누적청산이 100건에 못 미쳤다)
        </div>
      )}

      {stackOnly && data && (
        <ScoreLaneStackTable
          accent={LIQ_COLOR}
          axis={scoreDayAxis()}
          scrollRef={scrollEnd}
          at={(key, day) => {
            const lp = data.live_plain;
            const eq = lp?.equity as Liqv2Equity | undefined;
            const eqBy = Object.fromEntries((eq?.days ?? []).map((d) => [d.day, d]));
            const cats = catDays();
            if (key === '강제청산') {
              const e = day == null ? eq : eqBy[day];
              if (!e) return SCORE_BLANK;
              const m = liqMark(e);
              if (!m.y) return { text: '·', color: '#3a352c' };
              const sub = fmtPerps(m.perps);
              return { text: 'Y', color: '#f85149', sub: sub || undefined };
            }
            if (key === '진입정지') {
              if (day == null) {
                const t = catTotals();
                return t.n > 0
                  ? { text: String(t.n), color: '#f0b90b' }
                  : { text: '·', color: '#3a352c' };
              }
              const c = cats[day];
              if (!c) return SCORE_BLANK;
              const n = c.n_triggers ?? 0;
              if (n <= 0) return { text: '·', color: '#3a352c' };
              const sub = fmtPerps(c.perps);
              return { text: String(n), color: '#f0b90b', sub: sub || undefined };
            }
            const src = day == null ? lp : lp?.days?.find((d) => d.day === day);
            const e = day == null ? eq : eqBy[day];
            const cell = scoreMetricCell(key, src ? {
              ...src,
              realized_eq_pct: e?.realized_eq_pct ?? null,
              worst_eq_pct: e?.worst_eq_pct ?? null,
            } : (e ? {
              has_data: false,
              realized_eq_pct: e.realized_eq_pct,
              worst_eq_pct: e.worst_eq_pct,
            } : undefined), 65);
            if (key !== '실현%eq' || !e || cell.text === '-') return cell;
            const sub = fmtSizedScores(e.sized_perps).join('\n');
            return sub ? { ...cell, sub } : cell;
          }}
        />
      )}
      {!stackOnly && data && (data.n > 0 || (data.live_full && data.live_full.n > 0)
        || (data.cell_plain && data.cell_plain.n > 0)
        || (data.live_plain && data.live_plain.n > 0)) && (
        <>
          {/* 요약줄은 뺐다 (2026-09-09 유저) — n/건당/중앙/승률/합계/최악/MDD 는
              표의 계 열에 그대로 나온다. 조각·연장만 셀 제목행에 붙인다. */}

          {/* 표들은 하나의 가로 스크롤을 공유 — 날짜 열을 맞춰 비교한다.
              열 폭은 '+109.1%eq' 기준으로 고정. */}
          <div ref={scrollEnd} className={stackOnly ? 'score-grid' : undefined} style={{ overflowX: 'auto', marginTop: 8, containerType: 'inline-size' }}>
            {!stackOnly && (
            <>
            <table style={{ ...tableFixed, marginTop: 0 }}>
              <thead>
                <tr style={{ color: '#f0b90b' }}>
                  <th style={stickyLab('2px solid #f0b90b44', '#f0b90b')}>{u('지표')}</th>
                  {data.days.map((d) => (
                    <th key={d.day} style={cell}>{d.day}</th>
                  ))}
                  <th style={{
                    ...cell, borderLeft: '2px solid #f0b90b55', fontWeight: 700,
                  }}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {pinnedScoreTitle(data.days.length + 2, '#f0b90b', undefined, '4px 8px 4px', (
                  <>
                    {u('규칙 성적 — 셀(고점확인ON)')}
                    <span style={{ color: '#7d7362', fontWeight: 400 }}>
                      {[
                        u('고점확인 ON'),
                        data.config?.min_cum_nliq != null
                          ? `누적청산 ${data.config.min_cum_nliq}건+ 진입` : null,
                        data.config?.slot_k != null
                          ? `동시 레일 k${data.config.slot_k}` : null,
                        data.config?.same_symbol_max != null
                          ? `같은종목 겹침 ${data.config.same_symbol_max}` : null,
                        data.config?.max_legs != null
                          ? `조각 최대 ${data.config.max_legs}${
                            data.config.leg_step_pct != null
                              ? `·폭 ${data.config.leg_step_pct.toFixed(1)}%` : ''
                          }` : null,
                        data.config?.deep_mult != null && data.config.deep_mult > 1
                          ? `깊은꺾임 ×${data.config.deep_mult.toFixed(2)}` : null,
                        data.config?.sleeve_margin_pcts?.length
                          ? `슬리브 ${data.config.sleeve_margin_pcts[0].toFixed(1)}→${data.config.sleeve_margin_pcts[data.config.sleeve_margin_pcts.length - 1].toFixed(1)}% 증거금${
                            data.config.sleeve_ratio != null && Math.abs(data.config.sleeve_ratio - 1) > 1e-9
                              ? ` (ratio ${data.config.sleeve_ratio.toFixed(2)})` : ''
                          }`
                          : (data.config?.sleeve_margin_pct != null
                            ? `슬리브당 ${data.config.sleeve_margin_pct.toFixed(1)}% 증거금` : null),
                        `연장 ${data.n_extended}건`,
                      ].filter(Boolean).join(' · ').replace(/^/, ' · ')}
                    </span>
                  </>
                ))}
                {([
                  // 셀·스택·원장 동일 지표열 (업숏·v1과 같음): n/건당/중앙/승률/최악/합계/MDD
                  // 조각·연장은 상단 요약줄만 (표 지표열에서 제외).
                  ['n', (d: Liqv2LadderDay) => String(d.n), () => '#b7a882',
                    () => ({ t: String(data.n), c: '#e6dcc3' })],
                  [u('건당'), (d: Liqv2LadderDay) => `${num(d.avg_net_pct)}%`,
                    (d: Liqv2LadderDay) => sign(d.avg_net_pct),
                    () => ({ t: `${num(data.avg_net_pct)}%`, c: sign(data.avg_net_pct) })],
                  [u('중앙'), (d: Liqv2LadderDay) => `${num(d.med_net_pct)}%`,
                    (d: Liqv2LadderDay) => sign(d.med_net_pct),
                    () => ({ t: `${num(data.med_net_pct)}%`, c: sign(data.med_net_pct) })],
                  [u('승률'), (d: Liqv2LadderDay) => `${d.win_rate_pct.toFixed(0)}%`,
                    (d: Liqv2LadderDay) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
                    () => ({
                      t: `${data.win_rate_pct.toFixed(0)}%`,
                      c: data.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
                    })],
                  [u('최악'), (d: Liqv2LadderDay) => `${num(d.worst_pct)}%`,
                    (d: Liqv2LadderDay) => sign(d.worst_pct),
                    () => ({ t: `${num(data.worst_pct)}%`, c: sign(data.worst_pct) })],
                  [u('합계'), (d: Liqv2LadderDay) => `${num(d.sum_pct, 1)}%`,
                    (d: Liqv2LadderDay) => sign(d.sum_pct),
                    () => ({ t: `${num(data.sum_pct, 1)}%`, c: sign(data.sum_pct) })],
                  ['MDD', (d: Liqv2LadderDay) => `${num(d.mdd, 1)}%`,
                    (d: Liqv2LadderDay) => sign(d.mdd),
                    () => ({ t: `${num(data.mdd, 1)}%`, c: sign(data.mdd) })],
                ] as [string, (d: Liqv2LadderDay) => string, (d: Liqv2LadderDay) => string,
                  () => { t: string; c: string }][])
                  .map(([label, val, col, tot]) => {
                    const g = tot();
                    return (
                    <tr key={`cell-${label}`} style={{ borderTop: '1px solid #f0b90b18' }}>
                      <td style={stickyLab('2px solid #f0b90b44')}>{u(label)}</td>
                      {data.days.map((d) => (
                        <td key={d.day} style={{ ...cell, color: d.has_data ? col(d) : '#3a352c' }}>
                          {d.has_data ? val(d) : '–'}
                        </td>
                      ))}
                      <td style={{
                        ...cell, color: g.c, fontWeight: 700,
                        borderLeft: '2px solid #f0b90b55',
                      }}>{data.n > 0 ? g.t : ''}</td>
                    </tr>
                    );
                  })}
                {(() => {
                  const eq = data.equity as Liqv2Equity | undefined;
                  return eqPathRows(eq, data.days, '#f0b90b');
                })()}
              </tbody>
            </table>

          {/* 표 2 — 셀(고점확인OFF) */}
          {data.cell_plain && data.cell_plain.days?.length > 0 && (
            <table style={{ ...tableFixed, marginTop: 14 }}>
                <thead>
                  <tr style={{ color: '#d2a8ff' }}>
                    <th style={stickyLab('2px solid #d2a8ff44', '#d2a8ff')}>{u('지표')}</th>
                    {data.days.map((d) => (
                      <th key={d.day} style={cell}>{d.day}</th>
                    ))}
                    <th style={{
                      ...cell, borderLeft: '2px solid #d2a8ff55', fontWeight: 700,
                    }}>{u('계')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pinnedScoreTitle(data.days.length + 2, '#d2a8ff', undefined, '4px 8px 4px', (
                    <>
                      {u('규칙 성적 — 셀(고점확인OFF)')}
                      <span style={{ color: '#7d7362', fontWeight: 400 }}>
                        {u(' · 고점확인 OFF · 같은 k·조각·캡')}
                        {data.cell_plain.n > 0
                          ? ` · 연장 ${data.cell_plain.n_extended}건` : u(' · 아직 적재 없음')}
                      </span>
                    </>
                  ))}
                  {(() => {
                    const cp = data.cell_plain!;
                    const days = cp.days ?? data.days;
                    return ([
                      ['n', (d: Liqv2LadderDay) => String(d.n), () => '#8b949e',
                        () => ({ t: String(cp.n), c: '#8b949e' })],
                      [u('건당'), (d: Liqv2LadderDay) => `${num(d.avg_net_pct)}%`,
                        (d: Liqv2LadderDay) => sign(d.avg_net_pct),
                        () => ({ t: `${num(cp.avg_net_pct)}%`, c: sign(cp.avg_net_pct) })],
                      [u('중앙'), (d: Liqv2LadderDay) => `${num(d.med_net_pct)}%`,
                        (d: Liqv2LadderDay) => sign(d.med_net_pct),
                        () => ({ t: `${num(cp.med_net_pct)}%`, c: sign(cp.med_net_pct) })],
                      [u('승률'), (d: Liqv2LadderDay) => `${d.win_rate_pct.toFixed(0)}%`,
                        (d: Liqv2LadderDay) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
                        () => ({
                          t: `${cp.win_rate_pct.toFixed(0)}%`,
                          c: cp.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
                        })],
                      [u('최악'), (d: Liqv2LadderDay) => `${num(d.worst_pct)}%`,
                        (d: Liqv2LadderDay) => sign(d.worst_pct),
                        () => ({ t: `${num(cp.worst_pct)}%`, c: sign(cp.worst_pct) })],
                      [u('합계'), (d: Liqv2LadderDay) => `${num(d.sum_pct, 1)}%`,
                        (d: Liqv2LadderDay) => sign(d.sum_pct),
                        () => ({ t: `${num(cp.sum_pct, 1)}%`, c: sign(cp.sum_pct) })],
                      ['MDD', (d: Liqv2LadderDay) => `${num(d.mdd, 1)}%`,
                        (d: Liqv2LadderDay) => sign(d.mdd),
                        () => ({ t: `${num(cp.mdd, 1)}%`, c: sign(cp.mdd) })],
                    ] as [string, (d: Liqv2LadderDay) => string, (d: Liqv2LadderDay) => string,
                      () => { t: string; c: string }][])
                      .map(([label, val, col, tot]) => {
                        const g = tot();
                        return (
                          <tr key={`plain-${label}`} style={{ borderTop: '1px solid #d2a8ff18' }}>
                            <td style={stickyLab('2px solid #d2a8ff44', '#8b949e')}>{u(label)}</td>
                            {days.map((d) => (
                              <td key={d.day} style={{
                                ...cell, color: d.has_data ? col(d) : '#3a352c',
                              }}>
                                {d.has_data ? val(d) : '–'}
                              </td>
                            ))}
                            <td style={{
                              ...cell, color: g.c, fontWeight: 700,
                              borderLeft: '2px solid #d2a8ff55',
                            }}>{cp.n > 0 ? g.t : ''}</td>
                          </tr>
                        );
                      });
                  })()}
                  {(() => {
                    const eq = data.cell_plain?.equity as Liqv2Equity | undefined;
                    return eqPathRows(eq, data.cell_plain?.days ?? data.days, '#d2a8ff');
                  })()}
                </tbody>
              </table>
          )}
            </>
            )}

          {/* 표 3 — 라이브스택(고점확인ON) · 반사실. 성적 모달은 OFF만. */}
          {!stackOnly && (
          <table style={{ ...tableFixed, marginTop: 14 }}>
              <thead>
                <tr style={{ color: '#58a6ff' }}>
                  <th style={stickyLab('2px solid #58a6ff44', '#58a6ff')}>{u('지표')}</th>
                  {data.days.map((d) => (
                    <th key={d.day} style={cell}>{d.day}</th>
                  ))}
                  <th style={{
                    ...cell, borderLeft: '2px solid #58a6ff55', fontWeight: 700,
                  }}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {data.live_full && data.live_full.days?.length > 0 && (
                  <>
                    {pinnedScoreTitle(data.days.length + 2, '#58a6ff', '2px solid #58a6ff55', '10px 8px 4px', (
                        <div style={{
                          display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
                        }}>
                          <span>
                            라이브스택(고점확인ON) — 반사실 · 촉매 진입정지
                            <span style={{ color: '#7d7362', fontWeight: 400 }}>
                              {' · '}
                              {liveStackRuleLine(data.live_full.config, {
                                k: 15, legs: 1, step: 6, cap: 70,
                                ratio: 1, boost: 1, recover: 2,
                              })}
                              {u(' · LSK형 캘린더 창 신규 스킵')}
                            </span>
                            {data.live_full.n === 0 && (
                              <span style={{ color: '#7d7362', fontWeight: 400 }}>
                                {' · '}아직 적재 없음 (누적청산 ≥100 캐스케이드 대기)
                              </span>
                            )}
                            {data.live_full.sum_pct_weighted != null && data.live_full.n > 0 && (
                              <span style={{ color: '#b7a882', fontWeight: 400 }}>
                                {u(' · 배수 반영 합 ')}
                                <b style={{ color: data.live_full.sum_pct_weighted >= 0 ? '#3fb950' : '#f85149' }}>
                                  {`${data.live_full.sum_pct_weighted >= 0 ? '+' : ''}${data.live_full.sum_pct_weighted.toFixed(1)}%`}
                                </b>
                                {` (깊은꺾임 ${data.live_full.n_deep ?? 0}건 ×${(data.config?.deep_mult ?? 2).toFixed(1)}, 단위슬리브 기준)`}
                              </span>
                            )}
                          </span>
                          <span style={{
                            color: '#7d7362', fontWeight: 400, fontSize: '0.58rem',
                            whiteSpace: 'nowrap',
                          }}
                            title={u("합계는 taken 건당 net%를 그냥 더한 값. 캡·k·고점통과 배수는 실현%eq·잔고밀림만.")}>
                            합계=건당 net% 단순합 · 사이징·고점통과는 실현%eq
                          </span>
                        </div>
                    ))}
                    {(() => {
                      const lf = data.live_full!;
                      return ([
                        ['n', (d: Liqv2LadderDay) => String(d.n), () => '#8b949e',
                          () => ({ t: String(lf.n), c: '#8b949e' })],
                        [u('건당'), (d: Liqv2LadderDay) => `${num(d.avg_net_pct)}%`,
                          (d: Liqv2LadderDay) => sign(d.avg_net_pct),
                          () => ({ t: `${num(lf.avg_net_pct)}%`, c: sign(lf.avg_net_pct) })],
                        [u('중앙'), (d: Liqv2LadderDay) => `${num(d.med_net_pct)}%`,
                          (d: Liqv2LadderDay) => sign(d.med_net_pct),
                          () => ({ t: `${num(lf.med_net_pct)}%`, c: sign(lf.med_net_pct) })],
                        [u('승률'), (d: Liqv2LadderDay) => `${d.win_rate_pct.toFixed(0)}%`,
                          (d: Liqv2LadderDay) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
                          () => ({
                            t: `${lf.win_rate_pct.toFixed(0)}%`,
                            c: lf.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
                          })],
                        [u('최악'), (d: Liqv2LadderDay) => `${num(d.worst_pct)}%`,
                          (d: Liqv2LadderDay) => sign(d.worst_pct),
                          () => ({ t: `${num(lf.worst_pct)}%`, c: sign(lf.worst_pct) })],
                        [u('합계'), (d: Liqv2LadderDay) => `${num(d.sum_pct, 1)}%`,
                          (d: Liqv2LadderDay) => sign(d.sum_pct),
                          () => ({ t: `${num(lf.sum_pct, 1)}%`, c: sign(lf.sum_pct) })],
                        ['MDD', (d: Liqv2LadderDay) => `${num(d.mdd, 1)}%`,
                          (d: Liqv2LadderDay) => sign(d.mdd),
                          () => ({ t: `${num(lf.mdd, 1)}%`, c: sign(lf.mdd) })],
                      ] as [string, (d: Liqv2LadderDay) => string, (d: Liqv2LadderDay) => string,
                        () => { t: string; c: string }][])
                        .map(([label, val, col, tot]) => {
                          const g = tot();
                          return (
                          <tr key={`live-${label}`} style={{ borderTop: '1px solid #58a6ff18' }}>
                            <td style={stickyLab('2px solid #58a6ff44', '#8b949e')}>{u(label)}</td>
                            {(lf.days ?? []).map((d) => (
                              <td key={d.day} style={{
                                ...cell, color: d.has_data ? col(d) : '#3a352c',
                              }}>
                                {d.has_data ? val(d) : '–'}
                              </td>
                            ))}
                            <td style={{
                              ...cell, color: g.c, fontWeight: 700,
                              borderLeft: '2px solid #58a6ff55',
                            }}>{lf.n > 0 ? g.t : ''}</td>
                          </tr>
                          );
                        });
                    })()}
                    {(() => {
                      const eq = data.live_full?.equity as Liqv2Equity | undefined;
                      return eqPathRows(eq, data.live_full?.days ?? data.days, '#58a6ff');
                    })()}
                  </>
                )}
              </tbody>
            </table>
          )}

          {/* 표 4 — 라이브스택(고점확인OFF) · 지금 라이브.
              성적 모달은 이 표만 보여 준다. */}
          {data.live_plain && data.live_plain.days?.length > 0 && (
            <table style={{ ...tableFixed, marginTop: 14 }}>
                <thead>
                  <tr style={{ color: plainInk }}>
                    <th style={stickyLab(`2px solid ${plainInk}44`, plainInk)}>{u('지표')}</th>
                    {(stackOnly ? scoreDayAxis() : data.days.map((d) => d.day)).map((day) => (
                      <th key={day} style={cell}>{day}</th>
                    ))}
                    <th style={{
                      ...cell, borderLeft: `2px solid ${plainInk}55`, fontWeight: 700,
                    }}>{u('계')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pinnedScoreTitle((stackOnly ? scoreDayAxis().length : data.days.length) + 2, plainInk, undefined, '4px 8px 4px', (
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
                      }}>
                        <span>
                          라이브스택(고점확인OFF) — 지금 라이브 · 날짜=발동 KST
                          <span style={{ color: '#7d7362', fontWeight: 400 }}>
                            {' · '}
                            {liveStackRuleLine(data.live_plain.config, {
                              k: 5, legs: 6, step: 9, cap: 90,
                              ratio: 0.7, boost: 2.5, recover: 2,
                              overflow: 'best_avg',
                            })}
                            {data.live_plain.n > 0
                              ? ` · 연장 ${data.live_plain.n_extended}건` : u(' · 아직 적재 없음')}
                          </span>
                        </span>
                        <span style={{
                          color: '#7d7362', fontWeight: 400, fontSize: '0.58rem',
                          whiteSpace: 'nowrap',
                        }}
                          title={u("잔고밀림%=구간 시작 대비·실명목(원장과 동일). 숫자 차=CF 경로(net%/홀드) vs 실체결.")}>
                          날짜=발동 · 잔고밀림%=시작대비·실명목(원장과 동일)
                        </span>
                      </div>
                  ))}
                  {(() => {
                    const lp = data.live_plain!;
                    const days = lp.days ?? data.days;
                    const byDay = new Map(days.map((d) => [d.day, d]));
                    const cols = stackOnly ? scoreDayAxis() : days.map((d) => d.day);
                    return ([
                      ['n', (d: Liqv2LadderDay) => String(d.n), () => '#8b949e',
                        () => ({ t: String(lp.n), c: '#8b949e' })],
                      [u('건당'), (d: Liqv2LadderDay) => `${num(d.avg_net_pct)}%`,
                        (d: Liqv2LadderDay) => sign(d.avg_net_pct),
                        () => ({ t: `${num(lp.avg_net_pct)}%`, c: sign(lp.avg_net_pct) })],
                      [u('중앙'), (d: Liqv2LadderDay) => `${num(d.med_net_pct)}%`,
                        (d: Liqv2LadderDay) => sign(d.med_net_pct),
                        () => ({ t: `${num(lp.med_net_pct)}%`, c: sign(lp.med_net_pct) })],
                      [u('승률'), (d: Liqv2LadderDay) => `${d.win_rate_pct.toFixed(0)}%`,
                        (d: Liqv2LadderDay) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
                        () => ({
                          t: `${lp.win_rate_pct.toFixed(0)}%`,
                          c: lp.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
                        })],
                      [u('최악'), (d: Liqv2LadderDay) => `${num(d.worst_pct)}%`,
                        (d: Liqv2LadderDay) => sign(d.worst_pct),
                        () => ({ t: `${num(lp.worst_pct)}%`, c: sign(lp.worst_pct) })],
                      [u('합계'), (d: Liqv2LadderDay) => `${num(d.sum_pct, 1)}%`,
                        (d: Liqv2LadderDay) => sign(d.sum_pct),
                        () => ({ t: `${num(lp.sum_pct, 1)}%`, c: sign(lp.sum_pct) })],
                      ['MDD', (d: Liqv2LadderDay) => `${num(d.mdd, 1)}%`,
                        (d: Liqv2LadderDay) => sign(d.mdd),
                        () => ({ t: `${num(lp.mdd, 1)}%`, c: sign(lp.mdd) })],
                    ] as [string, (d: Liqv2LadderDay) => string, (d: Liqv2LadderDay) => string,
                      () => { t: string; c: string }][])
                      .filter(([label]) => !(stackOnly && label === u('중앙')))
                      .map(([label, val, col, tot]) => {
                        const g = tot();
                        return (
                          <tr key={`liveplain-${label}`} style={{ borderTop: `1px solid ${plainInk}18` }}>
                            <td style={stickyLab(`2px solid ${plainInk}44`, '#8b949e')}>{u(label)}</td>
                            {cols.map((day) => {
                              const d = byDay.get(day);
                              return (
                              <td key={day} style={{
                                ...cell, color: d?.has_data ? col(d) : '#3a352c',
                              }}>
                                {d?.has_data ? val(d) : '–'}
                              </td>
                              );
                            })}
                            <td style={{
                              ...cell, color: g.c, fontWeight: 700,
                              borderLeft: `2px solid ${plainInk}55`,
                            }}>{lp.n > 0 ? g.t : ''}</td>
                          </tr>
                        );
                      });
                  })()}
                  {(() => {
                    const eq = data.live_plain?.equity as Liqv2Equity | undefined;
                    return eqPathRows(
                      eq,
                      (stackOnly ? scoreDayAxis() : (data.live_plain?.days ?? data.days).map((d) => d.day))
                        .map((day) => ({ day })),
                      plainInk,
                    );
                  })()}
                </tbody>
              </table>
          )}

          {!stackOnly && (
          <>
          {/* 표 5 — 원장 */}
          <table style={{ ...tableFixed, marginTop: 14 }}>
              <thead>
                <tr style={{ color: '#3fb950' }}>
                  <th style={stickyLab('2px solid #3fb95044', '#3fb950')}>{u('지표')}</th>
                  {data.days.map((d) => (
                    <th key={d.day} style={cell}>{d.day}</th>
                  ))}
                  <th style={{
                    ...cell, borderLeft: '2px solid #3fb95055', fontWeight: 700,
                  }}>{u('계')}</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger && data.ledger.days?.length > 0 && (
                  <>
                    {pinnedScoreTitle(data.days.length + 2, '#3fb950', '2px solid #3fb95055', '10px 8px 4px', (
                      <>
                        {u('실제 원장 — live_ledger · v2 · 날짜=발동 KST (스택과 동일)')}
                        <span style={{ color: '#7d7362', fontWeight: 400 }}>
                          {' · '}잔고밀림%=시작대비·실명목
                          {data.ledger.n === 0 ? u(' · 정산 없음') : ''}
                        </span>
                      </>
                    ))}
                    {(() => {
                      const lg = data.ledger!;
                      return ([
                        ['n', (d: Liqv2LadderDay) => String(d.n), () => '#8b949e',
                          () => ({ t: String(lg.n), c: '#8b949e' })],
                        [u('건당'), (d: Liqv2LadderDay) => `${num(d.avg_net_pct)}%`,
                          (d: Liqv2LadderDay) => sign(d.avg_net_pct),
                          () => ({ t: `${num(lg.avg_net_pct)}%`, c: sign(lg.avg_net_pct) })],
                        [u('중앙'), (d: Liqv2LadderDay) => `${num(d.med_net_pct)}%`,
                          (d: Liqv2LadderDay) => sign(d.med_net_pct),
                          () => ({ t: `${num(lg.med_net_pct)}%`, c: sign(lg.med_net_pct) })],
                        [u('승률'), (d: Liqv2LadderDay) => `${d.win_rate_pct.toFixed(0)}%`,
                          (d: Liqv2LadderDay) => (d.win_rate_pct >= 65 ? '#3fb950' : '#f85149'),
                          () => ({
                            t: `${lg.win_rate_pct.toFixed(0)}%`,
                            c: lg.win_rate_pct >= 65 ? '#3fb950' : '#f85149',
                          })],
                        [u('최악'), (d: Liqv2LadderDay) => `${num(d.worst_pct)}%`,
                          (d: Liqv2LadderDay) => sign(d.worst_pct),
                          () => ({ t: `${num(lg.worst_pct)}%`, c: sign(lg.worst_pct) })],
                        [u('합계'), (d: Liqv2LadderDay) => `${num(d.sum_pct, 1)}%`,
                          (d: Liqv2LadderDay) => sign(d.sum_pct),
                          () => ({ t: `${num(lg.sum_pct, 1)}%`, c: sign(lg.sum_pct) })],
                        ['MDD', (d: Liqv2LadderDay) => `${num(d.mdd, 1)}%`,
                          (d: Liqv2LadderDay) => sign(d.mdd),
                          () => ({ t: `${num(lg.mdd, 1)}%`, c: sign(lg.mdd) })],
                      ] as [string, (d: Liqv2LadderDay) => string, (d: Liqv2LadderDay) => string,
                        () => { t: string; c: string }][])
                        .map(([label, val, col, tot]) => {
                          const g = tot();
                          return (
                          <tr key={`ledger-${label}`} style={{ borderTop: '1px solid #3fb95018' }}>
                            <td style={stickyLab('2px solid #3fb95044', '#8b949e')}>{u(label)}</td>
                            {(lg.days ?? []).map((d) => (
                              <td key={d.day} style={{
                                ...cell, color: d.has_data ? col(d) : '#3a352c',
                              }}>
                                {d.has_data ? val(d) : '–'}
                              </td>
                            ))}
                            <td style={{
                              ...cell, color: g.c, fontWeight: 700,
                              borderLeft: '2px solid #3fb95055',
                            }}>{lg.n > 0 ? g.t : ''}</td>
                          </tr>
                          );
                        });
                    })()}
                    {(() => {
                      const eq = data.ledger?.equity as Liqv2Equity | undefined;
                      return eqPathRows(eq, data.ledger?.days ?? data.days, '#3fb950');
                    })()}
                  </>
                )}

                {pinnedScoreTitle(data.days.length + 2, '#f0b90b', '2px solid #f0b90b55', '10px 8px 4px', (
                  <>
                    레인 건강도
                    <span style={{ color: '#7d7362', fontWeight: 400 }}>
                      {u(' · 규칙이 아니라 레인 자체 · 누적청산(Σn_liq) 구간별 성적')}
                      {data.config?.max_legs != null
                        ? ` · 조각 최대 ${data.config.max_legs}${
                          data.config.leg_step_pct != null
                            ? `·신고가 +${data.config.leg_step_pct.toFixed(1)}%마다` : ''
                        } (숏 평단 끌어올려 되돌림 여유 확보 — 꼬리 방어의 핵심)` : ''}
                      {data.config?.deep_mult != null && data.config.deep_mult > 1
                        ? ` · 깊은꺾임 ×${data.config.deep_mult.toFixed(2)}: 진입가가 캐스케이드 고점 ${
                          (data.config.deep_cut_pct ?? -12).toFixed(0)}% 아래면 그 슬리브만 키운다 (off_casc ≤ −12% = 8캐스케이드 승률 97%·BTR 참사는 이 밖)` : ''}
                    </span>
                  </>
                ))}
                {([
                  [u('트리거'), (d: Liqv2LadderDay) => String(d.n_trigger)],
                  [u('누적<50 건당'), (d: Liqv2LadderDay) => (d.ord1_avg == null ? '–' : `${num(d.ord1_avg)}%`)],
                  [u('누적<50 승률'), (d: Liqv2LadderDay) => (d.ord1_win == null ? '–' : `${d.ord1_win.toFixed(0)}%`)],
                  [u('50~99 건당'), (d: Liqv2LadderDay) => (d.ord3_avg == null ? '–' : `${num(d.ord3_avg)}%`)],
                  [u('50~99 승률'), (d: Liqv2LadderDay) => (d.ord3_win == null ? '–' : `${d.ord3_win.toFixed(0)}%`)],
                  [u('≥100 건당'), (d: Liqv2LadderDay) => (d.ord5_avg == null ? '–' : `${num(d.ord5_avg)}%`)],
                  [u('≥100 승률'), (d: Liqv2LadderDay) => (d.ord5_win == null ? '–' : `${d.ord5_win.toFixed(0)}%`)],
                ] as [string, (d: Liqv2LadderDay) => string][]).map(([label, val]) => (
                  <tr key={label} style={{ borderTop: '1px solid #f0b90b18' }}>
                    <td style={stickyLab('2px solid #3fb95044', '#b7a882')}>{u(label)}</td>
                    {data.days.map((d) => (
                      <td key={d.day} style={{
                        ...cell, color: d.n_trigger > 0 ? '#b7a882' : '#3a352c',
                      }}>
                        {d.n_trigger === 0
                          ? '–'
                          : val(d) === '0' && label !== u('트리거') ? '' : val(d)}
                      </td>
                    ))}
                    <td style={{
                      ...cell, borderLeft: '2px solid #f0b90b55',
                    }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </>
          )}
          </div>

          {!stackOnly && data.health?.order_edge_ok != null && (
            <div style={{
              marginTop: 6, fontSize: '0.64rem',
              color: data.health.order_edge_ok ? '#3fb950' : '#f85149',
            }}>
              {data.health.order_edge_ok
                ? u('사다리 구조 유지 — 누적청산 깊을수록(≥100) 초입(<50)보다 낫다')
                : u('⚠ 사다리 구조 역전 — 깊은 구간이 초입보다 나쁘다. v2 의 전제가 깨진 신호')}
            </div>
          )}

          {!stackOnly && (
          <div style={{ marginTop: 8, color: '#7d7362', fontSize: '0.62rem' }}>
            경보선 n≥10 · 승률 75% / 건당 +4.0% / MDD −40 (사전등록 2026-09-06).
            {' '}nliq-25 전환(2026-09-09) 전 기준선은 순번·가중·캡이 세 번 바뀌어
            지금 수치와 직접 비교 불가.
          </div>
          )}
        </>
      )}
      </>)}
    </div>
  );
}

function LiqRetuneModal({ r, onClose, onApplied, detail, ladderV2Live }: {
  r: BinanceLiqRetune;
  onClose: () => void;
  onApplied?: (kind: string) => void;
  /** 옛 u('상세') 버튼 내용 — 청캐 몫(필터 + abort 반사실)을 맨 아래에 낸다. */
  detail?: RetuneDetail;
  /** 부모가 이미 아는 값(data.liq_stats.ladder_v2.live). **동기로 넘긴다** —
   *  모달 자기 fetch 를 기다리면 첫 렌더가 v1(빨강)로 그려졌다가 바뀐다
   *  (2026-09-06 유저: "먼저 v1의 빨간 화면이 나왔다가 금방 v2로 바뀐다"). */
  ladderV2Live?: boolean;
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cohortDefault = r.cohort_n ?? r.window_hours ?? 24;
  const [cohortParsed, setCohortParsed] = React.useState<number | null>(cohortDefault);
  const onCohortChange = React.useCallback((n: number | null) => {
    setCohortParsed(n);
  }, []);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cell: React.CSSProperties = {
    padding: '5px 8px', borderBottom: '1px solid #f0b90b22', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  };
  const head: React.CSSProperties = {
    ...cell, color: '#f0b90b', fontSize: '0.68rem',
    borderBottom: '1px solid #f0b90b44',
  };
  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const num = (v: number | null | undefined, d = 1) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;

  // v2(사다리)가 라이브면 아래 v1 블록은 전부 참고용이다 — 진입을 안 하므로
  // 되돌림 권고·격자·적용 버튼이 가리키는 대상이 없다. 접어 둔다 (2026-09-06).
  const [v2, setV2] = React.useState<Liqv2Ladder | null>(null);
  // 부모 값이 있으면 그걸 쓴다(첫 렌더부터 확정). 없을 때만 자기 fetch 를 본다.
  const v2on = ladderV2Live ?? (
    v2?.config?.live === true && v2?.config?.ladder_v2 === true);

  const pkgRows = r.packages?.length
    ? r.packages
    : [{ label: u('배포'), role: 'deployed', cell: null, knobs: null }];
  const fadeRows = r.packages_fade?.length
    ? r.packages_fade
    : [{ label: u('배포'), role: 'deployed', cell: null, knobs: null }];


  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={u("청산 캐스케이드 재판정")}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#12100c',
          border: `1px solid ${
            v2on ? (v2?.verdict === 'alarm' ? '#f85149aa' : '#f0b90b55')
              : (r.needs_change ? '#f85149aa' : '#f0b90b55')}`,
          borderRadius: 8, maxWidth: 720, width: '100%', maxHeight: '82vh',
          overflowY: 'auto', padding: '18px 20px', color: '#f5f0e6',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 4,
        }}>
          {/* v2 가 돌면 제목도 v2 를 따라간다. v1 재판정 verdict(되돌림권고)로
              빨갛게 만들면 u("지금 뭔가 조치가 필요하다")로 읽히는데, v1 은
              진입을 안 하므로 그 권고는 아무 데도 안 쓰인다 (2026-09-06 유저). */}
          <h3 style={{
            margin: 0, fontSize: '0.95rem',
            color: v2on
              ? (v2?.verdict === 'alarm' ? '#f85149' : '#f0b90b')
              : (r.needs_change ? '#f85149' : '#f0b90b'),
          }}>
            {v2on ? (
              <>{u('청캐 재판정')}</>
            ) : (
              <>
                청산 캐스케이드 재판정 · {r.verdict}
                {' · '}창 {r.window_label ?? u('24시간')}
              </>
            )}
          </h3>
          <button
            onClick={onClose}
            aria-label={u("닫기")}
            style={{
              background: 'none', border: 'none', color: '#b7a882',
              fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* 일일채점: v2(사다리) + v3(얕은산) + 통합 원장.
            **v1·낙하칼 채점 박스는 뺐다** (2026-09-09 유저). */}
        <Liqv2LadderPanel onData={setV2} />
        <LiqV3DailyPanel />
        <LiqUnifiedDailyPanel />
        <LiqUnifiedLedgerPanel />

        {/* v2/v3 아래 굵은 구분선 너머는 옛 청캐 격자·재판정 (참고용). */}
        <div style={{ margin: '26px 0 0' }} aria-hidden>
          <div style={{
            height: 3, background:
              'repeating-linear-gradient(90deg,#f0b90b66 0 14px,transparent 14px 26px)',
            borderRadius: 2,
          }} />
        </div>

        {/* v2 가 돌면 접어 둔다. 되돌림 권고·격자·적용 버튼이 전부 v1 진입
            경로를 가리키는데 v1 은 진입을 안 한다 — 펼쳐 두면 "조치가 필요한
            상태"로 오독된다 (2026-09-06 유저: v1 관련은 다 꺼둬라).
            지우지는 않는다. v1 로 되돌릴 때 그대로 쓴다. */}
        <details style={{ marginTop: 12 }}>
          <summary style={{
            cursor: 'pointer', color: '#7d7362', fontSize: '0.68rem',
            letterSpacing: '0.04em', padding: '6px 0', textAlign: 'center',
            listStyle: 'revert',
          }}>
            기존 청캐(<b style={{ color: '#b7a882' }}>v1</b>) 격자 권고
            {u(' — 참고용 · 값은 위에서 고정')}
          </summary>
          <div style={{
            margin: '8px 0 14px', padding: '6px 8px', borderRadius: 4,
            background: '#7d736211', border: '1px solid #7d736233',
            color: '#b7a882', fontSize: '0.63rem', lineHeight: 1.55,
          }}>
            v1 되돌림은 <b>{u('고점6 · 꺾임2 · 감시15 · 홀드15')}</b> 로 고정됐다
            (2026-09-06). 아래 격자·적용은 참고용 — 매일 갈아끼우지 않는다.
            건강 감시는 위 일일 채점표.
          </div>

        <RetuneScheduleBlock
          schedule={LIQ_SCHEDULE}
          computedAt={r.computed_at}
          lastGate={r.last_gate}
          runKind="liq"
          runWhenOverride={r.run_when}
          lastElapsedS={r.elapsed_s}
          cohortN={cohortParsed}
        />

        <RetuneCohortControls
          lane="liq"
          cohortN={cohortDefault}
          seedDefault={r.cohort_seed_default}
          fromPrefs={r.cohort_from_prefs}
          unit={r.cohort_unit ?? 'hours'}
          label={r.cohort_label ?? u('판정 창(시간)')}
          min={r.cohort_min ?? 4}
          max={r.cohort_max ?? 168}
          lagMin={r.pb_window_lag_min ?? 240}
          onChange={onCohortChange}
          onSaved={() => onApplied?.('cohort')}
        />

        {/* 적용은 자동이 아니다 -- 이 문장을 지우면 화면만 보고 u("이미 반영됐겠지")
            라고 읽게 된다. 그게 이 레인에서 가장 위험한 오해다. */}
        <p style={{ margin: '0 0 10px', fontSize: '0.7rem', color: '#b7a882', lineHeight: 1.6 }}>
          자동 적용하지 않는다. 표 「적용」= 패스키로 DB 반영(다음 사이클에 라이브).
          {' · '}되돌림 격자(꺾임·고점·감시·홀드) 최적 패키지 — 즉시진입 hold_grid 와 다르다.
          {' · '}판정 {formatKstIso(r.computed_at, { withLabel: true })}
          {' · '}창 {r.window_label ?? u('24시간')}
          {r.pb_window_start && r.pb_window_end
            ? ` (${formatKstIso(r.pb_window_start)}~${formatKstIso(r.pb_window_end)} KST)`
            : ''}
          {' · '}트리거 {r.window_n}건 · 셀 {r.n_cells ?? '\u2013'}개
          {' · '}비용 {r.cost_bp?.toFixed(0) ?? '\u2013'}bp {r.cost_source ?? ''}
        </p>

        <div style={{
          border: `1px solid ${r.needs_change ? '#f8514955' : '#f0b90b33'}`,
          borderRadius: 6, padding: '8px 10px', marginBottom: 12,
          fontSize: '0.75rem', lineHeight: 1.7,
        }}>
          {r.reason}
          {r.drift ? (
            <div style={{ marginTop: 4, color: r.drift.moved ? '#d29922' : '#b7a882' }}>
              {/* 이름을 u("타이밍 드리프트")로 두면 아래 상자와 두 개가 같은 이름이
                  된다. 이건 **격자 안에서** 최적 홀드가 앞창/뒷창에 다르게 잡혔다는
                  얘기고, 아래는 경로 자체가 언제 도는지다. */}
              최적 홀드 드리프트: {r.drift.note}
            </div>
          ) : null}
        </div>

        {/* 하루 1회 점검 (2026-08-19). 격자 권고와 **다른 질문**이다 -- 권고는
            u("값을 바꿔야 하나"), 이건 u("지금 설정이 앞뒤가 맞고 배선이 살아 있나").
            표본이 모자라 권고를 못 내는 날에도 이 둘은 나온다. */}
        {r.timing || r.checks ? (
          <div style={{
            border: `1px solid ${(r.checks?.n_warn ?? 0) > 0 || r.timing?.drift
              ? '#d2992255' : '#f0b90b33'}`,
            borderRadius: 6, padding: '8px 10px', marginBottom: 12,
            fontSize: '0.72rem', lineHeight: 1.7,
          }}>
            <div style={{ color: '#f0b90b', fontSize: '0.68rem', marginBottom: 4 }}>
              하루 1회 점검
            </div>
            {r.timing ? (
              <div style={{ color: r.timing.drift ? '#d29922' : '#b7a882' }}>
                타이밍 · 진입 뒤 최선 ↓+{r.timing.t_best?.med ?? '–'}분
                {' '}(CI {r.timing.t_best?.ci_lo ?? '–'}~{r.timing.t_best?.ci_hi ?? '–'})
                {' · '}최대 역행 ↑+{r.timing.t_worst?.med ?? '–'}분
                {' '}(CI {r.timing.t_worst?.ci_lo ?? '–'}~{r.timing.t_worst?.ci_hi ?? '–'})
                {' · '}홀드 {r.timing.hold_min ?? '–'}분
                {' · '}최근 {r.timing.n}/{r.timing.n_window}건 · 지평 {r.timing.horizon_min}분
                <div style={{ marginTop: 2 }}>{r.timing.note}</div>
                {r.timing.abort_before_worst ? (
                  <div style={{ marginTop: 2, color: '#d29922' }}>
                    abort 확인 {r.timing.abort_check_min}분이 최대 역행보다 앞선다 —
                    아직 안 끝난 것을 보고 판정한다.
                  </div>
                ) : null}
                {/* 이 축은 값을 정하지 않는다. 업비트에서 t_peak → 최적 지연 매핑이
                    실측으로 깨졌다 -- 같은 실수를 이 레인에서 반복하지 않는다. */}
                <div style={{ marginTop: 2, color: '#8b7f66' }}>
                  이 축은 u("움직였나")만 답한다. 얼마나 옮길지는 아래 홀드 표(총합)가 정한다.
                </div>
              </div>
            ) : null}
            {r.checks ? (
              <div style={{ marginTop: 6 }}>
                <span style={{ color: r.checks.n_warn ? '#d29922' : '#b7a882' }}>
                  설정 · {r.checks.n - r.checks.n_warn}/{r.checks.n} 통과
                </span>
                {r.checks.rows?.filter((c) => !c.ok).map((c) => (
                  <div key={c.key} style={{ marginTop: 2, color: '#f85149' }}>
                    [{c.key}] {c.msg}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{
          margin: '0 0 6px', color: '#f0b90b', fontSize: '0.66rem',
          letterSpacing: '0.04em',
        }}>
          되돌림 격자 총합 top5 (창 {r.window_label ?? u('24시간')} · 관문 통과=최적 패키지)
        </div>
        {r.pullback_reject && !r.packages?.some((p) => p.role === 'recommended') ? (
          <div style={{
            marginBottom: 8, fontSize: '0.72rem', color: '#d29922',
          }}>
            관문 미달: {r.pullback_reject}
          </div>
        ) : null}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>{u('셀')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('설정')}</th>
              <th style={head}>n</th>
              <th style={head} title={u("판정 창 전체 수익(%p) = 슬롯을 채운 합")}>{u('총합')}</th>
              <th style={head}>{u('건당')}</th>
              <th style={head} title={u("손익>0 건수 / n (슬롯 통과분)")}>{u('승률')}</th>
              <th style={head} title={u("그 칸 최소 건손익(%p) — 꼬리 크기")}>{u('최악')}</th>
              <th style={head} title={u("배포 패키지 총합 대비 초과")}>{u('vs배포')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('3등분')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('비고')}</th>
              <th style={head} />
            </tr>
          </thead>
          <tbody>
            {pkgRows.map((row) => {
              const c = row.cell;
              const isRec = row.role === 'recommended';
              const isDep = row.role === 'deployed';
              return (
                <tr key={row.label}>
                  <td style={{
                    ...cell, textAlign: 'left', color: isRec ? '#f0b90b' : '#b7a882',
                    fontWeight: isDep || isRec ? 700 : 400,
                  }}>
                    {row.label}
                    {c?.matches_deployed ? (
                      <span style={{ color: '#b7a882', marginLeft: 5, fontSize: '0.64rem' }}>
                        =배포
                      </span>
                    ) : null}
                  </td>
                  {c ? (
                    <>
                      <td style={{ ...cell, textAlign: 'left' }}>
                        {row.knobs ?? '–'}
                      </td>
                      <td style={cell}>{c.n ?? '–'}</td>
                      <td style={{ ...cell, color: sign(c.total), fontWeight: 700 }}>
                        {num(c.total)}
                      </td>
                      <td style={{ ...cell, color: sign(c.avg) }}>{num(c.avg, 2)}</td>
                      <td style={cell}>
                        {c.win_pct != null ? `${num(c.win_pct, 0)}%` : '–'}
                        {c.win != null && c.n ? (
                          <span style={{ color: '#b7a882', marginLeft: 4, fontSize: '0.64rem' }}>
                            {c.win}/{c.n}
                          </span>
                        ) : null}
                      </td>
                      <td style={{ ...cell, color: sign(c.worst) }}>{num(c.worst, 2)}</td>
                      <td style={{ ...cell, color: sign(c.vs_deployed) }}>
                        {num(c.vs_deployed, 2)}
                      </td>
                      <td style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                        {c.thirds ? c.thirds.map((x) => num(x, 0)).join(' / ') : '–'}
                      </td>
                      <td style={{ ...cell, textAlign: 'left', color: '#b7a882', fontSize: '0.68rem' }}>
                        {c.win_rank != null ? (
                          <span style={{ color: '#f0b90b' }}>승률 {c.win_rank}위</span>
                        ) : null}
                        {(() => {
                          const extra = row.note ?? c.reason
                            ?? (isDep ? u('성적 없음')
                              : c.win_rank != null ? null : '–');
                          return extra
                            ? `${c.win_rank != null ? ' · ' : ''}${extra}` : null;
                        })()}
                        {c.mode === 'hybrid_usd' && c.exp_total != null
                          ? ` · expΣ${num(c.exp_total)}(n=${c.exp_n ?? '–'})` : null}
                        {c.live_safe === false ? ' · live_safe✗' : null}
                      </td>
                      <td style={{ ...cell, textAlign: 'center' }}>
                        <RetuneApplyBtn
                          kind="liq_pb"
                          cell={c as Record<string, unknown>}
                          label={row.label}
                          matchesDeployed={isDep || Boolean(c.matches_deployed)}
                          onApplied={onApplied}
                        />
                      </td>
                    </>
                  ) : (
                    <td colSpan={10} style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                      {row.label === u('최적 패키지') ? u('관문을 넘은 셀 없음') : '–'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{
          margin: '18px 0 6px', color: '#f0b90b', fontSize: '0.66rem',
          letterSpacing: '0.04em',
        }}>
          cascade-fade 격자 (파도 breadth · tick mark · 관문 통과=최적 패키지)
        </div>
        <p style={{ margin: '0 0 8px', fontSize: '0.68rem', color: '#b7a882', lineHeight: 1.5 }}>
          파도일 때만 쓰는 진입 규칙. 적용 kind=<code>liq_cascade_fade</code>
          {' · '}실주문은 청캐 레인과 동일 (<code>liq_cascade_live</code> · 화면 설정).
        </p>
        {r.cascade_fade_reject && !r.packages_fade?.some((p) => p.role === 'recommended') ? (
          <div style={{
            marginBottom: 8, fontSize: '0.72rem', color: '#d29922',
          }}>
            fade 관문 미달: {r.cascade_fade_reject}
          </div>
        ) : null}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>{u('셀')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('설정')}</th>
              <th style={head}>n</th>
              <th style={head} title={u("판정 창 전체 수익(%p)")}>{u('총합')}</th>
              <th style={head}>{u('건당')}</th>
              <th style={head}>{u('승률')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('비고')}</th>
              <th style={head} />
            </tr>
          </thead>
          <tbody>
            {fadeRows.map((row) => {
              const c = row.cell;
              const isRec = row.role === 'recommended';
              const isDep = row.role === 'deployed';
              return (
                <tr key={`fade-${row.label}`}>
                  <td style={{
                    ...cell, textAlign: 'left', color: isRec ? '#f0b90b' : '#b7a882',
                    fontWeight: isDep || isRec ? 700 : 400,
                  }}>
                    {row.label}
                    {c?.matches_deployed ? (
                      <span style={{ color: '#b7a882', marginLeft: 5, fontSize: '0.64rem' }}>
                        =배포
                      </span>
                    ) : null}
                  </td>
                  {c ? (
                    <>
                      <td style={{ ...cell, textAlign: 'left' }}>
                        {row.knobs ?? '–'}
                      </td>
                      <td style={cell}>{c.n ?? '–'}</td>
                      <td style={{ ...cell, color: sign(c.total), fontWeight: 700 }}>
                        {num(c.total)}
                      </td>
                      <td style={{ ...cell, color: sign(c.avg) }}>{num(c.avg, 2)}</td>
                      <td style={cell}>
                        {c.win_pct != null ? `${num(c.win_pct, 0)}%` : '–'}
                      </td>
                      <td style={{ ...cell, textAlign: 'left', color: '#b7a882', fontSize: '0.68rem' }}>
                        {c.win_rank != null ? (
                          <span style={{ color: '#f0b90b' }}>승률 {c.win_rank}위</span>
                        ) : null}
                        {(() => {
                          const extra = row.note ?? c.reason
                            ?? (isDep ? u('성적 없음')
                              : c.win_rank != null ? null : '–');
                          return extra
                            ? `${c.win_rank != null ? ' · ' : ''}${extra}` : null;
                        })()}
                      </td>
                      <td style={{ ...cell, textAlign: 'center' }}>
                        <RetuneApplyBtn
                          kind="liq_cascade_fade"
                          cell={c as Record<string, unknown>}
                          label={row.label}
                          matchesDeployed={isDep || Boolean(c.matches_deployed)}
                          onApplied={onApplied}
                        />
                      </td>
                    </>
                  ) : (
                    <td colSpan={7} style={{ ...cell, textAlign: 'left', color: '#b7a882' }}>
                      {row.label === u('최적 패키지') ? u('관문을 넘은 셀 없음') : '–'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{
          margin: '18px 0 6px', color: '#f0b90b', fontSize: '0.66rem',
          letterSpacing: '0.04em',
        }}>
          낙하칼 (수직낙하 즉시진입)
        </div>
        <p style={{ margin: '0 0 8px', fontSize: '0.68rem', color: '#b7a882', lineHeight: 1.5 }}>
          트리거 직후 낙폭이 크면 되돌림 대기를 건너뛰고 즉시 숏 → {r.knife?.exit_min ?? 8}분 강제탈출.
          {' · '}실주문 스위치는 <b>{u('청캐 레인과 동일')}</b>(별도 스위치 없음 — 위에서 청캐를 끄면 낙하칼도 같이 꺼진다).
          {' · '}프리레지 <code>stream-liq-knife-prereg-2026-09-04</code>.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>{u('규칙')}</th>
              <th style={head}>{u('낙폭 문턱')}</th>
              <th style={head}>{u('감지 지연')}</th>
              <th style={head}>{u('탈출')}</th>
              <th style={head}>{u('역행 stop')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('비고')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...cell, textAlign: 'left', fontWeight: 700 }}>
                낙하칼
                {(r.knife?.live === false || r.knife?.enabled === false) ? (
                  <span style={{ color: '#d29922', marginLeft: 5, fontSize: '0.64rem' }}>
                    {r.knife?.enabled === false ? u('칼 OFF') : u('주문 OFF')}
                  </span>
                ) : null}
              </td>
              <td style={cell}>{r.knife?.thr_pct != null ? `≥${r.knife.thr_pct}%` : '–'}</td>
              <td style={cell}>{r.knife?.lag_s != null ? `${r.knife.lag_s}s` : '–'}</td>
              <td style={cell}>{r.knife?.exit_min != null ? `${r.knife.exit_min}m` : '–'}</td>
              <td style={cell}>
                {r.knife?.stop_pct != null
                  ? (r.knife.stop_pct > 0 ? `+${r.knife.stop_pct}%` : u('없음'))
                  : '–'}
              </td>
              <td style={{ ...cell, textAlign: 'left', color: '#b7a882', fontSize: '0.68rem' }}>
                재판정 대상 아님 — 고정 규칙 (유저 2026-09-04)
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{
          margin: '18px 0 6px', color: '#f0b90b', fontSize: '0.66rem',
          letterSpacing: '0.04em',
        }}>
          진입 직전 재확인창
        </div>
        <p style={{ margin: '0 0 8px', fontSize: '0.68rem', color: '#b7a882', lineHeight: 1.5 }}>
          무장돼도 즉시 안 쏘고 {r.confirm_gate?.wait_s ?? 75}초 동안 계속 재확인 —
          그 안에 역행 {r.confirm_gate?.reject_pct ?? 1}% 이상이면 진입 포기(스퀴즈 지속형 컷).
          낙하칼·수동 진입은 대상 아님.
          {' · '}실주문 스위치는 <b>{u('청캐 레인과 동일')}</b>.
          {' · '}프리레지 <code>stream-liq-confirm-gate-prereg-2026-09-05</code>.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>{u('규칙')}</th>
              <th style={head}>{u('확인창')}</th>
              <th style={head}>{u('역행 문턱')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{u('비고')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...cell, textAlign: 'left', fontWeight: 700 }}>
                재확인창
                {r.confirm_gate?.enabled === false ? (
                  <span style={{ color: '#d29922', marginLeft: 5, fontSize: '0.64rem' }}>
                    OFF
                  </span>
                ) : null}
              </td>
              <td style={cell}>
                {r.confirm_gate?.wait_s != null ? `${r.confirm_gate.wait_s}s` : '–'}
              </td>
              <td style={cell}>
                {r.confirm_gate?.reject_pct != null ? `≥${r.confirm_gate.reject_pct}%` : '–'}
              </td>
              <td style={{ ...cell, textAlign: 'left', color: '#b7a882', fontSize: '0.68rem' }}>
                재판정 대상 아님 — 고정 규칙 (유저 2026-09-05)
              </td>
            </tr>
          </tbody>
        </table>

        </details>

        {detail ? <LaneDetail scope="liq" {...detail} /> : null}
      </div>
    </div>
  );
}

/** 새로고침 버튼을 그릴까. 2026-08-31 유저 지시로 **일시** 숨김 --
 *  코드를 지우지 않고 여기만 true 로 되돌리면 복구된다. */
const SHOW_REFRESH_BUTTON = false;

function RefreshIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M13.2 8A5.2 5.2 0 1 1 11.6 4.1" />
      <path d="M13.5 2.2v3.2h-3.2" />
    </svg>
  );
}

/** 신규상장 면제 반사실 블록. 스탑 반사실과 같은 자리·같은 골격이다.
 *  0건이 정상 상태이므로(월 0~1건) 빈 상태도 반드시 그린다 -- 안 뜨는 것과
 *  0건인 것을 화면에서 구분할 수 있어야 한다. */
function NewlistingReportBlock({ report }: { report: BinanceNewlistingReport }) {
  const t = useDashboardT();
  const pct = (v: number | null, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`;
  const rate = (v: number | null) => (v == null ? '–' : `${v.toFixed(0)}%`);
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        borderBottom: `1px solid ${COUNTERFACTUAL_COLOR}44`,
        paddingBottom: 4, marginBottom: 8,
      }}>
        <span style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.8rem', fontWeight: 600 }}>
          {t('binance.newlistingReport.title')}
        </span>
      </div>
      {report.n === 0 ? (
        <p style={{ margin: 0, fontSize: '0.7rem', color: binanceMuted, lineHeight: 1.6 }}>
          {t('binance.newlistingReport.empty', { days: report.days })}
        </p>
      ) : (
        <div style={{ fontSize: '0.72rem', lineHeight: 1.9 }}>
          <div>
            {t('binance.newlistingReport.sum', {
              n: report.n, sum: pct(report.sum_pct), avg: pct(report.avg_pct, 3),
            })}
          </div>
          <div style={{ color: binanceMuted }}>
            {t('binance.newlistingReport.rate', {
              win: rate(report.win_rate), stop: rate(report.stop_rate),
            })}
          </div>
        </div>
      )}
      {report.pending > 0 && (
        <div style={{ fontSize: '0.68rem', color: binanceMuted, marginTop: 4 }}>
          {t('binance.newlistingReport.pending', { n: report.pending })}
        </div>
      )}
      <p style={{ margin: '6px 0 0', fontSize: '0.66rem', color: binanceMuted, lineHeight: 1.6 }}>
        {t('binance.newlistingReport.gate', { need: report.need_n })}
      </p>
    </div>
  );
}

/** segment_high 카드 (부록 B). u("판정 아님") 배너는 need_n(40~50 밴드 재판정선) 미달일
 *  때 항상 보인다 -- t-검정 자체(ERA 단일종목 제외)는 사람이 문서를 다시 읽고 하는
 *  일이라 프론트가 "판정됨"으로 자동 승격하지 않는다. 스탑인데 abort_fired=false 인
 *  비율이 높을수록 "abort 사각지대"를 강조한다(부록 A: abort 는 급등 continuation만
 *  잡고, segment_pct 급등은 완만상승이라 구조적으로 못 잡는다). */
function SegmentHighReportBlock({ report }: { report: BinanceSegmentHighReport }) {
  const t = useDashboardT();
  const pct = (v: number | null, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`;
  const rate = (v: number | null) => (v == null ? '–' : `${v.toFixed(0)}%`);
  const bandHead: React.CSSProperties = {
    textAlign: 'right', padding: '3px 6px', color: binanceMuted,
    fontSize: '0.62rem', fontWeight: 500, whiteSpace: 'nowrap',
  };
  const bandCell: React.CSSProperties = {
    textAlign: 'right', padding: '3px 6px', fontSize: '0.7rem', whiteSpace: 'nowrap',
  };
  const bandTable = (title: string, bands: BinanceSegmentHighBand[]) => bands.length > 0 && (
    <div style={{ marginTop: 10 }}>
      <div style={{ color: binanceMuted, fontSize: '0.66rem', marginBottom: 3 }}>{title}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...bandHead, textAlign: 'left' }}>{t('binance.segmentHighReport.col.band')}</th>
              <th style={bandHead}>{t('binance.skipReport.col.count')}</th>
              <th style={bandHead}>{t('binance.skipReport.col.avg')}</th>
              <th style={bandHead}>{t('binance.skipReport.col.stopRate')}</th>
            </tr>
          </thead>
          <tbody>
            {bands.map((b) => (
              <tr key={b.band} style={{ borderTop: '1px solid #ffffff10' }}>
                <td style={{ ...bandCell, textAlign: 'left' }}>{b.band}</td>
                <td style={bandCell}>{b.n}</td>
                <td style={{ ...bandCell, color: COUNTERFACTUAL_COLOR }}>{pct(b.avg_net_pct, 3)}</td>
                <td style={bandCell}>{rate(b.stop_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  const judged = report.n >= report.need_n;
  const abortBlind = report.abort_scored > 0 && report.abort_fired === 0;
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        borderBottom: `1px solid ${COUNTERFACTUAL_COLOR}44`,
        paddingBottom: 4, marginBottom: 8,
      }}>
        <span style={{ color: COUNTERFACTUAL_COLOR, fontSize: '0.8rem', fontWeight: 600 }}>
          {t('binance.segmentHighReport.title')}
        </span>
        <span style={{ color: binanceMuted, fontSize: '0.66rem' }}>
          {t('binance.skipReport.window', { days: report.window_days })}
        </span>
      </div>
      {report.n === 0 ? (
        <p style={{ margin: 0, fontSize: '0.7rem', color: binanceMuted, lineHeight: 1.6 }}>
          {t('binance.segmentHighReport.empty')}
        </p>
      ) : (
        <>
          <div style={{ fontSize: '0.72rem', lineHeight: 1.9 }}>
            <div>
              {t('binance.segmentHighReport.sum', {
                n: report.n, sum: pct(report.sum_net_pct), avg: pct(report.avg_net_pct, 3),
              })}
            </div>
            <div style={{ color: binanceMuted }}>
              {t('binance.segmentHighReport.rate', {
                win: rate(report.win_rate), stop: rate(report.stop_rate),
              })}
            </div>
          </div>
          <div
            style={{
              marginTop: 6, fontSize: '0.68rem',
              color: abortBlind ? '#f85149' : binanceMuted,
              fontWeight: abortBlind ? 600 : 400,
            }}
            title={t('binance.segmentHighReport.abortHint')}
          >
            {t('binance.segmentHighReport.abort', {
              scored: report.abort_scored, fired: report.abort_fired,
            })}
          </div>
          {bandTable(t('binance.segmentHighReport.runupTitle'), report.runup_bands)}
          {bandTable(t('binance.segmentHighReport.segmentTitle'), report.segment_bands)}
        </>
      )}
      <p style={{
        margin: '8px 0 0', fontSize: '0.66rem', lineHeight: 1.6,
        color: judged ? binanceMuted : '#d29922',
      }}>
        {judged
          ? t('binance.segmentHighReport.gate.judged', { need: report.need_n })
          : t('binance.segmentHighReport.gate.notJudged', { n: report.n, need: report.need_n })}
      </p>
    </div>
  );
}

/** 백엔드 desc(한 덩어리 한국어 문단)를 읽기 좋게 문장 단위로 끊고 **볼드**를
 *  살린다 (2026-09-04 유저: "설명들이 너무 읽기 힘든데.. 개행좀"). "0.5%"·
 *  "t=2.88" 은 마침표 앞이 숫자라 문장 경계로 안 걸린다. */
function renderDesc(d: string): React.ReactNode {
  const bold = (s: string, kp: string): React.ReactNode =>
    s.split(/(\*\*[^*]+\*\*)/).map((part, i) => (
      (part.startsWith('**') && part.endsWith('**'))
        ? <strong key={`${kp}-${i}`} style={{ color: '#e8dcc0' }}>{part.slice(2, -2)}</strong>
        : <React.Fragment key={`${kp}-${i}`}>{part}</React.Fragment>
    ));
  // 한글 문장부호 뒤에서 나눈다. "0.5%"·"t=2.88" 은 마침표 앞이 숫자라 안 걸린다.
  const sentences = d
    .split(/(?<=[가-힣)\]"])\.\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return sentences.map((s, i) => (
    <div key={i} style={{ marginBottom: i === sentences.length - 1 ? 0 : 4 }}>
      {bold(s, String(i))}
    </div>
  ));
}

/** 옛 헤더 u('상세') 버튼(FilterModal)이 담던 「지금 적용 중인 필터 + 반사실」을
 *  레인별로 쪼갠 블록 -- 각 재판정 모달과 설정 모달 **맨 아래**에 붙는다
 *  (2026-09-04 유저: "레인에 해당하는 내용을 정리해서 각 재판정 화면 맨 아래").
 *
 *  scope 별 배치:
 *    upbit  → 업숏 필터 + 신규상장 면제 + flip 반사실 (flip 은 업숏 전용 노브다)
 *    bh     → 빗숏 필터 + segment_high 스킵 반사실
 *    liq    → 청캐 필터 + abort 반사실(다리별 포함)
 *    pr     → 편승 필터 (전용 반사실 리포트는 없다 — 총합은 재판정 표에서 본다)
 *    common → 공용 필터(어느 레인에도 안 걸리는 것 포함) + skip 반사실 → 설정 모달
 *
 *  값은 **배포된 설정**에서 읽는다 (문서가 아니라 지금 도는 봇). */
function LaneDetail({
  scope, filters, skipReport, flipReport, abortReport, abortLegBreakdown,
  newlistingReport, segmentHighReport,
}: RetuneDetail & { scope: 'upbit' | 'bh' | 'liq' | 'pr' | 'common' }) {
  const t = useDashboardT();
  const meta = effectMeta(t);

  // 효과 목록에서 빠진 값은 통째로 사라진다(FilterModal 시절 sleeve 누락 사고).
  // 열거에 없는 효과는 버리지 않고 맨 뒤에 모은다.
  const effOrder: string[] = ['skip', 'entry', 'size', 'sleeve', 'exit', 'collect', 'pending'];
  const effRank = (e: string) => {
    const i = effOrder.indexOf(e);
    return i === -1 ? effOrder.length : i;
  };
  const KNOWN_LANES = ['upbit', 'bh', 'liq', 'pr'];
  const laneOf = (f: BinanceShortLiveFilter) => {
    const l = f.lane ?? 'common';
    // 매핑에 없는 레인은 통째로 사라지면 안 된다 -- 공용으로 접어 설정 모달에 낸다.
    return KNOWN_LANES.includes(l) ? l : 'common';
  };
  const mine = (filters ?? []).filter((f) => laneOf(f) === scope);
  const effs = Array.from(new Set(mine.map((f) => f.effect)))
    .sort((a, b) => effRank(a) - effRank(b))
    .map((eff) => ({ eff, items: mine.filter((f) => f.effect === eff) }));

  const reports: React.ReactNode[] = [];
  if (scope === 'common' && skipReport && (skipReport.rows ?? []).length > 0) {
    reports.push(<SkipReportTable key="skip" report={skipReport} />);
  }
  if (scope === 'upbit' && flipReport) {
    reports.push(<FlipReportTable key="flip" report={flipReport} />);
  }
  if (scope === 'liq' && abortReport) {
    reports.push(
      <AbortReportTable key="abort" report={abortReport}
                        legBreakdown={abortLegBreakdown} />,
    );
  }
  if (scope === 'upbit' && newlistingReport) {
    reports.push(<NewlistingReportBlock key="nl" report={newlistingReport} />);
  }
  if (scope === 'bh' && segmentHighReport) {
    reports.push(<SegmentHighReportBlock key="sh" report={segmentHighReport} />);
  }

  if (!mine.length && !reports.length) return null;

  return (
    <div style={{ marginTop: 18, borderTop: '1px solid #f0b90b33', paddingTop: 12 }}>
      <div style={{
        color: '#f0b90b', fontSize: '0.66rem', letterSpacing: '0.04em',
        marginBottom: 8,
      }}>
        지금 적용 중인 필터 · 반사실 (배포된 설정에서 읽음)
      </div>
      {reports}
      {effs.map(({ eff, items }) => (
        <div key={eff} style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: '0.68rem', color: meta[eff]?.color ?? '#f5f0e6',
            textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
          }}>
            {meta[eff]?.label ?? eff}
          </div>
          {items.map((f) => (
            <div key={f.key} style={{
              borderLeft: `2px ${eff === 'pending' ? 'dashed' : 'solid'} ${
                meta[eff]?.color ?? '#333'}`,
              padding: '6px 0 6px 10px', marginBottom: 8,
              opacity: eff === 'pending' ? 0.82 : 1,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', gap: 12,
                fontSize: '0.78rem', marginBottom: 2,
              }}>
                <span>{f.label}</span>
                <span style={{ color: '#f0b90b', whiteSpace: 'nowrap' }}>{f.value}</span>
              </div>
              <div style={{ fontSize: '0.68rem', color: '#b7a882', lineHeight: 1.55 }}>
                {renderDesc(f.desc)}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function typeColor(t: string) {
  switch (t) {
    case 'SHORT': return '#f85149';
    // 재숏도 숏이라 같은 색이다 -- 방향이 같은데 색이 다르면 표를 훑을 때
    // 다른 레인처럼 읽힌다. 구분은 라벨(RESHORT)이 한다.
    case 'RESHORT': return '#f85149';
    case 'LONG': return '#3fb950';
    case 'EXIT': return '#58a6ff';
    case 'FAIL': return '#f85149';
    case 'SKIP': return '#d29922';
    case 'WATCH': return '#a371f7';
    case 'WATCH-END': return binanceMuted;
    case 'LONG-EXIT': return '#58a6ff';
    case 'COLLECT': return '#39c5cf';
    /* 청산 레인 abort/구조홀드 (2026-08-13). 라벨은 백엔드 TYPE_LABEL 이 준다.
       i18n 키는 일부러 안 만들었다 -- formatStatusLabel 이 키가 없으면 코드를
       그대로 보여주고(ABORT/HOLD+), 그게 어느 로케일에서도 읽힌다.
       ABORT 는 전제가 깨져 **일찍 자른** 것이라 손실 계열(빨강)로, ABORT-PASS/
       SKIP 은 아무것도 안 한 관측이라 muted 로 둔다 -- 이 둘이 EXIT 와 같은
       색이면 화면에서 u("뭔가 일어났다")로 잘못 읽힌다. */
    case 'ABORT': return '#ff7b72';
    case 'ABORT-PASS': return binanceMuted;
    case 'ABORT-SKIP': return binanceMuted;
    case 'HOLD+': return '#f0b90b';
    case 'HOLD-EXIT': return '#58a6ff';
    /* 연장 게이트(HOLD-SKIP)는 위 주석이 쓰인 뒤에 생겼는데 여기 안 들어와서
       default(흰색)로 떨어지고 있었다 -- ABORT-SKIP 과 같은 부류(연장을 안 한
       관측)라 같은 muted 로 맞춘다. */
    case 'HOLD-SKIP': return binanceMuted;
    /* stall 탈출 (2026-08-16). STALL-EXIT 는 홀드를 안 기다리고 **일찍 자른**
       것이라 ABORT 와 같은 손실 계열, STALL-SEEN 은 역행 문턱을 넘었다는
       관측(판정 모집단)일 뿐이라 muted 다. 이 둘이 같은 색이면 화면에서
       u("규칙이 발동했다") 와 u("규칙이 보고 통과시켰다") 가 구분되지 않는다. */
    case 'STALL-EXIT': return '#ff7b72';
    case 'STALL-SEEN': return binanceMuted;
    default: return '#f5f0e6';
  }
}

/** 칸이 좁아 짧게. title 에 풀네임. **렌더 때** u() — 모듈 로드 시 kr 고정 금지. */
function exchangeShort(ex: string): string {
  if (ex === 'upbit') return u('업');
  if (ex === 'bithumb') return u('빗');
  if (ex === 'gyeop') return u('겹');
  return ex;
}

function exchangeFull(ex: string): string {
  if (ex === 'upbit') return u('업비트');
  if (ex === 'bithumb') return u('빗썸');
  if (ex === 'gyeop') return u('겹롱');
  if (ex === 'unknown') return u('미상');
  return ex;
}

const STATUS_MSG_KEY: Partial<Record<string, DashboardMessageKey>> = {
  ALERT: 'binance.status.alert',
  TRIGGER: 'binance.status.trigger',
  PENDING: 'binance.status.pending',
  EXIT: 'binance.status.exit',
  OPEN: 'binance.status.open',
  SKIP: 'binance.status.skip',
  SHORT: 'binance.status.short',
  RESHORT: 'binance.status.reshort',
  LONG: 'binance.status.long',
  FAIL: 'binance.status.fail',
  COLLECT: 'binance.status.collect',
  WATCH: 'binance.status.watch',
  'WATCH-END': 'binance.status.watchEnd',
};

function formatStatusLabel(
  code: string,
  t: (key: DashboardMessageKey) => string,
): string {
  const key = STATUS_MSG_KEY[code];
  return key ? t(key) : code;
}

/** 청캐 감시 라벨. 기본 단어는 다른 행과 같은 번역(불어 SURVEILLE)을 쓴다.
 *  되돌림/fade 만 접미로 붙인다 (2026-09-04). 사다리 v2 는 접미가 없다. */
export function liqWatchStatusLabel(
  entryMode: string | null | undefined,
  t: (key: DashboardMessageKey) => string,
): string {
  const base = t('binance.status.watch');
  if (entryMode === 'cascade_fade') return `${base}(fade)`;
  if (entryMode === 'pullback') return `${base}(되돌림)`;
  return base;
}

/** 레인 표용 청캐 현재 상태 한 줄. */
export function formatLiqLiveStatus(s?: BinanceLiqStats | null): string {
  if (!s) return '';
  const wave = !s.fade_enabled
    ? 'fade OFF'
    : (s.wave ? u('파도') : u('평시'));
  const pref = s.entry_mode_pref === 'cascade_fade' ? u('fade고정')
    : s.entry_mode_pref === 'pullback' ? u('되돌림고정')
      : u('자동');
  const want = s.entry_mode_want === 'cascade_fade' ? '→fade' : u('→되돌림');
  const br = s.fade_enabled
    ? `breadth ${s.breadth ?? 0}/${s.breadth_min ?? 3}`
    : null;
  const pb = s.pending_by_mode;
  const pendN = s.pending ?? 0;
  let pend: string | null = null;
  if (pendN > 0) {
    const parts: string[] = [];
    if (pb?.pullback) parts.push(`되돌림${pb.pullback}`);
    if (pb?.cascade_fade) parts.push(`fade${pb.cascade_fade}`);
    if (pb?.unset) parts.push(`?${pb.unset}`);
    pend = `감시 ${parts.length ? parts.join('·') : pendN}`;
  }
  const kEff = (s.k_eff != null && s.k != null && s.k_eff !== s.k)
    ? `k${s.k_eff}`
    : null;
  return [wave, pref + want, br, pend, kEff].filter(Boolean).join(' · ');
}

/* 거래소 브랜드 색.
   빗썸 #ec7000 (로고 오렌지)은 이 패널의 어두운 배경에서 대비 약 6:1 이라 원색
   그대로 쓴다. 업비트 #0062df (로고 블루)는 같은 배경에서 약 3.4:1 로, 0.75rem
   글자에는 너무 어둡다 -- 색상은 그대로 두고 밝기만 올린 톤을 쓴다(약 6:1). */
const EXCHANGE_COLOR: Record<string, string> = {
  upbit: '#4d94ff',
  bithumb: '#ec7000',
  liq: '#f0b90b',        // 청산 캐스케이드 (유저 지정: 노란색)
  gyeop: '#a371f7',
  unknown: '#f85149',
};

function renderAlertTypeCell(ev: BinanceShortLiveEvent): React.ReactNode {
  // 청산 캐스케이드는 **알림이 없다**(트리거가 곧 진입). alert_type 이 비어 있어
  // 기본 exchange='upbit' 로 흘러가면 '구간 상승' 처럼 남의 라벨이 붙는다.
  // 2026-08-13 유저 결정: '청캐' 대신 **'–'**. 이 칸은 "어떤 알림을 받았나" 를
  // 말하는 자리인데 이 레인은 알림을 안 받는다 -- 레인 이름을 쓰면 알림 종류인
  // 것처럼 읽힌다(레인은 L 칸이 이미 색으로 구분한다).
  if (ev.action?.startsWith('liq_')
      || ev.action?.startsWith('liqv2_')
      || ev.action?.startsWith('liqv3_')
      || ev.action?.startsWith('gl_')) {
    return (
      <ThreeLineStack
        mutedColor={binanceMuted}
        lines={[<span style={{ color: binanceMuted }}>–</span>, null, null]}
      />
    );
  }
  const label = alertTypeLabel(ev.alert_type, ev.exchange || 'upbit');
  if (label === '–') {
    return (
      <ThreeLineStack
        mutedColor={binanceMuted}
        lines={[<span style={{ color: binanceMuted }}>–</span>, null, null]}
      />
    );
  }
  // Alert 칸은 ~2글자 폭. "구간 상승" 은 낱말 2줄. "신고가"·"신저가" 는
  // 2+1 로 끊는다 (신고/가) — 한 줄 ellipsis "신..."·한 글자씩 쌓기 둘 다 아님.
  const lines = splitAlertTypeLines(label);
  return (
    <div title={label}>
      <ThreeLineStack lines={lines} />
    </div>
  );
}

/** Alert 열 줄 나눔 — 공백=낱말, 3글자 한글=앞2·뒤1 (신고/가). */
function splitAlertTypeLines(
  label: string,
): [string, string | null, string | null] {
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return [words[0], words[1] ?? null, words[2] ?? null];
  }
  const chars = Array.from(words[0] ?? label);
  if (chars.length === 3) {
    return [chars[0] + chars[1], chars[2], null];
  }
  return [label, null, null];
}

function renderExchangeCell(row: {
  alert: BinanceShortLiveEvent;
  entry: BinanceShortLiveEvent | null;
  exit: BinanceShortLiveEvent | null;
}): React.ReactNode {
  // 액션으로도 판정한다. exchange 필드는 2026-09-06 부터 붙어서, 그 전에 기록된
  // liqv2 행은 필드가 없어 기본값 'upbit'('업')으로 떨어졌다.
  const ev = row.alert;
  const act = ev.action ?? '';
  if (act.startsWith('gl_')) {
    return (
      <ThreeLineStack
        mutedColor={binanceMuted}
        lines={[
          <span title={u("겹롱")} style={{ color: EXCHANGE_COLOR.gyeop, fontWeight: 600 }}>{u('겹')}</span>,
          null,
          null,
        ]}
      />
    );
  }
  // 청캐 v1 행도 v2. v3 진입, 또는 Detail 이 v3 인 스킵(누적청산 미달)은 v3.
  const tier = liqDisplayTier(row);
  if (tier) {
    const name = tier === 'v3' ? u('청캐 v3') : u('청캐 v2');
    const color = EXCHANGE_COLOR.liq;
    return (
      <ThreeLineStack
        mutedColor={binanceMuted}
        lines={[
          <span title={name} style={{ color, fontWeight: 600 }}>{u('청')}</span>,
          <span title={name} style={{ color, fontWeight: 600 }}>{tier}</span>,
          null,
        ]}
      />
    );
  }
  // 코인원은 이 칸에서 뺀다.
  if (ev.exchange === 'coinone') {
    return <ThreeLineStack mutedColor={binanceMuted} lines={[null, null, null]} />;
  }
  const ex = ev.exchange || 'upbit';
  return (
    <ThreeLineStack
      mutedColor={binanceMuted}
      lines={[
        <span
          title={exchangeFull(ex)}
          style={{ color: EXCHANGE_COLOR[ex] ?? binanceMuted, fontWeight: 600 }}
        >
          {exchangeShort(ex)}
        </span>,
        null,
        null,
      ]}
    />
  );
}


function renderSymbolCell(ev: BinanceShortLiveEvent): React.ReactNode {
  // 겹롱도 청캐와 같은 3줄(정식명 · 심볼 · perp). gl_* 는 이벤트에
  // symbol=perp(USDT) 만 넣어서 가운뎃줄이 겹치면 청캐처럼 '–' 로 둔다.
  const name = displayCoinName({
    kr_name: ev.kr_name,
    symbol: ev.symbol,
    perp: ev.perp,
  });
  const perp = (ev.perp || '').trim();
  const rawSym = (ev.symbol || '').trim();
  const sym = !rawSym || (perp && rawSym.toUpperCase() === perp.toUpperCase())
    ? '–'
    : rawSym;
  return (
    <ThreeLineStack mutedColor={binanceMuted} lines={[name, sym, perp || null]} />
  );
}

/** ThreeLineStack 은 고정 3줄이라 행 높이를 맞추는 보통 칸에는 맞지만, abort
 *  반사실 + 롱 실현액이 겹치는 행은 5줄까지 필요하다(금액·%·반사실금액·반사실
 *  설명·수수료). 같은 CSS 클래스를 그대로 써서 그 행만 키가 자란다. */
function VarLineStack({ lines, mutedColor }: {
  lines: { node: React.ReactNode; title?: string }[];
  mutedColor?: string;
}): React.ReactElement {
  return (
    <div className="broker-3line-stack">
      {lines.map(({ node, title }, i) => (
        <div
          key={i}
          className={i === 0 ? 'broker-3line-line' : 'broker-3line-line broker-3line-line--sub'}
          style={i > 0 && mutedColor ? { color: mutedColor } : undefined}
          // 폭 제한으로 "..." 줄임 처리될 때 마우스오버로 전체 텍스트를 보게 한다.
          title={title}
        >
          {node}
        </div>
      ))}
    </div>
  );
}

type CfPullbackRule = { watch_min?: number; hold_after_min?: number };

function parseCfPullback(raw: unknown): CfPullbackRule | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as CfPullbackRule;
  return o.hold_after_min != null ? o : null;
}

/** 옛 스탬프: 업숏/빗숏 되돌림 레인에서 rule_hold 가 알림기준 hold_exit_min(120)
 *  이면 hold_after 로 교정. 청캐·후보롱은 rule_hold 가 곧 실보유라 건드리면
 *  안 된다 — ONGUSDT 2026-08-25 17:45 liq no_pullback(120) 이 업숏 hold_after(90)
 *  으로 바뀌어 "90분 보유"로 보였다. */
function effectiveRuleHoldMin(
  ruleHold: number | null | undefined,
  alertHold: number | null | undefined,
  holdAfter: number | null | undefined,
  remapAlertHoldToAfter: boolean,
): number | null {
  if (
    remapAlertHoldToAfter
    && ruleHold != null && alertHold != null && holdAfter != null
    && ruleHold === alertHold
  ) {
    return holdAfter;
  }
  return ruleHold ?? holdAfter ?? null;
}

function resolveCfTiming(
  cfg: BinanceShortLiveConfig | undefined,
  isBh: boolean,
  skipPnl: BinanceShortLiveEvent['skip_pnl'] | null | undefined,
  abortCf: BinanceShortLiveEvent['abort_cf'] | null | undefined,
  lane: 'bh' | 'upbit' | 'liq' | 'cl' | 'pr' | 'ds' | 'gl' | null = null,
): { hold: number; watch: number | null } {
  const exch = skipPnl?.exchange ?? abortCf?.lane ?? null;
  const isLiq = lane === 'liq' || exch === 'liq' || abortCf?.lane === 'liq';
  const isCl = lane === 'cl' || exch === 'cand_long';
  const isPr = lane === 'pr' || exch === 'pump_ride';
  // 쌍숏(2026-09-02): 되돌림이지만 업숏(watch/hold_after)과 값이 달라(30/45 vs
  // 40/90) 자기 노브를 쓴다. dual_short_pullback_* / dual_short_hold_min.
  const isDs = lane === 'ds' || exch === 'dual_short';
  const isGl = lane === 'gl';
  // 청캐·후보롱·편승·쌍숏은 업숏 되돌림(watch/hold_after) 폴백을 쓰면 안 된다.
  const alertHold = isBh ? cfg?.bh_hold_exit_min
    : isLiq ? (cfg?.liq_cascade_hold_min ?? 120)
    : isCl ? null
    : isPr ? (cfg?.pump_ride_hold_min ?? 720)
    : isDs ? (cfg?.dual_short_hold_min ?? 45)
    : isGl ? 5
    : cfg?.hold_exit_min;
  const holdAfter = isBh ? cfg?.bh_hold_after_min
    : (isLiq || isCl || isPr || isDs || isGl) ? null
    : cfg?.hold_after_min;
  const watchFb = isBh ? cfg?.bh_watch_min
    : isPr ? (cfg?.pump_ride_watch_max_min ?? 180)
    : isDs ? (cfg?.dual_short_pullback_watch_min ?? 30)
    : (isLiq || isCl || isGl) ? null
    : cfg?.watch_min;
  const pb = parseCfPullback(skipPnl?.rule_pullback);
  const remap = !isLiq && !isCl && !isPr && !isDs && !isGl;
  const fallbackHold = holdAfter
    ?? (alertHold != null && cfg?.entry_delay_min != null
      && !isBh && !isLiq && !isCl && !isPr && !isDs && !isGl
      ? alertHold - cfg.entry_delay_min
      : alertHold)
    ?? (isLiq ? 120 : isPr ? 720 : isDs ? 45 : 30);
  if (skipPnl) {
    const hold = (isLiq || isCl || isPr || isDs || isGl)
      ? (skipPnl.rule_hold_exit_min ?? fallbackHold)
      : (pb?.hold_after_min
        ?? effectiveRuleHoldMin(skipPnl.rule_hold_exit_min, alertHold, holdAfter, remap)
        ?? fallbackHold);
    const watch = (isLiq || isCl)
      ? (skipPnl.rule_entry_delay_min != null && skipPnl.rule_entry_delay_min > 0
        ? skipPnl.rule_entry_delay_min
        : null)
      : (isPr || isDs)
        ? (watchFb ?? null)
      : (pb?.watch_min ?? skipPnl.rule_entry_delay_min ?? watchFb ?? null);
    return { hold, watch };
  }
  const hold = (isLiq || isCl || isPr || isDs || isGl)
    ? (abortCf?.rule_hold_exit_min ?? fallbackHold)
    : (effectiveRuleHoldMin(abortCf?.rule_hold_exit_min, alertHold, holdAfter, remap)
      ?? fallbackHold);
  return { hold, watch: (isPr || isDs) ? (watchFb ?? null) : null };
}

function renderPnlCell(
  profit: RowProfit | null,
  t: (key: DashboardMessageKey, params?: Record<string, string | number>) => string,
  compact = false,
  skipPnl: BinanceShortLiveEvent['skip_pnl'] | null = null,
  longProfit: RowProfit | null = null,
  abortCf: BinanceShortLiveEvent['abort_cf'] | null = null,
  // abort_cf 반사실("안 끊었으면?")이 실제로 시뮬레이션한 보유 분수. 레인마다
  // 다르다(업비트 30 / 빗썸 슬리브 10, 2026-08-06 유저결정) -- 라벨이 실제
  // 계산창과 맞아야 한다.
  cfHeldMinutes: number | null = null,
  // 스킵 반사실(skip_pnl)이 실제로 시뮬레이션한 보유 분수. abort_cf 와 별개
  // 스코어러라 값도 따로 받는다.
  skipHeldMinutes: number | null = null,
  skipWatchMinutes: number | null = null,
): React.ReactNode {
  if (profit == null && skipPnl && skipPnl.net_pct != null) {
    // 반사실: 진입하지 않은 거래다. 숫자는 %만 낸다 -- 사이즈가 없으니 USDT 는
    // 존재하지 않고, 지어내면 실현손익처럼 읽힌다.
    const pct = skipPnl.net_pct;
    // 되돌림 미발동은 "본전 거래"가 아니다 -- 감시창 안에 진입 자체가 없었다.
    // +0.00% · 감시·보유 로 그리면 차트(알림가 대비 가격)와 싸워 보인다
    // (2026-08-25 GMTUSDT: 차트 -4% / PnL +0.00% 감시40·보유119).
    if (skipPnl.source === 'pullback_no_trigger') {
      return (
        <ThreeLineStack
          mutedColor={binanceMuted}
          lines={[
            t('binance.skipPnl.noPullback'),
            skipWatchMinutes != null && skipWatchMinutes > 0
              ? t('binance.skipPnl.watchOnly', { watch: skipWatchMinutes })
              : null,
            null,
          ]}
        />
      );
    }
    const hold = skipHeldMinutes ?? 30;
    const skipRuleLine = skipWatchMinutes != null && skipWatchMinutes > 0
      ? t('binance.skipPnl.pullbackHeld', { watch: skipWatchMinutes, hold })
      : t(skipPnl.stopped ? 'binance.skipPnl.stopped' : 'binance.skipPnl.held',
        { min: hold });
    return (
      <ThreeLineStack
        mutedColor={binanceMuted}
        lines={[
          `${pct >= 0 ? '+' : ''}${pct.toFixed(compact ? 1 : 2)}%`,
          skipRuleLine,
          skipPnl.source === 'kline_1m' ? t('binance.skipPnl.coarse') : null,
        ]}
      />
    );
  }
  if (profit == null) {
    return <ThreeLineStack mutedColor={binanceMuted} lines={['–', null, null]} />;
  }
  const usdtDecimals = compact ? 1 : 2;
  const pctDecimals = compact ? 1 : 2;
  const fee =
    profit.commission == null
      ? null
      : `${t('binance.pnl.fee', { amount: profit.commission.toFixed(2) })}${
          profit.funding
            ? ` · ${t('binance.pnl.funding', { amount: `${profit.funding >= 0 ? '+' : ''}${profit.funding.toFixed(2)}` })}`
            : ''
        }`;
  // 1번째 줄: 실현액, abort→롱 다리가 있으면 그 실현액을 괄호로 이어 붙인다
  // (둘 다 실제 돈이라 색을 같은 계열로 -- 부호로만 구분).
  const amountText = `${profit.usdt >= 0 ? '+' : ''}${profit.usdt.toFixed(usdtDecimals)}`
    + (longProfit ? ` (${longProfit.usdt >= 0 ? '+' : ''}${longProfit.usdt.toFixed(usdtDecimals)})` : '')
    + ' USDT';
  const amountLine = (
    <>
      {`${profit.usdt >= 0 ? '+' : ''}${profit.usdt.toFixed(usdtDecimals)}`}
      {longProfit && (
        <span style={{ color: longProfit.usdt >= 0 ? '#3fb950' : '#f85149' }}>
          {` (${longProfit.usdt >= 0 ? '+' : ''}${longProfit.usdt.toFixed(usdtDecimals)})`}
        </span>
      )}
      {' USDT'}
    </>
  );
  const cfUsdt = abortCf?.base_usdt;
  const cfPct = abortCf?.base_pct;
  const pctText = `(${profit.pct >= 0 ? '+' : ''}${profit.pct.toFixed(pctDecimals)}%)`;
  const lines: { node: React.ReactNode; title?: string }[] = [
    { node: amountLine, title: amountText },
    { node: pctText, title: pctText },
  ];
  if (fee) lines.push({ node: fee, title: fee });
  // "안 끊었으면?" 반사실 -- 숫자만 있으면 롱 실현액과 헷갈린다(실제로 그랬다),
  // 그래서 바로 아래 줄에 무슨 숫자인지 설명을 붙인다.
  if (cfUsdt != null) {
    const cfText = `${cfUsdt >= 0 ? '+' : ''}${cfUsdt.toFixed(usdtDecimals)} USDT`
      + (cfPct != null ? ` (${cfPct >= 0 ? '+' : ''}${cfPct.toFixed(pctDecimals)}%)` : '');
    lines.push({
      node: <span style={{ color: COUNTERFACTUAL_COLOR }}>{cfText}</span>,
      title: cfText,
    });
    const heldLabel = t('binance.skipPnl.held', { min: cfHeldMinutes ?? 30 });
    lines.push({ node: heldLabel, title: heldLabel });
  }
  return <VarLineStack mutedColor={binanceMuted} lines={lines} />;
}

/** Position size and the risk budget it was sized under. */
function renderSizingCell(
  row: {
  entry: BinanceShortLiveEvent | null;
  exit: BinanceShortLiveEvent | null;
  addon?: BinanceShortLiveEvent | null;
},
  t: (key: DashboardMessageKey, params?: Record<string, string | number>) => string,
  /** 진입 이벤트에 risk_pct 가 없을 때(옛 청캐 로그) yaml 레인 값. */
  riskFallback?: number | null,
  /** 있으면 entry 에 찍힌 옛 값보다 우선 (업숏 공평화 risk, 2026-09-07). */
  riskOverride?: number | null,
): React.ReactNode {
  // liq_skip 등 미체결 이벤트는 마진체크 이전에 계산된 "계획된" 명목가만 갖고 있다 --
  // 실제 주문이 안 나갔으므로 사이징 칸도 "-" 로 비운다.
  const entryNotional = row.entry && isPositionEntry(row.entry.action) ? row.entry.notional ?? null : null;
  const baseNotional = entryNotional ?? row.exit?.notional ?? null;
  if (baseNotional == null) {
    return <ThreeLineStack mutedColor={binanceMuted} lines={['–', null, null]} />;
  }
  // linear 스케일(addon_short) 명목을 합산 — 본진입만 보이면 책의 반만 읽힌다
  // (2026-09-07 IOST $140 vs 실제 ~$281).
  // addon_short 는 isPositionEntry 가 아니다(슬롯·스위치용). 사이징만 여기서 센다.
  const addonNotional = (
    row.addon
    && row.addon.action === 'addon_short'
    && row.addon.notional != null
    && Number.isFinite(row.addon.notional)
    && row.addon.notional > 0
  ) ? row.addon.notional : null;
  const totalNotional = addonNotional != null
    ? baseNotional + addonNotional
    : baseNotional;
  // 명목%(자본 대비)가 있으면 그게 사이징 칸의 %다. 청캐 v2·업숏·빗숏.
  // risk_pct 는 배분에서 나온 위험 예산이라 명목과 다른 숫자다.
  const isV2 = row.entry?.action === 'liqv2_entry'
    || row.entry?.action === 'liqv2_add'
    || row.exit?.action === 'liqv2_settle';
  const notlPct = row.addon?.notional_pct
    ?? row.entry?.notional_pct
    ?? row.exit?.notional_pct
    ?? null;
  const risk = riskOverride
    ?? row.entry?.risk_pct ?? row.exit?.risk_pct
    ?? (isV2 ? null : riskFallback) ?? null;
  const fmtUsd = (n: number) => `$${Math.round(n).toLocaleString()}`;
  const scaleLine = addonNotional != null
    ? `첫 ${fmtUsd(baseNotional)} +스케일 ${fmtUsd(addonNotional)}`
    : null;
  return (
    <ThreeLineStack
      mutedColor={binanceMuted}
      lines={[
        fmtUsd(totalNotional),
        notlPct != null
          ? `${notlPct.toFixed(0)}%`
          : risk != null ? t('binance.sizing.risk', { pct: risk }) : null,
        scaleLine,
      ]}
    />
  );
}

function rowLeverage(row: {
  entry: BinanceShortLiveEvent | null;
  exit: BinanceShortLiveEvent | null;
}, cfgLeverage?: number | null): number | null {
  return row.entry?.leverage ?? row.exit?.leverage
    // linear 스케일 옛 이벤트는 leverage 가 빠졌다 (2026-09-07 IOST → SCALE 만).
    ?? (row.entry?.action === 'addon_short' ? (cfgLeverage ?? null) : null)
    ?? null;
}

type RowProfit = { usdt: number; pct: number; commission?: number | null; funding?: number | null };

function rowProfit(
  exit: BinanceShortLiveEvent | null,
  entry?: BinanceShortLiveEvent | null,
): RowProfit | null {
  if (!exit) return null;
  // 백엔드 detail 은 "{pnl} ({pct}%) USDT" 순서다 (mercury-binance
  // alert_binance_short_live_monitor.py 2026-08-07) -- 폭 제한 절삭 시 숫자가
  // 먼저 보이게 단위를 뒤로 뺐다. 옛 "USDT (...)" 순서는 더 이상 안 나온다.
  const m = exit.detail.match(/([+-]?\d+(?:\.\d+)?)\s*\(([+-]?\d+(?:\.\d+)?)%\)\s*USDT/);
  let usdt: number | null = null;
  let pct: number | null = null;
  if (m) {
    pct = parseFloat(m[2]);
    // net_usdt is measured from the exchange income ledger; the detail string
    // carries net * notional with a fee ESTIMATE. Prefer the measured number so
    // the row matches the wallet, and keep the parsed one as the fallback for
    // rows settled before that shipped.
    // cash_complete false 는 진입 수수료만 있는 스냅샷이다. 그 금액을 확정으로
    // 쓰지 않는다 (2026-10-14 WAL).
    usdt = exit.cash_complete === false
      ? parseFloat(m[1])
      : (exit.net_usdt ?? parseFloat(m[1]));
  } else if (exit.net != null && Number.isFinite(exit.net)) {
    // detail 배선이 빠진 레인(2026-08-27 pr_settle → 문자 그대로 "pr_settle")
    // 에서도 구조화 필드로 PnL 칸을 채운다.
    usdt = exit.cash_complete === false
      ? (exit.pnl_usdt ?? (exit.notional != null ? exit.net * exit.notional : null))
      : (exit.net_usdt
        ?? exit.pnl_usdt
        ?? (exit.notional != null ? exit.net * exit.notional : null));
    pct = exit.net * 100;
  } else if (exit.pnl_usdt != null && exit.notional != null && exit.notional > 0) {
    usdt = exit.pnl_usdt;
    pct = (exit.pnl_usdt / exit.notional) * 100;
  }
  if (usdt == null || !Number.isFinite(usdt)) return null;
  // 청캐 v2 조각 행: settle 은 슬리브 합산 — 조각 명목 비율로 나눈다
  // (행 합 = 슬리브 손익, 2026-09-13).
  if (entry
      && (entry.action === 'liqv2_entry' || entry.action === 'liqv2_add')
      && entry.notional != null && entry.notional > 0
      && exit.notional != null && exit.notional > 0) {
    usdt *= entry.notional / exit.notional;
  }
  return {
    usdt,
    pct: pct ?? 0,
    commission: exit.cash_complete === false ? null : (exit.commission_usdt ?? null),
    funding: exit.cash_complete === false ? null : (exit.funding_usdt ?? null),
  };
}

/**
 * 스킵 행의 반사실 손익 (§5-F). 실제 진입이 있었던 행에는 절대 붙이지 않는다 --
 * 그 행의 PnL 칸은 실현손익이 차지하고, 둘이 한 칸에서 겹치면 무엇이 진짜 돈인지
 * 구분이 안 된다.
 *
 * 백엔드는 스킵 이벤트에 skip_pnl 을 단다. 인테이크 스킵은 alert 와 entry 가 같은
 * 객체이고, 진입단계 스킵(slot_full 등)은 entry 쪽에만 있으므로 둘 다 본다.
 */
function rowSkipPnl(row: {
  alert: BinanceShortLiveEvent;
  entry: BinanceShortLiveEvent | null;
  exit: BinanceShortLiveEvent | null;
}): BinanceShortLiveEvent['skip_pnl'] | null {
  if (isPositionEntry(row.entry?.action)) return null;
  const sp = row.entry?.skip_pnl ?? row.alert.skip_pnl;
  return sp && sp.net_pct != null ? sp : null;
}

/** 반사실 = 실제 돈이 아니다. 실현손익의 초록/빨강과 절대 같은 색을 쓰지 않는다. */
const COUNTERFACTUAL_COLOR = '#a371f7';

/** abort 다리 4종 (analyser 8c8955b, 2026-08-05). 순서 = 문서 §3 표 순서.
 *  라벨·설명은 docs/frontend-abort-counterfactual-api.md 의 "제안 라벨" 그대로 --
 *  다리 이름은 규칙 이름이지 UI 문구가 아니라 alertType 라벨처럼 로케일과 무관하게
 *  고정한다(en 로케일도 한글 라벨을 쓰는 기존 관례, binanceAlertTypeLabels 참조). */
const ABORT_LEG_ORDER = ['px', 'oi_early', 'px_cont', 'oi_cvd', 'unknown'] as const;
const ABORT_LEG_META: Record<string, { label: string; hint: string; color: string }> = {
  px: { label: u('급가격'), hint: u('진입가 대비 +3% 역행(1~45초). 빠른 폭주형'), color: '#f85149' },
  oi_early: { label: u('조기-OI'), hint: u('역행 1.5% + OIΔ↑ + 매수세 지속(5~90초). 스퀴즈형'), color: '#d29922' },
  px_cont: { label: u('지연-가격'), hint: u('+3% 역행이 45초 이후 + continuation(45~120초)'), color: '#58a6ff' },
  oi_cvd: { label: 'OI·CVD', hint: u('OIΔ≥1.5% + 매수세 증가(30~45초). 느린 유입'), color: '#bc8cff' },
  // kind="stop"(다리가 못 막음) 또는 leg 필드가 생기기 전 구행.
  unknown: { label: u('스탑/미상'), hint: u('kind="stop" 이거나 leg 필드 도입 전 구행'), color: '#8b949e' },
};

/** abort_cf.leg → { n, sumDelta } (kind="abort" 만, leg 없으면 "unknown" 버킷).
 *  서버는 다리별 집계를 안 주므로(문서 §4, 의도적) 여기서 events 를 순회해 만든다. */
function abortLegBreakdown(
  events: BinanceShortLiveEvent[],
): Record<string, { n: number; sumDelta: number }> {
  const byLeg: Record<string, { n: number; sumDelta: number }> = {};
  for (const e of events) {
    const cf = e.abort_cf;
    if (!cf || cf.kind !== 'abort') continue;
    const key = cf.leg ?? 'unknown';
    const bucket = byLeg[key] ?? (byLeg[key] = { n: 0, sumDelta: 0 });
    bucket.n += 1;
    bucket.sumDelta += cf.delta_pct ?? 0;
  }
  return byLeg;
}

function stripLeverageFromDetail(detail: string): string {
  return detail.replace(/\s*·\s*\d+x\b/g, '');
}

type DetailRow = {
  alert: BinanceShortLiveEvent;
  entry: BinanceShortLiveEvent | null;
  exit: BinanceShortLiveEvent | null;
  addon?: BinanceShortLiveEvent | null;
};

/** Detail 칸의 조각들. **화면과 툴팁이 같은 출처**여야 한다 -- 클램프(3줄/2줄)에
 *  잘린 뒤쪽을 hover 로 보여주는 게 목적인데, 툴팁이 따로 문자열을 만들면
 *  둘이 갈라진다(잘리는 건 늘 뒤쪽 숫자다 -- 근거를 못 읽게 되는 그 자리). */
function rowDetailParts(row: DetailRow) {
  const detail = (() => {
    if (isIntakeSkip(row.alert)) return stripLeverageFromDetail(row.alert.detail);
    // 청캐: WATCH 전에 잘리면 liq_skip 이 행의 머리다 (kr_segment_high 등).
    // entry 칸이 비어 있어도 alert.detail 에 사유가 있다 -- 여기를 안 보면
    // Status 는 TRIGGER/–/– · Detail 은 – 로만 보인다 (BTR 08-26 21:56·22:10).
    if (row.alert.action === 'liq_skip' && row.alert.detail) {
      return stripLeverageFromDetail(row.alert.detail);
    }
    const entryDetail = (row.entry && !isIntakeSkip(row.entry))
      ? stripLeverageFromDetail(row.entry.detail || '')
      : '';
    if (entryDetail) return entryDetail;
    // 진입 전 ALERT/WATCH: alert.detail 이 전부다 (signal·bh_signal·ds_candidate…).
    // entry 만 읽으면 업비트 ALERT 가 전부 '–' 로 보인다 (시커 17:21, 2026-09-04).
    if (row.alert.detail) return stripLeverageFromDetail(row.alert.detail);
    return '';
  })();
  // 추가 진입 다리의 detail 은 여기까지 오지 않고 있었다 -- 이 함수가 alert/entry
  // 두 개만 봤다. 2026-08-15 에 애드온 문턱이 **심볼마다 다른 값**이 되면서
  // (upbit_addon_rv_mult: 상승 >= 1.5 x 평소 1분 진동) "왜 이게 통과했나" 를 읽으려면
  // 적용된 문턱이 보여야 하는데, 그 값은 addon_short 이벤트에만 실린다
  // (rv1m_pct / need_rise_pct / need_drop_pct). 본진입 detail 만 보면 화면에서
  // 애드온은 시각과 SHORT(7x) 뿐이라 근거가 사라진다.
  const addonDetail = row.addon ? stripLeverageFromDetail(row.addon.detail) : '';
  const prExitDetail = prSettleExitDetail(row.exit)
    || liqv2ExitDetail(row.exit)
    || glSettleExitDetail(row.exit);
  const pathExit = pathExitDetail(row.exit);
  return {
    detail,
    addonDetail,
    prExitDetail,
    pathExit,
    manualEntry: Boolean(row.entry?.manual),
    // 화면 버튼(manual)과 거래소에서 직접 닫은 건(reason=external)은 둘 다
    // 사람이 끊은 것이다. external 은 플래그가 안 실려도 사유만으로 읽힌다
    // (2026-10-17 AGTUSDT 17:13).
    manualClose: Boolean(row.exit?.manual || row.exit?.reason === 'external'),
  };
}

/** hover 툴팁(`title`) 문자열 -- 클램프에 잘린 부분까지 **전문**.
 *  Symbol 칸이 쓰는 것과 같은 네이티브 title 이다(표가 overflow:hidden 이라
 *  직접 띄우는 팝오버는 칸 밖에서 잘린다). 조각마다 줄을 나눠 화면 구조를 살린다. */
function rowDetailTitle(row: DetailRow): string | undefined {
  const p = rowDetailParts(row);
  const lines = [
    p.manualEntry ? u('수동진입') : '',
    p.manualClose ? u('수동청산') : '',
    p.detail,
    p.prExitDetail,
    p.pathExit,
    p.addonDetail,
  ].filter(Boolean);
  return lines.length ? uDeep(lines.join('\n')) : undefined;
}

function rowDetail(row: DetailRow): React.ReactNode {
  const { detail, addonDetail, prExitDetail, pathExit, manualEntry, manualClose } = rowDetailParts(row);
  // 사람이 끊은 건은 Detail 에 못박는다 (유저요구 2026-08-17). 규칙 탈출과 같은
  // 잣대로 채점하지만(kind=liq_manual) **왜 끝났는지는 다른 사실**이라, 표를
  // 눈으로 읽을 때 규칙이 판 것과 구분되지 않으면 안 된다.
  // 수동 진입도 같은 축 (2026-08-25) -- 규칙이 고른 진입과 섞이면 안 된다.
  const manualEntryTag = manualEntry ? (
    <span className="binance-alert-detail-addon" style={{ color: '#f0b90b', fontWeight: 600 }}>
      {u('수동진입')}
    </span>
  ) : null;
  const manualCloseTag = manualClose ? (
    <span className="binance-alert-detail-addon" style={{ color: '#f0b90b', fontWeight: 600 }}>
      {u('수동청산')}
    </span>
  ) : null;
  if (!detail && !addonDetail && !prExitDetail && !pathExit) {
    return manualEntryTag ?? manualCloseTag ?? (
      <span className="binance-alert-detail-text binance-alert-detail-text--empty">–</span>
    );
  }
  // **클램프 밖에 둔다.** .binance-alert-detail-text 는 -webkit-line-clamp: 3 +
  // overflow:hidden 이라, 안에 덧붙이면 3줄을 넘기는 순간 애드온 숫자가 통째로
  // 잘린다 -- 잘리는 건 늘 뒤쪽 숫자다. 애드온은 자기 클램프(2줄)를 따로 갖는다.
  return (
    <>
      {manualEntryTag}
      {manualCloseTag}
      {detail && <span className="binance-alert-detail-text">{uDeep(detail)}</span>}
      {prExitDetail && (
        <span className="binance-alert-detail-addon">{uDeep(prExitDetail)}</span>
      )}
      {pathExit && (
        <span className="binance-alert-detail-addon">{uDeep(pathExit)}</span>
      )}
      {addonDetail && (
        <span className="binance-alert-detail-addon">{uDeep(addonDetail)}</span>
      )}
    </>
  );
}

/** 차트는 진입 이벤트에만 붙는다 -- 스킵/수집 행에는 그릴 것이 없다.
 *  windowMin = hold_exit_min: 가로축을 이 분 수로 고정하고, 경과한 구간만 그린다. */
/** 차트 칸 **아래 선**의 색 — 다른 칸과 **똑같은** 표 구분선 색으로 못박는다.
 *
 * 경위: 08-10 캡처에서 청산된 행인데 차트 칸 구분선만 초록이었다(픽셀 대조:
 * 같은 y·두께, 경계는 정확히 차트 칸 모서리). 레포 CSS 에 그런 규칙이 없다.
 * 1차 수정에서 손익 색(초록/빨강)으로 바꿨는데, 그 선은 **모든 행에 있는 행
 * 구분선**이라 결과적으로 줄이 상시 강조돼 더 나빠졌다(유저 지적).
 *
 * 그래서 색을 주는 게 아니라 **튀지 않게 고정**한다. 인라인이라 !important 가
 * 아닌 어떤 스타일시트 규칙도 이걸 못 이긴다. 열린 행의 에메랄드 강조는
 * `.broker-row-open td { ... !important }` 가 여전히 이겨서 행 전체가 같이
 * 바뀐다 -- 그건 칸이 아니라 **행** 단위 신호라 그대로 두는 게 맞다. */
const CHART_RULE_COLOR = '#f0b90b22';

function plannedEntryHoldMin(
  row: { alert: BinanceShortLiveEvent; entry: BinanceShortLiveEvent | null },
  cfg: BinanceShortLiveConfig | undefined,
): number {
  if (row.entry?.chart_window_min != null) return row.entry.chart_window_min;
  if (row.entry?.hold_min != null) return row.entry.hold_min;
  const act = row.alert.action ?? '';
  if (act.startsWith('liq_') || act.startsWith('liqv2_')) return cfg?.liq_cascade_hold_min ?? 120;
  if (act.startsWith('cl_')) return cfg?.cand_long_hold_min ?? 30;
  if (act.startsWith('pr_')) return cfg?.pump_ride_hold_min ?? 720;
  // 겹롱 durée 는 보유 5분. 업숏 홀드로 떨어지면 축이 두 시간으로 빈다.
  if (act.startsWith('gl_')) return row.alert.hold_min ?? 5;
  // 빗숏 실보유는 bh_hold_after_min (폴백 bh_hold_exit_min). exit 만 쓰면
  // 차트 창이 너무 짧아 1초 꼬리·노란점이 오른쪽 밖으로 나간다(2026-08-26).
  if (act.startsWith('bh_')) {
    return cfg?.bh_hold_after_min ?? cfg?.bh_hold_exit_min ?? 10;
  }
  return Math.max(
    (cfg?.hold_after_min ?? cfg?.hold_exit_min ?? 30) - (cfg?.entry_delay_min ?? 1),
    1,
  );
}

function rowChart(
  row: {
    alert: BinanceShortLiveEvent;
    entry: BinanceShortLiveEvent | null;
    exit: BinanceShortLiveEvent | null;
  },
  holdWindowMin: number,
  liveTail?: [number, number][] | null,
): React.ReactNode {
  const ev = row.entry;
  const pre = row.alert;
  const rawPre = (pre.pre_chart && pre.pre_chart.length >= 2) ? pre.pre_chart : null;
  const hasEntryChart = !!(ev?.chart && ev.chart.length >= 2);
  const isOpenLive = hasEntryChart && !row.exit;
  // WATCH: 프리차트에 1초 꼬리. OPEN: 본차트에 붙인다 (정산되면 멈춤).
  const prePts = (!hasEntryChart)
    ? mergePreChartLiveTail(rawPre, liveTail)
    : rawPre;
  if (!hasEntryChart && !prePts) return null;

  const profit = rowProfit(row.exit, row.entry);
  let points = hasEntryChart
    ? chartPointsWithExit(ev!.chart!, ev!.entry_px, ev!.action, row.exit ? {
      time_kst: row.exit.time_kst,
      net: row.exit.net,
      pct: profit?.pct,
      net_usdt: row.exit.net_usdt,
      notional: row.exit.notional ?? ev!.notional,
    } : null)
    : null;
  if (isOpenLive && points) {
    points = mergePreChartLiveTail(points, liveTail) ?? points;
  }
  const exitPx = row.exit && points && points.length > 0
    ? points[points.length - 1][1] : undefined;

  // 프리+본이 같이 있으면 가로축은 **알림/감시 시작 → 홀드 끝**.
  // 대기만 있어도 대기+보유를 미리 잡아, 어두운 선이 폭을 꽉 채우지 않고
  // 오른쪽에 색 선이 이어질 자리를 남긴다(2026-08-23 유저).
  let tStartMs: number | undefined;
  let win = holdWindowMin;
  let bridgedPre = prePts;
  if (prePts && hasEntryChart && pre.time_kst && ev?.time_kst) {
    const alertMs = kstToMs(pre.time_kst);
    const entryMs = kstToMs(ev.time_kst);
    if (Number.isFinite(alertMs) && Number.isFinite(entryMs) && entryMs >= alertMs) {
      tStartMs = alertMs;
      const preMin = Math.max(0, Math.round((entryMs - alertMs) / 60_000));
      win = Math.max(preMin + holdWindowMin, holdWindowMin);
      // 이음새: 프리 끝이 본차트 첫 점과 만나야 "끊기고 다시 시작"이 안 된다.
      const e0 = points![0];
      bridgedPre = [...prePts.filter((p) => p[0] < e0[0]), e0];
    }
  } else if (prePts && !hasEntryChart) {
    const wait = pre.pre_chart_window_min ?? holdWindowMin;
    win = wait + holdWindowMin;
    if (pre.time_kst) {
      const ms = kstToMs(pre.time_kst);
      if (Number.isFinite(ms)) tStartMs = ms;
    }
    // 1초 꼬리가 홀드창 오른쪽 밖으로 나가 노란점이 안 보이던 걸 막는다
    // (제타체인 22:55, 창 70분·경과 100분+). 꼬리가 있으면 지금+1분까지 연다.
    if (liveTail && liveTail.length > 0 && tStartMs != null) {
      const liveEnd = liveTail[liveTail.length - 1][0];
      const needMin = Math.ceil((liveEnd - tStartMs) / 60_000) + 1;
      if (needMin > win) win = needMin;
    }
  }

  // 가로축이 수십 분이라 1초 점은 픽셀 미만 — 숫자·끝점으로 살아있음을 보여준다.
  const liveLast = (liveTail && liveTail.length > 0)
    ? liveTail[liveTail.length - 1] : null;
  const liveOk = !!(liveLast && (Date.now() - liveLast[0]) < 5_000);
  // 후보롱 감시행은 entry 가 아직 없어도 롱 방향이다 (하락=빨강).
  const isLong = LONG_ENTRY_ACTIONS.has(ev?.action ?? '')
    || pre.action === 'cl_candidate'
    || pre.action === 'pr_candidate'
    || (pre.action?.startsWith('pr_') ?? false)
    || (pre.action?.startsWith('cl_') ?? false)
    || (pre.action?.startsWith('gl_') ?? false);
  // OPEN 은 진입가 대비, WATCH 는 알림/프리 기준가 대비.
  // pre_chart_px 가 아직 없으면(첫 페인트·1초 꼬리만) 시리즈 첫 점으로
  // 기준을 잡는다 — 없으면 점선도 %도 안 나온다(2026-08-26).
  const seriesFirst = (bridgedPre?.[0]?.[1] ?? points?.[0]?.[1]);
  const basePx = isOpenLive
    ? (ev?.entry_px ?? seriesFirst)
    : (pre.pre_chart_px ?? seriesFirst);
  const livePct = (liveLast && basePx != null && basePx > 0)
    ? ((liveLast[1] - basePx) / basePx) * 100
    : null;
  const liveColor = livePct == null ? binanceMuted
    : isLong
      ? (livePct >= 0 ? '#3fb950' : '#f85149')
      : (livePct <= 0 ? '#3fb950' : '#f85149');
  const showLive = liveOk && (!hasEntryChart || isOpenLive);

  // 래퍼에 position:relative 를 두면 .binance-alert-spark 의 absolute 기준이
  // td(행)가 아니라 28px 박스가 되어 차트가 행 상단에 붙는다 (2026-08-25).
  // spark·live% 둘 다 td.binance-alert-col-chart(position:relative) 기준.
  return (
    <>
      <BinanceEntrySparkline
        points={points}
        entryPx={ev?.entry_px}
        exitPx={exitPx}
        prePoints={bridgedPre}
        prePx={pre.pre_chart_px ?? (typeof seriesFirst === 'number' ? seriesFirst : undefined)}
        postPoints={(ev?.post_chart && ev.post_chart.length >= 2) ? ev.post_chart : null}
        windowMin={win}
        tStartMs={tStartMs}
        isLong={isLong}
        liveTip={showLive}
      />
      {livePct != null && showLive && (
        <span
          className="binance-alert-live-pct"
          title={liveOk ? u('mark 1초 갱신') : u('mark 끊김')}
          style={{
            position: 'absolute',
            // 2026-09-04: 차트 맨 오른쪽(유저). 스파크라인 끝점 위에 겹침,
            // textShadow 로 선 위에서도 읽힌다. 표가 725px 라 가로 스크롤이
            // 없어 오른쪽 끝에 둬도 안 잘린다.
            right: 4,
            // 셀 상단으로 올리면 overflow 부모에 잘린다(2026-08-26).
            // 차트 칸 안 우하단에 둔다.
            bottom: 2,
            top: 'auto',
            fontSize: '0.68rem',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: liveOk ? liveColor : binanceMuted,
            opacity: liveOk ? 1 : 0.55,
            pointerEvents: 'none',
            lineHeight: 1.2,
            zIndex: 3,
            textShadow: '0 0 3px #0d1117, 0 0 3px #0d1117',
          }}
        >
          {`${livePct >= 0 ? '+' : ''}${livePct.toFixed(2)}%`}
        </span>
      )}
    </>
  );
}

function formatEventHm(ev: BinanceShortLiveEvent): string {
  if (ev.time_hm) return ev.time_hm;
  if (!ev.time_kst) return '–';
  return ev.time_kst.slice(11, 16);
}

/* pendingEntryHm 은 binanceAlertRows 로 옮겼다 (2026-08-20). u("진입(예정)") 필터가
 * 같은 판정을 써야 한다 -- 화면은 WATCH(+마감) 배지를 그리는데 필터는 그 행을
 * 접는 상태가 생기면 안 된다. 판정 규칙(예정시각 +3분)은 그 파일의 docstring 참조. */

function formatEntryStatusLabel(
  entryEv: BinanceShortLiveEvent,
  lev: number | null,
  t: (key: DashboardMessageKey) => string,
): string {
  const raw = entryOutcomeLabel(entryEv);
  const label = formatStatusLabel(raw, t);
  // linear 스케일 행은 SHORT 와 구분해 읽힌다 (같은 시각 두 SHORT 오인 방지).
  if (entryEv.action === 'addon_short' && entryEv.mode === 'linear_rung') {
    return lev != null ? `SCALE(${lev}x)` : 'SCALE';
  }
  if ((raw === 'SHORT' || raw === 'RESHORT' || raw === 'LONG') && lev != null) return `${label}(${lev}x)`;
  return label;
}

function StatusLine({
  time,
  label,
  color,
}: {
  time?: string;
  label: string;
  color: string;
}) {
  return (
    <div className="binance-status-line">
      <span className="binance-status-time">{time ?? '–'}</span>
      <span className="binance-status-label" style={{ color, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function TradeFlowCell({
  row,
  isOpen,
  entryDelayMin,
  cfgLeverage = null,
}: {
  row: {
    alert: BinanceShortLiveEvent;
    entry: BinanceShortLiveEvent | null;
    exit: BinanceShortLiveEvent | null;
    longLeg?: { entry: BinanceShortLiveEvent; exit: BinanceShortLiveEvent | null } | null;
    reshortLeg?: { entry: BinanceShortLiveEvent; exit: BinanceShortLiveEvent | null } | null;
    addon?: BinanceShortLiveEvent | null;
  };
  isOpen: boolean;
  entryDelayMin?: number | null;
  /** 업숏 yaml/DB leverage — addon_short 옛 이벤트 폴백. */
  cfgLeverage?: number | null;
}): React.ReactElement {
  const t = useDashboardT();
  const intakeSkip = isIntakeSkip(row.alert);
  const entryEv = intakeSkip ? row.alert : row.entry;
  const lev = rowLeverage(row, cfgLeverage);
  const entered = isPositionEntry(row.entry?.action) && !intakeSkip;
  const hasExit = row.exit != null;
  const longLeg = row.longLeg ?? null;
  // longLeg 가 있으면 숏의 결말은 항상 abort(EXIT) 다 -- OPEN 일 수 없다(그러면
  // longLeg 자체가 아직 존재할 수 없다, open_abort_long 은 settle 과 같은 틱에서만
  // 불린다). isOpen 은 숏 슬롯 기준이라 이 경우 그대로 둬도 안전하다.

  // 진입 전 대기 = 전부 WATCH (2026-08-25). 예전 PENDING(업숏 고정지연)과
  // WATCH(청캐·후보롱)가 같은 자리라 라벨만 갈라져 있었다.
  const preEntryHm = (!entryEv
    && ((row.alert.exchange ?? 'upbit') === 'upbit' || row.alert.action === 'bh_signal'))
    ? pendingEntryHm(row.alert, entryDelayMin) : null;
  // **감시 중**(후보-롱 후보 등록 ~ 트리거 전). 이 상태가 라벨로 안 나오면 그
  // 행은 `ALERT / – / –` 로 보인다 -- 알림만 뜨고 아무 일도 안 일어난 것처럼
  // 읽힌다(2026-08-14 유저 지적: "감시중이면 WATCH 가 나와야지"). 발동률이 30%
  // 라 대부분의 행이 이 상태로 머문다. 트리거가 나면 cl_entry 가, 창이 끝나면
  // cl_skip(no_trigger) 이 각각 자기 라벨로 덮는다.
  // 감시 중인가, 아니면 이미 끝났는가. **만료를 안 보면 화면이 옛 감시행으로
  // 뒤덮인다** -- 발동률이 30% 라 대부분이 트리거 없이 창을 넘긴다(2026-08-14
  // 유저 지적: "WATCH 가 엄청 많다"). 결말 행(cl_entry / cl_skip_no_trigger)이
  // 붙은 건은 위 entry 경로가 이미 덮으므로, 여기 남는 건 결말이 기록되기
  // **전에** 만료된 옛 행들이다.
  // 청캐 감시행(2026-08-22)도 같은 자리다. 기본 창이 레인마다 달라
  // (청캐 15분 / 후보-롱 113분) 폴백을 갈라 쓴다 -- 하나로 두면 청캐 감시행이
  // 만료 뒤 98분 동안 'WATCH' 로 남아 화면을 덮는다.
  const isLiqLane = (row.alert.exchange === 'liq'
    || row.alert.action?.startsWith('liq_')
    || row.alert.action?.startsWith('liqv2_')) ?? false;
  const isGlHead = row.alert.action?.startsWith('gl_') ?? false;
  const isLiqWatch = row.alert.action === 'liq_watch';
  // WATCH 전 스킵(kr_segment_high 등): liq_skip 이 머리 · entry 없음.
  // 이 줄을 안 보면 Status 2행이 '–' 로 비고 SKIP 이 안 보인다.
  const isLiqSkipHead = row.alert.action === 'liq_skip' && !entryEv;
  const isGlWatch = row.alert.action === 'gl_watch';
  const isCandidate = (row.alert.action === 'cl_candidate'
    || row.alert.action === 'pr_candidate'
    || isLiqWatch
    || isGlWatch) && !row.entry;
  const watchEndMs = isCandidate
    ? kstToMs(row.alert.time_kst)
      + (isGlWatch
        ? (row.alert.watch_sec ?? 15) * 1000
        : (row.alert.watch_min
          ?? (isLiqWatch ? LIQ_WATCH_MIN_DEFAULT
            : (row.alert.action === 'pr_candidate'
              ? PR_WATCH_MIN_DEFAULT : 113))) * 60_000)
    : null;
  const candidateExpired = watchEndMs != null && Date.now() > watchEndMs;
  // 업숏·빗숏: pendingEntryHm 이 null 인데 감시창이 있었던 경우 = 만료.
  const preEntryExpired = (() => {
    if (entryEv || preEntryHm) return false;
    if ((row.alert.exchange ?? 'upbit') !== 'upbit' && row.alert.action !== 'bh_signal') {
      return false;
    }
    const eff = row.alert.watch_min != null ? row.alert.watch_min + 1 : entryDelayMin;
    if (eff == null || !row.alert.time_kst) return false;
    const ms = kstToMs(row.alert.time_kst);
    if (!Number.isFinite(ms)) return false;
    return Date.now() > ms + (eff + 3) * 60_000;
  })();
  const watching = (isCandidate && !candidateExpired) || !!preEntryHm;
  const watchExpired = (isCandidate && candidateExpired) || preEntryExpired;
  // 업숏·빗숏 대기는 마감 HH:MM 을 붙인다. 청캐·후보롱 WATCH 는 시각 없이 라벨만.
  const row2Time = entryEv
    ? formatEventHm(entryEv)
    : isLiqSkipHead
      ? formatEventHm(row.alert)
      : (preEntryHm ?? undefined);
  const row2Label = entryEv
    ? formatEntryStatusLabel(entryEv, lev, t)
    : isLiqSkipHead
      ? formatStatusLabel('SKIP', t)
    : watching
      ? (isLiqWatch
        ? liqWatchStatusLabel(row.alert.entry_mode, t)
        : formatStatusLabel('WATCH', t))
      : watchExpired
        ? formatStatusLabel(isGlWatch ? 'SKIP' : 'WATCH-END', t)
        : '–';
  const row2Color = entryEv ? typeColor(entryOutcomeLabel(entryEv))
    : isLiqSkipHead ? typeColor('SKIP')
    : watching ? typeColor('WATCH')
      : watchExpired ? typeColor('WATCH-END')
        : binanceMuted;

  const row3Time = (() => {
    if (isOpen && entered && entryEv) return formatEventHm(entryEv);
    if (hasExit && row.exit) return formatEventHm(row.exit);
    return undefined;
  })();
  const row3Label = isOpen && entered
    ? formatStatusLabel('OPEN', t)
    : hasExit
      ? formatStatusLabel('EXIT', t)
      : '–';
  const row3Color = isOpen && entered ? '#f0b90b' : hasExit ? typeColor('EXIT') : binanceMuted;

  return (
    <div className="binance-status-stack">
      {/* 머리 줄. 청산 캐스케이드는 **알림이 없는 레인**이라 여기가 국내
          거래소 알림이 아니라 바이낸스 청산 스트림 트리거다 -- 'ALERT' 라고
          쓰면 오지도 않은 알림을 말한다(2026-08-22 유저 지적). exchange 는
          백엔드가 liq_* 액션 전부에 'liq' 로 못박아 내려준다. */}
      <StatusLine
        time={formatEventHm(row.alert)}
        label={formatStatusLabel((isLiqLane || isGlHead) ? 'TRIGGER' : 'ALERT', t)}
        color={binanceMuted}
      />
      <StatusLine time={row2Time} label={row2Label} color={row2Color} />
      {/* 추가 진입 -- 같은 거래의 두 번째 다리라 본진입 바로 아래 한 줄이다.
          라벨도 SHORT(Xx) 로 같게 둔다: 다른 배지를 주면 다른 거래로 읽힌다. */}
      {row.addon && (
        <StatusLine
          time={formatEventHm(row.addon)}
          label={formatEntryStatusLabel(row.addon, row.addon.leverage ?? lev, t)}
          color={typeColor('SHORT')}
        />
      )}
      <StatusLine time={row3Time} label={row3Label} color={row3Color} />
      {longLeg && (
        <>
          <StatusLine
            time={formatEventHm(longLeg.entry)}
            label={formatEntryStatusLabel(longLeg.entry, longLeg.entry.leverage ?? null, t)}
            color={typeColor('LONG')}
          />
          <StatusLine
            time={longLeg.exit ? formatEventHm(longLeg.exit) : undefined}
            label={longLeg.exit ? formatStatusLabel('EXIT', t) : formatStatusLabel('OPEN', t)}
            color={longLeg.exit ? typeColor('EXIT') : '#f0b90b'}
          />
        </>
      )}
      {/* 재숏 다리 (2026-08-22). **롱 다리 뒤**에 온다 -- 사슬 순서가 곧 시간
          순서다: SHORT -> EXIT -> LONG -> LONG-EXIT -> RESHORT -> EXIT.
          Status 에 RESHORT 로 서야 한다 -- Detail 문장에만 u("재숏") 이 있으면
          3줄에서 잘려 안 보인다(유저 지적). */}
      {row.reshortLeg && (
        <>
          <StatusLine
            time={formatEventHm(row.reshortLeg.entry)}
            label={formatEntryStatusLabel(row.reshortLeg.entry,
              row.reshortLeg.entry.leverage ?? null, t)}
            color={typeColor('RESHORT')}
          />
          <StatusLine
            time={row.reshortLeg.exit ? formatEventHm(row.reshortLeg.exit) : undefined}
            label={row.reshortLeg.exit
              ? formatStatusLabel('EXIT', t) : formatStatusLabel('OPEN', t)}
            color={row.reshortLeg.exit ? typeColor('EXIT') : '#f0b90b'}
          />
        </>
      )}
    </div>
  );
}

function SizingCell({
  row,
  riskFallback = null,
  riskOverride = null,
}: {
  row: {
    entry: BinanceShortLiveEvent | null;
    exit: BinanceShortLiveEvent | null;
    addon?: BinanceShortLiveEvent | null;
  };
  riskFallback?: number | null;
  riskOverride?: number | null;
}): React.ReactElement {
  const t = useDashboardT();
  return <>{renderSizingCell(row, t, riskFallback, riskOverride)}</>;
}

function PnlCell({
  profit,
  compact = false,
  skipPnl = null,
  longProfit = null,
  abortCf = null,
  cfHeldMinutes = null,
  skipHeldMinutes = null,
  skipWatchMinutes = null,
}: {
  profit: RowProfit | null;
  compact?: boolean;
  skipPnl?: BinanceShortLiveEvent['skip_pnl'] | null;
  longProfit?: RowProfit | null;
  abortCf?: BinanceShortLiveEvent['abort_cf'] | null;
  cfHeldMinutes?: number | null;
  skipHeldMinutes?: number | null;
  skipWatchMinutes?: number | null;
}): React.ReactElement {
  const t = useDashboardT();
  return <>{renderPnlCell(profit, t, compact, skipPnl, longProfit, abortCf, cfHeldMinutes,
    skipHeldMinutes, skipWatchMinutes)}</>;
}

/** 패스키 기기 등록 상자 (2026-08-17).
 *
 *  **아이폰에는 브라우저 콘솔이 없다.** 그래서 등록을 콘솔 스니펫으로 처리할 수
 *  없고, 화면에 있어야 한다. 맥북에서만 되는 방법은 방법이 아니다.
 *
 *  평소에는 **아예 안 보인다** -- 서버가 `enroll_open`(등록 토큰이 설정된 상태)일
 *  때만 뜬다. 그 창은 기기를 추가할 때만 사람이 VM 에서 열고 바로 닫는다.
 */
function PasskeyEnrollBox({ status, onDone }: {
  status: PasskeyStatus;
  onDone: () => void;
}): React.ReactElement | null {
  const [token, setToken] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (!status.enroll_open) return null;

  const run = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await enrollPasskey(token.trim(), label.trim() || u('기기'));
      setMsg(u('✅ 등록 완료'));
      setToken('');
      onDone();
    } catch (e) {
      setMsg(`실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const inp: React.CSSProperties = {
    background: '#0d1117', color: '#e6edf3', border: '1px solid #30363d',
    borderRadius: 4, padding: '0.25rem 0.4rem', fontSize: '0.78rem',
  };
  return (
    <div style={{
      border: '1px solid #f0b90b', borderRadius: 6, padding: '0.5rem 0.7rem',
      margin: '0 0 0.7rem', display: 'flex', flexWrap: 'wrap',
      alignItems: 'center', gap: '0.45rem', fontSize: '0.8rem',
    }}>
      <span style={{ color: '#f0b90b', fontWeight: 600 }}>{u('패스키 기기 등록')}</span>
      <span style={{ color: binanceMuted }}>등록됨 {status.enrolled}</span>
      <input style={{ ...inp, width: '20rem' }} type="password" value={token}
             onChange={(e) => setToken(e.target.value)}
             placeholder={u("등록 토큰")} autoComplete="off" />
      <input style={{ ...inp, width: '7rem' }} value={label}
             onChange={(e) => setLabel(e.target.value)}
             placeholder={u("기기 이름")} autoComplete="off" />
      <button type="button" disabled={busy || !token.trim()} onClick={run}
              style={{
                background: '#f0b90b', color: '#0d1117', border: 'none',
                borderRadius: 4, padding: '0.25rem 0.7rem',
                fontSize: '0.78rem', fontWeight: 700,
                cursor: busy || !token.trim() ? 'default' : 'pointer',
              }}>
        {busy ? u('등록 중…') : u('Face ID / Touch ID 로 등록')}
      </button>
      {msg && (
        <span style={{ color: msg.startsWith('✅') ? '#3fb950' : '#f85149' }}>{msg}</span>
      )}
    </div>
  );
}

/** 수동 청산 버튼 (2026-08-17, 유저요구). 진입해 있는 청산 레인 포지션을 손으로 판다.
 *
 *  누르면 패스키(Face ID / Touch ID) 확인이 뜨고, 통과하면 트레이더 VM 의 명령 큐에
 *  요청이 들어간다. **여기서 성공은 "주문됨" 이 아니라 "큐에 들어감" 이다** -- 실제
 *  매도는 봇이 다음 사이클(5초)에 자기 정산 경로로 한다. 문구도 그렇게 돼 있다.
 *
 *  확인을 두 단계로 둔 이유: 이 열은 표 안이라 손가락이 스치기 쉽다. 패스키가 이미
 *  확인 한 겹이지만, 그건 프롬프트를 띄운 **뒤**라 그 전에 한 겹이 더 필요하다.
 */
function LiqCloseButton({ lane, sid, perp, onDone }: {
  lane: string;
  sid: string;
  perp?: string;
  onDone: () => void;
}): React.ReactElement {
  const t = useDashboardT();
  const [phase, setPhase] = useState<'idle' | 'armed' | 'working' | 'done' | 'error'>('idle');
  const [note, setNote] = useState<string>('');

  // 팔무장 상태를 계속 두지 않는다 -- 눌러 놓고 잊은 버튼이 다음 스크롤에서
  // 눌리면 그게 곧 오발이다.
  useEffect(() => {
    if (phase !== 'armed') return undefined;
    const id = window.setTimeout(() => setPhase('idle'), 5000);
    return () => window.clearTimeout(id);
  }, [phase]);

  const run = useCallback(async () => {
    setPhase('working');
    try {
      const res = await manualClose(lane, sid);
      if (res.ok) {
        setPhase('done');
        setNote(t('binance.manualClose.queued', { eta: String(res.eta_s ?? 5) }));
        onDone();
      } else {
        setPhase('error');
        setNote(t('binance.manualClose.failed', { error: res.reason ?? '?' }));
      }
    } catch (e) {
      // 사용자가 Face ID 를 취소한 것은 실패가 아니다. 빨간 글씨로 남기면
      // 뭔가 고장난 줄 안다.
      const cancelled = e instanceof Error
        && (e.name === 'NotAllowedError' || /취소|cancel/i.test(e.message));
      setPhase(cancelled ? 'idle' : 'error');
      setNote(cancelled ? '' : t('binance.manualClose.failed', {
        error: e instanceof Error ? e.message : String(e),
      }));
    }
  }, [lane, sid, onDone, t]);

  if (phase === 'done') {
    return <span style={{ color: '#3fb950', fontSize: '0.68rem' }}>{note}</span>;
  }

  const armed = phase === 'armed';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
      <button
        type="button"
        disabled={phase === 'working'}
        onClick={() => (armed ? run() : setPhase('armed'))}
        title={t('binance.manualClose.title', { perp: perp ?? '' })}
        style={{
          background: armed ? '#f85149' : 'transparent',
          color: armed ? '#0d1117' : '#f85149',
          border: `1px solid ${'#f85149'}`,
          borderRadius: 3,
          padding: '0 0.32rem',
          fontSize: '0.68rem',
          fontWeight: 600,
          lineHeight: 1.5,
          cursor: phase === 'working' ? 'default' : 'pointer',
        }}
      >
        {phase === 'working'
          ? t('binance.manualClose.working')
          : armed ? t('binance.manualClose.confirm') : t('binance.manualClose.button')}
      </button>
      {phase === 'error' && (
        <span style={{ color: '#f85149', fontSize: '0.64rem' }}>{note}</span>
      )}
    </span>
  );
}

/** 수동 진입 버튼 (2026-08-25). WATCH/PENDING 행에서 되돌림 대기 없이 들어간다.
 *  finish 는 큐 등록만 알린다 — 실제 체결/거절은 entry/status 폴링으로 받아
 *  "큐 등록" 자리에 사유를 남긴다 (2026-08-25 ZRO lane_busy).
 *
 *  실패는 몇 초 뒤 진입 버튼으로 되돌린다. 성공은 "성공함"을 잠깐 보여 준 뒤
 *  **표의 open_liq 갱신을 기다리지 않고** 포지션 sid 로 청산 버튼을 바로 연다
 *  (2026-08-26 유저: 성공 후 청산 버튼이 영영 안 떴다 — 모니터 API 지연/캐시). */
/** PnL 칸에 수동 진입/청산 버튼을 그릴 것인가 (2026-08-30 유저 지시로 OFF).
 *  버튼 컴포넌트와 API 배선은 남겨 둔다 -- 다시 켤 때 이 값만 true 로. */
const MANUAL_BUTTONS_IN_PNL = false;

const MANUAL_ENTRY_FAIL_RESET_MS = 3000;
const MANUAL_ENTRY_SUCCESS_HOLD_MS = 2500;

type ManualEnteredTarget = { rowKey: string; lane: string; sid: string };

function ManualEntryButton({ lane, sid, perp, rowKey, onDone, onEntered }: {
  lane: string;
  sid: string;
  perp?: string;
  rowKey: string;
  onDone: () => void;
  onEntered: (target: ManualEnteredTarget) => void;
}): React.ReactElement {
  const t = useDashboardT();
  const [phase, setPhase] = useState<'idle' | 'armed' | 'working' | 'done' | 'error'>('idle');
  const [note, setNote] = useState<string>('');
  const [enteredSid, setEnteredSid] = useState<string | null>(null);

  useEffect(() => {
    if (phase !== 'armed') return undefined;
    const id = window.setTimeout(() => setPhase('idle'), 5000);
    return () => window.clearTimeout(id);
  }, [phase]);

  // 실패: 빨간 사유를 잠깐 보여 준 뒤 진입 버튼으로 복구.
  useEffect(() => {
    if (phase !== 'error') return undefined;
    const id = window.setTimeout(() => {
      setPhase('idle');
      setNote('');
    }, MANUAL_ENTRY_FAIL_RESET_MS);
    return () => window.clearTimeout(id);
  }, [phase]);

  // 성공: "성공함" → 부모에 포지션 sid 등록(청산 버튼) → 표 갱신.
  useEffect(() => {
    if (phase !== 'done') return undefined;
    const closeSid = enteredSid || sid;
    const id = window.setTimeout(() => {
      onEntered({ rowKey, lane, sid: closeSid });
      onDone();
    }, MANUAL_ENTRY_SUCCESS_HOLD_MS);
    return () => window.clearTimeout(id);
  }, [phase, enteredSid, sid, rowKey, lane, onEntered, onDone]);

  const reasonLabel = useCallback((reason?: string, holder?: string) => {
    if (!reason) return '?';
    if (reason === 'lane_busy') {
      if (holder) {
        const holderKey = `binance.manualEntry.holder.${holder}` as DashboardMessageKey;
        const holderLabel = t(holderKey, {});
        // 키가 없으면 원문 holder 를 쓴다.
        const shown = holderLabel.startsWith('binance.manualEntry') ? holder : holderLabel;
        return t('binance.manualEntry.reason.lane_busy', { holder: shown });
      }
      return t('binance.manualEntry.reason.lane_busy.unknown');
    }
    const key = `binance.manualEntry.reason.${reason}` as DashboardMessageKey;
    const labeled = t(key, {});
    return labeled.startsWith('binance.manualEntry') ? reason : labeled;
  }, [t]);

  const pollOutcome = useCallback(async (reqId: string, etaS: number) => {
    setNote(t('binance.manualEntry.queued', { eta: String(etaS) }));
    const deadline = Date.now() + 15000;
    while (Date.now() < deadline) {
      await new Promise((r) => window.setTimeout(r, 400));
      try {
        const st = await fetchEntryStatus(lane, reqId);
        if (!st.ok) {
          // not_found 등은 아직 done 전이거나 유실 — 잠깐 더 본다.
          continue;
        }
        if (st.pending) continue;
        const result = st.result ?? {};
        if (result.ok) {
          // 봇이 준 포지션 sid. 없으면 감시 sid(liq/업숏/빗숏은 보통 같음).
          const closeSid = (typeof result.sid === 'string' && result.sid)
            ? result.sid
            : sid;
          setEnteredSid(closeSid);
          setPhase('done');
          setNote(t('binance.manualEntry.entered'));
          return;
        }
        setPhase('error');
        setNote(t('binance.manualEntry.failed', {
          error: reasonLabel(result.reason, result.holder),
        }));
        return;
      } catch {
        // 일시 네트워크 — 다음 틱에 재시도
      }
    }
    setPhase('error');
    setNote(t('binance.manualEntry.timeout'));
  }, [lane, sid, reasonLabel, t]);

  const run = useCallback(async () => {
    setPhase('working');
    setNote('');
    setEnteredSid(null);
    try {
      const res = await manualEntry(lane, sid);
      if (res.ok && res.req_id) {
        // 폴링 중에도 큐 문구를 보이도록 working+note 로 둔다 (done 으로 올리면
        // success hold 타이머가 너무 일찍 돈다).
        await pollOutcome(res.req_id, res.eta_s ?? 1);
      } else if (res.ok) {
        // req_id 없는 옛 배포 — 감시 sid 로 청산을 연다.
        setEnteredSid(sid);
        setPhase('done');
        setNote(t('binance.manualEntry.queued', { eta: String(res.eta_s ?? 1) }));
      } else {
        setPhase('error');
        setNote(t('binance.manualEntry.failed', {
          error: reasonLabel(res.reason) || res.reason || '?',
        }));
      }
    } catch (e) {
      const cancelled = e instanceof Error
        && (e.name === 'NotAllowedError' || /취소|cancel/i.test(e.message));
      setPhase(cancelled ? 'idle' : 'error');
      setNote(cancelled ? '' : t('binance.manualEntry.failed', {
        error: e instanceof Error ? e.message : String(e),
      }));
    }
  }, [lane, sid, t, pollOutcome, reasonLabel]);

  if (phase === 'done' || (phase === 'error' && note)) {
    const color = phase === 'error' ? '#f85149' : '#3fb950';
    return <span style={{ color, fontSize: '0.68rem' }}>{note}</span>;
  }

  // 폴링 중: 버튼 대신 "큐 등록 · N초 내 진입".
  if (phase === 'working' && note) {
    return <span style={{ color: binanceMuted, fontSize: '0.68rem' }}>{note}</span>;
  }

  const armed = phase === 'armed';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
      <button
        type="button"
        disabled={phase === 'working'}
        onClick={() => (armed ? run() : setPhase('armed'))}
        title={t('binance.manualEntry.title', { perp: perp ?? '' })}
        style={{
          background: armed ? '#3fb950' : 'transparent',
          color: armed ? '#0d1117' : '#3fb950',
          border: '1px solid #3fb950',
          borderRadius: 3,
          padding: '0 0.32rem',
          fontSize: '0.68rem',
          fontWeight: 600,
          lineHeight: 1.5,
          cursor: phase === 'working' ? 'default' : 'pointer',
        }}
      >
        {phase === 'working'
          ? t('binance.manualEntry.working')
          : armed ? t('binance.manualEntry.confirm') : t('binance.manualEntry.button')}
      </button>
    </span>
  );
}

/** 열린 포지션 키. 레인을 반드시 포함한다 -- 업비트 레인과 빗썸 슬리브는 풀이
 *  분리돼 있어 같은 심볼을 동시에 들 수 있고, 레인이 빠지면 한쪽만 열려 있는데
 *  두 행 모두 '보유 중'으로 강조된다. */
function openPositionKey(lane: 'upbit' | 'bh' | 'liq' | 'cl' | 'pr' | 'ds' | 'gl', symbol?: string, perp?: string): string {
  return `${lane}|${symbol ?? ''}|${perp ?? ''}`;
}

function rowLane(row: { entry: BinanceShortLiveEvent | null; alert: BinanceShortLiveEvent }): 'upbit' | 'bh' | 'liq' | 'cl' | 'pr' | 'ds' | 'gl' {
  const a = row.entry?.action ?? row.alert.action;
  if (a.startsWith('gl_')) return 'gl';
  // 청캐 v3 도 open_liq 로 내려온다 — 빠지면 'upbit' 으로 떨어져 진입한 행이
  // '보유중' 으로 안 칠해진다 (2026-10-27 AINUSDT).
  if (a.startsWith('liq_') || a.startsWith('liqv2_') || a.startsWith('liqv3_')) return 'liq';
  // 후보-롱(2026-08-13)은 자기 레인이다. 'bh' 로 합치면 같은 빗썸 심볼의
  // 페이드-숏과 키가 겹쳐 한쪽만 열려 있어도 두 행이 '보유중'으로 칠해진다.
  if (a.startsWith('cl_')) return 'cl';
  if (a.startsWith('pr_')) return 'pr';
  // 쌍숏(2026-09-02)도 자기 책(open_ds) — 'upbit' 로 떨어지면 open 하이라이트가
  // 안 붙는다(키 불일치).
  if (a.startsWith('ds_')) return 'ds';
  return a.startsWith('bh_') ? 'bh' : 'upbit';
}

function isOpenRow(
  row: { alert: BinanceShortLiveEvent; entry: BinanceShortLiveEvent | null; exit: BinanceShortLiveEvent | null },
  openKeys: Set<string>,
): boolean {
  // 정산된 행은 무조건 아니다. 이 검사를 **먼저** 해야 한다 -- 같은 perp 로 새
  // 포지션이 열려 있으면 openKeys 에 그 perp 가 들어 있어서, 이미 끝난 행까지
  // '열림'(에메랄드)으로 칠해진다. 청산 캐스케이드는 쿨다운 300초로 같은 심볼을
  // 반복 진입하므로 이게 눈에 띄게 나타난다(2026-08-09 TUTUSDT).
  if (!isPositionEntry(row.entry?.action) || row.exit != null) return false;
  // 청산 레인은 알림이 없어 symbol 이 비어 있다 -- openKeys 도 perp 로만 넣는다.
  const lane = rowLane(row);
  // 청산 레인과 후보-롱은 행의 머리가 진입이라 alert.symbol 이 비어 있을 수 있다.
  const sym = (lane === 'liq' || lane === 'gl') ? undefined : (row.alert.symbol || row.entry?.symbol);
  return openKeys.has(openPositionKey(lane, sym, row.alert.perp));
}

type ScoreLedgerDay = {
  day: string;
  has_data?: boolean;
  n?: number;
  avg_net_pct?: number;
  med_net_pct?: number;
  win_rate_pct?: number;
  worst_pct?: number;
  sum_pct?: number;
  mdd?: number;
  worst_eq_pct?: number | null;
};

type ScoreLedgerBook = {
  n?: number;
  avg_net_pct?: number;
  med_net_pct?: number;
  win_rate_pct?: number;
  worst_pct?: number;
  sum_pct?: number;
  mdd?: number;
  worst_eq_pct?: number | null;
  equity?: {
    worst_eq_pct?: number | null;
    days?: { day: string; worst_eq_pct?: number | null }[] | null;
  } | null;
  days?: ScoreLedgerDay[] | null;
} | null | undefined;

type ScoreHealthRaw = {
  up: UpbitPbDaily;
  bh: BhPbDaily;
  v1: LiqV1Daily;
  v2: Liqv2Ladder;
  v3: LiqV3Daily;
  pr: PumpRideDaily;
};

type ScoreHealthHit = { label: string; text: string; value: number; bad?: boolean };

/** 성적 모달. 레인 건강도를 한 표로. 칸 위는 합칠 수 있는 값, 아래는 레인. */
function ScoreLaneHealth({ raw }: { raw: ScoreHealthRaw }) {
  const upBy = new Map(raw.up.days.map((d) => [d.day, d]));
  const bhSrc = (raw.bh.fits?.book?.n ?? 0) > 0 && raw.bh.fits?.book
    ? raw.bh.fits.book : raw.bh;
  const bhBy = new Map((bhSrc.days ?? []).map((d) => [d.day, d]));
  const v1By = new Map(raw.v1.days.map((d) => [d.day, d]));
  const v2By = new Map(raw.v2.days.map((d) => [d.day, d]));
  const v3By = new Map(raw.v3.days.map((d) => [d.day, d]));
  const prBy = new Map(raw.pr.days.map((d) => [d.day, d]));
  const axis = scoreDayAxis();
  const fold = useScoreFold(true);
  const scrollEnd = useScrollEnd(fold.scrollToken(axis.length));
  const num = (v: number, d = 2) => `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;
  const hit = (
    label: string, text: string, value: number, bad?: boolean,
  ): ScoreHealthHit => ({ label, text, value, bad });
  const flag = (
    label: string, ok: boolean | undefined, good: string, bad: string,
  ): ScoreHealthHit | null => (
    ok == null ? null : hit(label, ok ? good : bad, ok ? 1 : 0, ok === false)
  );
  const orderTot = (
    rows: { bucket: string; avg_net_pct?: number; win_rate_pct?: number }[] | undefined,
    bucket: string, label: string, kind: 'avg' | 'win',
  ): ScoreHealthHit | null => {
    const row = (rows ?? []).find((r) => r.bucket === bucket);
    const v = kind === 'avg' ? row?.avg_net_pct : row?.win_rate_pct;
    if (v == null) return null;
    return hit(label, kind === 'avg' ? `${num(v)}%` : `${v.toFixed(0)}%`, v);
  };

  const rows: {
    name: string;
    day: (day: string) => ScoreHealthHit[];
    tot: () => ScoreHealthHit[];
    top: 'sum' | 'max' | 'min' | 'line' | 'verdict';
  }[] = [
    {
      name: u('전제'),
      day: () => [],
      tot: () => [
        flag(u('업숏'), raw.up.health?.pullback_edge_ok, u('되돌림 유지'), u('되돌림 붕괴')),
        flag(u('업숏'), raw.up.health?.timing_ok, u('타이밍 유지'), u('타이밍 밀림')),
        flag(u('빗숏'), bhSrc.health?.timing_ok, u('타이밍 유지'), u('타이밍 늦음')),
        flag(u('빗숏'), bhSrc.health?.tail_ok, u('스탑 정상'), u('스탑 늘음')),
        flag(u('편승'), raw.pr.health?.confirm_edge_ok, u('확인 유지'), u('확인 붕괴')),
        flag(u('편승'), raw.pr.health?.timing_ok, u('타이밍 유지'), u('타이밍 밀림')),
        flag(u('청캐 v2'), raw.v2.health?.order_edge_ok, u('사다리 유지'), u('사다리 역전')),
        flag(u('청캐 v3'), raw.v3.health?.reenter_edge_ok, u('재진입 유지'), u('재진입 역전')),
      ].filter((x): x is ScoreHealthHit => x != null),
      top: 'verdict',
    },
    {
      name: u('트리거'),
      day: (day) => {
        const out: ScoreHealthHit[] = [];
        const a = v1By.get(day);
        const b = v2By.get(day);
        const c = v3By.get(day);
        if (a && a.n_trigger > 0) out.push(hit(u('청캐 v1'), String(a.n_trigger), a.n_trigger));
        if (b && b.n_trigger > 0) out.push(hit(u('청캐 v2'), String(b.n_trigger), b.n_trigger));
        if (c && c.n_trigger > 0) out.push(hit(u('청캐 v3'), String(c.n_trigger), c.n_trigger));
        return out;
      },
      tot: () => [
        raw.v1.health ? hit(u('청캐 v1'), String(raw.v1.health.n_trigger), raw.v1.health.n_trigger) : null,
        raw.v2.health ? hit(u('청캐 v2'), String(raw.v2.health.n_trigger), raw.v2.health.n_trigger) : null,
        raw.v3.health ? hit(u('청캐 v3'), String(raw.v3.health.n_trigger), raw.v3.health.n_trigger) : null,
      ].filter((x): x is ScoreHealthHit => x != null && x.value > 0),
      top: 'sum',
    },
    {
      name: u('감시'),
      day: (day) => {
        const d = prBy.get(day);
        if (!d || d.n_funnel <= 0) return [];
        return [hit(u('편승'), String(d.n_funnel), d.n_funnel)];
      },
      tot: () => (raw.pr.health && raw.pr.health.n_funnel > 0
        ? [hit(u('편승'), String(raw.pr.health.n_funnel), raw.pr.health.n_funnel)] : []),
      top: 'sum',
    },
    {
      name: u('확인률'),
      day: (day) => {
        const d = prBy.get(day);
        if (!d?.has_health) return [];
        return [hit(u('편승'), `${d.confirm_ok_pct.toFixed(0)}%`, d.confirm_ok_pct, d.confirm_ok_pct < 25)];
      },
      tot: () => (raw.pr.health
        ? [hit(u('편승'), `${raw.pr.health.confirm_ok_pct.toFixed(0)}%`, raw.pr.health.confirm_ok_pct, raw.pr.health.confirm_ok_pct < 25)]
        : []),
      top: 'min',
    },
    {
      name: u('진입률'),
      day: (day) => {
        const d = prBy.get(day);
        if (!d?.has_health || d.n_confirm_ok <= 0) return [];
        return [hit(u('편승'), `${d.enter_ok_pct.toFixed(0)}%`, d.enter_ok_pct)];
      },
      tot: () => (raw.pr.health && raw.pr.health.n_confirm_ok > 0
        ? [hit(u('편승'), `${raw.pr.health.enter_ok_pct.toFixed(0)}%`, raw.pr.health.enter_ok_pct)] : []),
      top: 'min',
    },
    {
      name: 'lag',
      day: (day) => {
        const out: ScoreHealthHit[] = [];
        const upDay = upBy.get(day);
        const b = bhBy.get(day);
        const p = prBy.get(day);
        if (upDay?.has_data) out.push(hit(u('업숏'), `${upDay.lag_med_min.toFixed(0)}m`, upDay.lag_med_min, upDay.lag_med_min > 90));
        if (b && b.n > 0) out.push(hit(u('빗숏'), `${b.lag_med_min.toFixed(0)}m`, b.lag_med_min, b.lag_med_min > 60));
        if (p?.has_health && p.lag_med_min > 0) {
          out.push(hit(u('편승'), `${p.lag_med_min.toFixed(0)}m`, p.lag_med_min, p.lag_med_min > 90));
        }
        return out;
      },
      tot: () => [
        raw.up.health ? hit(u('업숏'), `${raw.up.health.lag_med_min.toFixed(0)}m`, raw.up.health.lag_med_min, raw.up.health.lag_med_min > 90) : null,
        bhSrc.health ? hit(u('빗숏'), `${bhSrc.health.lag_med_min.toFixed(0)}m`, bhSrc.health.lag_med_min, bhSrc.health.lag_med_min > 60) : null,
        raw.pr.health && raw.pr.health.lag_med_min > 0
          ? hit(u('편승'), `${raw.pr.health.lag_med_min.toFixed(0)}m`, raw.pr.health.lag_med_min, raw.pr.health.lag_med_min > 90) : null,
      ].filter((x): x is ScoreHealthHit => x != null),
      top: 'max',
    },
    {
      name: u('peak도달'),
      day: (day) => {
        const d = upBy.get(day);
        if (!d?.has_health) return [];
        return [hit(u('업숏'), `${d.peak_ok_pct.toFixed(0)}%`, d.peak_ok_pct)];
      },
      tot: () => (raw.up.health
        ? [hit(u('업숏'), `${raw.up.health.peak_ok_pct.toFixed(0)}%`, raw.up.health.peak_ok_pct)] : []),
      top: 'min',
    },
    {
      name: u('drop성립'),
      day: (day) => {
        const d = upBy.get(day);
        if (!d?.has_health || d.n_peak_ok <= 0) return [];
        return [hit(u('업숏'), `${d.drop_ok_pct.toFixed(0)}%`, d.drop_ok_pct)];
      },
      tot: () => (raw.up.health && raw.up.health.n_peak_ok > 0
        ? [hit(u('업숏'), `${raw.up.health.drop_ok_pct.toFixed(0)}%`, raw.up.health.drop_ok_pct)] : []),
      top: 'min',
    },
    {
      name: u('스탑'),
      day: (day) => {
        const out: ScoreHealthHit[] = [];
        const upDay = upBy.get(day);
        const b = bhBy.get(day);
        if (upDay?.has_data) out.push(hit(u('업숏'), `${upDay.stop_pct.toFixed(0)}%`, upDay.stop_pct, upDay.stop_pct > 25));
        if (b && b.n > 0) out.push(hit(u('빗숏'), `${b.stop_pct.toFixed(0)}%`, b.stop_pct, b.stop_pct > 25));
        return out;
      },
      tot: () => [
        raw.up.health ? hit(u('업숏'), `${raw.up.health.stop_pct.toFixed(0)}%`, raw.up.health.stop_pct, raw.up.health.stop_pct > 25) : null,
        bhSrc.health ? hit(u('빗숏'), `${bhSrc.health.stop_pct.toFixed(0)}%`, bhSrc.health.stop_pct, bhSrc.health.stop_pct > 25) : null,
      ].filter((x): x is ScoreHealthHit => x != null),
      top: 'max',
    },
    {
      name: u('최악비중'),
      day: (day) => {
        const out: ScoreHealthHit[] = [];
        const upDay = upBy.get(day);
        const b = bhBy.get(day);
        if (upDay?.has_data) out.push(hit(u('업숏'), `${upDay.worst_share_pct.toFixed(0)}%`, upDay.worst_share_pct));
        if (b && b.n > 0) out.push(hit(u('빗숏'), `${b.worst_share_pct.toFixed(0)}%`, b.worst_share_pct));
        return out;
      },
      tot: () => [
        raw.up.health ? hit(u('업숏'), `${raw.up.health.worst_share_pct.toFixed(0)}%`, raw.up.health.worst_share_pct) : null,
        bhSrc.health ? hit(u('빗숏'), `${bhSrc.health.worst_share_pct.toFixed(0)}%`, bhSrc.health.worst_share_pct) : null,
      ].filter((x): x is ScoreHealthHit => x != null),
      top: 'max',
    },
    {
      name: u('상위1'),
      day: (day) => {
        const out: ScoreHealthHit[] = [];
        const upDay = upBy.get(day);
        const b = bhBy.get(day);
        if (upDay?.has_data && upDay.top1_share_pct > 0) out.push(hit(u('업숏'), `${upDay.top1_share_pct.toFixed(0)}%`, upDay.top1_share_pct));
        if (b && b.n > 0 && b.top1_share_pct > 0) out.push(hit(u('빗숏'), `${b.top1_share_pct.toFixed(0)}%`, b.top1_share_pct));
        return out;
      },
      tot: () => [
        raw.up.health && raw.up.health.top1_share_pct > 0
          ? hit(u('업숏'), `${raw.up.health.top1_share_pct.toFixed(0)}%`, raw.up.health.top1_share_pct) : null,
        bhSrc.health && bhSrc.health.top1_share_pct > 0
          ? hit(u('빗숏'), `${bhSrc.health.top1_share_pct.toFixed(0)}%`, bhSrc.health.top1_share_pct) : null,
      ].filter((x): x is ScoreHealthHit => x != null),
      top: 'max',
    },
  ];

  const pushPair = (
    nameAvg: string, nameWin: string,
    dayAvg: (day: string) => ScoreHealthHit[],
    dayWin: (day: string) => ScoreHealthHit[],
    totAvg: () => ScoreHealthHit[],
    totWin: () => ScoreHealthHit[],
  ) => {
    rows.push(
      { name: nameAvg, day: dayAvg, tot: totAvg, top: 'line' },
      { name: nameWin, day: dayWin, tot: totWin, top: 'line' },
    );
  };
  const net = (label: string, v?: number | null) => (
    v == null ? null : hit(label, `${num(v)}%`, v, v < 0)
  );
  const win = (label: string, v?: number | null) => (
    v == null ? null : hit(label, `${v.toFixed(0)}%`, v)
  );
  pushPair(
    u('rung1 건당'), u('rung1 승률'),
    (day) => { const d = prBy.get(day); const h = net(u('편승'), d?.rung1_avg); return h ? [h] : []; },
    (day) => { const d = prBy.get(day); const h = win(u('편승'), d?.rung1_win); return h ? [h] : []; },
    () => { const h = net(u('편승'), raw.pr.health?.rung1_avg); return h ? [h] : []; },
    () => { const h = win(u('편승'), raw.pr.health?.rung1_win); return h ? [h] : []; },
  );
  pushPair(
    u('rung2+ 건당'), u('rung2+ 승률'),
    (day) => { const d = prBy.get(day); const h = net(u('편승'), d?.rung2_avg); return h ? [h] : []; },
    (day) => { const d = prBy.get(day); const h = win(u('편승'), d?.rung2_win); return h ? [h] : []; },
    () => { const h = net(u('편승'), raw.pr.health?.rung2_avg); return h ? [h] : []; },
    () => { const h = win(u('편승'), raw.pr.health?.rung2_win); return h ? [h] : []; },
  );
  const gate = (d: { n_trigger?: number } | undefined) => !!d && (d.n_trigger ?? 0) > 0;
  pushPair(
    u('1st 건당'), u('1st 승률'),
    (day) => { const d = v1By.get(day); if (!gate(d)) return []; const h = net(u('청캐 v1'), d?.ord1_avg); return h ? [h] : []; },
    (day) => { const d = v1By.get(day); if (!gate(d)) return []; const h = win(u('청캐 v1'), d?.ord1_win); return h ? [h] : []; },
    () => { const h = orderTot(raw.v1.health?.by_order, '1st', u('청캐 v1'), 'avg'); return h ? [h] : []; },
    () => { const h = orderTot(raw.v1.health?.by_order, '1st', u('청캐 v1'), 'win'); return h ? [h] : []; },
  );
  pushPair(
    u('3rd+ 건당'), u('3rd+ 승률'),
    (day) => { const d = v1By.get(day); if (!gate(d)) return []; const h = net(u('청캐 v1'), d?.ord3_avg); return h ? [h] : []; },
    (day) => { const d = v1By.get(day); if (!gate(d)) return []; const h = win(u('청캐 v1'), d?.ord3_win); return h ? [h] : []; },
    () => { const h = orderTot(raw.v1.health?.by_order, '2nd+', u('청캐 v1'), 'avg'); return h ? [h] : []; },
    () => { const h = orderTot(raw.v1.health?.by_order, '2nd+', u('청캐 v1'), 'win'); return h ? [h] : []; },
  );
  pushPair(
    u('누적<50 건당'), u('누적<50 승률'),
    (day) => { const d = v2By.get(day); if (!gate(d)) return []; const h = net(u('청캐 v2'), d?.ord1_avg); return h ? [h] : []; },
    (day) => { const d = v2By.get(day); if (!gate(d)) return []; const h = win(u('청캐 v2'), d?.ord1_win); return h ? [h] : []; },
    () => { const h = orderTot(raw.v2.health?.by_order, '<50', u('청캐 v2'), 'avg'); return h ? [h] : []; },
    () => { const h = orderTot(raw.v2.health?.by_order, '<50', u('청캐 v2'), 'win'); return h ? [h] : []; },
  );
  pushPair(
    u('50~99 건당'), u('50~99 승률'),
    (day) => { const d = v2By.get(day); if (!gate(d)) return []; const h = net(u('청캐 v2'), d?.ord3_avg); return h ? [h] : []; },
    (day) => { const d = v2By.get(day); if (!gate(d)) return []; const h = win(u('청캐 v2'), d?.ord3_win); return h ? [h] : []; },
    () => { const h = orderTot(raw.v2.health?.by_order, '50-99', u('청캐 v2'), 'avg'); return h ? [h] : []; },
    () => { const h = orderTot(raw.v2.health?.by_order, '50-99', u('청캐 v2'), 'win'); return h ? [h] : []; },
  );
  pushPair(
    u('≥100 건당'), u('≥100 승률'),
    (day) => { const d = v2By.get(day); if (!gate(d)) return []; const h = net(u('청캐 v2'), d?.ord5_avg); return h ? [h] : []; },
    (day) => { const d = v2By.get(day); if (!gate(d)) return []; const h = win(u('청캐 v2'), d?.ord5_win); return h ? [h] : []; },
    () => { const h = orderTot(raw.v2.health?.by_order, '≥100', u('청캐 v2'), 'avg'); return h ? [h] : []; },
    () => { const h = orderTot(raw.v2.health?.by_order, '≥100', u('청캐 v2'), 'win'); return h ? [h] : []; },
  );
  const legDay = (
    pick: (d: LiqV3DailyDay) => number | undefined, kind: 'avg' | 'win',
  ) => (day: string) => {
    const d = v3By.get(day);
    if (!gate(d) || !d) return [];
    const v = pick(d);
    const h = kind === 'avg' ? net(u('청캐 v3'), v) : win(u('청캐 v3'), v);
    return h ? [h] : [];
  };
  const legTot = (idx: number, kind: 'avg' | 'win') => () => {
    const row = raw.v3.health?.by_leg?.[idx];
    const v = kind === 'avg' ? row?.avg_net_pct : row?.win_rate_pct;
    const h = kind === 'avg' ? net(u('청캐 v3'), v) : win(u('청캐 v3'), v);
    return h ? [h] : [];
  };
  pushPair(u('leg1≥20 건당'), u('leg1 승률'),
    legDay((d) => d.leg1_avg, 'avg'), legDay((d) => d.leg1_win, 'win'),
    legTot(0, 'avg'), legTot(0, 'win'));
  pushPair(u('leg2≥35 건당'), u('leg2 승률'),
    legDay((d) => d.leg2_avg, 'avg'), legDay((d) => d.leg2_win, 'win'),
    legTot(1, 'avg'), legTot(1, 'win'));
  pushPair(u('leg3≥50 건당'), u('leg3 승률'),
    legDay((d) => d.leg3_avg, 'avg'), legDay((d) => d.leg3_win, 'win'),
    legTot(2, 'avg'), legTot(2, 'win'));
  pushPair(u('leg4≥65 건당'), u('leg4 승률'),
    legDay((d) => d.leg4_avg, 'avg'), legDay((d) => d.leg4_win, 'win'),
    legTot(3, 'avg'), legTot(3, 'win'));

  const shown = rows.filter((row) => (
    row.tot().length > 0 || axis.some((day) => row.day(day).length > 0)
  ));
  const headline = (mode: typeof rows[number]['top'], hits: ScoreHealthHit[]) => {
    if (hits.length === 0) return null;
    if (mode === 'verdict') {
      const bad = hits.some((h) => h.bad);
      return { text: bad ? u('깨짐') : u('유지'), color: bad ? '#f85149' : '#3fb950' };
    }
    if (mode === 'line') {
      if (hits.length !== 1) return null;
      const h = hits[0];
      const signed = h.text.startsWith('+') || h.text.startsWith('-');
      const color = signed
        ? (h.value >= 0 ? '#3fb950' : '#f85149')
        : (h.bad ? '#f85149' : '#e6dcc3');
      return { text: h.text, color };
    }
    const values = hits.map((h) => h.value);
    const v = mode === 'sum' ? values.reduce((a, x) => a + x, 0)
      : mode === 'max' ? Math.max(...values) : Math.min(...values);
    const sample = hits.find((h) => h.value === v) ?? hits[0];
    const bad = hits.some((h) => h.bad);
    const shownText = mode === 'sum'
      ? String(v)
      : (sample.text.endsWith('m') ? `${v.toFixed(0)}m` : `${v.toFixed(0)}%`);
    return { text: shownText, color: bad ? '#f85149' : (mode === 'sum' ? '#8b949e' : '#e6dcc3') };
  };
  const stack = (top: { text: string; color: string } | null, lines: string[]) => {
    if (!top && lines.length === 0) return '–';
    return (
      <span style={{ display: 'inline-block', maxWidth: '100%' }}>
        {top ? <div style={{ color: top.color }}>{top.text}</div> : null}
        {lines.length > 0 ? (
          <div style={{
            fontSize: '0.55rem', color: '#7d7362',
            lineHeight: 1.2, whiteSpace: 'pre-line',
          }}>{lines.join('\n')}</div>
        ) : null}
      </span>
    );
  };
  const linesOf = (hits: ScoreHealthHit[]) => hits
    .filter((h) => PUMP_RIDE_STACK_VISIBLE || h.label !== u('편승'))
    .map((h) => `${h.label} ${h.text}`);

  return (
    <div style={{
      marginTop: 14, padding: '10px 12px', border: '1px solid #f0b90b44',
      borderRadius: 6, background: '#0f0f0f',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: '#f0b90b', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <ScoreFoldTitle fold={fold}>{u('레인 건강도')}</ScoreFoldTitle>
      </div>
      {!fold.folded && (<>
      {axis.length === 0 ? (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('건강도 적재가 없다.')}</div>
      ) : (
        <ScoreDateFrame
          accent="#f0b90b"
          axis={axis}
          scrollRef={scrollEnd}
          labels={shown.map((row) => row.name)}
          cellAt={(name, day) => {
            const row = shown.find((r) => r.name === name);
            if (!row) return null;
            const hits = row.day(day).filter((h) => PUMP_RIDE_STACK_VISIBLE || h.label !== u('편승'));
            return stack(headline(row.top, hits), linesOf(hits));
          }}
          totAt={(name) => {
            const row = shown.find((r) => r.name === name);
            if (!row) return null;
            const hits = row.tot().filter((h) => PUMP_RIDE_STACK_VISIBLE || h.label !== u('편승'));
            if (hits.length === 0) return '';
            return stack(headline(row.top, hits), linesOf(hits));
          }}
        />
      )}
      </>)}
    </div>
  );
}

/** 성적 모달 맨 아래. 원장 표는 하나고, 칸 안에 합계와 레인별 내역을 같이 쓴다. */
function ScoreLiveLedger() {
  const [raw, setRaw] = React.useState<ScoreHealthRaw | null>(null);
  const [books, setBooks] = React.useState<{
    upbit: ScoreLedgerBook;
    bh: ScoreLedgerBook;
    v1: ScoreLedgerBook;
    v1Known: boolean;
    v2: ScoreLedgerBook;
    v3: ScoreLedgerBook;
    pump: ScoreLedgerBook;
  } | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    Promise.all([
      fetchUpbitPbDaily(0),
      fetchBhPbDaily(0),
      fetchLiqV1Daily(60),
      fetchLiqv2Ladder(0),
      fetchLiqV3Daily(0),
      fetchPumpRideDaily(0),
    ]).then(([up, bh, v1, v2, v3, pr]) => {
      if (!alive) return;
      setRaw({ up, bh, v1, v2, v3, pr });
      setBooks({
        upbit: up.ledger,
        bh: bh.ledger,
        v1: v1.ledger_v1,
        v1Known: v1.ledger_v1 != null,
        v2: v2.ledger,
        v3: v3.ledger
          ? { ...v3.ledger, equity: v3.equity ?? null }
          : null,
        pump: pr.ledger,
      });
    }).catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, []);

  const lanes = books ? [
    { label: u('업숏'), book: books.upbit },
    { label: u('빗숏'), book: books.bh },
    { label: u('청캐 v1'), book: books.v1, missing: !books.v1Known },
    { label: u('청캐 v2'), book: books.v2 },
    { label: u('청캐 v3'), book: books.v3 },
    ...(PUMP_RIDE_STACK_VISIBLE ? [{ label: u('편승'), book: books.pump }] : []),
  ] : [];
  const laneViews = lanes.filter((l) => !l.missing).map((l) => {
    const by = new Map<string, ScoreLedgerDay>();
    for (const d of l.book?.days ?? []) by.set(d.day, { ...d });
    for (const e of l.book?.equity?.days ?? []) {
      const cur = by.get(e.day) ?? { day: e.day };
      if (cur.worst_eq_pct == null && e.worst_eq_pct != null) cur.worst_eq_pct = e.worst_eq_pct;
      by.set(e.day, cur);
    }
    return { label: l.label, book: l.book, by };
  });

  const axis = books ? scoreDayAxis() : [];
  const fold = useScoreFold(true);
  const scrollEnd = useScrollEnd(fold.scrollToken(axis.length));

  const sign = (v: number | null | undefined) =>
    v == null ? '#b7a882' : v >= 0 ? '#3fb950' : '#f85149';
  const eqTone = (v?: number | null) =>
    v == null ? '#3a352c' : (v <= -50 ? '#f85149' : sign(v));
  const bookWorst = (b: ScoreLedgerBook) =>
    (b?.worst_eq_pct != null ? b.worst_eq_pct : (b?.equity?.worst_eq_pct ?? null));
  const num = (v: number | null | undefined, d = 2) =>
    v == null ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;
  const sumN = (xs: { n?: number }[]) => xs.reduce((a, x) => a + (x.n ?? 0), 0);
  const wavg = (xs: { n?: number; v?: number }[]) => {
    const n = sumN(xs);
    if (n <= 0) return null;
    return xs.reduce((a, x) => a + (x.v ?? 0) * (x.n ?? 0), 0) / n;
  };
  const worstOf = (xs: (number | null | undefined)[]) => {
    const vs = xs.filter((v): v is number => v != null && Number.isFinite(v));
    return vs.length ? Math.min(...vs) : null;
  };
  type LaneHit = { label: string; d: ScoreLedgerDay };
  const hitsOn = (day: string): LaneHit[] => laneViews.flatMap((l) => {
    const d = l.by.get(day);
    if (!d?.has_data || !(d.n && d.n > 0)) return [];
    return [{ label: l.label, d }];
  });
  const bookHits = laneViews.filter((l) => (l.book?.n ?? 0) > 0);
  const stack = (
    top: { text: string; color: string } | null,
    lines: string[],
  ) => {
    if (!top && lines.length === 0) return '–';
    return (
      <span style={{ display: 'inline-block', maxWidth: '100%' }}>
        {top ? <div style={{ color: top.color }}>{top.text}</div> : null}
        {lines.length > 0 ? (
          <div style={{
            fontSize: '0.55rem', color: '#7d7362',
            lineHeight: 1.2, whiteSpace: 'pre-line',
          }}>{lines.join('\n')}</div>
        ) : null}
      </span>
    );
  };
  const rows: {
    name: string;
    dayTop: (hits: LaneHit[]) => { text: string; color: string } | null;
    dayLine: (d: ScoreLedgerDay) => string;
    totTop: () => { text: string; color: string } | null;
    totLine: (b: ScoreLedgerBook) => string;
  }[] = [
    {
      name: 'n',
      dayTop: (hits) => ({ text: String(sumN(hits.map((h) => h.d))), color: '#8b949e' }),
      dayLine: (d) => String(d.n ?? 0),
      totTop: () => ({ text: String(sumN(bookHits.map((l) => l.book ?? {}))), color: '#8b949e' }),
      totLine: (b) => String(b?.n ?? 0),
    },
    {
      name: u('건당'),
      dayTop: (hits) => {
        const v = wavg(hits.map((h) => ({ n: h.d.n, v: h.d.avg_net_pct })));
        return v == null ? null : { text: `${num(v)}%`, color: sign(v) };
      },
      dayLine: (d) => `${num(d.avg_net_pct)}%`,
      totTop: () => {
        const v = wavg(bookHits.map((l) => ({ n: l.book?.n, v: l.book?.avg_net_pct })));
        return v == null ? null : { text: `${num(v)}%`, color: sign(v) };
      },
      totLine: (b) => `${num(b?.avg_net_pct)}%`,
    },
    {
      name: u('승률'),
      dayTop: (hits) => {
        const v = wavg(hits.map((h) => ({ n: h.d.n, v: h.d.win_rate_pct })));
        return v == null ? null : {
          text: `${v.toFixed(0)}%`,
          color: v >= 50 ? '#3fb950' : '#f85149',
        };
      },
      dayLine: (d) => `${(d.win_rate_pct ?? 0).toFixed(0)}%`,
      totTop: () => {
        const v = wavg(bookHits.map((l) => ({ n: l.book?.n, v: l.book?.win_rate_pct })));
        return v == null ? null : {
          text: `${v.toFixed(0)}%`,
          color: v >= 50 ? '#3fb950' : '#f85149',
        };
      },
      totLine: (b) => `${(b?.win_rate_pct ?? 0).toFixed(0)}%`,
    },
    {
      name: u('최악'),
      dayTop: (hits) => {
        const v = worstOf(hits.map((h) => h.d.worst_pct));
        return v == null ? null : { text: `${num(v)}%`, color: sign(v) };
      },
      dayLine: (d) => `${num(d.worst_pct)}%`,
      totTop: () => {
        const v = worstOf(bookHits.map((l) => l.book?.worst_pct));
        return v == null ? null : { text: `${num(v)}%`, color: sign(v) };
      },
      totLine: (b) => `${num(b?.worst_pct)}%`,
    },
    {
      name: u('합계'),
      dayTop: (hits) => {
        const v = hits.reduce((a, h) => a + (h.d.sum_pct ?? 0), 0);
        return { text: `${num(v, 1)}%`, color: sign(v) };
      },
      dayLine: (d) => `${num(d.sum_pct, 1)}%`,
      totTop: () => {
        const v = bookHits.reduce((a, l) => a + (l.book?.sum_pct ?? 0), 0);
        return { text: `${num(v, 1)}%`, color: sign(v) };
      },
      totLine: (b) => `${num(b?.sum_pct, 1)}%`,
    },
    {
      name: 'MDD',
      dayTop: (hits) => {
        const v = worstOf(hits.map((h) => h.d.mdd));
        return v == null ? null : { text: `${num(v, 1)}%`, color: sign(v) };
      },
      dayLine: (d) => `${num(d.mdd, 1)}%`,
      totTop: () => {
        const v = worstOf(bookHits.map((l) => l.book?.mdd));
        return v == null ? null : { text: `${num(v, 1)}%`, color: sign(v) };
      },
      totLine: (b) => `${num(b?.mdd, 1)}%`,
    },
    {
      name: u('잔고밀림'),
      dayTop: (hits) => {
        const v = worstOf(hits.map((h) => h.d.worst_eq_pct));
        return v == null ? null : { text: `${num(v, 1)}%eq`, color: eqTone(v) };
      },
      dayLine: (d) => (d.worst_eq_pct == null ? '' : `${num(d.worst_eq_pct, 1)}%eq`),
      totTop: () => {
        const v = worstOf(laneViews.map((l) => bookWorst(l.book)));
        return v == null ? null : { text: `${num(v, 1)}%eq`, color: eqTone(v) };
      },
      totLine: (b) => {
        const v = bookWorst(b);
        return v == null ? '' : `${num(v, 1)}%eq`;
      },
    },
  ];

  return (
    <>
    <div style={{
      marginTop: 14, padding: '10px 12px', border: '1px solid #3fb95044',
      borderRadius: 6, background: '#0f0f0f',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        color: '#3fb950', fontSize: '0.72rem', fontWeight: 600,
      }}>
        <ScoreFoldTitle fold={fold}>{u('원장')}</ScoreFoldTitle>
      </div>
      {!fold.folded && (<>
      {err && <div style={{ color: '#f85149', fontSize: '0.66rem' }}>불러오기 실패: {err}</div>}
      {!err && !books && <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>}
      {books && axis.length === 0 && (
        <div style={{ color: '#b7a882', fontSize: '0.66rem' }}>{u('정산된 원장이 없다.')}</div>
      )}
      {books && axis.length > 0 && (
        <ScoreDateFrame
          accent="#3fb950"
          axis={axis}
          scrollRef={scrollEnd}
          labels={rows.map((row) => row.name)}
          cellAt={(name, day) => {
            const row = rows.find((r) => r.name === name);
            if (!row) return null;
            const hits = (row.name === u('잔고밀림') || row.name === u('잔고최악'))
              ? laneViews.flatMap((l) => {
                const d = l.by.get(day);
                if (d?.worst_eq_pct == null) return [];
                return [{ label: l.label, d }];
              })
              : hitsOn(day);
            const lines = hits
              .map((h) => {
                const t = row.dayLine(h.d);
                return t ? `${h.label} ${t}` : '';
              })
              .filter((t) => t !== '');
            const top = hits.length === 0 ? null : row.dayTop(hits);
            return !top && lines.length === 0 ? '–' : stack(top, lines);
          }}
          totAt={(name) => {
            const row = rows.find((r) => r.name === name);
            if (!row) return null;
            const parts = (row.name === u('잔고밀림') || row.name === u('잔고최악'))
              ? laneViews.filter((l) => bookWorst(l.book) != null)
              : bookHits;
            if (parts.length === 0) return '';
            const lines = parts
              .map((l) => {
                const t = row.totLine(l.book);
                return t ? `${l.label} ${t}` : '';
              })
              .filter((t) => t !== '');
            return stack(row.totTop(), lines);
          }}
        />
      )}
      </>)}
    </div>
    {raw ? <ScoreLaneHealth raw={raw} /> : null}
    </>
  );
}

const SCORE_RULE_GROUPS: { group: string; cols: { key: string; label: string; hint: string }[] }[] = [
  { group: u('사이즈'), cols: [
    { key: 'base', label: 'base', hint: u('구간 10%가 1단. 그 단의 명목, 자본 대비 %.') },
    { key: 'step', label: 'step', hint: u('구간이 10%p 오를 때마다 명목에 더하는 %p. 20%는 2단, 30%는 3단.') },
    { key: 'cap', label: u('캡'), hint: u('그 레인이 쓸 수 있는 증거금 상한. 자본 대비 %. 넘치면 남은 만큼만 들어간다.') },
    { key: 'notional', label: u('명목'), hint: u('포지션 금액이 자본의 몇 %. 업숏·빗숏은 구간 10%·20%·30%의 명목을 적는다. 캡을 넘기면 남은 만큼만 들어간다.') },
    { key: 'k', label: 'k', hint: u('동시에 들고 있는 포지션 개수 상한.') },
    { key: 'legs', label: u('조각'), hint: u('슬리브 하나 안의 최대 조각 수.') },
    { key: 'width', label: u('폭'), hint: u('직전 조각보다 이 % 더 오르면 다음 조각.') },
    { key: 'ratio', label: 'ratio', hint: u('슬리브 크기 비율. 1보다 작으면 앞 슬리브가 더 크고, 합은 캡이다.') },
    { key: 'boost', label: u('고점통과'), hint: u('고점을 통과한 슬리브의 크기 배수.') },
  ] },
  { group: u('보유'), cols: [
    { key: 'hold', label: u('홀드'), hint: u('연장 전 기본 보유 시간.') },
    { key: 'extend', label: u('연장'), hint: u('홀드에 지고 있으면 보유를 이 시간까지 늘린다.') },
    { key: 'recover', label: u('회복'), hint: u('연장 중 이만큼 회복하면 청산.') },
  ] },
  { group: u('탈출'), cols: [
    { key: 'stop', label: u('스탑'), hint: u('가격 손절. 없으면 없음.') },
    { key: 'eqStop', label: u('eq스탑'), hint: u('산시작 대비 잔고밀림 ≤−80%면 전량 시장가 청산. 잔량 0까지 매 사이클 연타. halted.') },
    { key: 'arm', label: u('무장'), hint: u('이 이익부터 고점 추적을 시작한다.') },
    { key: 'dd', label: u('되밀림'), hint: u('무장 뒤 고점에서 이만큼 되밀리면 청산.') },
  ] },
  { group: u('진입'), cols: [
    { key: 'bend', label: u('꺾임'), hint: u('진입 전에 요구하는 되돌림.') },
    { key: 'watch', label: u('감시'), hint: u('되돌림을 기다리는 시간.') },
    { key: 'peak', label: 'peak', hint: u('감시 중 요구하는 고점.') },
    { key: 'ageCap', label: u('고점뒤'), hint: u('고점 뒤 −되돌림까지 이 구간. 15초 미만은 age_floor, 15분 초과는 peak_age.') },
    { key: 'fastStop', label: u('빠른고점'), hint: u('알림 후 10분 안이고 직전 10분 ≥+3% 이며 고점이 +4.5~+6% 이면 뺀다 (fast_stop). 자리는 유지.') },
    { key: 'shallow', label: u('얕은고점'), hint: u('얕은seg·얕은peak·얕은retr·얕은r10 이 모두 맞으면 뺀다.') },
    { key: 'shSeg', label: u('얕은seg'), hint: u('얕은고점의 구간 상승.') },
    { key: 'shPeak', label: u('얕은peak'), hint: u('얕은고점의 고점.') },
    { key: 'shRetr', label: u('얕은retr'), hint: u('얕은고점의 되돌림 비율.') },
    { key: 'shR10', label: u('얕은r10'), hint: u('얕은고점의 직전 10분.') },
    { key: 'seg', label: 'seg', hint: u('구간 상승이 이 이상인 알림만.') },
    { key: 'hours', label: u('시간'), hint: u('진입을 허용하는 시각.') },
    { key: 'catalyst', label: u('촉매'), hint: u('촉매 종목을 진입에서 빼는지. 청캐는 캘린더 창 진입정지.') },
    { key: 'disaster', label: u('재해모드'), hint: u('MED면 숏 flat→LONG trail40→붕괴숏. eq−50 헤지 OFF. eq스탑(−80)은 별칸.') },
    { key: 'stopCool', label: u('스탑후'), hint: u('스탑 이후 다시 안 들어가는 시간.') },
    { key: 'exUp', label: u('업숏제외'), hint: u('업숏 알림 이후 빼는 시간.') },
    { key: 'exLiq', label: u('청캐제외'), hint: u('4시간 창의 청산이 19건 이상이거나 롤 $9만 이상, 또는 알림 뒤 고점 +7.5%인데 되돌림이 그 상승분의 35% 이하.') },
    { key: 'exSpike', label: u('되돌림10분'), hint: u('되돌림 틱의 직전 10분. 넘으면 뺀다.') },
    { key: 'hotFill', label: u('체결10분'), hint: u('체결 시각의 직전 10분. 넘으면 뺀다.') },
    { key: 'confirm', label: u('확인'), hint: u('진입 직전 확인. 편승은 확인에 필요한 상승. 겹롱은 저점 대비 상승.') },
    { key: 'lowWin', label: u('저점'), hint: u('확인 상승을 재는 최저가 구간. 겹롱은 8분.') },
    { key: 'pathRise', label: 'path', hint: u('확인 가격에서 추가로 이만큼 더 올라야 진입.') },
    { key: 'wait', label: u('대기'), hint: u('확인이 보는 시간. 겹롱은 path 가 이 시간 안에 끝나야 한다.') },
    { key: 'ret15', label: 'ret15', hint: u('진입 틱이 직전 15초 마크 대비 이만큼 이상 올랐을 때만. 겹롱은 ≥8%.') },
    { key: 'gap', label: u('간격'), hint: u('같은 종목은 이 시간이 지나야 다시 진입.') },
    { key: 'oi60', label: 'OI', hint: u('진입 직전 60초 미결제약정. 겹롱은 −2% 이하이고 막판 체결이 식으면 뺀다. 녹화가 없으면 남긴다.') },
    { key: 'tape', label: u('대금'), hint: u('직전 5초 거래대금이 그 앞 15초보다 식었는지. OI 조건과 같이 맞을 때만 제외.') },
    { key: 'reject', label: u('역행'), hint: u('그 시간 안 역행이 이 이상이면 진입 취소.') },
    { key: 'cum', label: u('누적'), hint: u('누적청산이 이 건수 이상일 때만 진입.') },
    { key: 'peakGate', label: u('고점확인'), hint: u('진입 전 고점확인을 쓰는지.') },
    { key: 'reenter', label: u('재진입'), hint: u('같은 산에서 다시 들어가는 누적청산 간격.') },
    { key: 'perMtn', label: u('산횟수'), hint: u('산 하나당 최대 진입 횟수.') },
    { key: 'tp', label: 'TP', hint: u('이 이익이면 청산.') },
    { key: 'white', label: u('화이트'), hint: u('이 목록의 종목만.') },
    { key: 'halt', label: u('진입정지'), hint: u('지금 진입이 막혀 있는 종목.') },
  ] },
  { group: u('업숏 책'), cols: [
    { key: 'lossBan', label: u('손실금지'), hint: u('가격%가 −10% 이하로 끝난 뒤 4시간은 그 종목을 다시 안 넣는다.') },
    { key: 'climb15', label: u('15분'), hint: u('바로 다음 구간 알림이 더 높고 15분 안이면 그 건은 안 들어간다. 산 진입에는 안 쓴다.') },
    { key: 'mtnAge', label: u('산나이'), hint: u('저점 대비 +10%로 산이 시작된 뒤 3시간이 지나야 한다. 고점 대비 −20%면 그 산은 끝이다.') },
    { key: 'mtnOi', label: u('산OI'), hint: u('산이 시작된 시각의 미결제약정 대비 +30% 이상.') },
    { key: 'mtnOff', label: u('산아래'), hint: u('미결제약정이 그 산의 고점보다 5%에서 15% 아래.') },
    { key: 'mtnPx', label: u('산가격'), hint: u('가격이 그 산의 고점 12% 안.') },
    { key: 'mtnIn', label: u('산진입'), hint: u('KST 18–24 밖이고 산 조건이 알림 시각에 맞으면 바로 숏. 되돌림을 기다리지 않는다. 빈 자리만.') },
    { key: 'mtnStop', label: u('산스탑'), hint: u('산 숏은 진입가 +25% STOP_MARKET. 그 전에 진입가 +15% 위에서 OI 가 진입 대비 −2% 이하면 시장가 (숏커버 스퀴즈, 2026-10-28). 아니면 210분 홀드.') },
  ] },
  { group: u('자리'), cols: [
    { key: 'seat', label: u('자리'), hint: u('자리가 없거나 다른 책과 겹칠 때의 처리.') },
    { key: 'book', label: u('책'), hint: u('화면 책이 따라가는 쪽.') },
    { key: 'order', label: u('주문'), hint: u('주문 스위치.') },
  ] },
];
const SCORE_RULE_COLS = SCORE_RULE_GROUPS.flatMap((g) => g.cols);

function scoreRungNotional(base: number, step: number): string {
  const at = (rung: number) => Math.round(base + (rung - 1) * step);
  return `10% ${at(1)}% · 20% ${at(2)}% · 30% ${at(3)}%`;
}

function ScoreStackRulesTable({
  liveCfg,
}: {
  liveCfg?: BinanceShortLiveConfig | null;
}) {
  const [rows, setRows] = React.useState<{
    lane: string; color: string; cells: Record<string, string>;
  }[] | null>(null);
  React.useEffect(() => {
    let alive = true;
    const dash = '-';
    const n = (v: number | null | undefined, d = 0) =>
      v == null || !Number.isFinite(Number(v)) ? null : Number(v).toFixed(d);
    const mins = (v: number | null | undefined) => {
      const s = n(v, 0);
      return s == null ? null : `${s}분`;
    };
    const pct = (v: number | null | undefined, d = 0) => {
      const s = n(v, d);
      return s == null ? null : `${s}%`;
    };
    const blank = () => {
      const o: Record<string, string> = {};
      SCORE_RULE_COLS.forEach((c) => { o[c.key] = dash; });
      return o;
    };
    Promise.allSettled([
      fetchUpbitPbDaily(0),
      fetchBhPbDaily(0),
      fetchLiqv2Ladder(),
      fetchLiqV3Daily(),
      fetchPumpRideDaily(0),
    ]).then(([up, bh, v2, v3, pr]) => {
      if (!alive) return;
      const upD = up.status === 'fulfilled' ? up.value : null;
      const bhD = bh.status === 'fulfilled' ? bh.value : null;
      const v2D = v2.status === 'fulfilled' ? v2.value : null;
      const v3D = v3.status === 'fulfilled' ? v3.value : null;
      const prD = pr.status === 'fulfilled' ? pr.value : null;
      const uc = upD?.config;
      const bc = bhD?.config;
      const bhOn = bc?.live === true || liveCfg?.bh_newhigh_live === true;
      const v2c = v2D?.live_plain?.config ?? v2D?.config;
      const pick = (v: number | null | undefined, fb: number) =>
        (v == null || !Number.isFinite(Number(v)) ? fb : Number(v));
      const v2ov = v2c?.overflow_policy ?? 'best_avg';
      const v2ovKo = v2ov === 'best_avg'
        ? u('같은종목 연책(평단 좋은 쪽)')
        : v2ov === 'worst_avg'
          ? u('같은종목 연책(평단 나쁜 쪽)')
          : (v2ov === 'newest' || v2ov === 'oldest')
            ? `같은종목 연책(${v2ov === 'newest' ? u('최신') : u('가장 오래된')} 책)`
            : u('같은종목 연책');
      const holds = ((v2D?.catalyst as Liqv2Catalyst | undefined)?.active_now ?? [])
        .map((h) => h.perp.replace(/USDT$/, '')).join(' · ');
      const v3c = v3D?.config;
      const wlList = (v3c?.whitelist ?? []).map((p) => p.replace(/USDT$/, ''));
      const wl = wlList.length ? `${wlList.length} (${wlList.join(' · ')})` : '';
      const fill = (row: Record<string, string>, key: string, val: string | null | undefined) => {
        if (val && row[key] === dash) row[key] = val;
      };
      const applyBhNote = (row: Record<string, string>, note?: string | null) => {
        if (!note) return;
        note.split('·').forEach((raw) => {
          const p = raw.trim();
          if (!p || p === u('라이브스택') || p === u('라이브스택=주문') || /틱/.test(p)) return;
          const bend = p.match(/^되돌림\s+(.+)$/);
          const hold = p.match(/^홀드\s+(.+)$/);
          const stop = p.match(/^스탑\s+(\d+(?:\.\d+)?%)$/);
          const seg = p.match(/^seg≥\s*(.+)$/);
          const hours = p.match(/^KST\s+(.+)$/);
          const cool = p.match(/^스탑\s*후\s+(.+)$/);
          const exUp = p.match(/^업숏\s*알림\s+(.+)$/);
          const exLiq = p.match(/^청캐\s*발동\s+(.+)$/);
          const spike = p.match(/^직전\s+(.+)$/);
          const ageCapRange = p.match(/^고점뒤\s+(\d+)\s*초\s*~\s*(\d+)\s*분$/);
          const ageCap = p.match(/^고점뒤\s+(\d+)\s*분$/);
          const fast = p.match(/^빠른고점/);
          const sz = p.match(/^(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)\/캡(\d+(?:\.\d+)?)$/);
          const k = p.match(/^k=(\d+)$/);
          if (bend) fill(row, 'bend', bend[1]);
          else if (hold) fill(row, 'hold', /분$/.test(hold[1]) ? hold[1] : `${hold[1]}분`);
          else if (stop) fill(row, 'stop', stop[1]);
          else if (seg) fill(row, 'seg', `≥${seg[1]}`);
          else if (hours) fill(row, 'hours', `KST ${hours[1]}`);
          else if (p === u('촉매')) fill(row, 'catalyst', u('제외'));
          else if (cool) fill(row, 'stopCool', cool[1]);
          else if (exUp) fill(row, 'exUp', exUp[1]);
          else if (exLiq) fill(row, 'exLiq', exLiq[1].replace(/\s*제외$/, ''));
          else if (spike) fill(row, 'exSpike', spike[1].replace(/\s*제외$/, ''));
          else if (ageCapRange) fill(row, 'ageCap', `${ageCapRange[1]}초~${ageCapRange[2]}분`);
          else if (ageCap) fill(row, 'ageCap', `≤${ageCap[1]}분`);
          else if (fast) fill(row, 'fastStop', u('제외'));
          else if (p === u('얕은고점 제외') || p.startsWith(u('얕은고점'))) fill(row, 'shallow', u('제외'));
          else if (sz) {
            fill(row, 'base', sz[1]); fill(row, 'step', sz[2]); fill(row, 'cap', sz[3]);
          } else if (k) fill(row, 'k', k[1]);
        });
      };
      const pc = prD?.config;
      const upRow = blank();
      upRow.base = n(uc?.base_pct) ?? '150';
      upRow.step = n(uc?.step_pct) ?? '0';
      upRow.cap = n(uc?.margin_cap_pct) ?? '100';
      upRow.k = n(uc?.k) ?? '2';
      upRow.hold = u('210분');
      upRow.bend = uc?.drop_pct != null ? `${n(uc.drop_pct, 1)}%` : '1.5%';
      upRow.watch = mins(uc?.watch_min) ?? u('120분');
      upRow.peak = uc?.peak_pct != null ? `${n(uc.peak_pct, 1)}%` : '2%';
      upRow.confirm = u('60초');
      upRow.reject = '1%';
      upRow.stop = '+10%';
      upRow.seg = '≥20';
      upRow.hours = 'KST 18–24';
      upRow.catalyst = u('제외');
      upRow.lossBan = u('−10% · 4시간');
      upRow.climb15 = u('더 높으면 미진입');
      upRow.mtnAge = u('≥3시간');
      upRow.mtnOi = '≥+30%';
      upRow.mtnOff = '5–15%';
      upRow.mtnPx = u('12% 안');
      upRow.mtnIn = u('알림에 바로');
      upRow.mtnStop = u('+25% · +15%&OI−2% 시장가');
      upRow.seat = u('k=2 · 같은 종목 210분');
      upRow.book = u('저녁은 꺾임 · 산은 산진입');
      if (uc?.live === true || liveCfg?.upbit_live === true) upRow.order = 'ON';
      else if (uc?.live === false || liveCfg?.upbit_live === false) upRow.order = 'OFF';
      upRow.notional = scoreRungNotional(Number(upRow.base), Number(upRow.step));
      const bhRow = blank();
      bhRow.base = n(bc?.base_pct) ?? '75';
      bhRow.step = n(bc?.step_pct) ?? '50';
      bhRow.cap = n(bc?.margin_cap_pct) ?? '150';
      bhRow.k = n(bc?.k) ?? dash;
      bhRow.hold = mins(liveCfg?.bh_hold_after_min ?? liveCfg?.bh_hold_exit_min ?? bc?.hold_min) ?? u('90분');
      bhRow.stop = pct(bc?.stop_pct, 0) ?? '6%';
      bhRow.bend = bc?.drop_pct != null ? `${n(bc.drop_pct, 1)}%` : '2.5%';
      bhRow.watch = mins(bc?.watch_min ?? liveCfg?.bh_watch_min) ?? u('120분');
      bhRow.peak = bc?.peak_pct != null ? `${n(bc.peak_pct, 1)}%` : '2%';
      // 화면 책=주문 (peak-age 2026-10-18 · fast_stop/age_floor 2026-10-23).
      // note 파싱 전에 기본값을 둔다.
      bhRow.ageCap = u('15초~15분');
      bhRow.fastStop = u('알림10분·r10+3·고점+4.5~+6');
      bhRow.shallow = u('제외');
      bhRow.shSeg = '≥50';
      bhRow.shPeak = '≥+7%';
      bhRow.shRetr = '<40%';
      bhRow.shR10 = '≥+3%';
      bhRow.book = u('주문');
      applyBhNote(bhRow, bhD?.fits?.note);
      // 빗숏 라이브스택 = 한 계 세 서브전략 (되돌림·급락·익절2).
      bhRow.book = u('되돌림 + 급락(bh_dump·홀드30) + 익절2(r10≤0∧lag≤30·TP2/S6/H90·명목300%) · 전부 주문');
      bhRow.exLiq = u('≥19건·$9만 또는 고점+7.5%·≤35%');
      if (!bhRow.ageCap || bhRow.ageCap === dash) bhRow.ageCap = u('15초~15분');
      if (!bhRow.fastStop || bhRow.fastStop === dash) {
        bhRow.fastStop = u('알림10분·r10+3·고점+4.5~+6');
      }
      if (bhRow.fastStop === u('제외')) {
        bhRow.fastStop = u('알림10분·r10+3·고점+4.5~+6');
      }
      if (!bhRow.shallow || bhRow.shallow === dash) bhRow.shallow = u('제외');
      bhRow.exSpike = '>+5%';
      bhRow.hotFill = '>+5%';
      bhRow.order = bhOn ? 'ON' : 'OFF';
      bhRow.notional = scoreRungNotional(Number(bhRow.base), Number(bhRow.step));
      const v2Row = blank();
      const v2boost = pick(v2c?.peak_boost_mult, 2.5);
      const v2recover = pick(v2c?.recover_pct, 2);
      v2Row.cap = pick(v2c?.margin_cap_pct, 90).toFixed(0);
      v2Row.k = pick(v2c?.k ?? v2c?.slot_k, 5).toFixed(0);
      v2Row.legs = pick(v2c?.max_legs, 6).toFixed(0);
      v2Row.width = `${pick(v2c?.leg_step_pct, 9).toFixed(0)}%`;
      v2Row.ratio = pick(v2c?.sleeve_ratio, 0.7).toFixed(1);
      v2Row.boost = v2boost > 1 ? `×${v2boost.toFixed(1)}` : dash;
      v2Row.hold = mins(v2c?.exit_min) ?? dash;
      v2Row.extend = mins(v2c?.extend_min) ?? dash;
      v2Row.stop = u('없음');
      v2Row.recover = v2recover > 0 ? `+${v2recover.toFixed(0)}%` : dash;
      v2Row.cum = `≥${pick(v2c?.min_cum_nliq, 100).toFixed(0)}`;
      v2Row.peakGate = 'OFF';
      v2Row.halt = holds || dash;
      v2Row.seat = v2ovKo;
      // 라이브스택 상세 — 청캐 v2 재해모드·촉매 (params / live_plain.config).
      if (v2c?.catalyst_hold === false) v2Row.catalyst = 'OFF';
      else if (v2c?.catalyst_hold === true) v2Row.catalyst = u('정지');
      if (v2c?.disaster_med_mode || (v2c?.disaster_med_rule
        && v2c.disaster_med_rule !== 'OFF')) {
        v2Row.disaster = (typeof v2c.disaster_med_rule === 'string'
          && v2c.disaster_med_rule)
          ? v2c.disaster_med_rule
          : 'MED·t40';
      } else if (v2c?.disaster_hedge || (v2c?.disaster_hedge_rule
        && v2c.disaster_hedge_rule !== 'OFF')) {
        v2Row.disaster = (typeof v2c.disaster_hedge_rule === 'string'
          && v2c.disaster_hedge_rule)
          ? v2c.disaster_hedge_rule
          : 'eq−50·2.5x·t40';
      }
      if (v2c?.eq_stop !== false && v2c?.eq_stop_rule !== 'OFF') {
        v2Row.eqStop = (typeof v2c?.eq_stop_rule === 'string' && v2c.eq_stop_rule)
          ? v2c.eq_stop_rule
          : 'eq≤−80·until_flat';
      }
      const v3Row = blank();
      v3Row.cap = pct(v3c?.margin_cap_pct, 0) ?? dash;
      v3Row.notional = pct(v3c?.notional_pct, 0) ?? dash;
      v3Row.k = n(v3c?.k) ?? dash;
      v3Row.hold = mins(v3c?.hold_min) ?? dash;
      v3Row.extend = mins(v3c?.extend_min) ?? dash;
      v3Row.recover = v3c?.recover_pct != null ? `+${n(v3c.recover_pct, 1)}%` : dash;
      v3Row.cum = v3c?.gate_cum != null ? `≥${n(v3c.gate_cum)}` : dash;
      v3Row.reenter = v3c?.reenter_every != null ? `+${n(v3c.reenter_every)}` : dash;
      v3Row.perMtn = v3c?.max_entries != null ? `≤${n(v3c.max_entries)}` : dash;
      v3Row.tp = pct(v3c?.tp_pct, 1) ?? dash;
      v3Row.white = wl || dash;
      v3Row.seat = 'cum≥100→v2';
      if (v3c?.catalyst_hold === false) v3Row.catalyst = 'OFF';
      else if (v3c?.catalyst_hold === true) v3Row.catalyst = u('정지');
      if (v3c?.disaster_med_mode || (v3c?.disaster_med_rule
        && v3c.disaster_med_rule !== 'OFF')) {
        v3Row.disaster = (typeof v3c.disaster_med_rule === 'string'
          && v3c.disaster_med_rule)
          ? v3c.disaster_med_rule
          : 'MED·t40';
      } else if (v3c?.disaster_hedge || (v3c?.disaster_hedge_rule
        && v3c.disaster_hedge_rule !== 'OFF')) {
        v3Row.disaster = (typeof v3c.disaster_hedge_rule === 'string'
          && v3c.disaster_hedge_rule)
          ? v3c.disaster_hedge_rule
          : 'eq−50·2.5x·t40';
      }
      if (v3c?.eq_stop !== false && v3c?.eq_stop_rule !== 'OFF') {
        v3Row.eqStop = (typeof v3c?.eq_stop_rule === 'string' && v3c.eq_stop_rule)
          ? v3c.eq_stop_rule
          : 'eq≤−80·until_flat';
      }
      const prRow = blank();
      prRow.k = n(pc?.k) ?? '∞';
      prRow.hold = mins(pc?.hold_min) ?? dash;
      prRow.stop = pct(pc?.hard_pct, 0) ?? dash;
      prRow.watch = mins(pc?.watch_max) ?? dash;
      prRow.confirm = pc?.confirm_pct != null ? `+${n(pc.confirm_pct, 1)}%` : dash;
      prRow.arm = pct(pc?.trail_arm, 1) ?? dash;
      prRow.dd = pct(pc?.trail_dd, 1) ?? dash;
      const glRow = blank();
      glRow.seg = '≥20';
      glRow.confirm = '+15%';
      glRow.lowWin = u('8분');
      glRow.pathRise = '+2%';
      glRow.wait = u('≤15초');
      glRow.ret15 = '≥8%';
      glRow.gap = u('4시간');
      glRow.oi60 = u('≤−2%/60초');
      glRow.tape = u('식음');
      glRow.hold = u('5분');
      glRow.stop = '5%';
      glRow.arm = '1.5%';
      glRow.dd = '1%';
      glRow.catalyst = u('정지');
      glRow.halt = u('촉매');
      glRow.order = 'OFF';
      glRow.base = '75';
      glRow.step = '50';
      glRow.cap = '100';
      glRow.k = '2';
      glRow.notional = scoreRungNotional(75, 50);
      setRows([
        { lane: u('업숏'), color: EXCHANGE_COLOR.upbit, cells: upRow },
        { lane: u('빗숏'), color: EXCHANGE_COLOR.bithumb, cells: bhRow },
        { lane: u('청캐 v2'), color: LIQ_COLOR, cells: v2Row },
        { lane: u('청캐 v3'), color: LIQ_COLOR, cells: v3Row },
        ...(PUMP_RIDE_STACK_VISIBLE
          ? [{ lane: u('편승'), color: PUMP_RIDE_COLOR, cells: prRow }]
          : []),
        { lane: u('겹롱'), color: PUMP_RIDE_COLOR, cells: glRow },
      ]);
    });
    return () => { alive = false; };
  }, [liveCfg]);
  const td: React.CSSProperties = {
    padding: '6px 8px', borderTop: '1px solid #f0b90b22',
    verticalAlign: 'top', fontSize: '0.66rem', lineHeight: 1.45, color: '#e8dcc0',
    whiteSpace: 'nowrap',
  };
  const pin: React.CSSProperties = {
    position: 'sticky', left: 0, zIndex: 1, background: '#12100c',
  };
  if (!rows) {
    return <div style={{ marginTop: 10, color: '#b7a882', fontSize: '0.66rem' }}>{u('불러오는 중…')}</div>;
  }
  return (
    <div style={{ marginTop: 8 }}>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.66rem' }}>
        <thead>
          <tr style={{ color: '#f0b90b' }}>
            <th rowSpan={2} style={{ ...td, ...pin, borderTop: 'none', textAlign: 'left' }}>{u('레인')}</th>
            {SCORE_RULE_GROUPS.map((g) => (
              <th
                key={g.group}
                colSpan={g.cols.length}
                style={{
                  ...td, borderTop: 'none', textAlign: 'center',
                  borderLeft: '1px solid #f0b90b44', color: '#f0b90b',
                }}
              >{g.group}</th>
            ))}
          </tr>
          <tr style={{ color: '#b7a882' }}>
            {SCORE_RULE_GROUPS.map((g) => g.cols.map((c, i) => (
              <th
                key={c.key}
                style={{
                  ...td, textAlign: 'left',
                  borderLeft: i === 0 ? '1px solid #f0b90b44' : undefined,
                }}
              >{c.label}</th>
            )))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.lane}>
              <td style={{ ...td, ...pin, color: row.color, fontWeight: 600 }}>{row.lane}</td>
              {SCORE_RULE_GROUPS.map((g) => g.cols.map((c, i) => (
                <td
                  key={c.key}
                  style={{
                    ...td,
                    color: row.cells[c.key] === '-' ? '#3a352c' : '#e8dcc0',
                    borderLeft: i === 0 ? '1px solid #f0b90b33' : undefined,
                  }}
                >
                  {row.cells[c.key]}
                </td>
              )))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div style={{ marginTop: 14, overflowX: 'auto', fontSize: '0.66rem' }}>
      <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
        <tbody>
          {SCORE_RULE_GROUPS.map((g) => (
            <React.Fragment key={g.group}>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    color: '#f0b90b', fontWeight: 600, padding: '10px 0 4px',
                    borderTop: '1px solid #f0b90b33',
                  }}
                >{g.group}</td>
              </tr>
              {g.cols.map((c) => (
                <tr key={c.key}>
                  <td style={{
                    color: '#b7a882', whiteSpace: 'nowrap',
                    padding: '2px 16px 2px 0', verticalAlign: 'top',
                  }}>{c.label}</td>
                  <td style={{ whiteSpace: 'nowrap', padding: '2px 0', lineHeight: 1.45 }}>{c.hint}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

function ScoreStackDetail({
  liveCfg, onClose,
}: {
  liveCfg?: BinanceShortLiveConfig | null;
  onClose: () => void;
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={u("라이브스택 상세")}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#12100c',
          border: '1px solid #f0b90b55',
          borderRadius: 8, padding: '14px 16px', maxWidth: 1280, width: '100%',
          margin: '24px 0',
          color: '#e8dcc0', fontSize: '0.78rem', lineHeight: 1.5,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: 4,
        }}>
          <strong style={{ color: '#f0b90b', fontSize: '0.95rem' }}>{u('라이브스택 상세')}</strong>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #f0b90b55', color: '#e8dcc0',
              borderRadius: 4, cursor: 'pointer', padding: '2px 8px',
            }}
          >{u('닫기')}</button>
        </div>
        <ScoreStackRulesTable liveCfg={liveCfg} />
      </div>
    </div>
  );
}

function LaneOrderModal({
  halted, liveCfg, onClose,
}: {
  halted: boolean;
  liveCfg?: BinanceShortLiveConfig | null;
  onClose: () => void;
}) {
  const [stackDetail, setStackDetail] = React.useState(false);
  const stackFold = useScoreFold(true);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (stackDetail) { setStackDetail(false); return; }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, stackDetail]);

  return (
    <>
    <div
      role="dialog"
      aria-modal="true"
      aria-label={u("성적")}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#12100c',
          border: '1px solid #f0b90b55',
          borderRadius: 8, padding: '14px 16px', maxWidth: 860, width: '100%',
          margin: '24px 0', minWidth: 0,
          overflowX: 'hidden',
          color: '#e8dcc0', fontSize: '0.78rem', lineHeight: 1.5,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: 10,
        }}>
          <strong style={{ color: '#f0b90b', fontSize: '0.95rem' }}>{u('성적')}</strong>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #f0b90b55', color: '#e8dcc0',
              borderRadius: 4, cursor: 'pointer', padding: '2px 8px',
            }}
          >{u('닫기')}</button>
        </div>
        {halted ? (
          <div style={{ marginBottom: 8, color: '#f85149', fontSize: '0.72rem' }}>
            전 레인 정지
          </div>
        ) : null}
        <ScoreScrollProvider>
        <div style={{
          marginTop: 14, padding: '10px 12px', border: '1px solid #f0b90b44',
          borderRadius: 6, background: '#0f0f0f',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            color: '#f0b90b', fontSize: '0.72rem', fontWeight: 600,
          }}>
            <ScoreFoldTitle fold={stackFold}>{u('라이브스택')}</ScoreFoldTitle>
            <button
              type="button"
              onClick={() => setStackDetail(true)}
              style={{
                background: 'none', border: '1px solid #f0b90b55', color: '#e8dcc0',
                borderRadius: 4, cursor: 'pointer', padding: '1px 8px',
                fontSize: '0.66rem',
              }}
            >{u('상세')}</button>
          </div>
          {stackFold.folded ? null : (
            <>
              <UpbitPbDailyPanel liveCfg={liveCfg} stackOnly />
              <BhPbDailyPanel liveCfg={liveCfg} stackOnly />
              <Liqv2LadderPanel stackOnly />
              <LiqV3DailyPanel stackOnly />
              {PUMP_RIDE_STACK_VISIBLE ? <PumpRideDailyPanel stackOnly /> : null}
              <GyeopLongStackPanel />
            </>
          )}
        </div>
        <ScoreLiveLedger />
        </ScoreScrollProvider>
      </div>
    </div>
    {stackDetail ? (
      <ScoreStackDetail liveCfg={liveCfg} onClose={() => setStackDetail(false)} />
    ) : null}
    </>
  );
}

function catalystSeedAlarm(seed: Liqv2Catalyst['seed']): string | null {
  const st = seed?.status;
  if (!st || st === 'ok') return null;
  const detail = seed?.detail ? ` — ${seed.detail}` : '';
  if (st === 'failed') return `촉매 시드 조사 실패${detail}`;
  if (st === 'stale') return `촉매 시드 조사 지연 — 마지막 성공 후 30시간 초과${detail}`;
  return `촉매 시드 상태 없음${detail}`;
}

const BinanceAlertShortLivePanel: React.FC = () => {
  const { locale, t } = useDashboardLocale();
  const [data, setData] = useState<BinanceShortLiveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [liqRetuneOpen, setLiqRetuneOpen] = useState(false);
  const [prRetuneOpen, setPrRetuneOpen] = useState(false);
  const [bhRetuneOpen, setBhRetuneOpen] = useState(false);
  const [upRetuneOpen, setUpRetuneOpen] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [laneSettingsOpen, setLaneSettingsOpen] = useState(false);
  const [laneSettings, setLaneSettings] = useState<BinanceLaneSettings | null>(null);
  // 서버가 준 페이로드 나이(초). null 이면 헤더가 없다(옛 서버).
  const [payloadAgeS, setPayloadAgeS] = useState<number | null>(null);

  const loadLaneSettings = useCallback(async () => {
    try {
      const s = await analyserFetchJSON<BinanceLaneSettings>(
        'binance',
        '/alert-binance-short-live/lane-settings',
        { timeoutMs: 15000 },
      );
      setLaneSettings({
        ...s,
        lanes: sortLaneSettingsLanes(s.lanes),
      });
    } catch {
      /* 메인 페이로드 enrich 가 실패했을 때만 이 경로가 살아남는다 */
    }
  }, []);

  const load = useCallback(async () => {
    try {
      // 2026-08-20 유저 결정: 조회 창 2일 -> 7일. 실측 페이로드가 189KB/12s 에서
      // 859KB/13s 로 커진다(둘 다 CloudFront 경유) -- 크기는 4.5배인데 시간은
      // 거의 그대로라 병목은 전송이 아니라 서버 쪽 조립이다. 그래도 여유를
      // 두려고 타임아웃을 25s -> 40s 로 올린다(폴링 주기 30s 보다 길어지지만,
      // load 는 재진입해도 setData 만 하고 겹친 응답은 뒤에 온 것이 이긴다).
      // 2026-08-23: 표·차트 1일. 3일은 프리차트 REST 타임아웃 → 만료 업숏
      // 차트를 빼는 우회가 생김. 창을 줄여 창 안 행은 전부 차트를 붙인다.
      // 2026-09-06: analyser 가 stale 캐시를 바로 주면 40s 대기는 드물어진다.
      // upstream 45s + enrich 여유에 맞춰 FE 한도만 55s 로 맞춤.
      const next = await analyserFetchJSON<BinanceShortLiveResponse>(
        'binance',
        '/alert-binance-short-live?days=1',
        // 페이로드 나이를 같이 받는다. analyser 가 상류(트레이더)를 못 부르면
        // **마지막 성공값**으로 답하는데(캐시), 그걸 모르면 멈춘 화면을 최신으로
        // 읽는다 -- 2026-08-19 에 유저가 닫은 포지션이 화면에 계속 남아 보였다.
        { timeoutMs: 55000, onAgeS: setPayloadAgeS },
      );
      setData(next);
      if (next.lane_settings) {
        setLaneSettings({
          ...next.lane_settings,
          lanes: sortLaneSettingsLanes(next.lane_settings.lanes),
        });
      }
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const patchRetuneAfterApply = useCallback((kind: string) => {
    if (kind === 'cohort') {
      void load();
      return;
    }
    const clearBh = { needs_change: false, verdict: u('반영'), matches_deployed: true };
    setData((d) => {
      if (!d) return d;
      if (kind === 'upbit_pb' || kind === 'upbit_hold') {
        return {
          ...d,
          upbit_retune: d.upbit_retune ? { ...d.upbit_retune, ...clearBh } : d.upbit_retune,
        };
      }
      if (kind === 'bh_pb' || kind === 'bh_hold') {
        return {
          ...d,
          bh_retune: d.bh_retune ? { ...d.bh_retune, ...clearBh } : d.bh_retune,
        };
      }
      if (kind === 'liq_pb' || kind === 'liq_hold' || kind === 'liq_cascade_fade') {
        return {
          ...d,
          liq_retune: d.liq_retune
            ? {
                ...d.liq_retune,
                needs_change: false,
                verdict: u('반영'),
                matches_deployed: kind === 'liq_cascade_fade'
                  ? d.liq_retune.matches_deployed
                  : true,
                matches_deployed_fade: kind === 'liq_cascade_fade'
                  ? true
                  : d.liq_retune.matches_deployed_fade,
              }
            : d.liq_retune,
        };
      }
      if (kind === 'pr') {
        return {
          ...d,
          pump_ride_retune: d.pump_ride_retune
            ? {
                ...d.pump_ride_retune,
                needs_change: false,
                verdict: u('반영'),
                matches_deployed: true,
              }
            : d.pump_ride_retune,
        };
      }
      if (kind === 'dual_short' || kind === 'dual_hold') {
        return {
          ...d,
          dual_retune: d.dual_retune ? { ...d.dual_retune, ...clearBh } : d.dual_retune,
        };
      }
      return d;
    });
    load();
  }, [load]);

  useEffect(() => {
    load();
    loadLaneSettings();
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [load, loadLaneSettings]);

  // 수동 청산 버튼은 **팔 수 있을 때만** 보인다 (2026-08-17). 서버에 패스키가
  // 구성돼 있고 기기가 하나라도 등록돼 있어야 한다 -- 없으면 눌러도 422 라
  // 버튼이 있는 게 거짓말이 된다.
  const [pkStatus, setPkStatus] = useState<PasskeyStatus | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const loadPkStatus = useCallback(() => {
    if (!passkeySupported()) return;
    fetchPasskeyStatus().then(setPkStatus).catch(() => setPkStatus(null));
  }, []);
  useEffect(() => { loadPkStatus(); }, [loadPkStatus]);
  const canClose = !!pkStatus && pkStatus.enabled && pkStatus.enrolled > 0;
  const canEntry = canClose;

  // 수동 진입 직후 모니터 payload 에 open_* 가 아직 없어도 청산 버튼을 띄운다.
  // key = 행 key (같은 perp 의 다른 WATCH 행에 버튼이 새지 않게).
  const [optimisticClose, setOptimisticClose] = useState<
    Map<string, { lane: string; sid: string }>
  >(() => new Map());
  const onManualEntered = useCallback((target: ManualEnteredTarget) => {
    setOptimisticClose((prev) => {
      const next = new Map(prev);
      next.set(target.rowKey, { lane: target.lane, sid: target.sid });
      return next;
    });
  }, []);

  // 빗썸/코인원 알림은 대부분 저장만 하는데 양이 업비트의 수십 배라, 표를 그대로
  // 두면 업비트 판단이 안 보인다 (실측 24시간 1,004건 중 973건이 수집, 상위 30건이
  // 전부 빗썸). 기본값을 켜 둔다.
  const [neededOnly, setNeededOnly] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(NEEDED_ONLY_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const toggleNeededOnly = useCallback(() => {
    setNeededOnly((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(NEEDED_ONLY_KEY, next ? '1' : '0'); } catch { /* 무시 */ }
      return next;
    });
  }, []);

  // "진입(예정)" = 돈이 걸렸거나 곧 걸리는 행만. "필요한 알림만" 이 인입 노이즈를
  // 접는 스위치라면 이건 한 칸 더 좁혀 **판단이 아니라 포지션**만 남긴다.
  const [enteredOnly, setEnteredOnly] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(ENTERED_ONLY_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const toggleEnteredOnly = useCallback(() => {
    setEnteredOnly((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(ENTERED_ONLY_KEY, next ? '1' : '0'); } catch { /* 무시 */ }
      return next;
    });
  }, []);

  const dualShortLive = useMemo(() => mergeNeededOnlyLaneLive(
    { dual_short_live: data?.config?.dual_short_live },
    data?.lane_settings?.lanes,
  ).dual_short_live === true, [data?.config?.dual_short_live, data?.lane_settings?.lanes]);
  const allRows = useMemo(
    () => filterDashboardHiddenLaneRows(groupBinanceAlertRows(
      data?.events ?? [],
      { dualShortLive },
    )),
    [data?.events, dualShortLive],
  );
  // §3-4: 서버는 abort 다리별 집계를 안 준다(의도) -- events 를 group-by 해서 만든다.
  const abortLegs = useMemo(
    () => abortLegBreakdown(data?.events ?? []),
    [data?.events],
  );
  // yaml counterfactual_live. 미전송(옛 모니터)은 표시 유지; 명시 false 만 숨김.
  const cfLive = data?.config?.counterfactual_live !== false;
  // 옛 헤더 '상세' 버튼 내용 -- 부모가 한 번 묶어 각 재판정·설정 모달에 그대로
  // 넘긴다. 모달 안에서 scope 로 자기 레인 몫만 그린다 (2026-09-04 유저).
  const retuneDetail: RetuneDetail = {
    filters: data?.filters ?? null,
    skipReport: cfLive ? (data?.skip_report ?? null) : null,
    flipReport: cfLive ? (data?.flip_report ?? null) : null,
    abortReport: cfLive ? (data?.abort_report ?? null) : null,
    abortLegBreakdown: cfLive ? abortLegs : {},
    newlistingReport: data?.newlisting_report ?? null,
    segmentHighReport: cfLive ? (data?.segment_high_report ?? null) : null,
  };
  // 주 경계(KST 월요일)는 백엔드가 정한다 -- 프론트에서 다시 계산하면 브라우저
  // 타임존에 따라 다른 주를 가리킬 수 있다.
  const thisWeek = data?.pnl_report?.current_week ?? null;

  // "필요한 알림만" = 실제로 판단이 걸려 있는 행. 업비트 알림(심볼 미해석 제외)
  // + 청산 레인. 빗썸/코인원 알림과 심볼 미해석(skip_unresolved)은 인입 노이즈라
  // 접어 둔다. 빗썸에서 남기는 타입은 **후보-롱이 실제로 쓰는 것**뿐이고, 그
  // 목록의 단일 출처는 yaml `cand_long_alert_types` 다(백엔드 config 로 내려온다).
  // 화면이 목록을 재구현하면 yaml 을 바꿔도 화면이 안 따라간다.
  //
  // 주문 스위치 정본은 DB `lane_settings`(2026-09-05). config/bh_stats 는
  // yaml·트레이더 폴백 — enrich 누락·저장 직후 어긋나면 레인은 꺼져 있는데
  // "필요한" 에 ALERT 가 남는다. lane_settings 로 덮는다.
  const neededRows = useMemo(
    () => (neededOnly
      ? filterNeededOnlyRows(
        allRows,
        data?.config?.cand_long_alert_types ?? ['segment_rise'],
        mergeNeededOnlyLaneLive(
          {
            upbit_live: data?.config?.upbit_live,
            cand_long_live: data?.config?.cand_long_live,
            bh_newhigh_live: data?.bh_stats?.live,
            liq_cascade_live: data?.config?.liq_cascade_live,
            liq_cascade_enabled: data?.config?.liq_cascade_enabled,
            pump_ride_live: data?.config?.pump_ride_live,
            pump_ride_exchanges: data?.config?.pump_ride_exchanges,
            dual_short_live: data?.config?.dual_short_live,
          },
          data?.lane_settings?.lanes,
        ),
      )
      : allRows),
    [allRows, neededOnly, data?.config?.cand_long_alert_types,
     data?.config?.upbit_live, data?.config?.cand_long_live, data?.bh_stats?.live,
     data?.config?.liq_cascade_live, data?.config?.liq_cascade_enabled,
     data?.config?.pump_ride_live,
     data?.config?.pump_ride_exchanges,
     data?.config?.dual_short_live,
     data?.lane_settings?.lanes],
  );
  // PENDING 판정이 시간에 걸려 있다(예정시각 +3분). 폴링(30초)마다 data 가 바뀌며
  // 다시 계산되므로 별도 타이머는 두지 않는다 -- 최대 30초 늦게 접힐 뿐이다.
  const alertRows = useMemo(
    () => (enteredOnly
      ? filterEnteredOnlyRows(neededRows, data?.config ?? null)
      : neededRows),
    // data 전체를 dep 으로 둔다(값 하나가 아니라): PENDING 판정이 시각에 걸려
    // 있어 폴링마다 다시 계산돼야 한다.
    [neededRows, enteredOnly, data],
  );
  // 스위치마다 자기가 감춘 건수를 단다 -- 합쳐 세면 어느 스위치가 감췄는지 모른다.
  const hiddenCount = allRows.length - neededRows.length;
  const enteredHiddenCount = neededRows.length - alertRows.length;

  const openKeys = useMemo(() => {
    const shorts = data?.open ?? [];
    const longs = data?.open_long ?? [];
    const bh = data?.open_bh ?? [];
    // 레인별로 따로 키를 만든다 -- 섞으면 같은 심볼에서 서로의 포지션을 자기
    // 것으로 착각한다.
    return new Set([
      ...shorts.map((p) => openPositionKey('upbit', p.symbol, p.perp)),
      // 롱은 자기 레인으로 키잉한다. abort→long 은 빗썸 슬리브에서도 열리는데
      // (abort_long_legs = oi_early/px/px_cont) 전부 'upbit' 으로 찍으면 그 건은
      // 빗썸 알림 행과 키가 안 맞아 '보유중' 이 안 붙는다 -- KRW-BICO
      // 2026-08-08T15:20:20Z 가 그 경우였다. lane 은 open_long 페이로드가 준다.
      ...longs.map((p) => openPositionKey(p.lane === 'bh_sleeve' ? 'bh' : 'upbit', p.symbol, p.perp)),
      ...bh.map((p) => openPositionKey('bh', p.symbol, p.perp)),
      // 청산 레인은 알림이 없어 symbol 이 없다 -- perp 로만 키를 만든다.
      ...(data?.open_liq ?? []).map((p) => openPositionKey('liq', undefined, p.perp)),
      ...(data?.open_pr ?? []).map((p) => openPositionKey('pr', p.symbol, p.perp)),
      // 쌍숏(2026-09-02). 자기 책(open_ds) — 빠지면 진입한 쌍숏 행이 '보유중'
      // 하이라이트도 안 되고 "진입(예정)" 필터에서 사라진다.
      ...(data?.open_ds ?? []).map((p) => openPositionKey('ds', p.symbol, p.perp)),
      ...(data?.open_gl ?? []).map((p) => openPositionKey('gl', undefined, p.perp)),
    ]);
  }, [data?.open, data?.open_long, data?.open_bh, data?.open_liq, data?.open_pr,
    data?.open_ds, data?.open_gl]);

  // 1초 mark: Binance 조회는 perp, 화면 부착은 **행 key**(signal_id 포함).
  // perp 로만 붙이면 같은 심볼의 SKIP/다른 레인 행에도 꼬리가 샌다
  // (2026-08-25 COW 20:15 upbit_prior + 20:05 OPEN).
  //
  // 대상은 **표에 보이는 차트 행**(프리/본차트 또는 OPEN). manualEntry 만
  // 보면 보유창·스킵 종류에 따라 빠져 노란점·% 가 안 붙는다(2026-08-26).
  const liveRowTargets = useMemo(() => {
    const out: { key: string; perp: string }[] = [];
    const seenKey = new Set<string>();
    const cfg = data?.config ?? null;
    const push = (row: (typeof alertRows)[number]) => {
      const perp = row.alert.perp || row.entry?.perp;
      if (!perp || seenKey.has(row.key)) return;
      seenKey.add(row.key);
      out.push({ key: row.key, perp });
    };
    // 프리차트만 있는 행(WATCH/SKIP)은 **자기 가로축이 아직 도는 동안만** 꼬리를
    // 붙인다. 축은 rowChart 와 같은 식이다 -- 알림 + (대기 + 홀드).
    // 무조건 붙이면 감시·홀드가 끝난 SKIP 행이 1초 폴링 대상으로 계속 남고,
    // rowChart 의 `needMin`(꼬리가 창 밖이면 축을 연다)이 축을 **지금**까지
    // 늘려서 끝난 차트가 몇 시간째 갱신된다 (2026-09-08 유저: "감시/홀드 시간이
    // 지났는데도 차트가 계속 갱신"). 폴링 perp 상한(24)도 끝난 행이 먹는다.
    // 아직 살아 있는 감시행은 아래 isLive*Watch / pendingEntryHm 분기가 따로
    // 잡으므로 여기서 빠져도 노란점·%를 잃지 않는다.
    const preChartRunning = (row: (typeof alertRows)[number]) => {
      const ms = row.alert.time_kst ? kstToMs(row.alert.time_kst) : NaN;
      if (!Number.isFinite(ms)) return false;
      const hold = plannedEntryHoldMin(row, cfg ?? undefined);
      const wait = row.alert.pre_chart_window_min ?? hold;
      // 여유를 두지 않는다 — 축 밖 점은 sparkTimeX 가 오른쪽 끝에 붙여 놔
      // 선이 끝에서 세로로만 꿈틀거리고, needMin 이 축을 그만큼 또 연다.
      return Date.now() <= ms + (wait + hold) * 60_000;
    };
    const hasChart = (row: (typeof alertRows)[number]) => {
      if ((row.entry?.chart?.length ?? 0) >= 2) return true;
      if ((row.alert.pre_chart?.length ?? 0) >= 2) return preChartRunning(row);
      return false;
    };
    for (const row of alertRows) {
      if (isOpenRow(row, openKeys)) push(row);
    }
    for (const row of alertRows) {
      if (isOpenRow(row, openKeys)) continue;
      if (row.exit) continue;
      if (manualEntryTarget(row, cfg)) {
        push(row);
        continue;
      }
      if (hasChart(row)) {
        push(row);
        continue;
      }
      if (row.entry) continue;
      const delay = cfg?.entry_delay_min;
      if (isLiveClWatch(row.alert) || isLiveLiqWatch(row.alert)
          || isLivePrWatch(row.alert, Date.now(), cfg?.pump_ride_exchanges)
          || isLiveDsWatch(row.alert)
          || isLiveBhPending(row.alert, delay)
          || ((row.alert.exchange ?? 'upbit') === 'upbit'
            && pendingEntryHm(row.alert, delay) != null)) {
        push(row);
      }
    }
    return out;
  }, [alertRows, openKeys, data?.config]);

  const watchLivePerps = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const t of liveRowTargets) {
      if (seen.has(t.perp)) continue;
      seen.add(t.perp);
      out.push(t.perp);
      if (out.length >= WATCH_LIVE_MAX_PERPS) break;
    }
    return out;
  }, [liveRowTargets]);
  const watchLiveKey = `${watchLivePerps.join(',')}|${liveRowTargets.map((t) => t.key).join(',')}`;

  const [liveTails, setLiveTails] = useState<Record<string, [number, number][]>>({});
  // liveRowTargets 는 30초 폴링마다 새 배열이라 effect dep 에 넣으면 인터벌이
  // 리셋된다. 키 문자열이 같을 땐 같은 대상으로 보고, 최신 배열만 ref 로 읽는다.
  const liveRowTargetsRef = useRef(liveRowTargets);
  liveRowTargetsRef.current = liveRowTargets;
  useEffect(() => {
    if (watchLivePerps.length === 0) {
      setLiveTails({});
      return;
    }
    const polled = new Set(watchLivePerps);
    let cancelled = false;
    const tick = async () => {
      try {
        const q = watchLivePerps.map((p) => `perp=${encodeURIComponent(p)}`).join('&');
        const next = await analyserFetchJSON<{
          ok?: boolean;
          marks?: Record<string, { mark_px?: number; mark_ts?: number; err?: string }>;
        }>('binance', `/alert-binance-short-live/watch-live?${q}`, { timeoutMs: 3000 });
        if (cancelled || !next?.marks) return;
        const now = Date.now();
        const targets = liveRowTargetsRef.current;
        setLiveTails((prev) => {
          const out: Record<string, [number, number][]> = {};
          for (const t of targets) {
            if (!polled.has(t.perp)) continue;
            const series = [...(prev[t.key] ?? [])];
            const m = next.marks![t.perp];
            if (m && m.mark_px != null && m.mark_px > 0 && !m.err) {
              // 가로축은 벽시계로 민다 — Binance mark_ts 가 분봉 끝보다 뒤처지면
              // 꼬리가 안 붙고( merge 가 lastT 이후만 받음 ) 화면도 안 움직인다.
              const ts = now;
              const last = series[series.length - 1];
              if (!last || last[0] < ts) series.push([ts, m.mark_px]);
              else series[series.length - 1] = [ts, m.mark_px];
            }
            out[t.key] = series.filter((pt) => pt[0] >= now - WATCH_LIVE_KEEP_MS);
          }
          return out;
        });
      } catch {
        /* 1초 폴링 실패는 표 전체를 안 죽인다 */
      }
    };
    tick();
    const id = window.setInterval(tick, WATCH_LIVE_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [watchLiveKey, watchLivePerps]);

  // 프리차트가 한 폴링에서 빠지면(REST 예산) 차트가 통째로 비었다가 다음 폴링에
  // 다시 생긴다. 행 key 로 마지막 성공 선을 붙잡아 깜빡임을 막는다 (2026-08-26).
  const stickyPreRef = useRef<Record<string, {
    pre_chart: [number, number][];
    pre_chart_px?: number;
    pre_chart_window_min?: number;
  }>>({});
  const rowForChart = useCallback((row: (typeof alertRows)[number]) => {
    const a = row.alert;
    if (a.pre_chart && a.pre_chart.length >= 2) {
      stickyPreRef.current[row.key] = {
        pre_chart: a.pre_chart,
        pre_chart_px: a.pre_chart_px,
        pre_chart_window_min: a.pre_chart_window_min,
      };
      return row;
    }
    const sticky = stickyPreRef.current[row.key];
    if (!sticky) return row;
    return {
      ...row,
      alert: {
        ...a,
        pre_chart: sticky.pre_chart,
        pre_chart_px: sticky.pre_chart_px ?? a.pre_chart_px,
        pre_chart_window_min: sticky.pre_chart_window_min ?? a.pre_chart_window_min,
      },
    };
  }, []);

  // 수동 청산 버튼이 지목할 sid (2026-08-17, 5개 레인 전부).
  //
  // 키는 **openKeys 와 같은 (레인|심볼|perp)** 다. perp 만으로 키잉하면 업비트 숏과
  // 빗썸 슬리브가 같은 심볼을 동시에 들 때 서로의 sid 를 집어 **엉뚱한 포지션을
  // 판다** -- 레인이 풀을 분리한 이유가 그것이고, openKeys 가 레인을 포함하는 이유와
  // 같다. 값에 lane 도 같이 담는다: 요청은 lane+sid 쌍이라야 서버가 버킷을 찾는다.
  const closeTargets = useMemo(() => {
    const m = new Map<string, { lane: string; sid: string }>();
    const put = (lane: 'upbit' | 'bh' | 'liq' | 'cl' | 'pr' | 'ds' | 'gl', apiLane: string,
                 symbol: string | undefined, perp: string | undefined,
                 sid: string | undefined) => {
      if (!sid) return;
      m.set(openPositionKey(lane, symbol, perp), { lane: apiLane, sid });
    };
    for (const p of data?.open ?? []) put('upbit', 'upbit', p.symbol, p.perp, p.sid);
    for (const p of data?.open_bh ?? []) put('bh', 'bh', p.symbol, p.perp, p.sid);
    // 청산 레인은 알림이 없어 symbol 이 비어 있다 -- openKeys 도 perp 로만 넣는다.
    for (const p of data?.open_liq ?? []) put('liq', 'liq', undefined, p.perp, p.sid);
    for (const p of data?.open_pr ?? []) put('pr', 'pr', p.symbol, p.perp, p.sid);
    // 쌍숏(2026-09-02) — 수동 청산 대상(ds_settle_process 의 manual_close_take "ds").
    for (const p of data?.open_ds ?? []) put('ds', 'ds', p.symbol, p.perp, p.sid);
    for (const p of data?.open_gl ?? []) put('gl', 'gl', undefined, p.perp, p.sid);
    // abort->롱은 자기 레인으로 키잉한다(openKeys 와 같은 규칙): 빗썸 슬리브에서
    // 열린 롱은 'bh' 키를 써야 그 행과 맞는다.
    for (const p of data?.open_long ?? []) {
      put(p.lane === 'bh_sleeve' ? 'bh' : 'upbit', 'long', p.symbol, p.perp, p.sid);
    }
    return m;
  }, [data?.open, data?.open_bh, data?.open_liq, data?.open_pr, data?.open_long,
    data?.open_ds, data?.open_gl]);
  // 2026-08-08 되살림: abort→long 이 라이브에서 실제로 열리는데(08-07 XEC,
  // 08-08 BICO) 화면에 아무것도 안 떴다 -- 백엔드는 open_long 을 내보내고 있었고
  // 이 블록만 주석이었다. 판정해야 할 규칙이 화면에서 안 보이던 상태.
  const openLong = data?.open_long ?? [];
  // watching / longStats 는 아래 블록이 아직 주석이라 같이 두면 미사용이 된다.
  // const watching = data?.watching ?? [];
  // const longStats = data?.long_stats ?? {};
  const cfg = data?.config ?? {};
  const risk = data?.risk ?? {};
  const bh = data?.bh_stats;
  const pr = data?.pump_ride_stats;
  // cfg.leverage is the REQUESTED value -- per-symbol liquidation-safety math
  // caps each real short lower (safe_leverage_for_symbol), so show what
  // actually filled recently, not the config knob nobody's trade used as-is.
  const leverageLabel = (() => {
    const { leverage_effective_min: lo, leverage_effective_max: hi } = cfg;
    if (lo != null && hi != null) return lo === hi ? `${lo}x` : `${lo}-${hi}x`;
    return cfg.leverage != null ? `~${cfg.leverage}x` : null;
  })();

  return (
    <div
      className="binance-alert-panel"
      style={{
        padding: '14px 16px',
        borderRadius: 8,
        border: '1px solid #f0b90b66',
        background: 'linear-gradient(135deg, rgba(240,185,11,0.08) 0%, rgba(0,0,0,0.35) 100%)',
      }}
    >
      {/* 기기 등록 (2026-08-17). 두 겹으로 숨는다: 서버가 등록 창을 열어 뒀고
          (enroll_open) **그리고** 헤더의 u('패스키') 버튼을 눌렀을 때만. 관리 동작이라
          평소 화면을 차지하면 안 된다. */}
      {pkStatus?.enroll_open && enrollOpen && (
        <PasskeyEnrollBox status={pkStatus} onDone={loadPkStatus} />
      )}
      {weeklyOpen ? (
        <PnlModal report={data?.pnl_report ?? {}} onClose={() => setWeeklyOpen(false)} />
      ) : null}
      {scoreOpen ? (
        <LaneOrderModal
          halted={data?.halted === true}
          liveCfg={data?.config}
          onClose={() => setScoreOpen(false)}
        />
      ) : null}
      {upRetuneOpen && data?.upbit_retune ? (
        <BhRetuneModal r={data.upbit_retune} lane={u("업숏")}
                       onClose={() => setUpRetuneOpen(false)}
                       onApplied={patchRetuneAfterApply}
                       detail={retuneDetail}
                       liveCfg={data.config} />
      ) : null}
      {bhRetuneOpen && data?.bh_retune ? (
        <BhRetuneModal r={data.bh_retune} onClose={() => setBhRetuneOpen(false)}
                       onApplied={patchRetuneAfterApply}
                       detail={retuneDetail}
                       liveCfg={data.config} />
      ) : null}
      {PUMP_RIDE_STACK_VISIBLE && prRetuneOpen && data?.pump_ride_retune ? (
        <PumpRideRetuneModal r={data.pump_ride_retune}
                             onClose={() => setPrRetuneOpen(false)}
                             onApplied={patchRetuneAfterApply}
                             detail={retuneDetail} />
      ) : null}
      {liqRetuneOpen && data?.liq_retune ? (
        <LiqRetuneModal r={data.liq_retune} onClose={() => setLiqRetuneOpen(false)}
                        onApplied={patchRetuneAfterApply}
                        detail={retuneDetail}
                        ladderV2Live />
      ) : null}
      {laneSettingsOpen && laneSettings ? (
        <LaneSettingsModal
          initial={laneSettings}
          canSave={pkStatus?.enabled === true && (pkStatus?.enrolled ?? 0) > 0}
          onClose={() => setLaneSettingsOpen(false)}
          detail={retuneDetail}
          onSaved={(next) => {
            setLaneSettings(next);
            // 설정 저장 직후 config/타이밍도 같이 맞춘다. lane_settings 만
            // 바꾸면 폴링(≤30s) 전까지 "필요한"·레인박스가 옛 ON 을 본다
            // (2026-09-05 두들스 ALERT).
            setData((d) => {
              if (!d) return d;
              const liveById: Record<string, boolean> = {};
              for (const l of next.lanes) liveById[l.id] = l.live;
              const cfg = { ...(d.config ?? {}) };
              if ('upbit' in liveById) cfg.upbit_live = liveById.upbit;
              if ('cand_long' in liveById) cfg.cand_long_live = liveById.cand_long;
              if ('liq' in liveById) cfg.liq_cascade_live = liveById.liq;
              if ('pump_ride' in liveById) cfg.pump_ride_live = liveById.pump_ride;
              if ('dual_short' in liveById) cfg.dual_short_live = liveById.dual_short;
              const bh_stats = d.bh_stats && ('bh' in liveById)
                ? { ...d.bh_stats, live: liveById.bh }
                : d.bh_stats;
              const lane_timing = d.lane_timing
                ? {
                  ...d.lane_timing,
                  lanes: (d.lane_timing.lanes ?? []).map((row) => (
                    row.lane in liveById
                      ? { ...row, live: liveById[row.lane] }
                      : row
                  )),
                }
                : d.lane_timing;
              return {
                ...d,
                lane_settings: next,
                config: cfg,
                bh_stats,
                lane_timing,
              };
            });
          }}
        />
      ) : null}
      <div className="binance-alert-header">
        {/* 제목과 버튼을 같은 줄에 둔다. 부제를 옆에 두면 버튼이 폭을 뺏어
            부제가 더 많이 개행되고, 패널이 세로로 길어진다(모바일·1fr 칼럼). */}
        <div className="binance-alert-header-top">
          <h3 style={{ ...binanceH3, margin: 0 }}>Perpetual Live</h3>
          <div className="binance-alert-header-actions">
            {/* 버튼: 성적 → 차트. 설정·재판정은 헤더에서 뺐다 (2026-10-11). */}
            <button
              type="button"
              className={`binance-alert-refresh${catalystSeedAlarm(data?.catalyst_seed) ? ' binance-liq-retune-alert' : ''}`}
              onClick={() => setScoreOpen(true)}
              aria-label={u("성적")}
              title={catalystSeedAlarm(data?.catalyst_seed) ?? undefined}
            >
              <span className="binance-alert-action-full">{u('성적')}</span>
              <span className="binance-alert-action-short">{u('성적')}</span>
            </button>
            {/* 차트 — 오늘 업비트 알림 심볼의 24시간 1분봉(별도 페이지).
                react-router 의 <Link> 를 **쓰지 않는다**: react-router-dom v7 은
                ESM exports 라 CRA 의 jest resolver 가 이 모듈을 못 풀고, 이 파일을
                import 하는 테스트 3개가 통째로 깨진다. 평범한 <a> 로 넘어간다
                (경로 라우팅은 SPA fallback 이 이미 처리한다 -- /dashboard 직접
                진입과 같은 경로다). */}
            <a
              href={binanceChartsPath(locale)}
              className="binance-alert-refresh"
              style={{ textDecoration: 'none' }}
              title={u("오늘 업비트 알림이 온 심볼들의 24시간 1분봉 (30초 갱신)")}
              aria-label={u("알림 심볼 차트")}
            >
              <span className="binance-alert-action-full">{u('차트')}</span>
              <span className="binance-alert-action-short">{u('차트')}</span>
            </a>
            {/* 패스키 기기 등록 (2026-08-17). 등록 창이 열려 있을 때만 나타나고,
                눌러야 상자가 펴진다 -- 평소 화면을 차지하면 안 되는 관리 동작이다. */}
            {pkStatus?.enroll_open && (
              <button
                type="button"
                className="binance-alert-refresh"
                onClick={() => setEnrollOpen((v) => !v)}
                title={`패스키 기기 등록 · 등록됨 ${pkStatus.enrolled}`}
                aria-label={u("패스키 기기 등록")}
              >
                패스키
              </button>
            )}
            {/* u('상세') 버튼은 제거 (2026-09-04 유저): 필터 목록·반사실 리포트는
                각 재판정 모달과 설정 모달 맨 아래(LaneDetail)로 옮겼다. HALTED 만
                여기 배지로 남긴다 -- 주문 정지는 버튼 유무와 무관하게 보여야 한다. */}
            {data?.halted ? (
              <span
                className="binance-alert-refresh"
                style={{ color: '#f85149', fontWeight: 700, cursor: 'default' }}
                title={u("주문 정지 (HALTED)")}
              >
                HALTED
              </span>
            ) : null}
            {/* 새로고침 버튼 **일시 숨김** (2026-08-31 유저 지시).
                지우지 않고 플래그로 접는다 -- 페이로드는 analyser 가 25초 캐시하고
                (liveProxyCacheTTL) 화면은 30초마다 스스로 폴링하므로, 눌러도 대개
                같은 값이 돌아온다. 되살리려면 이 상수만 true 로. */}
            {SHOW_REFRESH_BUTTON ? (
              <button
                type="button"
                className="binance-alert-refresh binance-alert-refresh--reload"
                onClick={() => { setLoading(true); load(); }}
                title={t('common.refresh')}
                aria-label={t('common.refresh')}
              >
                <span className="binance-alert-action-full">{t('common.refresh')}</span>
                <span className="binance-alert-action-short">
                  <RefreshIcon />
                </span>
              </button>
            ) : null}
          </div>
        </div>
        <div className="binance-alert-subtitle" style={{ color: binanceMuted, fontSize: '0.72rem', lineHeight: 1.45 }}>
          {t('binance.subtitle.line1')}
        </div>
      </div>

      {loading && !data && <div style={{ color: binanceMuted, fontSize: '0.78rem' }}>{t('common.loading')}</div>}
      {error && (
        <div style={{ color: '#f85149', fontSize: '0.78rem', marginBottom: 8 }}>{error}</div>
      )}

      {data && (
        <>
          <div className="binance-alert-stats">
            {/* 랩탑: 자본 숫자 | 레짐 표 가 한 줄, 차트는 그 아래 전폭.
                모바일: 레짐은 별도 칸 (embed 숨김). */}
            <div className="binance-stat-item binance-stat-item--full binance-stat-item-chart binance-stat-item--equity">
              <div className="binance-equity-top">
                <div className="binance-equity-pair">
                <div
                  className="binance-equity-meta"
                  role="button"
                  tabIndex={0}
                  onClick={() => setWeeklyOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setWeeklyOpen(true); }
                  }}
                  style={{ cursor: 'pointer' }}
                  title={t('binance.stat.weekPnl.title')}
                >
                  <div className="binance-equity-lines">
                    <div className="binance-transfer-row binance-phone-row">
                      <span className="binance-stat-label">Equity</span>
                      <span className="binance-stat-value">
                        {data.equity_usdt != null ? `${data.equity_usdt.toFixed(2)} USDT` : '–'}
                      </span>
                    </div>
                    <div
                      className="binance-transfer-row binance-phone-row"
                      title={t('binance.stat.available.title')}
                    >
                      <span className="binance-stat-label">{t('binance.stat.available')}</span>
                      <span className="binance-stat-value">
                        {data.available_usdt != null ? `${data.available_usdt.toFixed(2)} USDT` : '–'}
                      </span>
                    </div>
                    <div className="binance-transfer-row binance-phone-row">
                      <span className="binance-stat-label">{t('binance.stat.weekPnl')}</span>
                      <span style={{
                        color: thisWeek == null ? binanceMuted
                          : thisWeek.pnl_usdt >= 0 ? '#3fb950' : '#f85149',
                      }}>
                        {thisWeek == null ? '–' : `${thisWeek.pnl_usdt >= 0 ? '+' : ''}${thisWeek.pnl_usdt.toFixed(2)}`}
                        {thisWeek != null && (
                          <span style={{ color: binanceMuted }}>
                            {' · '}{t('binance.stat.weekPnl.tradeCount', { n: thisWeek.n })}
                          </span>
                        )}
                      </span>
                    </div>
                    {(() => {
                      const tr = data.transfer_report;
                      const amt = (v: number, sign: '+' | '-') => (
                        sign === '+'
                          ? `+${v.toFixed(2)}`
                          : (v > 0 ? `-${v.toFixed(2)}` : v.toFixed(2))
                      );
                      return (
                        <div
                          className="binance-transfer-row binance-phone-row"
                          title={t('binance.stat.transfer.title')}
                        >
                          <span className="binance-stat-label">{t('binance.stat.transfer')}</span>
                          {tr == null ? (
                            <span style={{ color: binanceMuted }}>–</span>
                          ) : (
                            <span>
                              <span style={{
                                color: tr.in_usdt > 0 ? '#3fb950' : binanceMuted,
                              }}>
                                {amt(tr.in_usdt, '+')}
                              </span>
                              <span style={{ color: binanceMuted }}> / </span>
                              <span style={{
                                color: tr.out_usdt > 0 ? '#f85149' : binanceMuted,
                              }}>
                                {amt(tr.out_usdt, '-')}
                              </span>
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {/* 구간 시작 대비 잔고밀림% — 청캐 표와 같은 축 (2026-10-18). */}
                    <div
                      className="binance-transfer-row binance-phone-row"
                      title={t('binance.stat.drawdown.title')}
                    >
                      <span className="binance-stat-label">{t('binance.stat.drawdown')}</span>
                      <span style={{
                        color: (() => {
                          const v = data.equity_drawdown_pct;
                          if (v == null) return binanceMuted;
                          if (v <= -35) return '#f85149';
                          if (v <= -15) return '#d29922';
                          if (v < 0) return '#b7a882';
                          return '#3fb950';
                        })(),
                      }}>
                        {data.equity_drawdown_pct == null
                          ? '–'
                          : `${data.equity_drawdown_pct >= 0 ? '+' : ''}${data.equity_drawdown_pct.toFixed(1)}%`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="binance-equity-transfers">
                  <PhoneStat nf={data.notifier} />
                  <TraderStat trader={data.trader} ageS={payloadAgeS} failed={error != null} />
                  <HostStat host={data.host} />
                </div>
                </div>
                <div
                  className="binance-regime-embed"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <RegimeStat weeks={data.regime_report ?? null} embedded />
                </div>
              </div>
              <div
                className="binance-stat-item-chart-body"
                role="button"
                tabIndex={0}
                onClick={() => setWeeklyOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setWeeklyOpen(true); }
                }}
                style={{ marginTop: 4, cursor: 'pointer' }}
                title={t('binance.stat.weekPnl.title')}
              >
                <BinanceEquityCurveChart curve={data.equity_curve} />
              </div>
            </div>
            <div className="binance-regime-standalone">
              <RegimeStat weeks={data.regime_report ?? null} />
            </div>
            {/* 보유/k · 실현손익 · lev/노셔널 · 타이밍을 한 표로.
                합계 행은 실현손익 헤더를 눌러야 나온다 -- 레인 비교가 먼저다.
                2026-08-14: 박스 전체에 걸려 있던 title 을 뺐다(유저 지시) --
                네 칸 설명을 이어붙인 문장이라 표 어디에 마우스를 올려도 떴다. */}
            <div className="binance-stat-item binance-stat-item--full">
              {(() => {
                const r = data.liq_report;
                const levTxt = leverageLabel ?? '–';
                // 업숏 lev 도 레버만 (2026-09-01). 되돌림 셀은 필터 카드가 보여준다
                // — 편승과 같은 자리(08-31 유저: "규칙은 필터 카드가 이미 보여준다").
                const bhLev = (() => {
                  if (!bh?.enabled) return '–';
                  const lo = bh.leverage_effective_min;
                  const hi = bh.leverage_effective_max;
                  if (lo != null && hi != null) {
                    return lo === hi ? `${lo}x` : `${lo}-${hi}x`;
                  }
                  // 체결이 없어도 업숏과 같이 설정 레버를 보여 준다.
                  return cfg.leverage != null ? `~${cfg.leverage}x` : '–';
                })();
                const eq = data.equity_usdt ?? 0;
                // 청캐 명목은 서버가 주문과 같은 함수로 낸 첫 조각.
                // 여기서 risk/tail 을 다시 곱하면 4.5/6 이 자본의 75% 가 되어
                // 업숏·빗숏과 같은 달러가 찍힌다.
                const liqNotl = data.liq_stats?.ladder_v2?.notional_usdt;
                // 편승: 옛 청캐와 같은 식 (2026-08-27 유저: "청캐랑 똑같이").
                const prRisk = cfg.pump_ride_risk_pct ?? 0;
                const prTail = (cfg.pump_ride_tail_pct ?? LIQ_TAIL_PCT_FALLBACK) / 100;
                const prCap = cfg.pump_ride_max_notional_usd ?? 0;
                const prFixed = cfg.pump_ride_notional_usdt ?? 0;
                const prRaw = prRisk > 0 && eq
                  ? eq * (prRisk / 100) / Math.max(prTail, 1e-9)
                  : (prFixed > 0 ? prFixed : null);
                const prNotl = prRaw == null ? null
                  : (prCap > 0 ? Math.min(prRaw, prCap) : prRaw);
                const prCapped = prRaw != null && prCap > 0 && prRisk > 0 && prRaw > prCap;
                // 빗숏 명목은 서버 bh_sleeve_notional (업숏과 같은 base/step).
                // risk/tail 을 여기서 다시 곱하면 yaml 2.55/6 이 남아 $532 로
                // 그려지고, 주문은 자본의 75% 다.
                const bhNotl = bh?.notional_usdt;
                // 쌍숏(dual_short)은 레인박스에서 뺐다 (2026-09-04 유저): 단독
                // 레인이 아니라 업숏·빗숏 종속이다. 진입/스킵 피드 행·수동청산·
                // open_ds 헬스체크는 유지, 사이징 칸만 제거.
                const timingByLane = Object.fromEntries(
                  (data.lane_timing?.lanes ?? []).map((x) => [x.lane, x]),
                );
                const pnlOf = (lane: 'upbit' | 'bh' | 'liq' | 'cand_long' | 'pump_ride' | 'dual_short' | 'gyeop_long') => (
                  lane === 'upbit'
                    ? (data.realized_by_lane?.upbit ?? data.realized_usdt ?? 0)
                    : (data.realized_by_lane?.[lane] ?? 0)
                );
                const pnlFmt = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;
                const pnlCol = (v: number) => (v >= 0 ? '#3fb950' : '#f85149');
                const liveOf = (id: string): boolean | undefined =>
                  data.lane_settings?.lanes?.find((l) => l.id === id)?.live;
                // 레인 순서는 손익·레짐 칸과 같다.
                // inactive: DB lane_settings 가 있으면 그걸 우선(설정 토글 정본).
                // 없으면 config/bh_stats 폴백. halted 는 전 레인 킬.
                // 전승(wr100) 명목300%. $본계/$전승.
                // 업숏 본계=150% → ×2. 빗숏 API notional 은 이미 seg≥30+캡(~150%)
                // (모니터 bh_sleeve_notional) 이라 ×2 — 75% 가정 ×4 하면 ~600%로 과장.
                const notlBookWr100 = (
                  bookUsd: number | null | undefined,
                  wrMult: number,
                  note: string,
                ): { notl: string; title: string } => {
                  if (bookUsd == null || !Number.isFinite(bookUsd)) {
                    return { notl: '–', title: '' };
                  }
                  const wr = bookUsd * wrMult;
                  return {
                    notl: `$${bookUsd.toFixed(0)}/$${wr.toFixed(0)}`,
                    title: `본계 $${bookUsd.toFixed(0)} · 전승(wr100) $${wr.toFixed(0)} (${note})`,
                  };
                };
                const upNotl = notlBookWr100(
                  risk.per_trade_notional_usdt, 2, u('업숏 명목150%→300%'),
                );
                const bhNotlSplit = notlBookWr100(
                  bh?.enabled ? bhNotl ?? undefined : undefined,
                  2, u('빗숏 본계~150%(seg≥30·캡)→300%'),
                );
                const rows: {
                  key: string; c: string; name: string;
                  open: number; k: number | string;
                  wins?: number; n?: number; pnl: number;
                  lev: string; notl: string; notlTitle?: string;
                  inactive?: boolean;
                  holdTitle?: string;
                  tier?: 'v2' | 'v3';
                }[] = [
                  { key: 'upbit', c: EXCHANGE_COLOR.upbit, name: t('binance.lane.upbit'),
                    open: data.open_count ?? 0, k: cfg.k ?? '–',
                    wins: data.wins ?? 0, n: data.n_settled ?? 0,
                    pnl: pnlOf('upbit'),
                    lev: levTxt,
                    notl: upNotl.notl,
                    notlTitle: upNotl.title,
                    // upbit_live 는 bh_newhigh_live / cand_long_live 와 같은
                    // 주문 스위치(2026-08-26). 없으면 예전처럼 halted 만.
                    inactive: (liveOf('upbit') ?? cfg.upbit_live) === false
                              || data.halted === true },
                  // 빗썸은 2026-08-23 되돌림 진입으로 재개(bh_newhigh_live: true).
                  // bh_newhigh_live 가 false 로 다시 꺼지면 취소선 + inactive.
                  { key: 'bh', c: EXCHANGE_COLOR.bithumb, name: t('binance.lane.bithumb'),
                    open: bh?.open ?? 0, k: bh?.k ?? '–',
                    wins: bh?.wins ?? 0, n: bh?.n_settled ?? 0,
                    pnl: pnlOf('bh'),
                    lev: bhLev,
                    notl: bhNotlSplit.notl,
                    notlTitle: bhNotlSplit.title,
                    inactive: (liveOf('bh') != null
                      ? liveOf('bh') === false
                      : (bh?.enabled === true && bh.live === false))
                              || data.halted === true },
                  { key: 'liq', c: LIQ_COLOR, name: t('binance.lane.liq'),
                    // v1 은 2026-09-09 하드 OFF(`liq_v1_enabled: false`) -- v1 칸은
                    // 항상 0 이라 표시할 이유가 없다. v2(사다리) 슬리브/k 만 그린다.
                    // v1 이 되살아나면(정책 변경) 이 칸이 v1 을 다시 못 보여준다는
                    // 뜻이니 그때 다시 손볼 것 (2026-09-12 유저 지시로 제거).
                    open: (() => {
                      const v2Open = (data.open_liq ?? [])
                        .filter((p) => p.engine === 'v2');
                      return data.liq_stats?.ladder_v2?.open ?? v2Open.length;
                    })(),
                    // 보유/k = 동시 슬리브 open/k. 조각(max_legs)은 옆 칼럼.
                    // (09-13 k=0 때 분모를 max_legs 로 둔 잔재를 09-19 에 분리.)
                    k: (() => {
                      const liqLane = data.lane_settings?.lanes?.find(
                        (l) => l.id === 'liq',
                      );
                      const kk = data.liq_stats?.ladder_v2?.k
                        ?? liqLane?.v2?.k
                        ?? liqLane?.k;
                      return kk != null && kk > 0 ? kk : (kk === 0 ? 0 : '–');
                    })(),
                    // 승/정산은 **전 기간** 원장(liq_lane_stats) -- 다른 레인과 같은
                    // 기준. liq_report 는 7일 창이라 여기 쓰면 9/21 처럼 작게 보였다
                    // (2026-09-04 유저). 옛 페이로드는 liq_lane_stats 가 없어 폴백.
                    wins: data.liq_lane_stats?.wins ?? r?.wins ?? 0,
                    n: data.liq_lane_stats?.n_settled ?? r?.n_settled ?? 0,
                    pnl: pnlOf('liq'),
                    lev: levTxt,
                    notl: liqNotl != null
                      ? `$${liqNotl.toFixed(0)}` : '–',
                    // enabled=마스터(감시), live=주문(2026-08-27). live 키가
                    // 아직 안 내려온 옛 페이로드는 enabled 만으로 판정(폴백).
                    inactive: cfg.liq_cascade_enabled !== true
                              || (liveOf('liq') ?? cfg.liq_cascade_live) === false
                              || data.halted === true },
                  { key: 'pump_ride', c: PUMP_RIDE_COLOR, name: t('binance.lane.pumpRide'),
                    open: (data.open_pr ?? []).length, k: cfg.pump_ride_k ?? 1,
                    wins: pr?.wins ?? 0, n: pr?.n_settled ?? 0,
                    pnl: pnlOf('pump_ride'),
                    // lev 칸은 레버만 (2026-08-31 유저). 종전엔 확인문턱·seg 구간·
                    // 트레일·거래소까지 한 줄에 붙여 "5x · +7%hyb · [10,20)/≥40 ·
                    // tr2/15 · 빗" 이 나왔는데, 규칙은 필터 카드가 이미 보여준다.
                    lev: `${cfg.pump_ride_leverage ?? 5}x`,
                    notl: prNotl != null
                      ? `$${prNotl.toFixed(0)}${prCapped ? u(' (상한)') : ''}` : '–',
                    inactive: !(cfg.pump_ride_enabled === true
                                && (liveOf('pump_ride') ?? cfg.pump_ride_live) === true)
                              || data.halted === true },
                  // 겹롱. 사이징은 업숏. 신호등은 gyeop_long_live.
                  { key: 'gyeop_long', c: PUMP_RIDE_COLOR, name: u('겹롱'),
                    open: (data.open_gl ?? []).length,
                    k: cfg.gyeop_long_k ?? cfg.k ?? '–',
                    wins: data.gyeop_long_stats?.wins,
                    n: data.gyeop_long_stats
                      ? (data.gyeop_long_stats.n_settled ?? 0)
                      : undefined,
                    pnl: pnlOf('gyeop_long'),
                    lev: levTxt,
                    notl: risk.per_trade_notional_usdt != null
                      ? `$${risk.per_trade_notional_usdt.toFixed(0)}` : '–',
                    inactive: cfg.gyeop_long_live !== true || data.halted === true },
                ];
                const strike = (off?: boolean): React.CSSProperties => ({
                  textDecoration: off ? 'line-through' : undefined,
                  color: off ? binanceMuted : undefined,
                });
                const lv3stat = data.liq_stats?.ladder_v3 ?? null;
                const laneRows = rows.flatMap((x) => {
                  if (x.key !== 'liq') return [x];
                  // 합(liq_lane_stats)에서 v3 몫을 떼 v2 행에 둔다. 안 떼면 v3
                  // 손익이 v2 행에 섞이고 v3 행은 늘 "–" 였다 (2026-10-27 AIN).
                  const v3s = data.liq_lane_stats?.v3 ?? null;
                  const v2row = {
                    ...x, name: u('청캐 v2'), tier: 'v2' as const,
                    ...(v3s ? {
                      wins: (x.wins ?? 0) - (v3s.wins ?? 0),
                      n: (x.n ?? 0) - (v3s.n_settled ?? 0),
                      pnl: (x.pnl ?? 0) - (v3s.realized_usdt ?? 0),
                    } : {}),
                  };
                  if (lv3stat == null) return [v2row];
                  const v3off = x.inactive === true || lv3stat.live !== true;
                  return [
                    v2row,
                    {
                      ...x,
                      key: 'liq_v3',
                      name: u('청캐 v3'),
                      tier: 'v3' as const,
                      pnl: v3s?.realized_usdt ?? 0,
                      wins: v3s?.wins,
                      n: v3s?.n_settled,
                      // v3 는 주문에 이 레버를 그대로 건다. `~` 는 체결 표본이 없을 때
                      // 요청값이라는 표시라, 여기엔 붙이면 추정치처럼 읽힌다.
                      lev: lv3stat.leverage != null ? `${lv3stat.leverage}x` : '–',
                      notl: lv3stat.notional_usdt != null
                        ? `$${lv3stat.notional_usdt.toFixed(0)}` : '–',
                      inactive: v3off,
                    },
                  ];
                });
                const visibleRows = laneRows.filter(
                  (x) => !DASHBOARD_HIDDEN_LANE_KEYS.has(x.key),
                );
                return (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="binance-lanes-table">
                      <thead>
                        <tr>
                          <th>{t('binance.stat.lanes.col.lane')}</th>
                          <th>{t('binance.stat.lanes.col.status')}</th>
                          <th title={u("open/k. 청캐 v2·v3는 각 행.")}>
                            {t('binance.stat.lanes.col.hold')}
                          </th>
                          <th title={u("청캐 v2는 조각(n_legs/max_legs), v3는 단건(n/1). 다른 레인은 –.")}>
                            {t('binance.stat.lanes.col.legs')}
                          </th>
                          <th>{t('binance.stat.lanes.col.wins')}</th>
                          <th title={t('binance.stat.realizedPnl.title')}>
                            {t('binance.stat.realizedPnl')}
                          </th>
                          <th>{`${t('binance.stat.lanes.col.lev')} · ${t('binance.stat.lanes.col.notional')}`}</th>
                          <th>{t('binance.stat.lanes.col.timing')}</th>
                          <th title={t('binance.stat.lanes.col.stop.hint')}>
                            {t('binance.stat.lanes.col.stop')}
                          </th>
                          <th title={t('binance.stat.lanes.col.strategy.hint')}>
                            {t('binance.stat.lanes.col.strategy')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map((x) => {
                          const tm = timingByLane[x.key];
                          const off = x.inactive || tm?.live === false;
                          const winTxt = x.n == null
                            ? '–'
                            : `${x.wins ?? 0}/${x.n}${(x.n ?? 0) > 0 ? ` ${(100 * (x.wins ?? 0) / x.n).toFixed(0)}%` : ''}`;
                          // 고점(롤링 t_peak) · 진입 지연 · 진입 후 보유.
                          // 청산 레인은 앵커가 알림이 아니라 트리거라 고점 축이
                          // 정의되지 않는다 -- 대신 진입 뒤 최선까지의 분(t_best)을
                          // 첫 칸에 놓고 라벨을 title 로 붙인다(2026-08-19).
                          // 주문이 정지된 레인은 취소선.
                          const lt = tm?.liq_timing;
                          // 보유 칸은 시간만. 기본(연장) — 연장 없으면 기본만.
                          const holdCell = (base?: number | null, ext?: number | null) => {
                            if (base == null || !Number.isFinite(Number(base))) return '–';
                            const b = Number(base);
                            if (ext == null || !Number.isFinite(Number(ext))) return `${b}`;
                            return `${b}(${Number(ext)})`;
                          };
                          const lv2 = x.tier === 'v2' ? (data.liq_stats?.ladder_v2 ?? null) : null;
                          const lv3 = x.tier === 'v3' ? (data.liq_stats?.ladder_v3 ?? null) : null;
                          const timingTxt = x.tier === 'v3'
                            ? holdCell(lv3?.hold_min, lv3?.extend_min)
                            : x.tier === 'v2'
                              ? holdCell(lv2?.exit_min, lv2?.extend_min)
                            : x.key === 'upbit'
                              // 저녁/산 — 둘 다 config 값 (스탑 칸과 같은 순서).
                              ? (cfg.mountain_hold_min != null
                                ? `${holdCell(cfg.hold_exit_min ?? cfg.hold_after_min)}/${holdCell(cfg.mountain_hold_min)}`
                                : holdCell(cfg.hold_exit_min ?? cfg.hold_after_min))
                              : x.key === 'bh'
                                ? (cfg.bh_dump_hold_min != null
                                  // 되돌림/급락(/익절2) — 전략·스탑 칸과 같은 순서.
                                  // 익절2 홀드는 되돌림과 같음(90).
                                  ? (cfg.bh_tp2_live !== false
                                    ? `${holdCell(cfg.bh_hold_after_min ?? cfg.bh_hold_exit_min)}/${Number(cfg.bh_dump_hold_min)}/${holdCell(cfg.bh_hold_after_min ?? cfg.bh_hold_exit_min)}`
                                    : `${holdCell(cfg.bh_hold_after_min ?? cfg.bh_hold_exit_min)}/${Number(cfg.bh_dump_hold_min)}`)
                                  : holdCell(cfg.bh_hold_after_min ?? cfg.bh_hold_exit_min))
                                : x.key === 'pump_ride'
                                  ? holdCell(cfg.pump_ride_hold_min)
                                  : x.key === 'gyeop_long'
                                    ? holdCell(5)
                                    : '–';
                          // 청산 레인 첫 칸의 뜻이 다르므로 그 사실을 손에 잡히게
                          // 적는다. 이게 없으면 같은 열의 두 숫자를 같은 축으로
                          // 읽는다.
                          const timingTitle = lt
                            ? [
                              lv2 ? [
                                u('청캐 v2(사다리). v1 은 OFF (2026-09-09).'),
                                `캐스케이드(${lv2.cascade_quiet_h ?? 6}h 무발동 경계) 누적 개별청산 `
                                  + `${lv2.min_cum_nliq ?? 100}건 이상이면 진입 · rung = 누적 // ${lv2.nliq_per_rung ?? 25}.`,
                                (lv2.drop_pct != null && lv2.drop_pct > 0
                                  ? `감시창 안에서 러닝고점 대비 −${lv2.drop_pct.toFixed(1)}% 꺾이면 첫 조각. `
                                  : `트리거가에 첫 조각. `)
                                  + `슬리브를 ${lv2.max_legs ?? 5}등분 — 신고가가 직전 조각 대비 +${(lv2.leg_step_pct ?? 6).toFixed(1)}% 뛰면 다음 조각(숏 평단을 끌어올려 되돌림 여유 확보). `
                                  + `슬리브 i 증거금% = 마진캡${lv2.margin_cap_pct != null ? ` ${lv2.margin_cap_pct.toFixed(0)}%` : ''} × ratio^i / Σratio^j`
                                  + `${lv2.sleeve_ratio != null ? ` (ratio ${lv2.sleeve_ratio.toFixed(2)})` : ''}.`,
                                (lv2.deep_mult != null && lv2.deep_mult > 1
                                  ? `깊은꺾임: 진입가가 캐스케이드 고점 대비 ${(lv2.deep_cut_pct ?? -12).toFixed(0)}% 이상 아래면(펌프 끝) 그 슬리브만 ×${lv2.deep_mult.toFixed(2)}. off_casc ≤ −12% = 8~9월 8개 캐스케이드 승률 97%·BTR 참사는 −12~−5 밴드라 이 밖. 못 재면 1×(안전).`
                                  : null),
                                `트리거로부터 ${lv2.exit_min}분에 청산, 그 시점 손실이면 ${lv2.extend_min}분까지 연장`,
                                `(연장 중 +${lv2.recover_pct.toFixed(1)}% 회복하면 조기청산). 스탑 없음.`,
                                (lv2.k != null && lv2.k > 0
                                  ? `k=${lv2.k} · 같은 종목 ${lv2.k}개까지 겹침.`
                                  : `k=0 · 동시 상한 없음(캡·실조각만).`),
                                (lv2.peak_confirm
                                  ? `고점확인 ON`
                                  : `고점확인 OFF`
                                    + (lv2.peak_boost_mult != null && lv2.peak_boost_mult > 1
                                      ? ` · 고점통과 ×${Number(lv2.peak_boost_mult).toFixed(1)}`
                                      : ''))
                                  + ` · 지금 감시 ${lv2.pending} · 보유 ${lv2.open}.`,
                                u('자리 없으면 같은종목 열린 책에 조각을 붙인다(숏 평단이 더 좋은 쪽).'),
                              ].join('\n') : null,
                              u('첫 칸(최선)과 셋째 칸(보유)은 **다른 축**이다 —')
                                + u(' 어긋나는 것이 정상이다. 최선은 사후 최선 시점이라')
                                + u(' 그 시각에 청산하는 규칙으로는 그 값을 못 얻고,')
                                + u(' 보유는 슬롯·비용까지 넣은 격자 총합의 답이다')
                                + u('(홀드 240분 = 자리 4시간 점유, k=3 이면 하루')
                                + u(' 18칸인데 트리거는 하루 240건).'),
                              `최선 탈출 ↓+${lt.t_best?.med ?? '–'}분`
                                + ` (CI ${lt.t_best?.ci_lo ?? '–'}~${lt.t_best?.ci_hi ?? '–'})`,
                              `최대 역행 ↑+${lt.t_worst?.med ?? '–'}분`
                                + ` (CI ${lt.t_worst?.ci_lo ?? '–'}~${lt.t_worst?.ci_hi ?? '–'})`,
                              `앵커=트리거+1초 · 지평 ${lt.horizon_min}분 · 최근 ${lt.n}건`,
                              lt.note,
                              lt.abort_before_worst
                                ? `abort 확인 ${lt.abort_check_min}분 -- 최대 역행보다 먼저 판정한다`
                                : null,
                              u('이 축은 u("움직였나")만 답한다 — 규칙 값은 지금 라이브 v2')
                              + `(누적청산 ≥${lv2?.min_cum_nliq ?? 100} · 조각 ${lv2?.max_legs ?? 6}`
                              + ` · 청산${lv2?.exit_min ?? 240}/연장${lv2?.extend_min ?? 480} · 스탑 없음)이다.`,
                              // 2026-08-23: 옛 hold_grid 는 되돌림을 모른 채
                              // "트리거 즉시 진입"을 가정해 라이브와 다른 규칙을
                              // 쟀다(같은 창에서 격자 545건 진입 vs 라이브 3건).
                              // 그래서 권고를 껐다 — 그 표는 관측으로만 읽는다.
                            ].filter(Boolean).join('\n')
                            : undefined;
                          // 실거래 칸(live_ledger 성적)은 뗐다 — 그 자리에 스탑 (2026-10-28 유저).
                          const stopCell = laneStopCell(x.key, x.tier, cfg);
                          const stratCell = laneStrategyCell(x.key, x.tier, cfg);
                          return (
                            <React.Fragment key={x.key}>
                            <tr>
                              <td>
                                <span style={{ color: x.c }}>
                                  {x.name}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span
                                  title={off ? t('binance.lane.inactive') : t('binance.lane.active')}
                                  style={{
                                    display: 'inline-block',
                                    width: 6, height: 6, borderRadius: '50%',
                                    verticalAlign: 'middle',
                                    background: off ? '#f85149' : '#3fb950',
                                  }}
                                />
                              </td>
                              {(() => {
                                // 청캐: v2 · v3 를 개행 (2026-10-07).
                                // 업숏·빗숏은 레인마다 open/k (2026-10-08).
                                if (x.tier === 'v2' || x.tier === 'v3') {
                                    const ladder = x.tier === 'v3'
                                      ? data.liq_stats?.ladder_v3
                                      : data.liq_stats?.ladder_v2;
                                    const liqLane = data.lane_settings?.lanes?.find(
                                      (l) => l.id === 'liq',
                                    );
                                    const openN = ladder?.open
                                      ?? (data.open_liq ?? [])
                                        .filter((p) => p.engine === x.tier).length;
                                    const kk = x.tier === 'v3'
                                      ? (data.liq_stats?.ladder_v3?.k ?? '–')
                                      : (data.liq_stats?.ladder_v2?.k
                                        ?? liqLane?.v2?.k
                                        ?? liqLane?.k
                                        ?? x.k);
                                    const line = `${openN}/${kk}`;
                                    return (
                                      <td title={x.holdTitle ?? `${x.tier} ${line}`}>
                                        {line}
                                      </td>
                                    );
                                  }
                                return (
                                  <td title={x.holdTitle}>
                                    {x.open}/{x.k}
                                  </td>
                                );
                              })()}
                              {(() => {
                                // 청캐 전용 조각 칼럼. 보유/k 와 축을 분리
                                // (동시 슬리브 vs 슬리브당 max_legs).
                                // v2·v3 개행 (2026-10-07).
                                if (x.tier !== 'v2' && x.tier !== 'v3') {
                                  return (
                                    <td style={{ color: binanceMuted }}>–</td>
                                  );
                                }
                                const liqLane = data.lane_settings?.lanes?.find(
                                  (l) => l.id === 'liq',
                                );
                                const maxLegs = liqLane?.v2?.max_legs
                                  ?? data.liq_stats?.ladder_v2?.max_legs
                                  ?? null;
                                const v2Open = (data.open_liq ?? [])
                                  .filter((p) => p.engine === 'v2');
                                let v2legsTxt: string;
                                let v2legsTitle: string;
                                if (maxLegs == null) {
                                  v2legsTxt = '–';
                                  v2legsTitle = u('v2 조각 상한 미상');
                                } else if (v2Open.length === 0) {
                                  // 열린 슬리브 없으면 분모만 단독으로 두면 –/6
                                  // 처럼 깨져 보인다. 상한은 title 에만.
                                  v2legsTxt = '–';
                                  v2legsTitle = `v2 슬리브당 조각 상한 ${maxLegs} (열린 슬리브 없음)`;
                                } else {
                                  const parts = v2Open.map(
                                    (p) => (p.n_legs != null ? String(p.n_legs) : '?'),
                                  );
                                  v2legsTxt = `${parts.join('+')}/${maxLegs}`;
                                  v2legsTitle = v2Open.map((p) => (
                                    `v2 ${p.perp ?? '?'} ${p.n_legs ?? '?'}/${maxLegs}`
                                  )).join(' · ');
                                }
                                if (x.tier === 'v2') {
                                  return (
                                    <td title={v2legsTitle}>{v2legsTxt}</td>
                                  );
                                }
                                const lv3 = data.liq_stats?.ladder_v3;
                                const v3Max = lv3?.max_legs ?? 1;
                                const v3Open = (data.open_liq ?? [])
                                  .filter((p) => p.engine === 'v3');
                                let v3legsTxt: string;
                                let v3legsTitle: string;
                                if (v3Open.length === 0) {
                                  v3legsTxt = '–';
                                  v3legsTitle = `v3 단건 (열린 책 없음 · 상한 ${v3Max})`;
                                } else {
                                  const parts = v3Open.map(
                                    (p) => (p.n_legs != null ? String(p.n_legs) : '1'),
                                  );
                                  v3legsTxt = `${parts.join('+')}/${v3Max}`;
                                  v3legsTitle = v3Open.map((p) => (
                                    `v3 ${p.perp ?? '?'} ${p.n_legs ?? 1}/${v3Max}`
                                  )).join(' · ');
                                }
                                return (
                                  <td title={v3legsTitle}>{v3legsTxt}</td>
                                );
                              })()}
                              <td style={{ color: x.n == null ? binanceMuted : undefined }}>{winTxt}</td>
                              <td>
                                {x.n == null ? (
                                  <span style={{ color: binanceMuted }}>–</span>
                                ) : (
                                  <span style={{ color: pnlCol(x.pnl) }}>{pnlFmt(x.pnl)}</span>
                                )}
                              </td>
                              <td
                                style={{ color: binanceMuted, ...strike(off) }}
                                title={x.notlTitle || undefined}
                              >
                                {[x.lev, x.notl].filter((v) => v && v !== '–').join(' · ') || '–'}
                              </td>
                              <td
                                style={{
                                  ...strike(off),
                                  whiteSpace: 'pre-line',
                                  lineHeight: 1.2,
                                }}
                                title={timingTitle}
                              >
                                {timingTxt}
                              </td>
                              <td
                                style={{ color: stopCell.txt === '–' ? binanceMuted : undefined, ...strike(off) }}
                                title={stopCell.title}
                              >
                                {stopCell.txt}
                              </td>
                              <td
                                style={{ color: stratCell.txt === '–' ? binanceMuted : undefined, ...strike(off) }}
                                title={stratCell.title}
                              >
                                {stratCell.txt}
                              </td>
                            </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>

          {/*
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 8,
              marginBottom: 12,
              padding: '8px 10px',
              borderRadius: 6,
              background: 'rgba(0,0,0,0.2)',
              fontSize: '0.72rem',
            }}
          >
            <Stat label={u("스탑")} value={cfg.stop_pct != null ? `${cfg.stop_pct}%` : '–'} />
            <Stat
              label={u("보유")}
              value={
                cfg.hold_after_min != null
                  ? (cfg.watch_min != null ? `감시${cfg.watch_min}·보유${cfg.hold_after_min}분` : `${cfg.hold_after_min}분`)
                  : cfg.hold_exit_min != null
                    ? `${cfg.hold_exit_min}분`
                    : '–'
              }
            />
            <Stat label={u("킬스위치")} value={cfg.kill_drawdown_pct != null ? `-${cfg.kill_drawdown_pct}%` : '–'} />
            <Stat
              label={u("킬 floor")}
              value={risk.kill_floor_usdt != null ? `${risk.kill_floor_usdt.toFixed(1)} USDT` : '–'}
            />
            <Stat
              label={u("킬 여유")}
              value={risk.margin_to_kill_usdt != null ? `${risk.margin_to_kill_usdt.toFixed(1)} USDT` : '–'}
              color={(risk.margin_to_kill_usdt ?? 0) < 80 ? '#f85149' : '#f5f0e6'}
            />
            <Stat
              label="peak"
              value={risk.peak_equity_live != null ? `${risk.peak_equity_live.toFixed(1)} USDT` : '–'}
            />
            <Stat
              label={u("필터")}
              value={[
                cfg.min_runup_pct != null ? `runup≥${cfg.min_runup_pct}%` : null,
                cfg.max_segment_pct != null ? `seg<${cfg.max_segment_pct}%` : null,
                cfg.max_btc60m_pct != null ? `btc60m<${cfg.max_btc60m_pct}%` : null,
              ].filter(Boolean).join(' · ') || '–'}
            />
            <Stat
              label="sizing"
              value={cfg.auto_capital ? 'auto (wallet)' : 'fixed'}
            />
          </div>
          */}

          {openLong.length > 0 && (
            <div style={{ marginBottom: 12, fontSize: '0.75rem', color: '#f5f0e6' }}>
              <div style={{ color: '#3fb950', fontWeight: 600, marginBottom: 4 }}>
                보유 (롱 · abort→long)
              </div>
              {openLong.map((p) => (
                <div key={`long-${p.lane ?? 'upbit'}-${p.symbol}-${p.perp}`}>
                  {p.kr_name || p.symbol} {p.perp}
                  {p.lane === 'bh_sleeve' ? u(' [빗숏]') : ''}
                  {p.abort_leg ? ` [${p.abort_leg}]` : ''}
                  {' '}entry={p.entry_px} qty={p.qty} peak={p.peak_px} stop={p.stop_price}
                  {p.notional != null ? ` notional=$${p.notional.toFixed(2)}` : ''}
                </div>
              ))}
            </div>
          )}

          {/* 2026-08-14: u("진입 대기") 블록 제거(유저 지시). 표 밖에 뜨는 별도
              목록이라 같은 알림이 두 군데에 보였다 -- 그 정보는 표 안의 행이
              WATCH 배지 + 예정 시각으로 이미 말한다(TradeFlowCell 의 preEntryHm).
              백엔드 payload(data.pending)는 그대로 둔다: 지우면 그게 유일한
              출처인 다른 소비자가 조용히 깨진다. */}

          {/*
          {watching.length > 0 && (
            <div style={{ marginBottom: 12, fontSize: '0.75rem', color: '#f5f0e6' }}>
              <div style={{ color: '#a371f7', fontWeight: 600, marginBottom: 4 }}>
                관찰 중 (OI 스퀴즈 후보 · 숏 보류)
              </div>
              {watching.map((w) => (
                <div key={`watch-${w.symbol}-${w.perp}`}>
                  {w.kr_name || w.symbol} {w.perp} OI+{w.oi_chg_pct}%
                </div>
              ))}
            </div>
          )}

          {(longStats.n_long_entries ?? 0) > 0 && (
            <div style={{ marginBottom: 12, fontSize: '0.72rem', color: binanceMuted }}>
              롱 전환 실험: 관찰 {longStats.n_watch_oi_squeeze ?? 0}건 · 진입 {longStats.n_long_entries ?? 0}건
              · 정산 {longStats.n_long_settled ?? 0}건
              · 누적 {(longStats.sum_long_net_pct ?? 0) >= 0 ? '+' : ''}{longStats.sum_long_net_pct ?? 0}%
            </div>
          )}
          */}

          <div className="binance-alert-section-head">
            <div className="binance-alert-section-title">{t('binance.tradeHistory.title')}</div>
            <div className="binance-alert-switch-group">
            <button
              type="button"
              role="switch"
              aria-checked={enteredOnly}
              onClick={toggleEnteredOnly}
              className={`binance-alert-switch${enteredOnly ? ' is-on' : ''}`}
              title={enteredHiddenCount > 0
                ? t('binance.tradeHistory.enteredOnly.title.hidden', { n: enteredHiddenCount })
                : t('binance.tradeHistory.enteredOnly.title.all')}
            >
              <span className="binance-alert-switch-track" aria-hidden="true">
                <span className="binance-alert-switch-knob" />
              </span>
              {t('binance.tradeHistory.enteredOnly')}
            </button>
            <button
              type="button"
              role="switch"
              aria-checked={neededOnly}
              onClick={toggleNeededOnly}
              className={`binance-alert-switch${neededOnly ? ' is-on' : ''}`}
              title={hiddenCount > 0
                ? t('binance.tradeHistory.neededOnly.title.hidden', { n: hiddenCount })
                : t('binance.tradeHistory.neededOnly.title.all')}
            >
              <span className="binance-alert-switch-track" aria-hidden="true">
                <span className="binance-alert-switch-knob" />
              </span>
              {t('binance.tradeHistory.neededOnly')}
            </button>
            </div>
          </div>
          <div className="binance-alert-table-wrap">
            <table key={locale} className="binance-alert-table" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              {/* % 섞이면 table-layout:fixed 가 전체를 찌그려 차트 120px 이
                  예전 12% 와 비슷해지고 Detail 빈칸만 커진다. 전부 px. */}
              <colgroup>
                <col className="binance-alert-col-symbol" />
                <col className="binance-alert-col-exchange" />
                <col className="binance-alert-col-alert-type" />
                <col className="binance-alert-col-status" />
                <col className="binance-alert-col-detail" />
                <col className="binance-alert-col-sizing" />
                <col className="binance-alert-col-pnl" />
                <col className="binance-alert-col-chart" />
              </colgroup>
              <thead>
                <tr>
                  <th className="binance-alert-col-symbol" style={th}>{t('binance.table.header.symbol')}</th>
                  <th className="binance-alert-col-exchange" style={th} title={t('binance.table.header.lane.title')}>L</th>
                  <th className="binance-alert-col-alert-type" style={th}>{t('binance.table.header.alert')}</th>
                  <th className="binance-alert-col-status" style={th}>{t('binance.table.header.status')}</th>
                  <th className="binance-alert-col-detail" style={th}>{t('binance.table.header.detail')}</th>
                  <th className="binance-alert-col-sizing" style={th}>{t('binance.table.header.sizing')}</th>
                  <th className="binance-alert-col-pnl" style={th}>{t('binance.table.header.pnl')}</th>
                  <th className="binance-alert-col-chart" style={th}>{t('binance.table.header.chart')}</th>
                </tr>
              </thead>
              <tbody>
                {alertRows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ ...td, color: binanceMuted, textAlign: 'center' }}>
                      {t('binance.table.empty')}
                    </td>
                  </tr>
                )}
                {alertRows.map((row) => {
                  const profit = (row.entry?.action === 'addon_short'
                    && row.entry?.mode === 'linear_rung')
                    // 본진입 행과 같은 settle 을 공유한다 — PnL 칸에 두 번 찍으면
                    // 합산처럼 읽힌다. 차트·OPEN 동결만 쓰고 금액은 본진입에만.
                    ? null
                    : rowProfit(row.exit, row.entry);
                  const skipPnl = (cfLive && profit == null) ? rowSkipPnl(row) : null;
                  const longProfit = row.longLeg?.exit ? rowProfit(row.longLeg.exit) : null;
                  const abortCf = cfLive ? (row.entry?.abort_cf ?? null) : null;
                  const isBhLane = abortCf?.lane === 'bh_sleeve'
                    || skipPnl?.exchange === 'bh_sleeve'
                    || skipPnl?.exchange === 'bithumb'
                    || rowLane(row) === 'bh';
                  const cfTiming = resolveCfTiming(
                    data?.config, isBhLane, skipPnl, abortCf, rowLane(row),
                  );
                  const cfHeldMinutes = skipPnl ? null : cfTiming.hold;
                  const skipHeldMinutes = skipPnl ? cfTiming.hold : null;
                  const skipWatchMinutes = skipPnl ? cfTiming.watch : null;
                  const isOpen = isOpenRow(row, openKeys);
                  // 행의 레인/심볼/perp 로 청산 대상을 찾는다 (isOpenRow 와 같은 키).
                  const rowLaneName = rowLane(row);
                  const rowOpenKey = openPositionKey(
                    rowLaneName,
                    (rowLaneName === 'liq' || rowLaneName === 'gl')
                      ? undefined : (row.alert.symbol || row.entry?.symbol),
                    row.alert.perp,
                  );
                  const optClose = optimisticClose.get(row.key);
                  const serverClose = closeTargets.get(rowOpenKey);
                  const closeTarget = serverClose
                    ?? (optClose ? { lane: optClose.lane, sid: optClose.sid } : undefined);
                  const entryTarget = manualEntryTarget(
                    row, data?.config ?? null,
                  );
                  // 정산된 행에는 팔 것이 없다. 수동 진입 직후는
                  // (1) optimistic sid 또는 (2) open_* 는 왔는데 entry 이벤트가
                  // 행에 아직 안 붙은 경우에도 청산 버튼을 연다.
                  // PnL 칸의 수동 진입/청산 버튼은 화면에서 뺐다 (2026-08-30
                  // 유저 지시). 판정·API 는 그대로 두고 표시만 끈다 — 되살릴
                  // 때 이 상수만 되돌리면 된다.
                  const showClose = MANUAL_BUTTONS_IN_PNL
                    && canClose && !!closeTarget && row.exit == null
                    && (isOpen
                      || !!optClose
                      || (!!serverClose && openKeys.has(rowOpenKey) && !!entryTarget));
                  const showEntry = MANUAL_BUTTONS_IN_PNL
                    && canEntry && !isOpen && !showClose && !!entryTarget;
                  return (
                    <tr key={row.key} className={isOpen ? 'broker-row-open' : undefined}>
                      <td
                        className="binance-alert-col-symbol"
                        style={td}
                        title={[
                          displayCoinName({
                            kr_name: row.alert.kr_name,
                            symbol: row.alert.symbol,
                            perp: row.alert.perp,
                          }),
                          row.alert.symbol,
                          row.alert.perp,
                        ].filter(Boolean).join(' · ') || undefined}
                      >
                        {renderSymbolCell(row.alert)}
                      </td>
                      <td className="binance-alert-col-exchange" style={td}>{renderExchangeCell(row)}</td>
                      <td className="binance-alert-col-alert-type" style={td}>{renderAlertTypeCell(row.alert)}</td>
                      <td className="binance-alert-col-status" style={td}>
                        <TradeFlowCell
                          row={row}
                          isOpen={isOpen}
                          entryDelayMin={data?.config?.entry_delay_min}
                          cfgLeverage={data?.config?.leverage ?? null}
                        />
                      </td>
                      {/* title = 클램프에 잘린 뒤쪽까지 전문 (2026-08-31 유저요구).
                          Symbol 칸과 같은 네이티브 hover 툴팁이다. */}
                      <td
                        className="binance-alert-col-detail"
                        style={td}
                        title={rowDetailTitle(row)}
                      >
                        {rowDetail(row)}
                      </td>
                      {/* row 에 addon 이 실려 있으면 rowDetail 이 그 다리의 상세도 낸다
                          (문턱이 심볼마다 다르므로 근거가 거기에만 있다). */}
                      <td className="binance-alert-col-sizing" style={td}>
                        <SizingCell
                          row={row}
                          riskFallback={
                            // 청캐는 예전 liq_entry 에 risk_pct 가 없었다. 레인 yaml 로 채운다.
                            rowLaneName === 'liq'
                              ? (data?.config?.liq_cascade_risk_pct ?? null)
                              : null
                          }
                          riskOverride={
                            // 업숏 옛 entry 는 risk_per_stop_pct=15 를 찍었다.
                            // 실제 사이징 SoT 는 DB upbit_risk_pct (2026-09-07).
                            rowLaneName === 'upbit'
                              && (data?.config?.upbit_risk_pct ?? 0) > 0
                              ? data?.config?.upbit_risk_pct ?? null
                              : null
                          }
                        />
                      </td>
                      <td
                        className="binance-alert-col-pnl"
                        style={{
                          ...td,
                          // 실현손익은 초록/빨강, 반사실은 보라 한 가지. 부호는 숫자가
                          // 들고 있으므로 색은 "진짜 돈이냐"만 구분한다.
                          color: profit != null
                            ? (profit.usdt >= 0 ? '#3fb950' : '#f85149')
                            : skipPnl ? COUNTERFACTUAL_COLOR : binanceMuted,
                        }}
                        title={skipPnl
                          ? t('binance.skipPnl.title', {
                            reason: skipPnl.skip_reason ?? '',
                            bucket: skipPnl.bucket ?? '',
                            lane: skipPnl.exchange ?? '',
                          })
                          : undefined}
                      >
                        {/* 수동 청산 버튼은 **PnL 자리를 대신 쓴다** (유저요구
                            2026-08-17). 열려 있는 행의 PnL 은 아직 '-' 이라 둘을
                            같이 두면 빈 값과 버튼이 나란히 서서 자리만 먹는다.
                            정산되면 자연히 실현손익이 그 자리에 들어온다. */}
                        {showClose ? (
                          <LiqCloseButton
                            lane={closeTarget!.lane}
                            sid={closeTarget!.sid}
                            perp={row.alert.perp}
                            onDone={() => {
                              setOptimisticClose((prev) => {
                                if (!prev.has(row.key)) return prev;
                                const next = new Map(prev);
                                next.delete(row.key);
                                return next;
                              });
                              load();
                            }}
                          />
                        ) : showEntry ? (
                          <ManualEntryButton
                            lane={entryTarget!.lane}
                            sid={entryTarget!.sid}
                            perp={row.alert.perp}
                            rowKey={row.key}
                            onDone={load}
                            onEntered={onManualEntered}
                          />
                        ) : (
                          <>
                            <span className="binance-alert-pnl-full">
                              <PnlCell profit={profit} skipPnl={skipPnl} longProfit={longProfit} abortCf={abortCf} cfHeldMinutes={cfHeldMinutes} skipHeldMinutes={skipHeldMinutes} skipWatchMinutes={skipWatchMinutes} />
                            </span>
                            <span className="binance-alert-pnl-compact" style={{ display: 'none' }}>
                              <PnlCell profit={profit} skipPnl={skipPnl} longProfit={longProfit} abortCf={abortCf} cfHeldMinutes={cfHeldMinutes} skipHeldMinutes={skipHeldMinutes} skipWatchMinutes={skipWatchMinutes} compact />
                            </span>
                          </>
                        )}
                      </td>
                      <td
                        className="binance-alert-col-chart"
                        style={{
                          ...td,
                          // td 기본은 top — 차트만 행 세로가운데 (CSS middle 을
                          // inline 이 덮어쓰지 않게).
                          verticalAlign: 'middle',
                          borderBottomColor: CHART_RULE_COLOR,
                        }}
                      >
                        {/* 행마다 **그 거래가 진입 때 약속한** 홀드로 그린다.
                            백엔드가 chart_window_min(· hold_min)을 붙여준다.
                            현재 cfg 홀드로 폴백하면 재판정 때마다 전 행이 일괄
                            신장되므로, 스탬프/창이 없을 때만 쓴다. */}
                        {rowChart(
                          rowForChart(row),
                          plannedEntryHoldMin(row, data?.config),
                          liveTails[row.key] ?? null,
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(data.issues?.length ?? 0) > 0 && (
            <div
              style={{
                marginTop: 10,
                padding: '8px 10px',
                borderRadius: 6,
                background: 'rgba(248,81,73,0.08)',
                border: '1px solid #f8514944',
                fontSize: '0.68rem',
                color: '#ffa198',
                lineHeight: 1.4,
              }}
            >
              {data.issues!.map((x, i) => <div key={i}>{x}</div>)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BinanceAlertShortLivePanel;
